import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { validateAndFocusFirstError } from '../../utils/form-validation';
import { friendlyError } from '../../utils/friendly-error';
import { SubscriptionService } from '../../services/subscription.service';
import { SuperAdminStats, SuperAdminBusiness, SuperAdminOwner, SuperAdminRevenue, SubscriptionPayment, PendingOwnerSignup } from '../../models/sala.model';
import { DialogService } from '../../services/dialog.service';
import { NotificationService } from '../../services/notification.service';
import { PaginationComponent } from '../ui/pagination.component';
import { ResponsiveTableDirective } from '../../directives/responsive-table.directive';
import { currentFrontendBaseUrl } from '../../utils/frontend-base-url';
import { formatSpacesLabel, formatLongDate, formatLongDateTime } from '../../utils/format';
import { AdminReservationPaymentGroup, PayoutService } from '../../services/payout.service';
import { CommissionContract, PlatformSettings, PlatformSettingsService } from '../../services/platform-settings.service';
import type { CommissionChangeEvent } from '../../services/platform-settings.service';
import { NoveltyService } from '../../services/novelty.service';
import { ActionCooldownService } from '../../services/action-cooldown.service';
import { CountUpDirective } from '../../directives/count-up.directive';
import { UiSkeletonComponent } from '../ui/ui-skeleton.component';
import { ManagementRecordCardComponent } from '../ui/management-record-card.component';
import { DateTimePickerDirective } from '../../directives/date-time-picker.directive';
import { AdminTabsComponent, AdminTabOption } from '../ui/admin-tabs.component';
import { PlatformContactsAdminComponent } from '../ui/platform-contacts-admin.component';
import { PlatformSocialLinksAdminComponent } from '../ui/platform-social-links-admin.component';
import { PlatformLegalDocumentsAdminComponent } from '../ui/platform-legal-documents-admin.component';
import { UiStateComponent } from '../ui/ui-state.component';
import { CooldownBubbleDirective } from '../../directives/cooldown-bubble.directive';
import { BusinessContextModalComponent } from './business-context-modal.component';
import { UserAccountEditorComponent } from './user-account-editor.component';
import { AdminUserAccount } from '../../services/subscription.service';
import { AuthService } from '../../services/auth.service';
import { ThemeService } from '../../services/theme.service';
import { readableOnLight } from '../../utils/tone-color';


@Component({
  selector: 'app-superadmin',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, PaginationComponent, ResponsiveTableDirective, CountUpDirective, UiSkeletonComponent, ManagementRecordCardComponent, DateTimePickerDirective, AdminTabsComponent, PlatformContactsAdminComponent, PlatformSocialLinksAdminComponent, PlatformLegalDocumentsAdminComponent, UiStateComponent, CooldownBubbleDirective, BusinessContextModalComponent, UserAccountEditorComponent],
  template: `
    <div class="superadmin-container paginated-module">
      <h2 class="page-title" *ngIf="!embeddedNavigation">Panel Super Administrador</h2>
      <!-- Bug real corregido: este enlace estaba oculto tras la misma
           condición que el título de arriba (!embeddedNavigation) -- pero el
           acceso normal a esta pantalla es EMBEBIDO dentro del dashboard
           (ver dashboard.component.ts, [embeddedNavigation]="true"), así que
           el enlace nunca llegaba a mostrarse en el uso real, solo en la
           ruta standalone /super-admin (poco usada). Siempre visible ahora. -->
      <!-- Oculto temporalmente a pedido del usuario -- mismo criterio que
           la entrada del sidebar en admin-navigation.component.ts. -->
      <a *ngIf="false" routerLink="/reembolsos-plataforma" class="sa-refunds-link" [class.sa-refunds-link-embedded]="embeddedNavigation"><i class="bi bi-arrow-counterclockwise"></i> Reembolsos pendientes de revisión</a>

      <!-- Loading -->
      <div *ngIf="isLoading()" class="loading-state">
        <ng-container *ngIf="activeTab() === 'estadisticas'; else standardLoading">
          <div class="analytics-loading" aria-label="Cargando estadísticas globales">
            <div class="analytics-loading-head skeleton-pulse"></div>
            <div class="analytics-loading-kpis"><i class="skeleton-pulse" *ngFor="let item of [1,2,3,4]"></i></div>
            <div class="analytics-loading-panels"><i class="skeleton-pulse"></i><i class="skeleton-pulse"></i></div>
          </div>
        </ng-container>
        <ng-template #standardLoading><app-ui-skeleton variant="table" [count]="7" [columnCount]="5" label="Cargando datos de la plataforma"></app-ui-skeleton></ng-template>
      </div>

      <!-- Error -->
      <div *ngIf="errorMsg()" class="error-banner">{{ errorMsg() }}</div>

      <ng-container *ngIf="!isLoading() && !errorMsg()">

        <!-- Stats Cards -->
        <div class="overview-groups" *ngIf="activeTab() === 'overview' && stats() as s">
          <div class="overview-group">
            <div class="overview-group-header"><span class="eyebrow">PLATAFORMA</span><h4>Alcance general</h4></div>
            <div class="stats-grid">
              <div class="stat-card">
                <span class="stat-label">Negocios</span>
                <span class="stat-value">{{ s.totals.businesses }}</span>
              </div>
              <div class="stat-card">
                <span class="stat-label">Propietarios</span>
                <span class="stat-value">{{ s.totals.owners }}</span>
              </div>
              <div class="stat-card">
                <span class="stat-label">Clientes</span>
                <span class="stat-value">{{ s.totals.customers }}</span>
              </div>
            </div>
          </div>

          <div class="overview-group">
            <div class="overview-group-header"><span class="eyebrow">INGRESOS</span><h4>Dinero que mueve la plataforma</h4></div>
            <div class="stats-grid">
              <div class="stat-card highlight">
                <span class="stat-label">Ingresos totales</span>
                <span class="stat-value">\${{ s.revenue.total | number:'1.2-2' }}</span>
              </div>
              <div class="stat-card">
                <span class="stat-label">Ingresos suscripciones</span>
                <span class="stat-value">\${{ s.revenue.subscriptions | number:'1.2-2' }}</span>
              </div>
              <div class="stat-card">
                <span class="stat-label">Ingresos reservas</span>
                <span class="stat-value">\${{ s.revenue.reservations | number:'1.2-2' }}</span>
              </div>
              <div class="stat-card tone-success">
                <span class="stat-label">Comisión cobrada</span>
                <span class="stat-value">\${{ s.commission.collected | number:'1.2-2' }}</span>
              </div>
              <div class="stat-card" [class.tone-warning]="s.commission.pending > 0">
                <span class="stat-label">Comisión pendiente</span>
                <span class="stat-value">\${{ s.commission.pending | number:'1.2-2' }}</span>
              </div>
            </div>
          </div>

          <div class="overview-group">
            <div class="overview-group-header"><span class="eyebrow">SUSCRIPCIONES</span><h4>Estado del portafolio</h4></div>
            <div class="stats-grid">
              <div class="stat-card">
                <span class="stat-label">Activas</span>
                <span class="stat-value">{{ s.subscriptions.active }}</span>
              </div>
              <div class="stat-card">
                <span class="stat-label">En prueba</span>
                <span class="stat-value">{{ s.subscriptions.trial }}</span>
              </div>
              <div class="stat-card" [class.tone-warning]="s.subscriptions.expiring_soon > 0">
                <span class="stat-label">Por vencer (3 días)</span>
                <span class="stat-value">{{ s.subscriptions.expiring_soon }}</span>
              </div>
              <div class="stat-card">
                <span class="stat-label">Canceladas</span>
                <span class="stat-value">{{ s.subscriptions.cancelled }}</span>
              </div>
            </div>
          </div>
        </div>

        <section *ngIf="activeTab() === 'overview' && stats() as s" class="overview-visuals">
          <article class="overview-panel overview-wide"><header><div><span class="eyebrow">LECTURA RÁPIDA</span><h3>Salud de suscripciones</h3></div><span class="overview-caption">{{ s.subscriptions.active }} activas de {{ s.totals.subscriptions }}</span></header><div class="health-meter"><span [style.width.%]="subscriptionHealthPercent(s)"></span></div><div class="health-legend"><span><i class="dot-green"></i> Activas {{ s.subscriptions.active }}</span><span><i class="dot-blue"></i> En prueba {{ s.subscriptions.trial }}</span><span><i class="dot-yellow"></i> Pendientes {{ s.subscriptions.pending }}</span><span><i class="dot-red"></i> Canceladas/expiradas {{ s.subscriptions.cancelled + s.subscriptions.expired }}</span></div></article>
          <article class="overview-panel"><header><div><span class="eyebrow">MIX COMERCIAL</span><h3>Planes contratados</h3></div></header><div class="mini-bars"><div *ngFor="let item of s.subscriptions.byPlan | keyvalue"><span>{{ planLabel(item.key) }}</span><div><i [style.width.%]="planPercent(item.value, s.subscriptions.byPlan)"></i></div><strong>{{ item.value }}</strong></div></div></article>
          <article class="overview-panel overview-highlight"><span class="eyebrow">INGRESO RECURRENTE</span><strong>{{ s.revenue.subscriptions | currency:'USD':'symbol':'1.0-0' }}</strong><p>La métrica principal del negocio TuSala.</p></article>
        </section>

        <section *ngIf="activeTab() === 'estadisticas' && stats() as s" class="platform-statistics analytics-dashboard">
          <header class="analytics-hero"><div><span class="eyebrow"><i class="bi bi-activity"></i> INTELIGENCIA DE SUSCRIPCIONES</span><h3>Rendimiento global de TuSala</h3><p>Indicadores comerciales para evaluar crecimiento, adopción de planes y salud de la cartera.</p></div><div class="export-actions"><button class="secondary-btn" (click)="exportPlatformAnalyticsPdf()" [disabled]="cooldown.getSignal('sa-pdf-export')()" cooldownBubble data-cooldown-key="sa-pdf-export"><i class="bi bi-file-earmark-pdf"></i> Descargar PDF</button></div></header>
          <section class="global-statistics-filters" aria-label="Filtros de estadísticas globales"><div class="gsf-row"><label><span>Desde</span><div><i class="bi bi-calendar3"></i><input [(ngModel)]="globalStatsFrom" appDatePicker="date" placeholder="Seleccionar fecha"></div></label><label><span>Hasta</span><div><i class="bi bi-calendar3"></i><input [(ngModel)]="globalStatsTo" appDatePicker="date" placeholder="Seleccionar fecha"></div></label><button class="secondary-btn" type="button" (click)="resetGlobalStatsPeriod()" [disabled]="tabLoading()||cooldown.getSignal('sa-stats-filter')()" cooldownBubble data-cooldown-key="sa-stats-filter"><i class="bi bi-infinity"></i>Todos los tiempos</button><button class="action-btn" type="button" (click)="applyGlobalStatsPeriod()" [disabled]="tabLoading()||cooldown.getSignal('sa-stats-filter')()" cooldownBubble data-cooldown-key="sa-stats-filter"><i class="bi bi-funnel"></i>Aplicar filtro</button><span class="global-period-label">{{ globalStatsPeriodLabel() }}</span></div></section>
          <div class="analytics-kpis">
            <article class="analytics-kpi kpi-primary"><div class="kpi-icon"><i class="bi bi-gem"></i></div><div><span>Suscripciones activas</span><strong [appCountUp]="s.subscriptions.active"></strong><small><b [appCountUp]="subscriptionHealthPercent(s)" countUpFormat="percent"></b> de la cartera total</small></div></article>
            <article class="analytics-kpi"><div class="kpi-icon"><i class="bi bi-person-badge"></i></div><div><span>Propietarios registrados</span><strong [appCountUp]="s.totals.owners"></strong><small><b [appCountUp]="s.totals.businesses"></b> negocios administrados</small></div></article>
            <article class="analytics-kpi"><div class="kpi-icon"><i class="bi bi-people"></i></div><div><span>Clientes en la plataforma</span><strong [appCountUp]="s.totals.customers"></strong></div></article>
            <article class="analytics-kpi kpi-revenue"><div class="kpi-icon"><i class="bi bi-graph-up-arrow"></i></div><div><span>Ingreso por suscripciones</span><strong [appCountUp]="s.revenue.subscriptions" countUpFormat="currency" [countUpDuration]="900"></strong><small>Pagos aprobados acumulados</small></div></article>
          </div>
          <div class="analytics-grid analytics-main-grid">
            <article class="analytics-panel revenue-trend-panel">
              <header><div><span class="panel-kicker">EVOLUCIÓN MENSUAL</span><h4>Ingresos aprobados por suscripciones</h4><p>Comparación del monto cobrado en cada mes.</p></div><div class="chart-month-navigation"><button type="button" (click)="moveAnalyticsMonth(-1)" aria-label="Ver meses anteriores" title="Meses anteriores"><i class="bi bi-chevron-left"></i></button><strong>{{ monthLong(analyticsFocusMonth()) }}</strong><button type="button" (click)="moveAnalyticsMonth(1)" [disabled]="!canMoveAnalyticsForward()" aria-label="Ver meses siguientes" title="Meses siguientes"><i class="bi bi-chevron-right"></i></button></div></header>
              <div class="revenue-chart-summary"><div><span>Total del período</span><strong [appCountUp]="subscriptionRevenueTotal()" countUpFormat="currency" [countUpDuration]="900"></strong></div><div><span>Mejor mes</span><strong>{{ bestRevenueMonthLabel() }}</strong><small [appCountUp]="bestRevenueMonthAmount()" countUpFormat="currency" [countUpDuration]="900"></small></div></div>
              <div class="monthly-bar-chart" *ngIf="revenue().length; else noRevenueTrend" role="img" aria-label="Gráfica de barras de ingresos mensuales por suscripciones">
                <div class="bar-y-axis"><span>{{ analyticsMaxRevenue() | currency:'USD':'symbol':'1.0-0' }}</span><span>{{ analyticsMaxRevenue()/2 | currency:'USD':'symbol':'1.0-0' }}</span><span>$0</span></div>
                <div class="bar-plot"><span class="grid-line grid-top"></span><span class="grid-line grid-mid"></span><span class="grid-line grid-bottom"></span><div class="month-column" *ngFor="let row of visibleAnalyticsRevenue(); let index = index" [class.focus-month]="row.month === analyticsFocusMonth()" [style.--bar-delay.ms]="index * 45" [attr.title]="monthLong(row.month) + ': ' + formatCurrencyValue(row.subscriptions)" (click)="filterAnalyticsByMonth(row.month)" role="button" tabindex="0" (keydown.enter)="filterAnalyticsByMonth(row.month)"><span class="bar-value">{{ compactCurrency(row.subscriptions) }}</span><div class="bar-track"><i [style.height.%]="revenueBarPercent(row.subscriptions)" [class.has-value]="row.subscriptions > 0"></i></div><strong>{{ monthShort(row.month) }}</strong></div></div>
              </div>
              <ng-template #noRevenueTrend><div class="analytics-empty"><i class="bi bi-bar-chart"></i><span>Aún no hay ingresos mensuales aprobados.</span></div></ng-template>
              <footer class="chart-explanation"><i class="bi bi-info-circle"></i><span>Cada columna representa únicamente pagos de suscripciones aprobados durante ese mes.</span></footer>
            </article>
            <article class="analytics-panel lifecycle-panel"><header><div><span class="panel-kicker">SALUD DE CARTERA</span><h4>Estado de suscripciones</h4></div><span class="analytics-chip"><b [appCountUp]="s.totals.subscriptions"></b> total</span></header><div class="lifecycle-content"><div class="lifecycle-ring" [style.background]="subscriptionRing(s)"><div><strong [appCountUp]="subscriptionHealthPercent(s)" countUpFormat="percent"></strong><span>activas</span></div></div><div class="lifecycle-legend"><div><i class="active"></i><span>Activas</span><strong [appCountUp]="s.subscriptions.active"></strong></div><div><i class="pending"></i><span>Pendientes / inactivas</span><strong [appCountUp]="s.subscriptions.pending + s.subscriptions.inactive"></strong></div><div><i class="closed"></i><span>Canceladas / expiradas</span><strong [appCountUp]="s.subscriptions.cancelled + s.subscriptions.expired"></strong></div></div></div></article>
          </div>
          <div class="analytics-grid analytics-secondary-grid">
            <article class="analytics-panel plan-mix-panel"><header><div><span class="panel-kicker">ADOPCIÓN COMERCIAL</span><h4>Distribución por plan</h4></div></header><div class="plan-bars"><div *ngFor="let item of s.subscriptions.byPlan | keyvalue; let index = index" [style.--fill-delay.ms]="index * 90"><div class="plan-row-head"><span>{{ planLabel(item.key) }}</span><strong><b [appCountUp]="item.value"></b><span aria-hidden="true"> · </span><small [appCountUp]="planShare(item.value, s)" countUpFormat="percent"></small></strong></div><div class="plan-track"><i [style.width.%]="planShare(item.value, s)"></i></div></div></div></article>
            <article class="analytics-panel audience-panel"><header><div><span class="panel-kicker">ALCANCE DE PLATAFORMA</span><h4>Propietarios y clientes</h4></div></header><div class="audience-comparison"><div class="audience-bar owners"><span><i class="bi bi-person-badge"></i> Propietarios</span><strong [appCountUp]="s.totals.owners"></strong><i [style.width.%]="audiencePercent(s.totals.owners, s)"></i></div><div class="audience-bar customers"><span><i class="bi bi-people"></i> Clientes</span><strong [appCountUp]="s.totals.customers"></strong><i [style.width.%]="audiencePercent(s.totals.customers, s)"></i></div></div></article>
            <article class="analytics-panel decision-panel"><header><div><span class="panel-kicker">LECTURA EJECUTIVA</span><h4>Indicadores para decidir</h4></div></header><div class="decision-list"><div><i class="bi bi-shield-check"></i><span><b>{{ subscriptionHealthPercent(s) }}%</b> de suscripciones mantienen acceso activo.</span></div><div><i class="bi bi-buildings"></i><span><b>{{ businessCoverage(s) }}%</b> de negocios cuentan con una suscripción registrada.</span></div><div><i class="bi bi-cash-stack"></i><span><b>{{ subscriptionRevenueShare(s) }}%</b> del ingreso aprobado procede de suscripciones.</span></div></div></article>
          </div>
          <p class="analytics-note"><i class="bi bi-info-circle"></i> Las cifras financieras incluyen únicamente pagos aprobados. Los ingresos por reservas no se mezclan con el rendimiento principal de suscripciones.</p>
        </section>

        <!-- Tabs -->
        <div class="tabs-bar" *ngIf="!embeddedNavigation">
          <button class="tab-btn" [class.active]="activeTab() === 'owners'"     (click)="setTab('owners')">Propietarios</button>
          <button class="tab-btn" [class.active]="activeTab() === 'users'"      (click)="setTab('users')">Usuarios</button>
          <button class="tab-btn" [class.active]="activeTab() === 'subs'"       (click)="setTab('subs')">Suscripciones</button>
          <button class="tab-btn" [class.active]="activeTab() === 'planes'"     (click)="setTab('planes')">Planes</button>
          <button class="tab-btn" [class.active]="activeTab() === 'pagos'"      (click)="setTab('pagos')">Pagos de suscripciones</button>
          <button class="tab-btn" [class.active]="activeTab() === 'pagosNegocios'" (click)="setTab('pagosNegocios')">Pagos a negocios</button>
          <button class="tab-btn" [class.active]="activeTab() === 'personalizacion'" (click)="setTab('personalizacion')">Personalización</button>
        </div>

        <div class="data-toolbar" *ngIf="hasDirectoryTools()">
          <div class="search-control" *ngIf="['owners','users','subs','pagos','pagosNegocios'].includes(activeTab())">
            <label class="search-field">
              <i class="bi bi-search" aria-hidden="true"></i>
              <input type="search" [value]="searchTerm()" [placeholder]="searchPlaceholder()"
                (input)="setSearch($any($event.target).value)" (keyup.enter)="applySearchNow()" [disabled]="tabLoading()" />
            </label>
            <button class="search-submit" type="button" aria-label="Buscar" (click)="applySearchNow()" [disabled]="tabLoading()||cooldown.getSignal('sa-search')()" cooldownBubble data-cooldown-key="sa-search">
              <i class="bi bi-search" aria-hidden="true"></i><span>Buscar</span>
            </button>
            <button class="search-reset" type="button" title="Restablecer búsqueda" aria-label="Restablecer búsqueda" (click)="resetDirectorySearch()" [disabled]="tabLoading()">
              <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
            </button>
          </div>
          <select *ngIf="activeTab() === 'users'" [value]="userRoleFilter" (change)="filterUsers($any($event.target).value)" [disabled]="tabLoading()">
            <option value="">Todos los roles</option><option value="customer">Clientes</option>
            <option value="owner">Propietarios</option><option value="employee">Subadministradores</option><option value="super_admin">Superadministradores</option>
          </select>
          <select *ngIf="activeTab() === 'subs'" [value]="subsStatusFilter" (change)="filterSubscriptions($any($event.target).value)" [disabled]="tabLoading()">
            <option value="">Todos los estados</option><option value="active">Activas</option>
            <option value="trial">En prueba</option>
            <option value="pending">Pendientes</option><option value="cancelled">Canceladas</option><option value="expired">Expiradas</option>
          </select>
          <!-- Bug real corregido: faltaba la opción "Gratis" -- el filtro no
               podía ni mostrar ni excluir esas suscripciones a propósito, y
               el backend tampoco la aceptaba (ver getAllSubscriptions). -->
          <select *ngIf="activeTab() === 'subs'" [value]="subsPlanFilter" (change)="filterSubscriptionPlan($any($event.target).value)" [disabled]="tabLoading()">
            <option value="">Todos los planes</option><option value="gratis">Gratis</option><option value="basico">Básico</option>
            <option value="estandar">Estándar</option><option value="premium">Premium</option>
          </select>
          <span *ngIf="activeTab() === 'pagos'" class="scope-chip">Listado global: suscripciones</span>
          <span *ngIf="activeTab() === 'pagosNegocios'" class="scope-chip">Negocios con pagos de reservas</span>
          <select *ngIf="activeTab() === 'pagos'" [value]="paymentStatusFilter" (change)="filterPaymentStatus($any($event.target).value)" [disabled]="tabLoading()">
            <option value="">Todos los estados</option><option value="succeeded">Aprobados</option><option value="pending">Pendientes</option><option value="failed">Rechazados</option>
          </select>
          <label class="results-note page-size-control" *ngIf="hasDirectoryTools()">
            <span>Registros</span>
            <select [ngModel]="pageSize" (ngModelChange)="onPageSizeChange($event)" [disabled]="tabLoading()">
              <option *ngFor="let size of pageSizeOptions" [ngValue]="size">{{ size }} por página</option>
            </select>
          </label>
        </div>

        <app-ui-skeleton
          *ngIf="tabLoading()"
          class="filtered-content-skeleton"
          [variant]="activeTab() === 'pagosNegocios' ? 'cards' : 'table'"
          [count]="activeTab() === 'pagosNegocios' ? 4 : 7"
          [columnCount]="6"
          label="Actualizando resultados de los filtros"
        ></app-ui-skeleton>

        <div *ngIf="activeTab() === 'planes' && !tabLoading()" class="tab-content plans-content">
          <div class="tab-loading" *ngIf="tabLoading()"><div class="spinner"></div></div>
          <div *ngIf="!tabLoading()">
            <div class="plans-intro">
              <div><span class="eyebrow">CONFIGURACIÓN COMERCIAL</span><h3>Planes y permisos</h3><p>Administra precios, cupos de espacios, comisión de la plataforma y cuentas de prueba.</p></div>
              <span class="plan-count">{{ plans().length }} planes</span>
            </div>
            <app-admin-tabs [tabs]="planesModuleTabs" [active]="planesTab()" ariaLabel="Secciones del módulo de planes" (tabSelect)="selectPlanesTab($event)"></app-admin-tabs>

            <div class="planes-tab-panel" *ngIf="planesTab()==='planes'">
              <section class="settings-editor">
                <div><span class="eyebrow">PERIODO DE GRACIA</span><h4>Días de margen tras vencer el pago antes de bloquear el acceso</h4><p>Aplica solo a suscripciones que nunca fueron personalizadas manualmente. Un negocio con "Días de gracia" ya asignado en Personalizar suscripción conserva el suyo, sin importar este valor.</p></div>
                <div class="settings-editor-fields">
                  <label>Días de gracia por defecto<input placeholder="Ej: 2" #graceDaysInput type="number" min="0" max="365" step="1" [(ngModel)]="platformSettingsForm.grace_days" [class.field-error]="graceDaysInvalid()"></label>
                </div>
                <button class="action-btn" type="button" (click)="saveGlobalGraceDays()" [disabled]="savingGraceDays()||cooldown.getSignal('sa-grace-save')()" cooldownBubble data-cooldown-key="sa-grace-save">{{ savingGraceDays() ? 'Guardando...' : 'Guardar periodo de gracia' }}</button>
              </section>
              <div class="plan-cards-grid">
                <article class="landing-plan-card" *ngFor="let plan of plans()"><div class="landing-plan-top"><span class="status-badge" [class]="plan.is_active ? 'status-active' : 'status-inactive'">{{ plan.is_active ? 'Activo' : 'Archivado' }}</span></div><h3>{{ plan.name }}</h3><p>{{ plan.description || 'Plan configurable para negocios TuSala.' }}</p><strong class="landing-plan-price">{{ plan.price_monthly | currency:'USD':'symbol':'1.2-2' }}<small>/mes</small></strong><div class="plan-limit-preview"><span>Hasta {{ formatSpacesLabel(plan.limits?.max_spaces, true) }}</span><span>{{ plan.limits?.max_customers || '—' }} clientes</span></div><div class="benefit-chips"><span *ngFor="let benefit of enabledBenefitList(plan)"><i class="bi bi-check-lg" aria-hidden="true"></i> {{ benefit }}</span></div><div class="plan-card-actions"><button class="secondary-btn" (click)="openPlanHistory(plan)">Ver histórico</button><button class="action-btn" (click)="editPlan(plan)">Editar plan</button></div></article><div *ngIf="plans().length === 0" class="empty-cell">No hay planes configurados.</div>
              </div>
            </div>

            <div class="planes-tab-panel" *ngIf="planesTab()==='comision'">
              <section class="settings-editor">
                <div><span class="eyebrow">COMISIÓN DE PLATAFORMA</span><h4>Porcentaje cobrado por cada pago de reserva</h4><p>Aplica solo a negocios nuevos. Los negocios existentes conservan la comisión que tenían cuando se registraron; una selección explícita solo registra los negocios realmente afectados.</p></div>
                <div class="settings-editor-fields">
                  <label>Comisión (%)<input placeholder="Ej: 5" #commissionRateInput type="number" min="0" max="100" step="0.01" [(ngModel)]="platformSettingsForm.commission_rate" [class.field-error]="commissionRateInvalid()"></label>
                </div>
                <button class="action-btn" type="button" (click)="saveGlobalCommission()" [disabled]="savingPlatformSettings()||cooldown.getSignal('sa-commission-save')()" cooldownBubble data-cooldown-key="sa-commission-save">{{ savingPlatformSettings() ? 'Guardando...' : 'Guardar comisión' }}</button>
              </section>
              <section class="commission-contracts-panel">
                <div class="section-actions"><div><span class="eyebrow">CONTRATOS POR NEGOCIO</span><h4>Aplicar una comisión a negocios seleccionados</h4></div><span class="scope-chip">{{ selectedCommissionBusinessIds.size }} seleccionados</span></div>
                <p class="field-hint">Busca un negocio, selecciónalo y luego pulsa <strong>Aplicar comisión a seleccionados</strong>. El valor se toma del campo Comisión (%) superior.</p>
                <div class="commission-contract-toolbar">
                  <label class="commission-search"><i class="bi bi-search"></i><input [(ngModel)]="commissionBusinessSearch" (ngModelChange)="loadCommissionContracts(commissionBusinessSearch)" placeholder="Buscar por nombre o empresa" aria-label="Buscar negocio por nombre o empresa"><button *ngIf="commissionBusinessSearch" type="button" (click)="commissionBusinessSearch=''; loadCommissionContracts('')" aria-label="Limpiar búsqueda">×</button></label>
                  <span>{{ commissionContracts().length }} resultados</span>
                </div>
                <div class="commission-contract-list" *ngIf="commissionContracts().length">
                  <label class="commission-contract-row" *ngFor="let business of commissionContracts()">
                    <input type="checkbox" [checked]="selectedCommissionBusinessIds.has(business.id)" (change)="toggleCommissionBusiness(business.id, $any($event.target).checked)">
                    <span>{{ business.name }}</span><strong>{{ business.commission_rate === null ? 'Sin asignar' : (business.commission_rate + '%') }}</strong>
                  </label>
                </div>
                <div class="commission-contract-actions"><button type="button" class="secondary-btn" (click)="selectAllCommissionBusinesses()">Seleccionar resultados</button><button type="button" class="secondary-btn" (click)="clearCommissionBusinesses()">Limpiar selección</button><button type="button" class="action-btn" (click)="applyCommissionToSelected()" [disabled]="!selectedCommissionBusinessIds.size || savingPlatformSettings()||cooldown.getSignal('sa-commission-apply-selected')()" cooldownBubble data-cooldown-key="sa-commission-apply-selected">Aplicar comisión a seleccionados</button></div>
              </section>
              <div class="section-actions"><div><span class="eyebrow">HISTORIAL DE CAMBIOS</span><h4>Comisiones por cambio</h4></div><span class="scope-chip">{{ commissionHistory().length }} eventos</span></div>
              <div class="commission-history" *ngIf="commissionHistoryLoading()"><app-ui-skeleton variant="table" [count]="4" [columnCount]="3" label="Cargando historial de comisiones"></app-ui-skeleton></div>
              <div class="commission-history" *ngIf="!commissionHistoryLoading() && commissionHistory().length">
                <article class="commission-change-card" *ngFor="let change of commissionHistory()" (click)="openCommissionChangeDetail(change)" [title]="'Ver detalle del cambio de ' + change.previous_rate + '% a ' + change.new_rate + '%'">
                  <div class="commission-change-header">
                    <div class="commission-change-rates">
                      <span class="commission-rate-old">{{ change.previous_rate }}%</span>
                      <i class="bi bi-arrow-right"></i>
                      <span class="commission-rate-new">{{ change.new_rate }}%</span>
                    </div>
                    <span class="commission-change-date">{{ formatLongDateTime(change.created_at) }}</span>
                  </div>
                  <div class="commission-change-meta">
                    <span class="commission-change-by">{{ change.changed_by?.name || 'Sistema' }}</span>
                    <span class="commission-change-preserved">{{ change.affected_businesses.length }} negocio(s) afectado(s)</span>
                  </div>
                </article>
              </div>
              <div class="commission-history-empty" *ngIf="!commissionHistoryLoading() && !commissionHistory().length">
                <i class="bi bi-clock-history"></i>
                <span>Aún no hay cambios de comisión registrados.</span>
              </div>
            </div>

            <div *ngIf="commissionChangeDetail()" class="modal-overlay" (click)="closeCommissionChangeDetail()">
              <section class="modal-box commission-detail-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true" [attr.aria-label]="'Detalle del cambio de comisión'">
                <header class="modal-header">
                  <span class="modal-icon"><i class="bi bi-percent"></i></span>
                  <div><small>DETALLE DEL CAMBIO</small><h3>{{ commissionChangeDetail()?.previous_rate }}% → {{ commissionChangeDetail()?.new_rate }}%</h3></div>
                  <button class="close-btn" aria-label="Cerrar" (click)="closeCommissionChangeDetail()"><i class="bi bi-x-lg"></i></button>
                </header>
                <div class="modal-body">
                  <div class="cc-metric"><span>Tasa anterior</span><strong>{{ commissionChangeDetail()?.previous_rate }}%</strong></div>
                  <div class="cc-metric"><span>Tasa nueva</span><strong>{{ commissionChangeDetail()?.new_rate }}%</strong></div>
                  <div class="cc-metric"><span>Fecha</span><strong>{{ formatLongDateTime(commissionChangeDetail()?.created_at || '') }}</strong></div>
                  <div class="cc-metric"><span>Cambiado por</span><strong>{{ commissionChangeDetail()?.changed_by?.name || 'Sistema' }}</strong></div>
                  <div class="cc-businesses">
                    <div class="cc-businesses-head"><i class="bi bi-building"></i><strong>{{ commissionChangeDetail()?.affected_businesses?.length || 0 }} negocio(s) afectados</strong></div>
                    <ul>
                      <li *ngFor="let biz of commissionChangeDetail()?.affected_businesses">
                        <span class="cc-biz-name">{{ biz.name }}</span>
                        <span class="cc-biz-rate">{{ biz.commission_rate ?? commissionChangeDetail()?.new_rate }}%</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>

          </div>
        </div>

        <!-- Tab: Seguridad (2FA) -->
        <div *ngIf="activeTab() === 'seguridad' && !tabLoading()" class="tab-content">
          <section class="settings-editor">
            <div>
              <span class="eyebrow">CUENTA SUPERADMINISTRADORA</span>
              <h4>Verificación en dos pasos (2FA)</h4>
              <p>Exige, además de tu contraseña, un código de tu app de autenticación (Google Authenticator o similar) cada vez que inicies sesión en este panel.</p>
            </div>

            <div *ngIf="twoFactorStatusLoading()" class="tab-loading"><div class="spinner"></div></div>

            <ng-container *ngIf="!twoFactorStatusLoading()">
              <!-- Estado: 2FA activo -->
              <ng-container *ngIf="twoFactorStatus()?.enabled && !twoFactorSetupData()">
                <div class="settings-editor-fields">
                  <span class="status-badge status-active"><i class="bi bi-shield-check"></i> Verificación en dos pasos activada</span>
                </div>
                <button [class]="showDisableTwoFactorForm() ? 'secondary-btn' : 'danger-btn'" type="button" (click)="toggleDisableTwoFactorForm()">
                  {{ showDisableTwoFactorForm() ? 'Cancelar' : 'Desactivar 2FA' }}
                </button>

                <div class="settings-editor-fields" *ngIf="showDisableTwoFactorForm()" style="margin-top:.75rem">
                  <label>Contraseña actual<span class="password-visibility-field"><input [type]="showDisableTwoFactorPassword() ? 'text' : 'password'" [(ngModel)]="disableTwoFactorPassword" autocomplete="current-password" placeholder="Tu contraseña"><button type="button" class="password-visibility-btn" (click)="showDisableTwoFactorPassword.set(!showDisableTwoFactorPassword())" [attr.aria-label]="showDisableTwoFactorPassword() ? 'Ocultar contraseña' : 'Mostrar contraseña'" [attr.aria-pressed]="showDisableTwoFactorPassword()"><i class="bi" [class.bi-eye]="!showDisableTwoFactorPassword()" [class.bi-eye-slash]="showDisableTwoFactorPassword()" aria-hidden="true"></i></button></span></label>
                  <label>Código 2FA actual<input type="text" inputmode="numeric" maxlength="6" [(ngModel)]="disableTwoFactorCode" placeholder="6 dígitos"></label>
                </div>
                <p class="reset-error" *ngIf="showDisableTwoFactorForm() && disableTwoFactorError()">{{ disableTwoFactorError() }}</p>
                <button class="action-btn" type="button" *ngIf="showDisableTwoFactorForm()" [disabled]="disableTwoFactorLoading() || !disableTwoFactorPassword" (click)="confirmDisableTwoFactor()">
                  {{ disableTwoFactorLoading() ? 'Desactivando...' : 'Confirmar desactivación' }}
                </button>
              </ng-container>

              <!-- Estado: 2FA inactivo, sin configuración en curso -->
              <ng-container *ngIf="!twoFactorStatus()?.enabled && !twoFactorSetupData()">
                <div class="settings-editor-fields">
                  <span class="status-badge status-inactive"><i class="bi bi-shield-exclamation"></i> Verificación en dos pasos desactivada</span>
                </div>
                <button class="action-btn" type="button" [disabled]="twoFactorSetupLoading()" (click)="startTwoFactorSetup()">
                  {{ twoFactorSetupLoading() ? 'Generando...' : 'Activar 2FA' }}
                </button>
              </ng-container>

              <!-- Configuración en curso: mostrar QR y pedir el primer código -->
              <ng-container *ngIf="twoFactorSetupData() as setup">
                <div class="settings-editor-fields two-factor-setup">
                  <p>Escanea este código con tu app de autenticación, o ingresa la clave manualmente.</p>
                  <img [src]="setup.qrCode" alt="Código QR para configurar la verificación en dos pasos" width="180" height="180">
                  <code>{{ setup.secret }}</code>
                  <label>Código de verificación<input type="text" inputmode="numeric" maxlength="6" [(ngModel)]="twoFactorVerifyCode" placeholder="6 dígitos"></label>
                </div>
                <p class="reset-error" *ngIf="twoFactorVerifyError()">{{ twoFactorVerifyError() }}</p>
                <div style="display:flex;gap:.5rem">
                  <button class="action-btn" type="button" [disabled]="twoFactorVerifyLoading() || twoFactorVerifyCode.trim().length !== 6" (click)="confirmTwoFactorSetup()">
                    {{ twoFactorVerifyLoading() ? 'Confirmando...' : 'Confirmar y activar' }}
                  </button>
                  <button class="secondary-btn" type="button" (click)="cancelTwoFactorSetup()">Cancelar</button>
                </div>
              </ng-container>
            </ng-container>
          </section>
        </div>

        <!-- Tab: Negocios -->
        <div *ngIf="activeTab() === 'negocios' && !tabLoading()" class="tab-content">
          <div class="tab-loading" *ngIf="tabLoading()"><div class="spinner"></div></div>
          <div *ngIf="!tabLoading()">
            <table class="data-table subscription-payments-table">
              <thead>
                <tr>
                  <th>ID</th><th>Negocio</th><th>Email</th><th>Owner</th><th>Plan</th><th>Estado Sub.</th><th>Activo</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let b of businesses()">
                  <td>{{ b.id }}</td>
                  <td>{{ b.name }}</td>
                  <td>{{ b.email }}</td>
                  <td>{{ b.owner?.name || '—' }}</td>
                  <td>{{ planLabel(b.subscription?.plan) }}</td>
                  <td>
                    <span class="status-badge" [class]="'status-' + (b.subscription?.status ?? 'none')">
                      {{ statusLabel(b.subscription?.status) }}
                    </span>
                  </td>
                  <td>
                    <span class="status-dot" [class.active]="b.status"></span>
                  </td>
                </tr>
                <tr *ngIf="businesses().length === 0">
                  <td colspan="7" class="empty-cell">Sin datos</td>
                </tr>
              </tbody>
            </table>
            <app-pagination [page]="currentPage()" [pages]="totalPages()" [total]="totalRecords()" [limit]="pageSize" [disabled]="tabLoading()" itemLabel="negocios" (pageChange)="goToPage($event)"></app-pagination>
          </div>
        </div>

        <!-- Tab: Owners -->
        <div *ngIf="activeTab() === 'owners' && !tabLoading()" class="tab-content owners-tab-content">
          <app-admin-tabs [tabs]="ownersSectionTabs" [active]="ownersSection()" [compact]="true" ariaLabel="Secciones del módulo de propietarios" (tabSelect)="selectOwnersSection($event)"></app-admin-tabs>

          <!-- Sub-tab: Control de propietarios -->
          <ng-container *ngIf="ownersSection() === 'control'">
            <div class="owners-content">
              <div class="owner-directory" *ngIf="owners().length">
                <app-management-record-card *ngFor="let o of owners()" [tone]="(o.business ? o.business.status : o.owner.is_active) ? 'default' : 'inactive'" kind="people" [status]="(o.business ? o.business.status : o.owner.is_active) ? 'success' : 'danger'" (recordActivate)="openOwnerRecord(o)">
                  <span recordIcon>{{ (o.owner.name || 'P').charAt(0) }}</span>
                  <div recordPrimary><small>PROPIETARIO #{{ o.owner.user_id }}</small><strong>{{ o.owner.name }}</strong><span>{{ o.owner.email }}</span></div>
                  <!-- Corrección pedida: un super_admin no contrata plan
                       (subscription_exempt) -- mostrarle "Sin plan" da la
                       impresión de que algo falta, cuando en realidad no
                       aplica. Un propietario normal SIN plan sí es una
                       situación real a revisar, así que ahí "Sin plan" se
                       deja tal cual. Bug real corregido: esto solo miraba
                       o.owner.role, así que un propietario DELEGADO normal
                       del mismo negocio de un super_admin (business_plan_exempt,
                       calculado por el negocio -- no por la fila) seguía
                       mostrando "Sin plan" como si faltara información. -->
                  <div recordSummary><small>NEGOCIO</small><strong>{{ o.business?.name || 'Sin negocio vinculado' }}</strong>
                    <span *ngIf="o.owner.role !== 'super_admin' && !o.business_plan_exempt">{{ planLabel(o.subscription?.plan) }} · {{ statusLabel(o.subscription?.status) }}</span>
                    <span *ngIf="o.owner.role === 'super_admin'" class="super-admin-plan-tag"><i class="bi bi-shield-fill-check"></i> Super Administrador -- no aplica plan</span>
                    <span *ngIf="o.owner.role !== 'super_admin' && o.business_plan_exempt" class="super-admin-plan-tag"><i class="bi bi-shield-fill-check"></i> Negocio de un Super Administrador -- no aplica plan</span>
                  </div>
                  <!-- ACCESO refleja business.status (el mismo campo que activa/desactiva
                       el botón de pausa/play de abajo), no owner.is_active -- ambos podían
                       desincronizarse (p. ej. tras restaurar un negocio desde la papelera,
                       donde el acceso del propietario se restaura pero el negocio en sí
                       puede seguir desactivado) y la tarjeta se veía "activa" mientras el
                       propio botón, correcto, ofrecía "Activar". -->
                  <div recordStatus><small>ACCESO</small><strong>{{ (o.business ? o.business.status : o.owner.is_active) ? 'Activo' : 'Desactivado' }}</strong><span>{{ o.owner.last_login_at ? formatLongDateTime(o.owner.last_login_at) : 'Sin inicio de sesi&oacute;n' }}</span></div>
                  <!-- Corrección pedida: "Editar negocio" vivía aquí como un
                       modal aparte -- ahora se edita directamente dentro de
                       la ficha del propietario (pestaña Resumen, tarjeta
                       "Negocio"), así que este botón se quitó. -->
                  <div recordActions class="owner-record-actions"><button type="button" class="action-btn owner-icon-action owner-manage-btn" title="Gestionar negocio" aria-label="Gestionar negocio" (click)="openBusinessContext(o.business?.id, o.business?.name, o.owner.name)" [disabled]="!o.business"><i class="bi bi-box-arrow-in-up-right"></i></button><button type="button" class="action-btn owner-icon-action owner-account-btn" title="Gestionar cuenta (datos y contraseña)" aria-label="Gestionar cuenta" (click)="openUserEditor(o.owner.user_id)"><i class="bi bi-person-gear"></i></button><button class="action-btn owner-icon-action" [class.owner-activate-btn]="o.business ? !o.business.status : !o.owner.is_active" [title]="o.business?.status ? 'Desactivar propietario' : 'Activar propietario'" [attr.aria-label]="o.business?.status ? 'Desactivar propietario' : 'Activar propietario'" (click)="toggleBusiness(o)" [disabled]="!o.business"><i class="bi" [ngClass]="o.business?.status ? 'bi-pause-circle' : 'bi-play-circle'"></i></button><button class="danger-btn owner-icon-action" title="Dar de baja" aria-label="Dar de baja" (click)="archiveOwnerBusiness(o)" [disabled]="!o.business"><i class="bi bi-person-x"></i></button></div>
                </app-management-record-card>
              </div>
              <app-ui-state *ngIf="owners().length === 0" tone="empty"
                [title]="searchTerm() ? 'Sin resultados para tu búsqueda' : 'No se encontraron propietarios'"
                [message]="searchTerm() ? 'Revisa el criterio de búsqueda o elimina el filtro para consultar el directorio completo.' : 'Todavía no hay propietarios registrados en la plataforma.'">
              </app-ui-state>

              <div class="section-actions"><div><span class="eyebrow">CONTROL DE PROPIETARIOS</span><p>Gestiona cada propietario junto con la información completa de su negocio, plan y acceso.</p></div><div class="owner-header-actions"><button class="action-btn" (click)="openUserCreateModal()">Crear propietario o Super Admin</button></div></div>
              <app-pagination [page]="currentPage()" [pages]="totalPages()" [total]="totalRecords()" [limit]="pageSize" [disabled]="tabLoading()" itemLabel="propietarios" (pageChange)="goToPage($event)"></app-pagination>
            </div>
          </ng-container>

          <!-- Sub-tab: Pagos pendientes -->
          <ng-container *ngIf="ownersSection() === 'pendientes'">
            <div class="owners-content pending-owner-signups">
              <header class="owners-subsection-head"><div><span class="eyebrow">PENDIENTES DE PAGO</span><h3>Altas manuales sin completar</h3><p>Cuentas creadas con un plan de pago que todavía no completan su pago — sin acceso hasta que el propietario pague por el enlace enviado.</p></div></header>
              <app-ui-skeleton *ngIf="pendingOwnerSignupsLoading()" variant="cards"></app-ui-skeleton>
              <ng-container *ngIf="!pendingOwnerSignupsLoading()">
                <div class="owner-directory" *ngIf="filteredPendingOwnerSignups().length">
                  <app-management-record-card *ngFor="let p of filteredPendingOwnerSignups()" [clickable]="false" kind="people" status="warning">
                    <span recordIcon><i class="bi bi-hourglass-split"></i></span>
                    <div recordPrimary><small>{{ p.company }}</small><strong>{{ p.full_name }}</strong><span>{{ p.email }}</span></div>
                    <div recordSummary><small>PLAN</small><strong>{{ planLabel(p.plan) }}</strong><span>{{ p.amount | currency:'USD':'symbol':'1.2-2' }}/mes</span></div>
                    <div recordStatus><small>VENCE</small><strong>{{ formatLongDate(p.expires_at) }}</strong><span class="state-off">Pendiente de pago</span></div>
                    <div recordActions>
                      <button type="button" class="icon-action" title="Editar datos de esta alta pendiente" aria-label="Editar" (click)="openEditPendingSignup(p)"><i class="bi bi-pencil-square"></i></button>
                      <button type="button" class="icon-action" title="Registrar como pagado por otro medio (efectivo, transferencia, etc.)" aria-label="Registrar pago externo" (click)="openConfirmPendingExternalPayment(p)"><i class="bi bi-cash-coin"></i></button>
                      <button type="button" class="icon-action" title="Copiar enlace de pago (WhatsApp u otra red)" aria-label="Copiar enlace de pago" [disabled]="copyingPendingSignupId() === p.id" (click)="copyPendingOwnerLink(p)"><i class="bi bi-clipboard-check"></i></button>
                      <button type="button" class="icon-action" title="Reenviar enlace de pago por correo" aria-label="Reenviar enlace de pago por correo" [disabled]="resendingPendingSignupId() === p.id" (click)="resendPendingOwnerLink(p)"><i class="bi bi-send"></i></button>
                    </div>
                  </app-management-record-card>
                </div>
                <app-ui-state *ngIf="filteredPendingOwnerSignups().length === 0" tone="empty"
                  [title]="pendingOwnerSignups().length === 0 ? 'No hay pagos pendientes' : 'Sin resultados para tu búsqueda'"
                  [message]="pendingOwnerSignups().length === 0 ? 'Todas las altas manuales con plan de pago ya completaron su pago, o todavía no se ha creado ninguna.' : 'Revisa el criterio de búsqueda o elimina el filtro para ver todas las cuentas pendientes.'">
                </app-ui-state>
              </ng-container>
            </div>
          </ng-container>

          <!-- Corrección pedida: la única acción sobre una alta pendiente de
               pago era reenviar/copiar el enlace -- ahora también se puede
               corregir sus datos o confirmarla si el cliente pagó por otro
               medio, sin depender de que use el enlace en línea. -->
          <div *ngIf="editingPendingSignup() as editItem" class="modal-overlay" (click)="closeEditPendingSignup()">
            <section class="modal-box user-create-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
              <header class="modal-header"><div><span class="eyebrow">ALTA PENDIENTE DE PAGO</span><h3>Editar datos</h3></div><button type="button" class="close-plan-modal" (click)="closeEditPendingSignup()">×</button></header>
              <p class="modal-intro">Corrige los datos antes de que el propietario complete el pago. El enlace de pago vigente sigue siendo válido; si cambias el plan o el precio, comparte el enlace de nuevo para que refleje el nuevo monto.</p>
              <form #pendingEditFormRef="ngForm" #pendingEditFormEl novalidate>
              <div class="plan-form-grid user-create-grid">
                <label>Nombre completo<input placeholder="Ej: Ana Pérez" [(ngModel)]="pendingEditForm.full_name" name="peFullName" required maxlength="50" data-no-limit-hint></label>
                <label>Correo electrónico<input placeholder="nombre@correo.com" [(ngModel)]="pendingEditForm.email" name="peEmail" type="email" required pattern="\\S+@\\S+\\.\\S+" maxlength="100" data-no-limit-hint></label>
                <label>Teléfono<input placeholder="Ej: +503 7000-0000" [(ngModel)]="pendingEditForm.phone" name="pePhone" maxlength="20" data-no-limit-hint></label>
                <label>Nombre del negocio<input placeholder="Ej: Centro de Negocios Cumbre" [(ngModel)]="pendingEditForm.company" name="peCompany" required maxlength="100" data-no-limit-hint></label>
                <label>Correo del negocio <small>(opcional)</small><input placeholder="contacto@negocio.com" [(ngModel)]="pendingEditForm.business_email" name="peBusinessEmail" type="email" maxlength="100" data-no-limit-hint></label>
                <label>Plan<select [(ngModel)]="pendingEditForm.plan" name="pePlan" required><option *ngFor="let plan of subscriptionPlanOptions()" [value]="plan.code">{{ plan.name }} · {{ plan.price_monthly | currency:'USD':'symbol':'1.2-2' }}/mes</option></select></label>
                <label>Precio mensual personalizado <small>(opcional)</small><input type="number" min="0" step="0.01" [(ngModel)]="pendingEditForm.custom_price_monthly" name="peCustomPrice" placeholder="Usar precio del plan"></label>
                <label>Comisión personalizada (%) <small>(opcional)</small><input type="number" min="0" max="100" step="0.01" [(ngModel)]="pendingEditForm.commission_rate" name="peCommission" placeholder="Usar comisión global"></label>
                <label class="full-field">Días de gracia <small>(opcional)</small><input type="number" min="0" max="365" step="1" [(ngModel)]="pendingEditForm.grace_days" name="peGraceDays" [placeholder]="'Usar periodo de gracia global (' + platformSettingsForm.grace_days + ' días)'"></label>
              </div>
              <p *ngIf="editPendingSignupError()" class="plan-form-error">{{ editPendingSignupError() }}</p>
              </form>
              <footer class="modal-actions"><button type="button" class="secondary-btn" (click)="closeEditPendingSignup()">Cancelar</button><button type="button" class="action-btn" (click)="saveEditPendingSignup()" [disabled]="isSavingPendingEdit()">{{ isSavingPendingEdit() ? 'Guardando...' : 'Guardar cambios' }}</button></footer>
            </section>
          </div>

          <div *ngIf="confirmingPendingExternalPayment() as payItem" class="modal-overlay" (click)="closeConfirmPendingExternalPayment()">
            <section class="modal-box user-create-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
              <header class="modal-header"><div><span class="eyebrow">ALTA PENDIENTE DE PAGO</span><h3>Registrar pago externo</h3></div><button type="button" class="close-plan-modal" (click)="closeConfirmPendingExternalPayment()">×</button></header>
              <p class="modal-intro">Usa esto si <strong>{{ payItem.full_name }}</strong> ({{ payItem.company }}) ya pagó por otro medio en vez de usar el enlace en línea. La cuenta se activará de inmediato, sin enviar ningún enlace de pago.</p>
              <form #pendingPayFormRef="ngForm" #pendingPayFormEl novalidate>
              <div class="plan-form-grid external-payment-grid">
                <label>Método de pago<select [(ngModel)]="pendingExternalPaymentForm.method" name="pepMethod" required><option *ngFor="let m of externalPaymentMethods" [value]="m.value">{{ m.label }}</option></select></label>
                <label>Referencia (opcional)<input [(ngModel)]="pendingExternalPaymentForm.reference" name="pepReference" maxlength="100" placeholder="N.° de comprobante, recibo, etc." data-no-limit-hint></label>
                <label class="full-field receipt-upload-field">Comprobante de pago <small>(opcional)</small>
                  <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" (change)="onPendingExternalPaymentReceiptFileChange($event)" [disabled]="isUploadingPendingReceipt()">
                  <small class="receipt-upload-hint" *ngIf="isUploadingPendingReceipt()"><i class="bi bi-arrow-repeat spin"></i> Subiendo comprobante...</small>
                  <ul class="receipt-upload-list" *ngIf="pendingExternalPaymentForm.receipts.length">
                    <li *ngFor="let r of pendingExternalPaymentForm.receipts; let i = index"><i class="bi bi-paperclip"></i><span>{{ r.original_name }}</span><button type="button" (click)="removePendingExternalPaymentReceipt(i)" title="Quitar comprobante"><i class="bi bi-x-lg"></i></button></li>
                  </ul>
                </label>
              </div>
              <p *ngIf="confirmPendingPaymentError()" class="plan-form-error">{{ confirmPendingPaymentError() }}</p>
              </form>
              <footer class="modal-actions"><button type="button" class="secondary-btn" (click)="closeConfirmPendingExternalPayment()">Cancelar</button><button type="button" class="action-btn" (click)="submitConfirmPendingExternalPayment()" [disabled]="isConfirmingPendingPayment()">{{ isConfirmingPendingPayment() ? 'Activando...' : 'Activar cuenta' }}</button></footer>
            </section>
          </div>

          <!-- Sub-tab: Propietarios eliminados -->
          <ng-container *ngIf="ownersSection() === 'eliminados'">
            <div class="owners-content archived-owners-panel">
              <header class="archived-owners-panel-head"><div><span class="eyebrow">RECUPERACI&Oacute;N ADMINISTRATIVA</span><h3>Propietarios eliminados</h3><p>Estos negocios est&aacute;n ocultos y sus usuarios no pueden iniciar sesi&oacute;n.</p></div>
                <label class="results-note page-size-control">
                  <span>Registros</span>
                  <select [ngModel]="archivedOwnersPageSize" (ngModelChange)="changeArchivedOwnersPageSize($event)" [disabled]="archivedOwnersLoading()">
                    <option *ngFor="let size of archivedOwnersPageSizeOptions" [ngValue]="size">{{ size }} por página</option>
                  </select>
                </label>
              </header>
              <app-ui-skeleton *ngIf="archivedOwnersLoading()" variant="cards"></app-ui-skeleton>
              <div class="archived-owner-list" *ngIf="!archivedOwnersLoading() && archivedOwners().length">
                <article *ngFor="let business of archivedOwners()">
                  <span class="archived-owner-avatar">{{ (business.owner?.name || business.name || 'P').charAt(0) }}</span>
                  <div><small>PROPIETARIO</small><strong>{{ business.owner?.name || 'Propietario no identificado' }}</strong><span>{{ business.owner?.email || business.email }}</span></div>
                  <div><small>NEGOCIO ELIMINADO</small><strong>{{ business.name }}</strong><span>{{ planLabel(business.subscription?.plan) }}</span></div>
                  <div class="archived-date"><small>FECHA DE ELIMINACI&Oacute;N</small><span>{{ business.archived_at ? formatLongDateTime(business.archived_at) : 'Sin fecha registrada' }}</span></div>
                  <button class="restore-btn" type="button" (click)="requestOwnerRestore(business)" [disabled]="restoringBusinessId() === business.id"><i class="bi bi-arrow-counterclockwise"></i>{{ restoringBusinessId() === business.id ? 'Reactivando...' : 'Volver a activar' }}</button>
                </article>
              </div>
              <app-ui-state *ngIf="!archivedOwnersLoading() && archivedOwners().length === 0" tone="empty"
                [title]="archivedOwnersSearch ? 'Sin resultados para tu búsqueda' : 'No hay propietarios eliminados'"
                [message]="archivedOwnersSearch ? 'Revisa el criterio de búsqueda o elimínalo para ver todos los propietarios eliminados.' : 'Todos los negocios registrados se encuentran disponibles.'">
              </app-ui-state>
              <app-pagination *ngIf="!archivedOwnersLoading()" [page]="archivedOwnersPage()" [pages]="archivedOwnersPages()" [total]="archivedOwnersTotal()" [limit]="archivedOwnersPageSize" [disabled]="archivedOwnersLoading()" itemLabel="propietarios eliminados" (pageChange)="loadArchivedOwners($event)"></app-pagination>
            </div>
          </ng-container>
        </div>

        <!-- Tab: Usuarios -->
        <div *ngIf="activeTab() === 'users' && !tabLoading()" class="tab-content users-content">
          <app-admin-tabs [tabs]="userSectionTabs" [active]="userSection()" [compact]="true" ariaLabel="Secciones del módulo de usuarios" (tabSelect)="selectUserSection($event)"></app-admin-tabs>
          <ng-container *ngIf="userSection() === 'usuarios'" id="usuarios">
            <header class="users-heading">
              <div><span class="eyebrow">DIRECTORIO DE ACCESOS</span><h3>Usuarios de la plataforma</h3><p>Consulta y gestiona cada cuenta: datos de acceso, contraseña, sesiones y el negocio al que pertenece.</p></div>
              <span class="users-total"><strong>{{ totalRecords() }}</strong> registros</span>
            </header>
            <div class="user-directory" *ngIf="users().length">
              <app-management-record-card *ngFor="let u of users()" [clickable]="false" kind="people" [status]="u.is_active ? 'success' : 'danger'">
                <span recordIcon>{{ (u.name || u.email || 'U').charAt(0).toUpperCase() }}</span>
                <div recordPrimary><small>USUARIO #{{ u.user_id }}</small><strong>{{ u.name }}</strong><span>{{ u.email }}</span></div>
                <div recordSummary><small>NEGOCIO</small><strong>{{ u.business?.name || 'Sin negocio' }}</strong><span>{{ roleLabel(u.role) }}</span></div>
                <div recordStatus><small>&Uacute;LTIMA CONEXI&Oacute;N</small><strong>{{ u.last_login_at ? formatLongDateTime(u.last_login_at) : 'Sin ingresos' }}</strong><span [class.state-off]="!u.is_active">{{ u.is_active ? 'Acceso activo' : 'Acceso inactivo' }}</span></div>
                <div recordActions>
                  <button type="button" class="icon-action" [class.danger]="u.is_active" title="{{ u.is_active ? 'Desactivar acceso' : 'Activar acceso' }}" aria-label="{{ u.is_active ? 'Desactivar acceso' : 'Activar acceso' }}" (click)="toggleUserStatus(u)"><i class="bi" [class.bi-pause-circle]="u.is_active" [class.bi-play-circle]="!u.is_active"></i></button>
                  <button type="button" class="icon-action" title="Enviar enlace de recuperación" aria-label="Enviar enlace de recuperación" [disabled]="!canSendAdminRecovery(u)" (click)="openResetModal(u)"><i class="bi bi-envelope-arrow-up-fill"></i></button>
                  <button type="button" class="icon-action" title="Gestionar cuenta (datos, contraseña y sesiones)" aria-label="Gestionar cuenta" (click)="openUserEditor(u.user_id)"><i class="bi bi-person-gear"></i></button>
                  <button type="button" class="icon-action" *ngIf="u.business && u.role !== 'customer'" title="Gestionar negocio" aria-label="Gestionar negocio" (click)="openBusinessContext(u.business.id, u.business.name, u.name)"><i class="bi bi-box-arrow-in-up-right"></i></button>
                </div>
              </app-management-record-card>
            </div>
            <app-ui-state class="section-empty-state" *ngIf="users().length === 0" tone="empty" title="No se encontraron usuarios" message="Prueba con otro nombre, correo o tipo de acceso."></app-ui-state>
            <div class="users-pagination">
              <app-pagination [page]="currentPage()" [pages]="totalPages()" [total]="totalRecords()" [limit]="pageSize" [disabled]="tabLoading()" itemLabel="usuarios" (pageChange)="goToPage($event)"></app-pagination>
            </div>
          </ng-container>
          <ng-container *ngIf="userSection() === 'personal'" id="personal">
            <app-platform-contacts-admin></app-platform-contacts-admin>
          </ng-container>
        </div>

        <div *ngIf="showUserCreateModal()" class="modal-overlay" (click)="closeUserCreateModal()">
          <section class="modal-box user-create-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
            <header class="modal-header"><div><span class="eyebrow">ALTA ASISTIDA</span><h3>Crear cuenta</h3></div><button type="button" class="close-plan-modal" (click)="closeUserCreateModal()">×</button></header>
            <p class="modal-intro">Registra únicamente la información necesaria. TuSala genera una contraseña temporal segura y la envía por correo.</p>
            <form #manualUserFormRef="ngForm" #manualUserFormEl novalidate>
            <div class="plan-form-grid user-create-grid">
              <label>Nombre completo<input placeholder="Ej: Ana Pérez" [(ngModel)]="manualUserForm.name" name="manualName" required maxlength="50" autocomplete="name" data-no-limit-hint></label>
              <label>Correo electrónico<input placeholder="nombre@correo.com" [(ngModel)]="manualUserForm.email" name="manualEmail" type="email" required pattern="\\S+@\\S+\\.\\S+" maxlength="100" autocomplete="email" data-no-limit-hint></label>
              <label>Teléfono<input placeholder="Ej: +503 7000-0000" [(ngModel)]="manualUserForm.phone_number" name="manualPhone" maxlength="20" autocomplete="tel" data-no-limit-hint></label>
              <label>Tipo de acceso<select [(ngModel)]="manualUserForm.role" name="manualRole"><option value="owner">Propietario</option><option value="super_admin">Superadministrador</option></select></label>
              <ng-container *ngIf="manualUserForm.role === 'owner'">
                <label>Nombre del negocio<input placeholder="Ej: Centro de Negocios Cumbre" [(ngModel)]="manualUserForm.business_name" name="manualBusinessName" required maxlength="100" data-no-limit-hint></label>
                <label>Plan<select [(ngModel)]="manualUserForm.plan" name="manualPlan" required><option value="" disabled>Selecciona un plan</option><option *ngFor="let plan of plans()" [value]="plan.code">{{ plan.name }} · {{ plan.price_monthly | currency:'USD':'symbol':'1.2-2' }}/mes</option></select></label>
                <label>Precio mensual personalizado <small>(opcional)</small><input type="number" min="0" step="0.01" [(ngModel)]="manualUserForm.custom_price_monthly" name="manualCustomPrice" placeholder="Usar precio del plan"></label>
                <label>Comisión personalizada (%) <small>(opcional)</small><input type="number" min="0" max="100" step="0.01" [(ngModel)]="manualUserForm.commission_rate" name="manualCommission" placeholder="Usar comisión global"></label>
                <label class="full-field">Días de gracia <small>(opcional)</small><input type="number" min="0" max="365" step="1" [(ngModel)]="manualUserForm.grace_days" name="manualGraceDays" [placeholder]="'Usar periodo de gracia global (' + platformSettingsForm.grace_days + ' días)'"></label>
              </ng-container>
            </div>
            <ng-container *ngIf="manualUserForm.role === 'owner' && manualUserForm.plan && manualUserForm.plan !== 'gratis'">
              <label class="switch-row"><input type="checkbox" [checked]="manualUserForm.external_payment_enabled" (change)="manualUserForm.external_payment_enabled = $any($event.target).checked" name="externalPaymentEnabled"><span>El negocio ya pagó por otro medio (fuera del enlace en línea)</span></label>
              <div class="plan-form-grid external-payment-grid" *ngIf="manualUserForm.external_payment_enabled">
                <label>Método de pago<select [(ngModel)]="manualUserForm.external_payment_method" name="externalPaymentMethod" required><option *ngFor="let m of externalPaymentMethods" [value]="m.value">{{ m.label }}</option></select></label>
                <label>Referencia (opcional)<input [(ngModel)]="manualUserForm.external_payment_reference" name="externalPaymentReference" maxlength="100" placeholder="N.° de comprobante, recibo, etc." data-no-limit-hint></label>
                <!-- Corrección pedida: el comprobante es opcional (el admin
                     puede no tenerlo a mano en el momento) -- mismo patrón de
                     subida inmediata que ya usa el comprobante de pago
                     externo de reservas (onReceiptFileChange en
                     reservas.component.ts), solo que sin exigirlo. -->
                <label class="full-field receipt-upload-field">Comprobante de pago <small>(opcional)</small>
                  <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" (change)="onManualUserReceiptFileChange($event)" [disabled]="isUploadingManualUserReceipt()">
                  <small class="receipt-upload-hint" *ngIf="isUploadingManualUserReceipt()"><i class="bi bi-arrow-repeat spin"></i> Subiendo comprobante...</small>
                  <ul class="receipt-upload-list" *ngIf="manualUserForm.external_payment_receipts.length">
                    <li *ngFor="let r of manualUserForm.external_payment_receipts; let i = index"><i class="bi bi-paperclip"></i><span>{{ r.original_name }}</span><button type="button" (click)="removeManualUserReceipt(i)" title="Quitar comprobante"><i class="bi bi-x-lg"></i></button></li>
                  </ul>
                </label>
              </div>
            </ng-container>
            <div class="notification-audience" *ngIf="manualUserForm.role !== 'owner' || manualUserForm.plan === 'gratis'"><i class="bi bi-envelope-check"></i><span>Al confirmar se enviará una contraseña temporal por correo. El usuario tendrá que cambiarla y volver a iniciar sesión antes de acceder al panel.</span></div>
            <div class="notification-audience" *ngIf="manualUserForm.role === 'owner' && manualUserForm.plan && manualUserForm.plan !== 'gratis' && !manualUserForm.external_payment_enabled"><i class="bi bi-credit-card-2-front"></i><span>Este plan tiene costo: la cuenta quedará <strong>pendiente</strong> y sin acceso hasta que el propietario complete el pago en línea por el enlace que se le enviará por correo (vigente por 1 día). La contraseña temporal solo se envía cuando el pago se confirma.</span></div>
            <div class="notification-audience" *ngIf="manualUserForm.role === 'owner' && manualUserForm.plan && manualUserForm.plan !== 'gratis' && manualUserForm.external_payment_enabled"><i class="bi bi-check-circle"></i><span>La cuenta se activará de inmediato con el pago registrado — no se generará ningún enlace de pago.</span></div>
            <p *ngIf="manualUserError()" class="plan-form-error">{{ manualUserError() }}</p>
            </form>
            <footer class="modal-actions"><button type="button" class="secondary-btn" (click)="closeUserCreateModal()">Cancelar</button><button type="button" class="action-btn" (click)="submitManualUser()" [disabled]="isCreatingUser()">{{ isCreatingUser() ? 'Creando...' : 'Crear cuenta' }}</button></footer>
          </section>
        </div>

        <!-- Tab: Reciclaje -->
        <div *ngIf="false && activeTab() === 'reciclaje'" class="tab-content" aria-hidden="true">
          <div class="plans-intro"><div><span class="eyebrow">CONTROL Y RECUPERACIÓN</span><h3>Reciclaje</h3><p>Los negocios archivados se ocultan de Propietarios, pero permanecen disponibles para restaurarlos.</p></div></div>
          <div class="tab-loading" *ngIf="tabLoading()"><div class="spinner"></div></div>
          <table class="data-table" *ngIf="!tabLoading()"><thead><tr><th>Negocio</th><th>Correo</th><th>Propietario</th><th>Acción</th></tr></thead><tbody><tr *ngFor="let b of businesses()"><td>{{ b.name }}</td><td>{{ b.email }}</td><td>{{ b.owner?.name || '—' }}</td><td><button class="action-btn" (click)="restoreArchivedBusiness(b)">Restaurar</button></td></tr><tr *ngIf="businesses().length === 0"><td colspan="4" class="empty-cell">El reciclaje está vacío.</td></tr></tbody></table>
        </div>

        <!-- Tab: Suscripciones -->
        <div *ngIf="activeTab() === 'subs' && !tabLoading()" class="tab-content subscriptions-content">
          <header class="subscriptions-heading"><div><span class="eyebrow">CARTERA DE PLANES</span><h3>Suscripciones de negocios</h3><p>Administra condiciones, pagos y fechas de renovación desde un solo lugar.</p></div><span class="subscriptions-total"><strong>{{ totalRecords() }}</strong> registros</span></header>
          <div class="subscription-directory" *ngIf="subscriptions().length">
            <app-management-record-card
              *ngFor="let s of subscriptions()"
              [clickable]="false"
              [tone]="s.subscription.status === 'cancelled' || s.subscription.status === 'expired' ? 'inactive' : 'default'"
              kind="finance"
              [status]="subscriptionTone(s)"
              [columns]="'48px minmax(190px,1.3fr) minmax(190px,1.3fr) minmax(150px,.9fr) auto'"
            >
              <span recordIcon><i class="bi bi-gem"></i></span>
              <div recordPrimary>
                <small>SUSCRIPCIÓN #{{ s.subscription.id }}</small>
                <strong class="clamp-1">{{ s.business?.name || 'Negocio no identificado' }}</strong>
                <span class="clamp-1">{{ s.owner?.name || 'Propietario no identificado' }}</span>
              </div>
              <div recordSummary>
                <small>PLAN Y CAPACIDAD</small>
                <strong class="clamp-1">{{ isTrialItem(s) ? 'Cuenta de prueba' : planLabel(s.subscription.plan) }}</strong>
                <span class="clamp-1">{{ isTrialItem(s) ? trialCapacityLabel() : subscriptionSpaceLimitLabel(s) }}<ng-container *ngIf="s.subscription.custom_max_spaces && !isTrialItem(s)"> · Personalizado</ng-container></span>
              </div>
              <div recordStatus>
                <small>ESTADO</small>
                <span class="subscription-state" [class]="'state-' + s.subscription.status" [class.state-trial]="isTrialItem(s)" [style.color]="subscriptionStateColor(s)"><i class="bi bi-circle-fill"></i>{{ isTrialItem(s) && s.subscription.status === 'expired' ? 'Prueba finalizada' : statusLabel(s.subscription.status) }}</span>
                <span class="clamp-1 subscription-price-line"><b>{{ isTrialItem(s) ? 'Sin costo (prueba)' : (s.effective?.price_monthly | currency:'USD':'symbol':'1.2-2') }}</b><em>{{ isTrialItem(s) ? ('Vence ' + (s.subscription.trial_ends_at ? formatLongDate(s.subscription.trial_ends_at) : 'sin fecha')) : ('Renueva ' + (s.subscription.renewal_at ? formatLongDate(s.subscription.renewal_at) : 'sin fecha')) }}</em></span>
                <span class="renewal-urgency-badge" [class.overdue]="subscriptionRenewalUrgency(s) === 'overdue'" *ngIf="subscriptionRenewalUrgency(s) !== 'ok'"><i class="bi bi-alarm"></i>{{ subscriptionUrgencyLabel(s) }}</span>
              </div>
              <div recordActions>
                <button type="button" class="icon-action subscription-history" (click)="openSubscriptionContractHistory(s)" title="Ver historial contractual" aria-label="Ver historial contractual"><i class="bi bi-clock-history"></i></button>
                <button type="button" class="icon-action subscription-edit" (click)="openSubscriptionCustomization(s)" title="Personalizar" aria-label="Personalizar suscripción"><i class="bi bi-pencil-square"></i></button>
                <!-- Bug real corregido: el plan gratis no tiene nada que
                     "confirmar como pagado" -- cuesta $0 y su vigencia es la
                     prueba de 7 días, no una renovación mensual. Antes este
                     botón aparecía para CUALQUIER suscripción (incluida una
                     prueba gratuita vigente) y, si se usaba, el backend le
                     aplicaba un ciclo mensual real (ver confirmManualSubscriptionPayment),
                     dejando la prueba indistinguible de un plan pagado. -->
                <button type="button" class="icon-action subscription-payment" *ngIf="s.subscription.plan !== 'gratis'" (click)="openConfirmPaymentModal(s)" title="Confirmar pago" aria-label="Confirmar pago"><i class="bi bi-check2-circle"></i></button>
                <!-- Cobro de plan: solo tiene sentido para un plan de pago
                     (no una prueba gratuita) que esté próximo a vencer o ya
                     vencido -- ver subscriptionRenewalUrgency(). -->
                <ng-container *ngIf="!isTrialItem(s) && subscriptionRenewalUrgency(s) !== 'ok'">
                  <button type="button" class="icon-action subscription-collect-email" [disabled]="collectionLinkEmailSendingId()===s.subscription.id" (click)="sendSubscriptionCollectionLinkEmail(s)" title="Enviar correo de cobro" aria-label="Enviar correo de cobro"><i class="bi" [class.bi-envelope-paper]="collectionLinkEmailSendingId()!==s.subscription.id" [class.bi-hourglass-split]="collectionLinkEmailSendingId()===s.subscription.id"></i></button>
                  <button type="button" class="icon-action subscription-collect-copy" [disabled]="collectionLinkCopyingId()===s.subscription.id" (click)="copySubscriptionCollectionLink(s)" title="Copiar enlace de cobro" aria-label="Copiar enlace de cobro"><i class="bi" [class.bi-link-45deg]="collectionLinkCopyingId()!==s.subscription.id" [class.bi-hourglass-split]="collectionLinkCopyingId()===s.subscription.id"></i></button>
                </ng-container>
                <!-- "Cambiar fecha" oculto a propósito: es una herramienta de
                     QA para adelantar/atrasar a mano la fecha de renovación o
                     de fin de prueba y así previsualizar los avisos que ve el
                     propietario (ver el texto de showRenewalDateModal más
                     abajo), no una acción de negocio real -- no debe estar
                     visible en el panel de producción. Queda funcional por
                     debajo (modal, validaciones y endpoint intactos) por si
                     hace falta retomarla para pruebas. -->
                <button *ngIf="false" type="button" class="icon-action subscription-renewal" (click)="openRenewalDateModal(s)" title="Cambiar fecha" aria-label="Cambiar fecha de renovación"><i class="bi bi-calendar-event"></i></button>
              </div>
            </app-management-record-card>
          </div>
          <app-ui-state class="section-empty-state" *ngIf="subscriptions().length === 0" tone="empty" title="No se encontraron suscripciones" message="Ajusta los filtros para consultar otros planes o estados."></app-ui-state>
          <div class="subscriptions-pagination">
            <app-pagination [page]="currentPage()" [pages]="totalPages()" [total]="totalRecords()" [limit]="pageSize" [disabled]="tabLoading()" itemLabel="suscripciones" (pageChange)="goToPage($event)"></app-pagination>
          </div>
        </div>

        <div *ngIf="showSubscriptionCustomizationModal()" class="modal-overlay" (click)="closeSubscriptionCustomization()">
          <section class="payment-modal subscription-custom-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
            <button class="close-modal" (click)="closeSubscriptionCustomization()">×</button>
            <span class="eyebrow">ACUERDO COMERCIAL</span><h3>Personalizar suscripción</h3>
            <p>Los campos vacíos conservan automáticamente la capacidad y el precio definidos en el plan {{ planLabel(subscriptionCustomizationTarget()?.subscription?.plan) }}.</p>
            <div class="subscription-defaults"><span><small>Capacidad base del plan</small><strong>{{ planSpaceLimit(subscriptionCustomizationTarget()?.subscription?.planDefinition) }}</strong></span><span><small>Precio contractual actual</small><strong>{{ subscriptionCustomizationTarget()?.effective?.price_monthly | currency:'USD':'symbol':'1.2-2' }}</strong></span></div>
            <div *ngIf="!subscriptionRestorationPending()" class="requester-field"><label>¿Quién solicitó el cambio?<select [(ngModel)]="subscriptionCustomizationForm.requested_by_user_id" [disabled]="subscriptionBusinessUsersLoading()" [class.field-error]="subscriptionCustomizationField() === 'requested_by_user_id'" [attr.aria-invalid]="subscriptionCustomizationField() === 'requested_by_user_id'"><option [ngValue]="null">Seleccionar solicitante</option><option *ngFor="let user of subscriptionBusinessUsers()" [ngValue]="user.user_id">{{ user.name }} · {{ user.role_label }}{{ user.is_active === false ? ' (cuenta desactivada)' : '' }}</option></select></label><small *ngIf="subscriptionBusinessUsersLoading()">Cargando usuarios del negocio…</small><small *ngIf="!subscriptionBusinessUsersLoading() && subscriptionBusinessUsers().length === 0">Este negocio no tiene un propietario ni empleados registrados.</small></div>
            <div *ngIf="!subscriptionRestorationPending()" class="notification-audience"><i class="bi bi-bell"></i><span>La novedad será visible para todos los usuarios activos del negocio. El correo se enviará al propietario y, si seleccionas un empleado, también a ese empleado. Administración recibirá su aviso de plataforma por separado.</span></div>
            <div class="subscription-custom-grid"><label>Plan de suscripción<select [(ngModel)]="subscriptionCustomizationForm.new_plan" (ngModelChange)="subscriptionRestorationPending.set(false); subscriptionPriceApplication = false"><option *ngIf="subscriptionCustomizationTarget()?.subscription?.plan === 'gratis'" value="gratis" disabled>Prueba gratuita (actual) -- elige un plan para activar</option><option *ngFor="let plan of subscriptionPlanOptions()" [value]="plan.code">{{ plan.name }} · {{ plan.price_monthly | currency:'USD':'symbol':'1.2-2' }}</option></select></label><label>Máximo de espacios<input type="number" min="1" step="1" [(ngModel)]="subscriptionCustomizationForm.custom_max_spaces" (ngModelChange)="subscriptionRestorationPending.set(false)" placeholder="Usar valor del plan" [class.field-error]="subscriptionCustomizationField() === 'amounts'" [attr.aria-invalid]="subscriptionCustomizationField() === 'amounts'"></label><label>Precio mensual USD<input type="number" min="0" step="0.01" [(ngModel)]="subscriptionCustomizationForm.custom_price_monthly" (ngModelChange)="subscriptionRestorationPending.set(false); subscriptionPriceApplication = false" placeholder="Precio personalizado o usar el plan" [class.field-error]="subscriptionCustomizationField() === 'amounts'" [attr.aria-invalid]="subscriptionCustomizationField() === 'amounts'"></label><label>Comisión (%)<input type="number" min="0" max="100" step="0.01" [(ngModel)]="subscriptionCustomizationForm.commission_rate" placeholder="Usar comisión global"></label><label>Días de gracia<input type="number" min="0" max="365" step="1" [(ngModel)]="subscriptionCustomizationForm.grace_days" [placeholder]="'Usar periodo de gracia global (' + platformSettingsForm.grace_days + ' días)'"></label><label class="custom-notes">Condiciones o notas<textarea rows="3" maxlength="500" [(ngModel)]="subscriptionCustomizationForm.custom_pricing_notes" (ngModelChange)="subscriptionRestorationPending.set(false)" placeholder="Ej.: 3 espacios adicionales incluidos por $22 mensuales"></textarea></label></div>
            <div class="plan-price-application"><span><i class="bi bi-info-circle"></i> Para cobrar el precio vigente del plan seleccionado, usa esta opción. El precio anterior se reemplazará desde la próxima renovación.</span><button type="button" class="secondary-btn" (click)="applyCurrentPlanPrice()" [class.active]="subscriptionPriceApplication" [disabled]="isSavingSubscriptionCustomization()"><i class="bi" [class.bi-check-circle-fill]="subscriptionPriceApplication" [class.bi-arrow-right-circle]="!subscriptionPriceApplication"></i>{{ subscriptionPriceApplication ? 'Precio vigente seleccionado' : 'Aplicar precio vigente' }}</button></div>
            <div *ngIf="subscriptionPriceApplication" class="plan-price-selected"><i class="bi bi-check-circle-fill"></i><span>Se aplicará <strong>{{ selectedPlanCurrentPrice() | currency:'USD':'symbol':'1.2-2' }}</strong> en este negocio al guardar. Los demás negocios no cambiarán.</span></div>
            <div *ngIf="subscriptionRestorationPending()" class="notification-audience"><i class="bi bi-arrow-counterclockwise"></i><span>Se retirará la personalización. La novedad y los correos indicarán claramente que Administración de TuSala restauró la capacidad y el precio predeterminados del plan.</span></div>
            <small *ngIf="subscriptionCustomizationError()" class="reset-error">{{ subscriptionCustomizationError() }}</small>
            <div class="subscription-custom-actions"><button class="secondary-btn" type="button" (click)="resetSubscriptionCustomization()" [disabled]="isSavingSubscriptionCustomization()">Restaurar plan</button><button class="action-btn" type="button" (click)="saveSubscriptionCustomization()" [disabled]="isSavingSubscriptionCustomization()">{{ isSavingSubscriptionCustomization() ? 'Guardando...' : (subscriptionRestorationPending() ? 'Confirmar restauración' : 'Guardar personalización') }}</button></div>
          </section>
        </div>

        <div *ngIf="showRenewalDateModal()" class="modal-overlay" (click)="closeRenewalDateModal()">
          <section class="payment-modal renewal-date-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
            <button class="close-modal" (click)="closeRenewalDateModal()">×</button>
            <span class="eyebrow">{{ isTrialRenewalTarget() ? 'PRUEBA DE VENCIMIENTO' : 'PRUEBA DE RENOVACIÓN' }}</span>
            <h3>Modificar fecha límite</h3>
            <p *ngIf="!isTrialRenewalTarget()">Esta herramienta permite comprobar el aviso que el propietario ve durante los tres días previos a su renovación.</p>
            <p *ngIf="isTrialRenewalTarget()">Esta herramienta permite comprobar el bloqueo por prueba vencida que ve el propietario, adelantando o retrasando el fin de su periodo de prueba.</p>
            <label>Propietario<input [value]="renewalDateTarget()?.owner?.name || '—'" disabled></label>
            <label><span><i class="bi bi-calendar3" aria-hidden="true"></i> {{ isTrialRenewalTarget() ? 'Nueva fecha de vencimiento de prueba' : 'Nueva fecha de renovación' }}</span><input type="date" [(ngModel)]="renewalDateInput" [attr.aria-invalid]="renewalDateError() === 'Selecciona una fecha válida.'" [class.field-error]="renewalDateError()"></label>
            <button class="action-btn payment-btn" (click)="saveRenewalDate()" [disabled]="isSavingRenewalDate()">{{ isSavingRenewalDate() ? 'Guardando...' : 'Guardar fecha de prueba' }}</button>
            <small *ngIf="renewalDateError()" class="reset-error">{{ renewalDateError() }}</small>
          </section>
        </div>

        <div *ngIf="showConfirmPaymentModal()" class="modal-overlay" (click)="closeConfirmPaymentModal()">
          <section class="payment-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
            <button class="close-modal" (click)="closeConfirmPaymentModal()">×</button>
            <span class="eyebrow">PAGO ADMINISTRATIVO</span>
            <h3>Confirmar pago de suscripción</h3>
            <p>Registra un pago recibido fuera de línea. La siguiente renovación se calcula desde la fecha que ya tenía la suscripción, conservando el periodo mensual.</p>
            <div class="subscription-defaults">
              <span><small>Negocio</small><strong class="clamp-1">{{ confirmPaymentTarget()?.business?.name || 'Negocio no identificado' }}</strong></span>
              <span><small>Propietario</small><strong class="clamp-1">{{ confirmPaymentTarget()?.owner?.name || 'No identificado' }}</strong></span>
              <span><small>Plan actual</small><strong>{{ planLabel(confirmPaymentTarget()?.subscription?.plan) }}</strong></span>
              <span><small>Monto a registrar</small><strong>{{ confirmPaymentAmount(confirmPaymentTarget()) | currency:'USD':'symbol':'1.2-2' }}</strong></span>
            </div>

            <ng-container *ngIf="confirmPaymentStep() === 'form'">
              <label>Método de pago<select [(ngModel)]="confirmPaymentForm.payment_method"><option value="efectivo">Efectivo</option><option value="transferencia">Transferencia</option><option value="apay">Enlace APay</option><option value="tarjeta">Tarjeta</option><option value="otro">Otro</option></select></label>
              <label>Referencia o comprobante<input [(ngModel)]="confirmPaymentForm.payment_reference" maxlength="255" data-no-limit-hint placeholder="Ej.: caja-2026-001, número de transferencia" [class.field-error]="confirmPaymentError()" [attr.aria-invalid]="!!confirmPaymentError()"></label>
              <button class="action-btn payment-btn" (click)="reviewConfirmPayment()">Continuar</button>
            </ng-container>

            <ng-container *ngIf="confirmPaymentStep() === 'review'">
              <div class="confirm-payment-review">
                <i class="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>
                <span>Vas a registrar <strong>{{ confirmPaymentAmount(confirmPaymentTarget()) | currency:'USD':'symbol':'1.2-2' }}</strong> por <strong>{{ confirmPaymentMethodLabel() }}</strong>, referencia «{{ confirmPaymentForm.payment_reference }}». Esto activa la suscripción, reactiva el negocio y la cuenta del propietario si estaban desactivados, y se notifica por correo al propietario y a administración. Esta acción no se puede deshacer.</span>
              </div>
              <div class="confirm-payment-actions">
                <button type="button" class="secondary-btn" (click)="backToConfirmPaymentForm()" [disabled]="isConfirmingPayment()">Volver</button>
                <button class="action-btn payment-btn" (click)="saveConfirmPayment()" [disabled]="isConfirmingPayment()">{{ isConfirmingPayment() ? 'Confirmando...' : 'Sí, confirmar pago' }}</button>
              </div>
            </ng-container>

            <small *ngIf="confirmPaymentError()" class="reset-error">{{ confirmPaymentError() }}</small>
          </section>
        </div>

        <div *ngIf="showSubscriptionHistoryModal()" class="modal-overlay" (click)="closeSubscriptionContractHistory()">
          <section class="payment-modal contract-history-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
            <header class="history-modal-header">
              <div><span class="eyebrow">HISTORIAL CONTRACTUAL</span><h3>{{ subscriptionHistoryTarget()?.business?.name || 'Suscripción' }}</h3></div>
              <button class="close-modal" (click)="closeSubscriptionContractHistory()" aria-label="Cerrar histórico">×</button>
            </header>
            <div class="history-modal-body">
              <p>Registro de plan y precio acordados. El precio efectivo permanece separado del catálogo público.</p>
              <div class="contract-history-list" *ngIf="subscriptionContractHistoryLoading()">Cargando historial…</div>
              <div class="contract-history-list" *ngIf="!subscriptionContractHistoryLoading() && subscriptionContractHistory().length">
                <article *ngFor="let entry of subscriptionContractHistory()">
                  <strong>{{ subscriptionHistoryActionLabel(entry.action) }}</strong>
                  <span>{{ formatLongDateTime(entry.created_at) }}</span>
                  <span *ngIf="entry.previous_plan_name || entry.plan_name">{{ entry.previous_plan_name || 'Sin plan' }} → {{ entry.plan_name || 'Sin plan' }}</span>
                  <span>{{ entry.previous_agreed_price_monthly === null || entry.previous_agreed_price_monthly === undefined ? '—' : (entry.previous_agreed_price_monthly | currency:'USD':'symbol':'1.2-2') }} → {{ entry.agreed_price_monthly === null || entry.agreed_price_monthly === undefined ? '—' : (entry.agreed_price_monthly | currency:'USD':'symbol':'1.2-2') }}</span>
                </article>
              </div>
              <div class="contract-history-list" *ngIf="!subscriptionContractHistoryLoading() && !subscriptionContractHistory().length">No hay movimientos contractuales registrados.</div>
            </div>
          </section>
        </div>

        <div *ngIf="showPlanHistoryModal()" class="modal-overlay" (click)="closePlanHistory()">
          <section class="payment-modal contract-history-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
            <header class="history-modal-header">
              <div><span class="eyebrow">HISTORIAL DEL CATÁLOGO</span><h3>{{ planHistoryTarget()?.name || 'Plan' }}</h3></div>
              <button class="close-modal" (click)="closePlanHistory()" aria-label="Cerrar histórico">×</button>
            </header>
            <div class="history-modal-body">
              <p>Estos son los cambios del plan público. No sustituyen los precios acordados de cada negocio.</p>
              <div class="contract-history-list" *ngIf="planHistoryLoading()">Cargando histórico…</div>
              <div class="contract-history-list" *ngIf="!planHistoryLoading() && planHistory().length">
                <article *ngFor="let entry of planHistory()">
                  <strong>{{ planHistoryActionLabel(entry.action, entry.snapshot) }}</strong>
                  <span>{{ formatLongDateTime(entry.created_at) }}</span>
                  <span *ngIf="entry.snapshot?.price_change; else planPriceOnly">{{ entry.snapshot.price_change.previous_price | currency:'USD':'symbol':'1.2-2' }} → {{ entry.snapshot.price_monthly | currency:'USD':'symbol':'1.2-2' }}</span>
                  <ng-template #planPriceOnly><span>{{ entry.snapshot?.price_monthly | currency:'USD':'symbol':'1.2-2' }}</span></ng-template>
                </article>
              </div>
              <div class="contract-history-list" *ngIf="!planHistoryLoading() && !planHistory().length">No hay cambios registrados para este plan.</div>
            </div>
          </section>
        </div>

        <!-- Tab: Pagos a negocios -->
        <!-- Personalización: configuración global de identidad que TuSala
             muestra en sus frentes públicos. Cada pestaña es un componente
             independiente que carga y guarda sus propios datos. -->
        <div *ngIf="activeTab() === 'personalizacion' && !tabLoading()" class="tab-content personalization-content">
          <!-- El título y la descripción ya los muestra el encabezado de la
               página (dashboard): aquí solo van las pestañas del módulo. -->
          <app-admin-tabs [tabs]="personalizacionTabs" [active]="personalizacionTab()" ariaLabel="Secciones de personalización" (tabSelect)="selectPersonalizacionTab($event)"></app-admin-tabs>
          <div class="personalization-panel" *ngIf="personalizacionTab() === 'redes'" role="tabpanel">
            <app-platform-social-links-admin></app-platform-social-links-admin>
          </div>
          <div class="personalization-panel" *ngIf="personalizacionTab() === 'legales'" role="tabpanel">
            <app-platform-legal-documents-admin></app-platform-legal-documents-admin>
          </div>
        </div>

        <div *ngIf="activeTab() === 'pagosNegocios' && !tabLoading()" class="tab-content business-payouts-content">
          <div class="tab-loading" *ngIf="tabLoading()"><div class="spinner"></div></div>
          <div *ngIf="!tabLoading()">
            <div class="section-actions payout-section-heading">
              <div><span class="eyebrow">PAGOS A NEGOCIOS</span><h3>Transferencias pendientes por empresa</h3><p>Consulta el saldo de reservas y registra transferencias realizadas por medios externos.</p></div>
              <div class="payout-heading-summary"><strong>{{businessesPendingTotal()|currency:'USD'}}</strong><small>{{businessesWithBalance()}} negocios con saldo</small></div>
              <div class="payout-heading-summary payout-heading-debt" *ngIf="businessesWithDebt()"><strong>{{businessesDebtTotal()|currency:'USD'}}</strong><small>{{businessesWithDebt()}} negocios deben a TuSala</small></div>
            </div>
            <div class="payout-business-grid">
              <button class="payout-business-card" *ngFor="let b of payoutBusinesses(); trackBy: trackPayoutBusiness" (click)="openPayoutBusiness(b)">
                <span class="business-card-icon"><i class="bi bi-building"></i></span>
                <div class="business-card-identity"><span>NEGOCIO</span><strong>{{b.company||b.name}}</strong><small>{{b.email}}</small><small>Propietario: {{b.owner_name||'Sin nombre'}} · {{b.owner_email||'Sin correo'}}</small><div class="business-card-tags"><b><i class="bi bi-receipt"></i>{{b.reservation_payments}} pagos</b><b><i class="bi bi-bank"></i>{{b.payout_methods}} destinos</b></div></div>
                <div class="business-card-metric">
                  <ng-container *ngIf="b.available_balance>0"><span>SALDO POR TRANSFERIR</span><strong class="payout-amount">{{b.available_balance|currency:'USD'}}</strong></ng-container>
                  <ng-container *ngIf="b.available_balance<=0"><span>SALDO POR TRANSFERIR</span><strong class="payout-amount payout-amount-zero">{{b.available_balance|currency:'USD'}}</strong></ng-container>
                  <!-- Dirección OPUESTA a lo de arriba: esto es lo que el
                       NEGOCIO le debe a TuSala (comisión no cubierta por la
                       pasarela), nunca se resta ni se mezcla con el saldo a
                       transferir. -->
                  <span class="payout-debt-chip" *ngIf="b.debt_balance>0"><i class="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>Debe {{b.debt_balance|currency:'USD'}} a TuSala</span>
                  <small>{{b.pending_payments}} pagos nuevos</small>
                  <small>Última transferencia: {{b.last_transfer_at?(b.last_transfer_at|date:'mediumDate'):'Sin registros'}}</small>
                </div>
                <div class="business-card-status">
                  <span [class.ready]="b.available_balance<=0 && b.debt_balance<=0" [class.debt]="b.debt_balance>0">
                    <i class="bi" [class.bi-exclamation-triangle-fill]="b.debt_balance>0" [class.bi-clock-history]="b.debt_balance<=0 && b.available_balance>0" [class.bi-check-circle]="b.debt_balance<=0 && b.available_balance<=0"></i>{{b.debt_balance>0?'Debe a TuSala':(b.available_balance>0?'Requiere gestión':'Al día')}}
                  </span>
                  <small>Ver detalle <i class="bi bi-arrow-right"></i></small>
                </div>
              </button>
              <app-ui-state class="section-empty-state" *ngIf="!payoutBusinesses().length" tone="empty" title="Sin negocios coincidentes" message="Prueba con otro nombre o correo."></app-ui-state>
            </div>
            <app-pagination [page]="currentPage()" [pages]="totalPages()" [total]="totalRecords()" [limit]="pageSize" [disabled]="tabLoading()" itemLabel="negocios" ariaLabel="Paginación de pagos a negocios" (pageChange)="goToPage($event)"></app-pagination>
          </div>
        </div>

        <!-- Tab: Pagos de suscripciones -->
        <div *ngIf="activeTab() === 'pagos' && !tabLoading()" class="tab-content subscription-payments-content">
          <div class="tab-loading" *ngIf="tabLoading()"><div class="spinner"></div></div>
          <div *ngIf="!tabLoading()">
            <div class="section-actions subscription-section-heading"><div><span class="eyebrow">PAGOS DE SUSCRIPCIONES</span><h3>Control de ingresos por planes</h3><p>Consulta estados, montos, métodos y comprobantes de las suscripciones TuSala.</p></div><div class="subscription-heading-total"><strong>{{subscriptionPaymentsTotal()|currency:'USD'}}</strong><small>Total registrado</small></div></div>
            <div class="sp-tabs-wrap">
              <app-admin-tabs [tabs]="paymentStatusTabs()" [active]="paymentStatusFilter" ariaLabel="Filtrar pagos por estado" (tabSelect)="selectPaymentStatusTab($event)"></app-admin-tabs>
            </div>
            <div class="sp-list">
              <article class="sp-card" *ngFor="let p of allPayments()" [class]="'sp-' + p.status">
                <div class="sp-indicator"></div>
                <div class="sp-icon"><i class="bi bi-credit-card-2-front"></i></div>
                <div class="sp-main">
                  <h3 class="sp-title">{{ p.business_name || p.owner_name || p.email }}</h3>
                  <p class="sp-summary">{{ p.owner_name || 'Sin nombre' }} · {{ p.email }}</p>
                </div>
                <div class="sp-tags">
                  <span class="status-badge sp-status-tag" [class]="'status-' + p.status">{{ statusLabel(p.status) }}</span>
                  <span class="sp-tag"><i class="bi bi-bank"></i>{{ paymentMethodLabel(p.payment_provider) }}</span>
                  <span class="sp-tag"><i class="bi bi-tag"></i>{{ paymentConceptLabel(p) }}</span>
                </div>
                <div class="sp-context">
                  <strong class="sp-amount">{{ p.amount | currency:'USD' }}</strong>
                  <small class="sp-date"><i class="bi bi-calendar3"></i>{{ formatLongDateTime(p.payment_date || p.created_at) }}</small>
                </div>
                <div class="sp-receipt-col">
                  <button *ngIf="p.status === 'succeeded'" class="sp-receipt" type="button" (click)="downloadSubscriptionReceipt(p)" title="Descargar comprobante PDF"><i class="bi bi-file-earmark-pdf"></i> PDF</button>
                  <span *ngIf="p.status !== 'succeeded'" class="sp-receipt-missing">Sin comprobante</span>
                </div>
              </article>
              <p *ngIf="allPayments().length === 0" class="empty-cell">Sin datos</p>
            </div>
            <app-pagination [page]="currentPage()" [pages]="totalPages()" [total]="totalRecords()" [limit]="pageSize" [disabled]="tabLoading()" itemLabel="suscripciones" (pageChange)="goToPage($event)"></app-pagination>
          </div>
        </div>
        <div *ngIf="payoutDetail()" class="modal-overlay" (click)="payoutDetail.set(null)">
          <section class="modal-box payout-modal" (click)="$event.stopPropagation()">
            <div class="modal-header">
              <div><span class="eyebrow">TRANSFERENCIA EXTERNA</span><h3>{{payoutDetail().business.company||payoutDetail().business.name}}</h3><p>Registra el resultado de una transferencia realizada fuera de TuSala.</p></div>
              <button class="close-btn" (click)="payoutDetail.set(null)">×</button>
            </div>
            <div class="owner-contact-strip" *ngIf="payoutDetail().business.owner_name || payoutDetail().business.owner_phone">
              <span><i class="bi bi-person-badge"></i> {{payoutDetail().business.owner_name || 'Propietario sin nombre registrado'}}</span>
              <a *ngIf="payoutDetail().business.owner_phone" [href]="'tel:' + payoutDetail().business.owner_phone"><i class="bi bi-telephone-fill"></i> {{payoutDetail().business.owner_phone}}</a>
              <span *ngIf="!payoutDetail().business.owner_phone" class="owner-contact-missing"><i class="bi bi-telephone-x"></i> Sin teléfono registrado</span>
            </div>
            <div class="payment-summary-grid">
              <article class="payment-summary total"><span>Total generado</span><strong>{{payoutDetail().total_gross_generated|currency:'USD'}}</strong><small>Histórico completo, antes de comisión (pasarela + externo)</small></article>
              <!-- Corrección pedida: "A pagar tras comisión" solo describe el
                   dinero cobrado VÍA PASARELA (lo único que TuSala de verdad
                   transfiere -- ver payoutBalance()/netPlatformPayable en el
                   backend), nunca el total generado de arriba (que incluye
                   pagos externos). Sin aclararlo, un negocio con todo su
                   historial cobrado externamente ve "Total generado $4,607"
                   junto a "A pagar tras comisión $0.00" y parece un error --
                   cuando en realidad es correcto: ese dinero nunca pasó por
                   TuSala, así que no hay nada que TuSala deba transferir por
                   él (su comisión se cobra aparte, en "Comisión pendiente" /
                   "Debe a TuSala"). -->
              <article class="payment-summary pending"><span>A pagar tras comisión (pasarela)</span><strong>{{payoutDetail().total_net_payable|currency:'USD'}}</strong><small>Neto histórico de pasarela, pagado + pendiente -- no incluye pagos externos</small></article>
              <article class="payment-summary approved"><span>Total pendiente</span><strong>{{payoutDetail().balance.available|currency:'USD'}}</strong><small>{{payoutPendingBreakdownLabel() || 'Disponible para registrar'}}</small></article>
              <!-- Corrección pedida: la deuda del NEGOCIO hacia TuSala vivía
                   solo hasta el final del modal (dentro de "Estado de
                   cuenta") -- con la misma relevancia visual que "Total
                   pendiente" (lo contrario), no como un dato secundario. -->
              <article class="payment-summary failed" *ngIf="(payoutDetail().origin_breakdown?.business_owes_company||0)>0"><span>Debe a TuSala</span><strong>{{payoutDetail().origin_breakdown.business_owes_company|currency:'USD'}}</strong><small>Comisión pendiente por cobrar</small></article>
              <article class="payment-summary pending"><span>Total transferido</span><strong>{{payoutDetail().balance.paid_total|currency:'USD'}}</strong><small>Historial acumulado</small></article>
              <article class="payment-summary total"><span>Método principal</span><strong>{{primaryPayoutMethod()?.institution_name||'Sin configurar'}}</strong><small>•••• {{primaryPayoutMethod()?.account_last4||'—'}}</small></article>
            </div>
            <div class="sa-origin-breakdown" *ngIf="payoutDetail().origin_breakdown">
              <div class="sa-origin-grid">
                <div class="sa-origin-cell"><span>Total del periodo vigente</span><strong>{{payoutDetail().origin_breakdown.total_gross|currency:'USD'}}</strong><small>Reservas aún sin agrupar en una liquidación (plataforma + externo) -- no incluye lo del cuadro "Total generado" de arriba que ya quedó agrupado</small></div>
                <div class="sa-origin-cell"><span>Cobrado por TuSala</span><strong>{{payoutDetail().origin_breakdown.platform_gross|currency:'USD'}}</strong><small>Vía pasarela (enlace de pago)</small></div>
                <div class="sa-origin-cell"><span>Cobrado por el negocio (externo)</span><strong>{{payoutDetail().origin_breakdown.external_gross|currency:'USD'}}</strong><small>Pagado directo al negocio por otro medio</small></div>
                <div class="sa-origin-cell"><span>Comisión ({{payoutDetail().origin_breakdown.commission_rate}}%)</span><strong>-{{payoutDetail().origin_breakdown.commission_amount|currency:'USD'}}</strong><small>Se aplica sobre el total, sin importar el origen</small></div>
              </div>
              <div class="sa-origin-position" [class.to-company]="(payoutDetail().origin_breakdown.business_owes_company||0)>0">
                <i class="bi" [class.bi-cash-stack]="(payoutDetail().origin_breakdown.company_owes_business||0)>0" [class.bi-arrow-up-circle]="(payoutDetail().origin_breakdown.business_owes_company||0)>0"></i>
                <div>
                  <small>Resultado al cierre de mes (periodo vigente)</small>
                  <ng-container *ngIf="(payoutDetail().origin_breakdown.company_owes_business||0)>0">
                    <strong>TuSala debe al negocio {{payoutDetail().origin_breakdown.company_owes_business|currency:'USD'}}</strong>
                    <span>Dinero cobrado por la pasarela menos la comisión sobre el total.</span>
                  </ng-container>
                  <ng-container *ngIf="(payoutDetail().origin_breakdown.company_owes_business||0)<=0 && (payoutDetail().origin_breakdown.business_owes_company||0)>0">
                    <strong>El negocio debe a TuSala {{payoutDetail().origin_breakdown.business_owes_company|currency:'USD'}}</strong>
                    <span>Comisión no cubierta porque el cliente pagó al negocio por otro medio. Puedes cobrarla ahora, sin esperar a que termine el mes.</span>
                  </ng-container>
                  <ng-container *ngIf="(payoutDetail().origin_breakdown.company_owes_business||0)<=0 && (payoutDetail().origin_breakdown.business_owes_company||0)<=0">
                    <strong>Cuentas en equilibrio</strong>
                    <span>No hay montos pendientes entre TuSala y este negocio.</span>
                  </ng-container>
                </div>
                <button
                  type="button"
                  class="action-btn debt-position-pay-btn"
                  *ngIf="(payoutDetail().origin_breakdown.business_owes_company||0)>0 && (payoutDetail().origin_breakdown.company_owes_business||0)<=0"
                  [disabled]="debtSettlementSaving()"
                  (click)="collectCurrentDebtNow(payoutDetail().business.id)"
                >{{debtSettlementSaving()?'Actualizando…':(pendingAdminDebtSettlement()?'Ver / cobrar liquidación':'Cobrar ahora')}}</button>
              </div>
              <p class="sa-debt-tab-hint" *ngIf="debtSettlements().length">
                <i class="bi bi-clock-history" aria-hidden="true"></i> Consulta el detalle de cada periodo facturado en la pestaña <strong>Comisión pendiente</strong>.
              </p>
            </div>

            <app-admin-tabs [tabs]="payoutModalTabs" [active]="payoutModalTab()" [compact]="true" ariaLabel="Secciones del detalle de pagos" (tabSelect)="selectPayoutTab($event)"></app-admin-tabs>

            <div class="payout-tab-panel" *ngIf="payoutModalTab()==='general'">
              <div class="payout-destination-grid" *ngIf="payoutDetail().methods.length; else noPayoutDestination">
                <article *ngFor="let method of payoutDetail().methods" [class.primary-destination]="method.is_primary">
                  <div><span>{{method.is_primary?'DESTINO PRINCIPAL':'DESTINO ALTERNATIVO'}}</span><strong>{{method.label}}</strong></div>
                  <dl>
                    <div><dt>Banco</dt><dd>{{method.institution_name}}</dd></div>
                    <div><dt>Titular</dt><dd>{{method.account_holder}}</dd></div>
                    <div><dt>Número de cuenta</dt><dd class="account-number">{{method.account_number}}</dd></div>
                    <div><dt>Tipo de cuenta</dt><dd>{{method.bank_account_type==='savings'?'Ahorro':'Corriente'}}</dd></div>
                  </dl>
                </article>
              </div>
              <ng-template #noPayoutDestination><p class="payout-warning">Este negocio todavía no ha registrado información para recibir fondos.</p></ng-template>
              <section class="simple-transfer-form" *ngIf="payoutDetail().balance.available>0 && payoutDetail().methods.length">
                <div><span class="eyebrow">REGISTRAR RESULTADO</span><h4>Transferencia realizada fuera de TuSala</h4><p>Indica el total transferido o registra un pago parcial.</p></div>
                <div class="simple-transfer-fields">
                  <label>Monto transferido<input placeholder="Ej: 150.00" type="number" min="0.01" step="0.01" [max]="payoutDetail().balance.available" [(ngModel)]="businessTransferForm.amount"></label>
                  <label>Destino<select [(ngModel)]="businessTransferForm.payout_method_id"><option [ngValue]="null">Selecciona</option><option *ngFor="let method of payoutDetail().methods" [ngValue]="method.id">{{method.label}} · {{method.institution_name}}</option></select></label>
                  <label>Referencia <small>(opcional)</small><input [(ngModel)]="businessTransferForm.transfer_reference" maxlength="190" placeholder="Código o referencia externa"></label>
                  <label class="comment-field">Comentario para el propietario <small>(opcional)</small><textarea [(ngModel)]="businessTransferForm.notes" maxlength="500" rows="3" placeholder="Comentario breve"></textarea></label>
                </div>
                <button class="action-btn" type="button" [disabled]="payoutTransferSaving()||cooldown.getSignal('sa-transfer-save')()" (click)="recordBusinessTransfer()" cooldownBubble data-cooldown-key="sa-transfer-save">{{payoutTransferSaving()?'Registrando...':'Registrar transferencia externa'}}</button>
              </section>
            </div>

            <!-- Pedido del usuario: organizar esta pestaña como el módulo de
                 Reservas -- las fechas de un conjunto multi-día colapsan en
                 una sola tarjeta (con desplegar/contraer), y se agrega
                 buscador + filtro. Usa su propio endpoint/paginación, POR
                 GRUPO (ver getAdminBusinessReservationPayments) -- la lista
                 embebida en payoutDetail() sigue existiendo para no romper
                 la pantalla del propietario, pero esta pestaña ya no la usa. -->
            <div class="payout-tab-panel" *ngIf="payoutModalTab()==='reservas'">
              <section class="business-payment-history payout-reservations-section">
                <div class="section-actions"><div><span class="eyebrow">PAGOS DE RESERVAS DEL NEGOCIO</span><p>Estos movimientos pertenecen únicamente a {{payoutDetail().business.company||payoutDetail().business.name}}. Las reservas de varias fechas aparecen agrupadas en una sola tarjeta.</p></div><span class="scope-chip">{{payoutReservationGroupsTotal()}} {{payoutReservationGroupsTotal()===1?'reserva':'reservas'}}</span></div>
                <div class="payout-reservation-filter">
                  <label>Buscar por espacio o número de reserva
                    <input type="text" [ngModel]="payoutReservationSearch()" (ngModelChange)="onPayoutReservationSearchChange($event)" (keyup.enter)="applyPayoutReservationFilters()" placeholder="Ej. Sala Ártico, R-0151…">
                  </label>
                  <button type="button" class="btn-refresh" (click)="applyPayoutReservationFilters()" [disabled]="payoutReservationGroupsLoading()">Buscar</button>
                  <button type="button" class="btn-secondary icon-only" (click)="resetPayoutReservationFilters()" [disabled]="payoutReservationGroupsLoading()" title="Restablecer filtros" aria-label="Restablecer filtros"><i class="bi bi-arrow-counterclockwise"></i></button>
                </div>
                <app-ui-skeleton *ngIf="payoutReservationGroupsLoading()" variant="table" [count]="5" [columnCount]="3" label="Cargando pagos de reservas" [compact]="true"></app-ui-skeleton>
                <div class="reservation-payment-list" *ngIf="!payoutReservationGroupsLoading()">
                  <ng-container *ngFor="let g of payoutReservationGroups(); trackBy: trackPayoutReservationGroup">
                    <article class="payout-reservation-group" [class.is-group]="g.is_group">
                      <div class="payout-reservation-group-main" [class.expandable]="g.is_group" (click)="g.is_group && togglePayoutReservationGroup(g.group_key)">
                        <div>
                          <strong>{{g.space_title}}</strong>
                          <small>{{g.representative_date|date:'medium'}} · {{g.is_group ? ('Reservas #'+g.min_reservation_number+'–#'+g.max_reservation_number+' · '+g.payment_count+' fechas') : ('Reserva #'+g.min_reservation_number)}}</small>
                        </div>
                        <span class="status-badge status-active">Aprobado</span>
                        <div class="reservation-payment-amount">
                          <strong>{{g.net_amount|currency:'USD'}}</strong>
                          <small>Cobrado {{g.gross_amount|currency:'USD'}} · Comisión -{{g.commission_amount|currency:'USD'}}</small>
                          <small *ngIf="g.all_external">Cobrado directo (externo) -- nada por transferir</small>
                          <small *ngIf="!g.all_external">{{g.all_settled?'Procesado':'Pendiente de transferencia'}}</small>
                        </div>
                        <i class="bi payout-reservation-group-chevron" *ngIf="g.is_group" [class.bi-chevron-down]="!payoutReservationExpanded().has(g.group_key)" [class.bi-chevron-up]="payoutReservationExpanded().has(g.group_key)" aria-hidden="true"></i>
                      </div>
                      <div class="payout-reservation-group-members" *ngIf="g.is_group && payoutReservationExpanded().has(g.group_key)">
                        <div class="payout-reservation-member" *ngFor="let m of g.members; trackBy: trackPayoutReservationMember">
                          <span>{{(m.reservation_date||m.payment_date)|date:'mediumDate'}} · Reserva #{{m.business_reservation_number}}</span>
                          <span>{{m.net_amount|currency:'USD'}} <small>(cobrado {{m.gross_amount|currency:'USD'}} · comisión -{{m.commission_amount|currency:'USD'}})</small></span>
                        </div>
                      </div>
                    </article>
                  </ng-container>
                  <p *ngIf="!payoutReservationGroups().length" class="empty-cell">{{payoutReservationSearch() ? 'No encontramos pagos con ese criterio.' : 'Este negocio todavía no posee pagos de reservas.'}}</p>
                </div>
                <app-pagination [page]="payoutReservationGroupsPage()" [pages]="payoutReservationGroupsPages()" [total]="payoutReservationGroupsTotal()" [limit]="payoutReservationGroupsLimit()" [disabled]="payoutReservationGroupsLoading()" itemLabel="reservas" ariaLabel="Paginación de pagos de reservas" (pageChange)="goToPayoutReservationPage($event)"></app-pagination>
              </section>
            </div>

            <div class="payout-tab-panel" *ngIf="payoutModalTab()==='transferencias'">
              <div class="simple-transfer-history" *ngIf="transferHistory().length">
                <div class="section-actions"><div><span class="eyebrow">HISTORIAL</span><h4>Transferencias registradas</h4></div><span class="scope-chip">{{payoutDetail().settlements_total||0}} liquidaciones</span></div>
                <article *ngFor="let transfer of transferHistory(); trackBy: trackPayoutTransfer"><div><strong>{{transfer.amount|currency:'USD'}}</strong><small>{{transfer.performed_at|date:'medium'}} · {{transfer.performed_by_name}}</small></div><div><span>{{transfer.transfer_reference||'Sin referencia'}}</span><small>{{transfer.notes||'Sin comentario'}}</small></div></article>
              </div>
              <p *ngIf="!transferHistory().length" class="empty-cell">Este negocio todavía no tiene transferencias registradas.</p>
              <app-pagination [page]="payoutDetail().settlements_page||1" [pages]="payoutSettlementPages()" [total]="payoutDetail().settlements_total||0" [limit]="payoutDetail().settlements_limit||25" itemLabel="liquidaciones" ariaLabel="Paginación de transferencias" (pageChange)="loadPayoutSettlementPage($event)"></app-pagination>
            </div>

            <!-- Comisión pendiente (negocio -> TuSala): pestaña propia para
                 que el detalle no empuje el resto hacia abajo cuando hay
                 muchos periodos -- el resumen del periodo vigente sigue
                 siempre visible arriba, en "Estado de cuenta". -->
            <div class="payout-tab-panel" *ngIf="payoutModalTab()==='deuda' && payoutDetail() as pd">
              <div class="section-actions">
                <div><span class="eyebrow">DEUDA DEL NEGOCIO A TUSALA</span><h4>Comisión pendiente por periodo</h4><p>Cuando el cliente le paga al negocio directo (efectivo, transferencia, etc.), TuSala no recibe nada vía pasarela para cubrir su comisión sobre esa reserva -- ese faltante se cierra aquí, periodo por periodo.</p></div>
              </div>

              <div class="debt-settlement-history" *ngIf="debtSettlements().length; else noDebtSettlements">
                <article class="debt-settlement-card" *ngFor="let s of debtSettlements()">
                  <header class="debt-settlement-card-head">
                    <div><small>PERIODO FACTURADO</small><strong>{{s.period_start|date:'mediumDate'}} – {{s.period_end|date:'mediumDate'}}</strong></div>
                    <span class="debt-status" [class.paid]="s.status==='paid'">{{s.status==='paid'?'Pagado':'Pendiente'}}</span>
                  </header>
                  <dl class="debt-settlement-detail-grid">
                    <div><dt>Total generado en el periodo</dt><dd>{{s.total_gross_amount|currency:'USD'}}</dd></div>
                    <div><dt>Cobrado por TuSala (pasarela)</dt><dd>{{s.platform_gross_amount|currency:'USD'}}</dd></div>
                    <div><dt>Cobrado por el negocio (externo)</dt><dd>{{s.external_gross_amount|currency:'USD'}}</dd></div>
                    <div><dt>Comisión ({{s.commission_rate}}%) sobre el total</dt><dd>{{s.commission_amount|currency:'USD'}}</dd></div>
                    <div class="debt-settlement-highlight"><dt>{{s.status==='paid'?'Monto pagado a TuSala':'Monto a pagar'}}</dt><dd>{{s.amount_due|currency:'USD'}}</dd></div>
                  </dl>
                  <footer class="debt-settlement-card-footer" *ngIf="s.status==='paid'">
                    <span><i class="bi bi-check-circle" aria-hidden="true"></i> Pagado por <strong>{{s.payment_method}}</strong> el {{s.paid_at|date:'medium'}}</span>
                    <span *ngIf="s.payment_reference">Referencia: {{s.payment_reference}}</span>
                  </footer>
                  <footer class="debt-settlement-card-footer debt-settlement-card-actions" *ngIf="s.status==='pending'">
                    <button type="button" class="secondary-btn" [disabled]="debtLinkCopyingId()===s.id" (click)="copyDebtSettlementLink(s)">{{debtLinkCopyingId()===s.id?'Generando…':'Copiar enlace de pago'}}</button>
                    <button type="button" class="secondary-btn" [disabled]="debtEmailSendingId()===s.id" (click)="sendDebtSettlementLinkEmail(s)">{{debtEmailSendingId()===s.id?'Enviando…':'Enviar enlace por correo'}}</button>
                    <button type="button" class="action-btn" (click)="toggleDebtConfirmForm(s.id)">{{debtConfirmOpenId()===s.id?'Cancelar':'Registrar pago manual'}}</button>
                  </footer>
                  <div class="debt-confirm-form" *ngIf="debtConfirmOpenId()===s.id && debtConfirmForm[s.id] as form">
                    <label>Método de pago<select [(ngModel)]="form.payment_method"><option value="efectivo">Efectivo</option><option value="transferencia">Transferencia bancaria</option><option value="apay">APay</option><option value="tarjeta">Tarjeta</option><option value="otro">Otro</option></select></label>
                    <label>Referencia <small>(opcional)</small><input [(ngModel)]="form.payment_reference" maxlength="190" placeholder="N.° de comprobante"></label>
                    <label class="comment-field">Comentario <small>(opcional)</small><textarea placeholder="Ej: Transferencia realizada desde Banco Agrícola" [(ngModel)]="form.notes" maxlength="500" rows="2"></textarea></label>
                    <button type="button" class="action-btn" [disabled]="debtConfirmingId()===s.id" (click)="confirmDebtSettlementPayment(s)">{{debtConfirmingId()===s.id?'Registrando…':'Confirmar pago recibido'}}</button>
                  </div>
                </article>
              </div>
              <ng-template #noDebtSettlements><p class="payout-warning">Este negocio no tiene liquidaciones de deuda registradas.</p></ng-template>

              <section class="simple-transfer-form" *ngIf="(pd.origin_breakdown?.business_owes_company||0)>0 && !pendingAdminDebtSettlement()">
                <div><span class="eyebrow">CERRAR PERIODO</span><h4>Crear liquidación de deuda</h4><p>Por defecto cobra la deuda VIGENTE (desde el pago más antiguo sin liquidar hasta hoy). Usa las fechas solo para cerrar un periodo histórico puntual.</p></div>
                <button class="action-btn" type="button" [disabled]="debtSettlementSaving()" (click)="collectCurrentDebtNow(pd.business.id)">{{debtSettlementSaving()?'Cerrando…':'Cerrar y cobrar periodo vigente'}}</button>
                <div class="simple-transfer-fields">
                  <label>Desde <small>(opcional -- periodo histórico)</small><input type="date" [(ngModel)]="debtSettlementForm.period_start"></label>
                  <label>Hasta <small>(opcional -- periodo histórico)</small><input type="date" [(ngModel)]="debtSettlementForm.period_end"></label>
                  <label class="comment-field">Comentario <small>(opcional)</small><textarea placeholder="Ej: Comisiones de septiembre 2026" [(ngModel)]="debtSettlementForm.notes" maxlength="500" rows="2"></textarea></label>
                </div>
                <button class="secondary-btn" type="button" [disabled]="debtSettlementSaving()" (click)="createDebtSettlement()">{{debtSettlementSaving()?'Creando...':'Crear liquidación para el periodo indicado'}}</button>
              </section>
            </div>
          </section>
        </div>

        <div *ngIf="showPlanModal()" class="modal-overlay plan-modal-overlay" (click)="closePlanModal()">
          <section class="plan-editor" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
            <header class="plan-editor-header">
              <div><span class="eyebrow">CONFIGURACIÓN DEL PLAN</span><h3>{{ planForm.name || 'Nuevo plan' }}</h3><p>Los valores se aplican a las validaciones del servidor.</p></div>
              <button type="button" class="close-plan-modal" (click)="closePlanModal()">×</button>
            </header>

            <form #planEditorForm="ngForm" #planEditorFormEl (ngSubmit)="savePlan()" novalidate>
            <div class="plan-form-grid">
              <label>Nombre público<input placeholder="Ej: Plan Estándar" [(ngModel)]="planForm.name" name="planName" required maxlength="100"></label>
              <label>Código interno<input placeholder="Ej: estandar" [(ngModel)]="planForm.code" name="planCode" required minlength="3" maxlength="50" pattern="[a-z0-9_-]+"></label>
              <label>Precio mensual (USD)<input placeholder="Ej: 29.99" [(ngModel)]="planForm.price_monthly" name="planPrice" type="number" required min="0" step="0.01"></label>
              <label *ngIf="hasPlanPriceChange()">Aplicación del nuevo precio<select [(ngModel)]="planForm.price_scope" name="priceScope"><option value="preserve_existing">Solo nuevas suscripciones</option><option value="apply_existing">Suscripciones existentes sin precio personalizado</option></select></label>
              <small *ngIf="hasPlanPriceChange()" class="full-field field-hint">Los acuerdos personalizados se conservan. La aplicación global enviará avisos y quedará registrada.</small>
              <label *ngIf="hasPlanPriceChange() && planForm.price_scope === 'apply_existing'" class="full-field">Motivo del cambio (obligatorio)<textarea [(ngModel)]="planForm.price_change_reason" name="priceChangeReason" required rows="2" maxlength="500" placeholder="Explica por qué cambia el precio — se incluye tal cual en el correo a los propietarios afectados."></textarea></label>
              <label>Máximo de espacios<input placeholder="Ej: 5" [(ngModel)]="planForm.limits.max_spaces" name="maxSpaces" type="number" required min="1" step="1"></label>
              <label>Máximo de imágenes por espacio<input placeholder="Ej: 10" [(ngModel)]="planForm.limits.max_images_per_space" name="maxImages" type="number" required min="1" step="1"></label>
              <label *ngIf="planForm.benefits.featured_listing === 1">Máximo de espacios destacados<input placeholder="Ej: 2" [(ngModel)]="planForm.limits.max_featured_spaces" name="maxFeaturedSpaces" type="number" required min="1" step="1"></label>
              <label *ngIf="planForm.code === 'gratis'">Días de prueba<input placeholder="Ej: 14" [(ngModel)]="planForm.trial_days" name="trialDays" type="number" required min="1" step="1"></label>
              <small *ngIf="planForm.code === 'gratis'" class="full-field field-hint">Duración de la cuenta de prueba con este plan; el límite de espacios lo define "Máximo de espacios" arriba.</small>
              <label class="switch-row"><input type="checkbox" [checked]="planForm.is_active === 1" (change)="planForm.is_active = $any($event.target).checked ? 1 : 0"><span>Plan disponible para nuevas compras</span></label>
              <label class="full-field">Descripción<textarea [(ngModel)]="planForm.description" name="planDescription" rows="3" maxlength="500" placeholder="Explica a quién está dirigido y qué ofrece este plan."></textarea></label>
            </div>

            <section class="feature-section">
              <div><span class="eyebrow">BENEFICIOS Y ACCESOS</span><p>Activa o restringe capacidades específicas. Los permisos existentes se aplican directamente en el backend.</p></div>
              <div class="feature-grid">
                <label class="feature-toggle"><input type="checkbox" [checked]="planForm.benefits.extras === 1" (change)="setPlanBenefit('extras', $any($event.target).checked)"><span><strong>Extras</strong><small>Permite crear servicios adicionales por sala.</small></span></label>
                <label class="feature-toggle"><input type="checkbox" [checked]="planForm.benefits.schedules === 1" (change)="setPlanBenefit('schedules', $any($event.target).checked)"><span><strong>Horarios</strong><small>Permite configurar disponibilidad de espacios.</small></span></label>
                <label class="feature-toggle"><input type="checkbox" [checked]="planForm.benefits.pricing_tiers === 1" (change)="setPlanBenefit('pricing_tiers', $any($event.target).checked)"><span><strong>Precios por tramo</strong><small>Permite tarifas según duración de reserva.</small></span></label>
                <label class="feature-toggle"><input type="checkbox" [checked]="planForm.benefits.advanced_statistics === 1" (change)="setPlanBenefit('advanced_statistics', $any($event.target).checked)"><span><strong>Estadísticas avanzadas</strong><small>Acceso a indicadores detallados de reservas e ingresos.</small></span></label>
                <label class="feature-toggle"><input type="checkbox" [checked]="planForm.benefits.reports_export === 1" (change)="setPlanBenefit('reports_export', $any($event.target).checked)"><span><strong>Exportar reportes</strong><small>Permite imprimir o exportar el reporte de estadísticas en PDF.</small></span></label>
                <label class="feature-toggle"><input type="checkbox" [checked]="planForm.benefits.featured_listing === 1" (change)="setPlanBenefit('featured_listing', $any($event.target).checked)"><span><strong>Publicación destacada</strong><small>Habilita promoción prioritaria de espacios.</small></span></label>
                <label class="feature-toggle"><input type="checkbox" [checked]="planForm.benefits.personalized_site === 1" (change)="setPlanBenefit('personalized_site', $any($event.target).checked)"><span><strong>Sitio web personalizado</strong><small>Permite editar "Mi sitio": portada, colores, textos, secciones, redes y contacto.</small></span></label>
                <label class="feature-toggle"><input type="checkbox" [checked]="planForm.benefits.custom_site_url === 1" (change)="setPlanBenefit('custom_site_url', $any($event.target).checked)"><span><strong>URL personalizada del sitio</strong><small>Permite elegir el enlace público (tusala.net/tu-negocio). Requiere el sitio personalizado activo.</small></span></label>
                <label class="feature-toggle"><input type="checkbox" [checked]="planForm.benefits.private_spaces === 1" (change)="setPlanBenefit('private_spaces', $any($event.target).checked)"><span><strong>Espacios/negocio privados</strong><small>Permite ocultar salas o todo el negocio de la galería pública, accesibles solo con un código de 4 dígitos.</small></span></label>
              </div>
              <div class="included-service"><span><i class="bi bi-check-lg" aria-hidden="true"></i></span><div><strong>Soporte técnico 24/7</strong><small>Incluido para todas las cuentas activas.</small></div></div>
            </section>

            <p *ngIf="planFormError()" class="plan-form-error">{{ planFormError() }}</p>
            <footer class="modal-actions"><button class="secondary-btn" type="button" (click)="closePlanModal()">Cancelar</button><button class="action-btn" type="submit" [disabled]="isSavingPlan()">{{ isSavingPlan() ? 'Guardando...' : 'Guardar cambios' }}</button></footer>
            </form>
          </section>
        </div>

        <div *ngIf="archiveOwnerTarget()" class="modal-overlay owner-archive-overlay" (click)="closeOwnerArchive()">
          <section class="owner-archive-modal" role="dialog" aria-modal="true" aria-labelledby="owner-archive-title" (click)="$event.stopPropagation()">
            <span class="archive-warning-icon"><i class="bi bi-shield-exclamation"></i></span>
            <span class="eyebrow">BAJA ADMINISTRATIVA</span>
            <h3 id="owner-archive-title">{{ archiveOwnerStep() === 1 ? 'Dar de baja este negocio' : 'Confirma tu identidad' }}</h3>
            <ng-container *ngIf="archiveOwnerStep() === 1; else passwordConfirmation">
              <p>Se ocultar&aacute;n <strong>{{ archiveOwnerTarget()?.owner?.name }}</strong> y <strong>{{ archiveOwnerTarget()?.business?.name }}</strong> de todos los m&oacute;dulos. Sus usuarios perder&aacute;n el acceso y los espacios dejar&aacute;n de estar publicados.</p>
              <div class="archive-consequences"><span><i class="bi bi-eye-slash"></i>El negocio ya no ser&aacute; visible</span><span><i class="bi bi-people"></i>Se revocar&aacute;n sus accesos</span><span><i class="bi bi-bar-chart"></i>El historial financiero se conservar&aacute;</span><span><i class="bi bi-database-lock"></i>Solo podr&aacute; restaurarse desde base de datos</span></div>
              <footer><button class="secondary-btn" type="button" (click)="closeOwnerArchive()">Cancelar</button><button class="danger-btn" type="button" (click)="continueOwnerArchive()">Continuar</button></footer>
            </ng-container>
            <ng-template #passwordConfirmation>
              <p>Por seguridad, ingresa la contrase&ntilde;a de tu cuenta de Super Administrador. Esta acci&oacute;n quedar&aacute; auditada y se notificar&aacute; por correo.</p>
              <label class="archive-password">Contrase&ntilde;a<span class="password-visibility-field"><input #archivePasswordInput [type]="showArchiveOwnerPassword() ? 'text' : 'password'" [(ngModel)]="archiveOwnerPassword" autocomplete="current-password" maxlength="128" (keyup.enter)="confirmOwnerArchive()" placeholder="Tu contrase&ntilde;a actual"><button type="button" class="password-visibility-btn" (click)="showArchiveOwnerPassword.set(!showArchiveOwnerPassword())" [attr.aria-label]="showArchiveOwnerPassword() ? 'Ocultar contraseña' : 'Mostrar contraseña'" [attr.aria-pressed]="showArchiveOwnerPassword()"><i class="bi" [class.bi-eye]="!showArchiveOwnerPassword()" [class.bi-eye-slash]="showArchiveOwnerPassword()" aria-hidden="true"></i></button></span></label>
              <p *ngIf="archiveOwnerError()" class="plan-form-error" role="alert">{{ archiveOwnerError() }}</p>
              <footer><button class="secondary-btn" type="button" (click)="archiveOwnerStep.set(1)" [disabled]="archiveOwnerSaving()">Atr&aacute;s</button><button class="danger-btn" type="button" (click)="confirmOwnerArchive(archivePasswordInput)" [disabled]="archiveOwnerSaving()">{{ archiveOwnerSaving() ? 'Procesando baja...' : 'Confirmar baja definitiva' }}</button></footer>
            </ng-template>
          </section>
        </div>

        <div *ngIf="restoreOwnerTarget()" class="modal-overlay" (click)="closeRestoreConfirmation()">
          <section class="owner-archive-modal restore-confirm-modal" role="alertdialog" aria-modal="true" aria-labelledby="restore-owner-title" (click)="$event.stopPropagation()">
            <div class="restore-confirm-icon"><i class="bi bi-arrow-counterclockwise"></i></div><span class="eyebrow">CONFIRMAR REACTIVACI&Oacute;N</span>
            <h3 id="restore-owner-title">&iquest;Volver a activar este propietario?</h3>
            <p><strong>{{ restoreOwnerTarget()?.owner?.name || 'El propietario' }}</strong> recuperar&aacute; el acceso a <strong>{{ restoreOwnerTarget()?.name }}</strong>. Tambi&eacute;n se restaurar&aacute;n sus membres&iacute;as, usuarios, espacios y la suscripci&oacute;n que estaban activos antes de la baja.</p>
            <footer><button class="secondary-btn" type="button" (click)="closeRestoreConfirmation()" [disabled]="restoringBusinessId() !== null">Cancelar</button><button class="restore-btn" type="button" (click)="confirmOwnerRestore()" [disabled]="restoringBusinessId() !== null"><i class="bi bi-arrow-counterclockwise"></i>{{ restoringBusinessId() !== null ? 'Reactivando...' : 'Confirmar reactivaci&oacute;n' }}</button></footer>
          </section>
        </div>
      </ng-container>
    </div>

    <!-- Gestionar negocio: el panel completo del negocio, tal como lo ve su
         propietario (ver business-context-modal.component.ts). -->
    <app-business-context-modal *ngIf="managedBusiness() as mb"
      [businessId]="mb.id" [businessName]="mb.name" [ownerName]="mb.ownerName"
      (closed)="closeBusinessContext()" (manageOwner)="openUserEditor($event)"></app-business-context-modal>

    <!-- Gestión de cuentas: datos de acceso, contraseña y sesiones. -->
    <app-user-account-editor *ngIf="editingUserId() as uid" [userId]="uid"
      (closed)="closeUserEditor()" (changed)="onUserAccountChanged($event)"
      (openBusiness)="openBusinessFromEditor($event)"></app-user-account-editor>
  `,
  styles: [`
    .superadmin-container { padding: 1.75rem; min-height: 100%; color: var(--ts-text-navy-850-3); background: linear-gradient(145deg, var(--ts-bg-sky-0-2) 0%, var(--ts-bg-blue-50-15) 100%); }
    .page-title { font-size: clamp(1.5rem, 2.5vw, 2rem); letter-spacing: -0.04em; font-weight: 800; margin: 0 0 1.5rem; color: var(--ts-text-navy-850-4); }
    .sa-refunds-link { display: inline-flex; align-items: center; gap: 7px; margin: -0.9rem 0 1.5rem; color: var(--ts-text-blue-400-3); font-weight: 700; font-size: 0.88rem; text-decoration: none; }
    .sa-refunds-link:hover { text-decoration: underline; }
    .sa-refunds-link.sa-refunds-link-embedded { margin: 0 0 1.25rem; }
    .loading-state { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; padding: 2rem; color: var(--ts-text-blue-550-3); }
    .spinner { width: 32px; height: 32px; border: 3px solid var(--ts-border-sky-100-14); border-top-color: var(--ts-border-blue-400-4); border-radius: 50%; animation: spin 0.8s linear infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }
    .error-banner { background: var(--ts-bg-red-50-8); color: var(--ts-text-red-650); border-radius: 8px; padding: 0.75rem 1rem; margin-bottom: 1rem; }
    .stats-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 1rem; margin-bottom: 1.5rem; }
    .stat-card { position: relative; overflow: hidden; background: var(--ts-bg-gray-0-a90); border: 1px solid var(--ts-border-blue-50-2); border-radius: 16px; padding: 1.15rem 1.25rem; display: flex; flex-direction: column; gap: .45rem; box-shadow: 0 8px 22px var(--ts-shadow-navy-800-a6-2); }
    .stat-card::before { content: ''; position: absolute; width: 42px; height: 42px; right: -14px; top: -14px; border-radius: 50%; background: var(--ts-bg-blue-50-16); }
    .stat-card.highlight { border-color: var(--ts-primary); background: linear-gradient(135deg, var(--ts-bg-sky-50-11), var(--ts-text)); }
    .stat-label { font-size: .76rem; color: var(--ts-text-blue-550-9); font-weight: 700; text-transform: uppercase; letter-spacing: .055em; }
    .stat-value { font-size: 1.55rem; font-weight: 800; color: var(--ts-text-navy-800-2); letter-spacing: -.035em; }
    .tabs-bar { display: grid; grid-template-columns:repeat(auto-fit,minmax(130px,1fr)); gap: .4rem; padding: .4rem; border: 1px solid var(--ts-border-blue-100-4); border-radius: 14px; margin-bottom: 1rem; overflow: hidden; background: var(--ts-text); box-shadow: 0 5px 16px var(--ts-shadow-navy-750-a4); }
    .tab-btn { min-width:0; background: transparent; border: 0; border-radius: 9px; padding: .62rem .7rem; cursor: pointer; color: var(--ts-text-blue-550-9); font-size: .86rem; font-weight: 650; white-space: normal; transition: .18s ease; }
    .tab-btn:hover { background: var(--ts-bg-blue-50-17); color: var(--ts-text-blue-550-10); }
    .tab-btn.active { color: var(--ts-text); background: linear-gradient(135deg, var(--ts-bg-blue-500-3), var(--ts-bg-sky-400)); box-shadow: 0 5px 12px var(--ts-shadow-blue-500-a22-2); }
    .tab-content { min-height: 200px; overflow: auto; background: var(--ts-text); border: 1px solid var(--ts-border-blue-50-3); border-radius: 15px; box-shadow: 0 8px 26px var(--ts-shadow-navy-800-a6-3); }
    .data-toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: .65rem; margin: 0 0 1rem; }
    .search-field { display: flex; align-items: center; gap: .5rem; min-width: min(100%, 300px); padding: 0 .75rem; background: var(--ts-text); border: 1px solid var(--ts-border-blue-100-5); border-radius: 10px; box-shadow: 0 3px 12px var(--ts-shadow-navy-750-a4-2); color: var(--ts-text-blue-500-11); }
    .search-field input { border: 0; outline: 0; padding: .66rem 0; width: 100%; font: inherit; color: var(--ts-text-navy-850-3); background: transparent; }
    .data-toolbar select { min-height: 38px; padding: 0 .65rem; border: 1px solid var(--ts-border-blue-100-5); border-radius: 9px; color: var(--ts-text-navy-650); background: var(--ts-text); font: inherit; }
    .results-note { margin-left: auto; color: var(--ts-text-blue-450-3); font-size: .8rem; font-weight: 600; }
    .tab-loading { display:none; }
    .filtered-content-skeleton{display:block;padding:1rem 0}.filtered-content-skeleton~.tab-content{display:none!important}
    .sub-filter { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem; font-size: 0.9rem; }
    .sub-filter select { padding: 5px 10px; border: 1px solid var(--ts-border-sky-150-6); border-radius: 6px; font-size: 0.88rem; }
    .data-table { width: 100%; border-collapse: collapse; font-size: .87rem; }
    .data-table th { background: var(--ts-bg-blue-0-6); text-align: left; padding: .78rem .9rem; font-weight: 750; color: var(--ts-text-blue-550-11); border-bottom: 1px solid var(--ts-border-blue-50-3); white-space: nowrap; text-transform: uppercase; font-size: .7rem; letter-spacing: .055em; }
    .data-table td { padding: .75rem .9rem; border-bottom: 1px solid var(--ts-border-sky-50-5); vertical-align: middle; color: var(--ts-text-navy-700-4); }
    .data-table tr:hover td { background: var(--ts-bg-sky-0-2); }
    .empty-cell { text-align: center; color: var(--ts-text-blue-350); padding: 1.5rem !important; }
    .status-badge { display: inline-block; padding: 2px 8px; border-radius: 20px; font-size: 0.75rem; font-weight: 600; }
    .status-active    { background: var(--ts-bg-green-50-3); color: var(--ts-text-green-750); }
    .status-pending   { background: var(--ts-bg-amber-100-3); color: var(--ts-text-orange-700); }
    .status-inactive  { background: var(--ts-bg-sky-50-12); color: var(--ts-text-blue-550-3); }
    .status-cancelled { background: var(--ts-bg-red-50-8); color: var(--ts-text-red-650); }
    .status-expired   { background: var(--ts-bg-red-50); color: var(--ts-text-red-700-2); }
    .status-completed { background: var(--ts-bg-green-50-3); color: var(--ts-text-green-750); }
    .status-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: var(--ts-bg-blue-350-7); }
    .status-dot.active { background: var(--ts-bg-green-550); }
    .action-btn { border: 1px solid var(--ts-border-blue-500-2); background: var(--ts-bg-blue-500-3); color: var(--ts-text); padding: .42rem .75rem; border-radius: 8px; cursor: pointer; font-size: .78rem; font-weight: 700; transition: .18s ease; }
    .action-btn:hover { background: var(--ts-bg-blue-600-2); transform: translateY(-1px); }
    .action-btn:disabled { opacity: 0.6; cursor: not-allowed; }
    .two-factor-setup { grid-template-columns: 1fr !important; justify-items: start; gap: .6rem !important; }
    .two-factor-setup img { border-radius: 10px; border: 1px solid var(--ts-border-strong); }
    .two-factor-setup code { padding: .3rem .55rem; border-radius: 6px; background: var(--ts-bg-sky-50-12); color: var(--ts-text-navy-750-6); font-size: .8rem; letter-spacing: .04em; }
    .secondary-btn { border: 1px solid var(--ts-border-sky-150-6); background: var(--ts-text); color: var(--ts-text-navy-750-6); padding: 5px 10px; border-radius: 6px; cursor: pointer; font-size: 0.8rem; }
    .modal-overlay { position: fixed; inset: 0; background: var(--ts-bg-navy-900-a45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
    .modal-box { padding: 1rem; border-radius: 10px; width: min(420px, 92vw); box-shadow: 0 10px 25px var(--ts-shadow-gray-1000-a15); display: flex; flex-direction: column; gap: 0.75rem; }
    .reset-input { width: 100%; border: 1px solid var(--ts-border-sky-150-6); border-radius: 6px; padding: 0.55rem 0.65rem; }
    .modal-actions { display: flex; justify-content: flex-end; gap: 0.5rem; }
    .reset-error { margin: .8rem 0; padding: .7rem .9rem; border: 1px solid var(--ts-border-red-500-a45); border-radius: 10px; color: var(--ts-text-red-100-2); background: var(--ts-bg-red-500-a14); font-size: .8rem; font-weight: 600; line-height: 1.4; }
    .field-error, .field-error:focus { border-color: var(--ts-border-red-400-3) !important; box-shadow: 0 0 0 3px var(--ts-shadow-red-400-a25) !important; }
    .pagination { display: flex; align-items: center; gap: 0.75rem; justify-content: center; padding: 1rem 0; font-size: 0.88rem; }
    .pagination button { padding: 5px 12px; border: 1px solid var(--ts-border-sky-150-6); border-radius: 6px; background: var(--ts-text); cursor: pointer; }
    .pagination button:disabled { opacity: 0.4; cursor: not-allowed; }
    .total-banner { background: var(--ts-bg-green-50-4); color: var(--ts-text-green-750); padding: .7rem 1rem; border-radius: 10px; margin: .8rem; font-size: .9rem; }
    .plans-content { padding-bottom: .25rem; }
    .plans-intro { display: flex; justify-content: space-between; gap: 1rem; align-items: center; padding: 1.1rem 1rem .85rem; }
    .plans-content app-admin-tabs { display: block; margin: 0 1rem .9rem; }
    /* Celulares angostos: el botón Buscar queda solo con el ícono para que el campo tenga espacio. */
    @media (max-width:430px) { .search-submit > span { display:none; } .search-submit { padding-inline:.8rem; } }
    @media (max-width:560px) { .plans-content app-admin-tabs { width: auto; } }
    .planes-tab-panel { display: grid; gap: 1rem; padding: 0 1rem 1rem; }
    .settings-editor { display: grid; gap: .85rem; padding: 1.1rem 1.2rem; border: 1px solid var(--ts-border-strong); border-radius: 14px; background: linear-gradient(145deg, var(--ts-bg-navy-850-12), var(--ts-bg-navy-900-3)); box-shadow: 0 10px 26px var(--ts-shadow-gray-1000-a12); }
    .settings-editor h4 { margin: .2rem 0 .2rem; color: var(--ts-text); font-size: 1.02rem; }
    .settings-editor p { margin: 0; color: var(--ts-text-sky-300-15); font-size: .78rem; line-height: 1.5; }
    .settings-editor-fields { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, .45fr)); gap: .8rem; }
    .settings-editor-fields label { display: grid; gap: .35rem; color: var(--ts-text-sky-250-13); font-size: .7rem; font-weight: 700; }
    .settings-editor-fields input { box-sizing: border-box; width: 100%; min-height: 42px; padding: .6rem .7rem; color: var(--ts-text); border: 1px solid var(--ts-border-strong); border-radius: 9px; background: var(--ts-bg-navy-900); font-size: .85rem; }
    .password-visibility-field{position:relative;display:block}
    .password-visibility-field input{padding-right:3rem!important}
    .password-visibility-btn{position:absolute;top:0;bottom:0;right:.35rem;display:grid;place-items:center;width:34px;height:34px;margin:auto 0;padding:0;color:var(--ts-text-sky-400);border:0;border-radius:8px;background:transparent;cursor:pointer}
    .password-visibility-btn:hover,.password-visibility-btn:focus-visible{color:var(--ts-text-blue-250-2);background:var(--ts-bg-blue-400-a12);outline:none}
    .settings-editor .action-btn { justify-self: start; }
    .trial-preview-card { max-width: 320px; }
    .plans-intro h3 { margin: .12rem 0; font-size: 1.1rem; color: var(--ts-text-navy-800-2); }
    .plans-intro p { margin: 0; color: var(--ts-text-blue-450-4); font-size: .85rem; }
    .eyebrow { color: var(--ts-text-blue-500-12); font-size: .67rem; letter-spacing: .1em; font-weight: 800; }
    .plan-count, .benefit-summary { display: inline-block; border-radius: 999px; background: var(--ts-bg-blue-50-11); color: var(--ts-text-blue-550-12); padding: .3rem .58rem; font-size: .76rem; font-weight: 700; }
    .d-block { display: block; margin-top: .15rem; color: var(--ts-text-blue-400-4); font-size: .72rem; }
    .plan-modal-overlay { z-index: 1200; padding: 1rem; background: var(--ts-bg-navy-900-a62); overflow-y: auto; }
    .plan-editor { width: min(850px, 100%); max-height: calc(100vh - 2rem); overflow: auto; border-radius: 17px; background: var(--ts-text); box-shadow: 0 26px 80px var(--ts-shadow-gray-1000-a28); }
    .plan-editor-header { display: flex; justify-content: space-between; gap: 1rem; padding: 1.3rem 1.45rem; color: var(--ts-text); background: linear-gradient(125deg, var(--ts-bg-navy-750-5), var(--ts-bg-sky-500-4)); } .plan-editor-header h3 { margin: .18rem 0; font-size: 1.3rem; } .plan-editor-header p { margin: 0; color: var(--ts-text-blue-50-8); font-size: .84rem; } .plan-editor-header .eyebrow { color: var(--ts-text-sky-150-21); }
    .close-plan-modal { width: 34px; height: 34px; border: 1px solid var(--ts-border-gray-0-a35); border-radius: 50%; color: var(--ts-text); background: transparent; font-size: 1.3rem; cursor: pointer; }
    .plan-form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .9rem; padding: 1.35rem 1.45rem .9rem; } .plan-form-grid label, .advanced-field { display: grid; gap: .34rem; color: var(--ts-text-navy-650-2); font-size: .78rem; font-weight: 750; } .plan-form-grid input, .plan-form-grid select, .plan-form-grid textarea, .advanced-field textarea { width: 100%; box-sizing: border-box; min-height: 42px; border: 1px solid var(--ts-border-sky-100-15); border-radius: 8px; padding: .65rem .7rem; color: var(--ts-text-navy-800-5); font: inherit; line-height: 1.3; resize: vertical; } .plan-form-grid input, .plan-form-grid select { height: 42px !important; line-height: normal; } .plan-form-grid select { appearance: none; -webkit-appearance: none; padding-right: 2.1rem; padding-top: 0 !important; padding-bottom: 0 !important; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='%23536581' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 8l4 4 4-4'/%3E%3C/svg%3E") !important; background-repeat: no-repeat !important; background-position: right .65rem center !important; background-size: 15px !important; cursor: pointer; } .plan-form-grid input[type=number] { appearance: textfield; -moz-appearance: textfield; } .plan-form-grid input[type=number]::-webkit-inner-spin-button, .plan-form-grid input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; } .full-field { grid-column: 1 / -1; } .field-hint { grid-column: 1 / -1; margin: -.55rem 0 0; color: var(--ts-text-sky-300-23); font-size: .74rem; font-weight: 500; line-height: 1.4; }
    .switch-row { grid-column: 1 / -1; display: flex !important; align-items: center; gap: .55rem; padding: .7rem .8rem; border: 1px solid var(--ts-border-sky-100-2); border-radius: 9px; background: var(--ts-bg-sky-0-2); cursor: pointer; } .switch-row input, .feature-toggle input { width: 17px; height: 17px; accent-color: var(--ts-accent-blue-500); }
    .feature-section { margin: 0 1.45rem 1.1rem; padding: 1rem; border: 1px solid var(--ts-border-sky-100-16); border-radius: 12px; background: var(--ts-bg-blue-0-7); } .feature-section > div > p { margin: .28rem 0 .75rem; color: var(--ts-text-blue-500-13); font-size: .8rem; } .feature-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: .65rem; } .feature-toggle { display: flex; gap: .55rem; align-items: flex-start; padding: .75rem; border: 1px solid var(--ts-border-sky-100-17); border-radius: 9px; background: var(--ts-text); cursor: pointer; } .feature-toggle strong, .feature-toggle small { display: block; } .feature-toggle strong { color: var(--ts-text-navy-700-5); font-size: .82rem; } .feature-toggle small { margin-top: .2rem; color: var(--ts-text-blue-450-5); font-size: .72rem; line-height: 1.35; } .advanced-field { margin-top: .9rem; } .advanced-field small { color: var(--ts-text-blue-450-6); font-weight: 500; }
    .included-service { display: flex; align-items: center; gap: .6rem; margin-top: .85rem; padding: .7rem .8rem; border-radius: 9px; background: var(--ts-bg-green-50-5); color: var(--ts-text-green-700); } .included-service > span { display: grid; place-items: center; width: 22px; height: 22px; border-radius: 50%; background: var(--ts-bg-green-600-2); color: var(--ts-text); font-weight: 800; } .included-service strong, .included-service small { display: block; } .included-service small { margin-top: .15rem; font-size: .73rem; color: var(--ts-text-green-600); }
    .plan-form-error { margin: 0 1.45rem 1rem; padding: .65rem .8rem; border-radius: 8px; color: var(--ts-danger-text); background: var(--ts-danger-soft); border: 1px solid var(--ts-danger-soft-2); font-size: .82rem; font-weight: 650; } .plan-editor .modal-actions { padding: 1rem 1.45rem 1.25rem; border-top: 1px solid var(--ts-border-blue-50-4); }
    .platform-statistics { background:var(--ts-text); border:1px solid var(--ts-border-sky-100-11); border-radius:16px; padding:1.3rem; box-shadow:0 8px 22px var(--ts-shadow-navy-750-a5); } .stats-intro h3 { margin:.18rem 0; color:var(--ts-text-navy-750); font-size:1.25rem; } .stats-intro p { margin:0; color:var(--ts-text-blue-500-14); font-size:.86rem; } .global-stat-tables { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:1rem; margin-top:1.2rem; } .global-stat-tables article { border:1px solid var(--ts-border-sky-50-2); border-radius:12px; overflow:hidden; } .global-stat-tables h4 { margin:0; padding:.75rem .9rem; color:var(--ts-text-navy-650-3); background:var(--ts-bg-blue-50-8); font-size:.88rem; } .global-stat-tables dl { margin:0; } .global-stat-tables dl div { display:flex; justify-content:space-between; gap:.75rem; padding:.68rem .85rem; border-top:1px solid var(--ts-border-sky-50-6); } .global-stat-tables dt { color:var(--ts-text-blue-500-15); font-size:.79rem; } .global-stat-tables dd { margin:0; color:var(--ts-text-navy-750-7); font-weight:750; font-size:.82rem; text-align:right; } @media(max-width:900px){.global-stat-tables{grid-template-columns:1fr;}}
    .plan-cards-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(235px,1fr)); gap:1rem; padding:0 1rem 1rem; }
    .landing-plan-card { display:flex; flex-direction:column; gap:.7rem; min-height:330px; padding:1.15rem; border:1px solid var(--ts-border-sky-100-16); border-radius:15px; background:linear-gradient(160deg,var(--ts-text) 0%,var(--ts-bg-sky-0-4) 100%); box-shadow:0 8px 20px var(--ts-shadow-navy-750-a6-3); }
    .landing-plan-card h3 { margin:0; color:var(--ts-text-navy-750); font-size:1.2rem; }
    .landing-plan-card p { margin:0; color:var(--ts-text-blue-500-13); font-size:.8rem; line-height:1.45; min-height:2.3rem; }
    .landing-plan-top { display:flex; align-items:center; justify-content:space-between; gap:.5rem; }
    .plan-code { color:var(--ts-text-blue-500-12); font-size:.7rem; font-weight:800; letter-spacing:.1em; text-transform:uppercase; }
    .landing-plan-price { color:var(--ts-text-navy-750); font-size:1.85rem; letter-spacing:-.04em; }
    .landing-plan-price small { color:var(--ts-text-blue-500-13); font-size:.75rem; font-weight:600; }
    .plan-limit-preview { display:flex; justify-content:space-between; gap:.5rem; color:var(--ts-text-blue-600-5); font-size:.76rem; padding:.55rem .65rem; border-radius:8px; background:var(--ts-bg-blue-50-11); }
    .benefit-chips { display:flex; flex-wrap:wrap; gap:.35rem; align-content:flex-start; min-height:3.7rem; }
    .benefit-chips span { padding:.25rem .45rem; border-radius:999px; background:var(--ts-bg-green-50-5); color:var(--ts-text-green-700-2); font-size:.7rem; font-weight:650; }
    .landing-plan-card .action-btn { margin-top:auto; width:100%; }
    .section-actions { display:flex; align-items:center; justify-content:space-between; gap:1rem; padding:1rem; border-bottom:1px solid var(--ts-border-sky-50-7); }
    .section-actions p { margin:.25rem 0 0; color:var(--ts-text-blue-500-13); font-size:.83rem; }
    .row-actions { display:flex; flex-wrap:wrap; gap:.35rem; }
    .danger-btn { border:1px solid var(--ts-border-red-150-4); background:var(--ts-bg-red-0-2); color:var(--ts-text-red-600-5); padding:.42rem .7rem; border-radius:8px; cursor:pointer; font-size:.78rem; font-weight:700; }
    .danger-btn:disabled { opacity:.55; cursor:not-allowed; }
    .status-off-btn { background:var(--ts-bg-red-500-3); border-color:var(--ts-border-red-500); }
    .overview-visuals { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:1rem; margin:0 0 1.25rem; }
    .overview-panel { min-height:155px; padding:1rem; border:1px solid var(--ts-border-sky-100-18); border-radius:15px; background:var(--ts-text); box-shadow:0 8px 22px var(--ts-shadow-navy-800-a5); }
    .overview-wide { grid-column:span 2; }
    .overview-panel header { display:flex; align-items:flex-start; justify-content:space-between; gap:.75rem; }
    .overview-panel h3 { margin:.2rem 0; color:var(--ts-text-navy-750); font-size:1rem; }
    .overview-caption,.health-legend { color:var(--ts-text-blue-500-13); font-size:.76rem; }
    .health-meter { height:12px; margin:1.25rem 0 .65rem; overflow:hidden; border-radius:999px; background:var(--ts-bg-blue-50-18); }
    .health-meter span { display:block; height:100%; border-radius:inherit; background:linear-gradient(90deg,var(--ts-bg-green-550-2),var(--ts-bg-sky-400-2)); }
    .health-legend { display:flex; flex-wrap:wrap; gap:.65rem; }
    .health-legend i { display:inline-block; width:8px; height:8px; margin-right:.2rem; border-radius:50%; }
    .dot-green { background:var(--ts-bg-green-550-2); } .dot-yellow { background:var(--ts-bg-amber-450-2); } .dot-red { background:var(--ts-bg-red-400-4); }
    .mini-bars { display:grid; gap:.55rem; margin-top:.9rem; }
    .mini-bars > div { display:grid; grid-template-columns:68px 1fr 24px; align-items:center; gap:.45rem; color:var(--ts-text-blue-550-13); font-size:.72rem; }
    .mini-bars > div > div { height:8px; overflow:hidden; border-radius:999px; background:var(--ts-bg-blue-50-18); }
    .mini-bars i { display:block; height:100%; border-radius:inherit; background:var(--ts-bg-sky-400-3); }
    .mini-bars strong { color:var(--ts-text-navy-700-6); text-align:right; }
    .overview-highlight { display:flex; flex-direction:column; align-items:flex-start; justify-content:center; background:linear-gradient(145deg,var(--ts-bg-sky-50-13),var(--ts-text)); }
    .overview-highlight > strong { margin:.4rem 0 .15rem; color:var(--ts-text-navy-750); font-size:1.75rem; }
    .overview-highlight p { margin:0 0 .7rem; color:var(--ts-text-blue-500-13); font-size:.77rem; }
    .business-modal { width:min(700px,calc(100vw - 2rem)); max-height:calc(100vh - 2rem); overflow:auto; border-radius:16px; background:var(--ts-text); box-shadow:0 25px 70px var(--ts-shadow-navy-850-a28); }
    .modal-header { display:flex; align-items:flex-start; justify-content:space-between; gap:1rem; padding:1.15rem 1.35rem; border-bottom:1px solid var(--ts-border-sky-50-7); }
    .modal-header h3 { margin:.2rem 0 0; color:var(--ts-text-navy-750); }
    .close-btn { border:0; background:transparent; color:var(--ts-text-blue-500-16); font-size:1.4rem; cursor:pointer; }
    .modal-header .modal-icon { display:grid; place-items:center; flex:0 0 auto; width:38px; height:38px; border-radius:10px; color:var(--ts-text-blue-500-17); background:var(--ts-bg-blue-50-19); font-size:1.05rem; }
    /* Cierre visual local: evita que reglas heredadas claras compitan con el tema TuSala. */
    .platform-statistics { color:var(--ts-text); background:linear-gradient(145deg,var(--ts-bg-navy-850-41),var(--ts-bg-navy-900-18)); border-color:var(--ts-border-navy-750-8); box-shadow:0 14px 32px var(--ts-shadow-gray-1000-a20); }
    .stats-intro h3,.global-stat-tables dd { color:var(--ts-text); }
    .stats-intro p,.global-stat-tables dt { color:var(--ts-text-blue-300); }
    .global-stat-tables article { border-color:var(--ts-border-strong); background:var(--ts-bg-elevated); }
    .global-stat-tables h4 { color:var(--ts-text-blue-250-22); background:var(--ts-bg-navy-800); }
    .global-stat-tables dl div { border-top-color:var(--ts-border-navy-750-9); }
    .data-toolbar { display:grid; grid-template-columns:minmax(240px,1fr) repeat(3,minmax(135px,auto)); align-items:end; }
    .search-field { min-width:0; color:var(--ts-text-sky-350-31); background:var(--ts-bg-navy-900-19); border-color:var(--ts-border-navy-700-15); box-shadow:none; }
    .search-field input { color:var(--ts-text-soft-2); }
    .data-toolbar select,.sub-filter select { width:auto; min-width:135px; color:var(--ts-text-blue-50-9); background:var(--ts-bg-navy-900-19); border-color:var(--ts-border-navy-700-15); }
    .results-note { color:var(--ts-text-blue-300); }
    .plan-count,.benefit-summary { color:var(--ts-text-blue-250-23); background:var(--ts-bg-blue-450-a18-2); }
    .plan-limit-preview { color:var(--ts-text-sky-150-22); background:var(--ts-bg-navy-850-42); border:1px solid var(--ts-border-navy-700-16); }
    .benefit-chips span { color:var(--ts-text-green-300-9); background:var(--ts-bg-green-600-a14); border:1px solid var(--ts-border-green-450-a24); }
    .payment-summary-grid { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:.65rem; }
    .payment-summary-grid .payment-summary { min-width:0; width:100%; }
    .plan-editor { color:var(--ts-text); background:var(--ts-bg-elevated); border:1px solid var(--ts-border-strong); }
    .plan-form-grid label,.advanced-field { color:var(--ts-text-sky-200-18); }
    .plan-form-grid input,.plan-form-grid textarea,.advanced-field textarea { color:var(--ts-text-blue-50); background:var(--ts-scrollbar-track); border-color:var(--ts-divider); }
    .plan-form-grid select { color:var(--ts-text-blue-50); background-color:var(--ts-scrollbar-track); border-color:var(--ts-divider); background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='%23b8c9de' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 8l4 4 4-4'/%3E%3C/svg%3E") !important; }
    .switch-row,.feature-section,.feature-toggle { color:var(--ts-text-on-accent-22); background:var(--ts-bg-navy-850-2); border-color:var(--ts-divider); }
    .feature-section > div > p,.feature-toggle small,.advanced-field small { color:var(--ts-text-sky-300-23); }
    .feature-toggle strong { color:var(--ts-text-soft-2); }
    .included-service { color:var(--ts-text-green-300-7); background:var(--ts-bg-green-600-a13); border:1px solid var(--ts-border-green-450-a22); }
    .included-service small { color:var(--ts-text-green-300-10); }
    .plan-editor .modal-actions { border-top-color:var(--ts-border-strong); background:var(--ts-bg-navy-900-20); }

    /* Design System del Centro de Operaciones */
    .superadmin-container { width:100%; max-width:1500px; box-sizing:border-box; margin:0 auto; padding:.25rem 1rem 1.5rem; color:var(--ts-text-blue-50-10); background:transparent; }
    .page-title { color:var(--ts-text-blue-0-2); }
    .loading-state { min-height:240px; justify-content:center; color:var(--ts-text-sky-300); }
    .spinner { border-color:var(--ts-border-navy-700-15); border-top-color:var(--ts-border-blue-300-4); }
    .error-banner { border:1px solid var(--ts-border-red-350-a34); background:var(--ts-bg-red-350-a10); color:var(--ts-text-red-150-10); }
    /* Corrección pedida: antes forzaba exactamente 2 columnas sin importar
       el ancho disponible ni cuántas tarjetas tuviera el grupo -- en
       pantallas anchas dejaba espacio vacío desperdiciado, y en angostas no
       se adaptaba con suavidad. auto-fit + minmax deja que cada grupo
       encuentre su propio número de columnas según el espacio real. */
    .stats-grid { grid-template-columns:repeat(auto-fit,minmax(168px,1fr)); gap:.75rem; margin-bottom:1rem; }
    .stat-card { min-height:104px; justify-content:center; padding:1rem 1.1rem; border-color:var(--ts-border-strong); border-radius:13px; background:linear-gradient(145deg,var(--ts-bg-navy-850-34),var(--ts-bg-elevated)); box-shadow:0 10px 24px var(--ts-shadow-gray-1000-a11); }
    .stat-card::before { width:54px; height:54px; right:-18px; top:-18px; background:var(--ts-bg-blue-350-a10); }
    .stat-card.highlight { border-color:var(--ts-border-blue-300-a50); background:linear-gradient(145deg,var(--ts-bg-blue-450-a20),var(--ts-bg-navy-850-43)); }
    .stat-label { color:var(--ts-text-sky-350-11); font-size:.68rem; }
    .stat-value { color:var(--ts-text); font-size:1.48rem; }
    /* Pedido del usuario: reorganizar "Resumen general" en grupos con
       encabezado propio (Plataforma / Ingresos / Suscripciones) en vez de
       una sola cuadrícula plana de 8+ tarjetas sin relación visible entre
       ellas -- más claro qué mide cada cosa. */
    .overview-groups { display:grid; gap:1.35rem; margin-bottom:1.5rem; }
    .overview-group-header { display:flex; align-items:baseline; gap:.55rem; margin-bottom:.6rem; }
    .overview-group-header h4 { margin:0; color:var(--ts-text-blue-150-8); font-size:.82rem; font-weight:650; }
    .overview-group .stats-grid { margin-bottom:0; }
    .stat-card.tone-success { border-color:var(--ts-border-green-500-a32); background:linear-gradient(145deg,var(--ts-bg-green-500-a14),var(--ts-bg-navy-850-44)); }
    .stat-card.tone-success .stat-value { color:var(--ts-text-green-300-11); }
    .stat-card.tone-warning { border-color:var(--ts-border-orange-450-a40); background:linear-gradient(145deg,var(--ts-bg-orange-450-a16-2),var(--ts-bg-navy-850-44)); }
    .stat-card.tone-warning .stat-value { color:var(--ts-text-amber-250-3); }
    .dot-blue { background:var(--ts-bg-sky-400-2); }
    .overview-panel { min-height:165px; border-color:var(--ts-border-strong); background:linear-gradient(145deg,var(--ts-bg-navy-850-45),var(--ts-bg-elevated)); box-shadow:0 10px 26px var(--ts-shadow-gray-1000-a10); }
    .overview-panel h3,.overview-highlight > strong,.mini-bars strong { color:var(--ts-text-blue-50); }
    .overview-caption,.health-legend,.overview-highlight p,.mini-bars>div { color:var(--ts-text-sky-300-8); }
    .health-meter,.mini-bars>div>div { background:var(--ts-bg-navy-900-5); }
    .overview-highlight { background:linear-gradient(145deg,var(--ts-bg-blue-400-a18-4),var(--ts-bg-elevated)); }
    .eyebrow { color:var(--ts-text-blue-300-10); }
    .tabs-bar { border-color:var(--ts-border-strong); background:var(--ts-bg-elevated); box-shadow:none; }
    .tab-btn { color:var(--ts-text-sky-300-24); }.tab-btn:hover { background:var(--ts-bg-navy-800-14); color:var(--ts-text-sky-100-10); }
    /* Personalización: bloque vertical sin scroll interno, para que la barra
       de guardado (position: sticky) se fije respecto a la página. */
    .tab-content.personalization-content { display:block; overflow:visible; padding:1.25rem 1.3rem 1.4rem; }
    .personalization-panel { padding-top:1.1rem; }
    @media (max-width:720px) { .tab-content.personalization-content { padding:1rem .8rem 1.1rem; } }
    .tab-content { border-color:var(--ts-border-strong); background:var(--ts-bg-elevated); box-shadow:0 10px 28px var(--ts-shadow-gray-1000-a11); scrollbar-color:var(--ts-accent-sky-650) var(--ts-bg-navy-900-20); }
    /* Corrección pedida: la barra de búsqueda/filtros se desplazaba fuera de
       vista al bajar por listas largas (p. ej. "Pago a negocios" con 22+
       negocios) -- .tab-content ya es su propio contenedor con scroll
       (overflow:auto), así que position:sticky con top:0 la deja fija sin
       necesidad de calcular el alto de ningún header externo. */
    .data-toolbar { position:sticky; top:0; z-index:5; grid-template-columns:minmax(280px,1fr) repeat(3,minmax(150px,auto)) auto; gap:.65rem; padding:.75rem; margin-bottom:.85rem; border:1px solid var(--ts-border-strong); border-radius:12px; background:var(--ts-bg-navy-850-32); }
    .search-field,.data-toolbar select { min-height:42px; box-sizing:border-box; border-color:var(--ts-border-navy-700-17); border-radius:9px; background:var(--ts-bg-navy-900-8); }
    .search-field input::placeholder { color:var(--ts-text-sky-450-14); }
    .results-note { align-self:center; margin-left:0; padding:.35rem .5rem; border-radius:7px; color:var(--ts-text-sky-300-25); background:var(--ts-bg-sky-450-a8-2); white-space:nowrap; }
    .page-size-control { display:inline-flex; align-items:center; gap:.5rem; font-size:.78rem; font-weight:700; }
    .page-size-control select { min-width:145px; min-height:36px; border:1px solid var(--ts-border-navy-700-17); border-radius:8px; padding:0 .5rem; color:var(--ts-text-blue-50-9); background:var(--ts-bg-navy-900-19); }
    .data-table { min-width:850px; color:var(--ts-text); }
    .data-table th { position:sticky; top:0; z-index:1; padding:.82rem .95rem; border-bottom-color:var(--ts-border-navy-700-17); background:var(--ts-bg-navy-800-14); color:var(--ts-text-sky-300-26); }
    .data-table td { padding:.78rem .95rem; border-bottom-color:var(--ts-border-navy-750-10); color:var(--ts-text-strong); line-height:1.35; }
    .data-table tbody tr:nth-child(even) td { background:var(--ts-bg-gray-0-a1); }
    .data-table tr:hover td { background:var(--ts-bg-blue-400-a8-2); }
    .d-block { color:var(--ts-text-sky-350-32); }
    .status-badge { padding:.25rem .55rem; border:1px solid currentColor; background:var(--ts-wash-035); }
    .status-active,.status-completed { color:var(--ts-text-green-300-12); background:var(--ts-bg-green-550-a9); }
    .status-pending { color:var(--ts-text-amber-350-2); background:var(--ts-bg-amber-550-a9); }
    .status-inactive { color:var(--ts-text-sky-250-28); background:var(--ts-bg-blue-350-a8-2); }
    .status-cancelled,.status-expired { color:var(--ts-text-red-200-12); background:var(--ts-bg-red-400-a9); }
    .action-btn,.secondary-btn,.danger-btn { height:38px !important; min-height:0 !important; display:inline-flex; align-items:center; justify-content:center; padding:0 .9rem !important; border-radius:8px; font-weight:700; font-size:.84rem; line-height:1; }
    .modal-actions .action-btn,.modal-actions .secondary-btn,.modal-actions .danger-btn { flex:0 1 auto !important; min-width:130px; }
    .action-btn { border-color:var(--ts-border-blue-400-6); background:linear-gradient(135deg,var(--ts-bg-blue-450-7),var(--ts-bg-indigo-350-14)); }
    .secondary-btn { border-color:var(--ts-border-navy-700-17); background:var(--ts-bg-navy-850-13); color:var(--ts-text-sky-100-3); }
    .secondary-btn:hover { border-color:var(--ts-border-sky-550-5); background:var(--ts-bg-navy-800-15); }
    .danger-btn { border-color:var(--ts-border-red-350-a38); background:var(--ts-bg-red-350-a9); color:var(--ts-text-red-200-6); }
    .row-actions { min-width:260px; align-items:center; }
    .pagination { color:var(--ts-text-sky-300-27); }.pagination button { min-width:36px; min-height:34px; border-color:var(--ts-border-navy-700-17); background:var(--ts-bg-navy-850-13); color:var(--ts-text-on-accent-22); }
    .section-actions { padding:1rem 1.1rem; border-bottom-color:var(--ts-border-strong); background:linear-gradient(90deg,var(--ts-bg-blue-400-a8-3),transparent); }
    .section-actions p,.plans-intro p { color:var(--ts-text-sky-300-28); }
    .plans-intro h3,.landing-plan-card h3,.landing-plan-price { color:var(--ts-text-blue-50); }
    .landing-plan-card { min-height:340px; border-color:var(--ts-border-strong); background:linear-gradient(155deg,var(--ts-bg-navy-850-45),var(--ts-bg-navy-850-9)); box-shadow:0 10px 26px var(--ts-shadow-gray-1000-a10); }
    .landing-plan-card p,.landing-plan-price small { color:var(--ts-text-sky-300-29); }
    .total-banner { margin:.75rem; border:1px solid var(--ts-border-teal-450-a25); background:var(--ts-bg-teal-450-a8); color:var(--ts-text-green-300-13); }
    .stats-intro { display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; }
    .export-actions { display:flex; flex-wrap:wrap; justify-content:flex-end; gap:.5rem; }
    .custom-value{display:block;margin-top:.18rem;color:var(--ts-text-blue-250-24);font-size:.58rem;font-weight:800}.subscription-custom-modal{width:min(680px,calc(100vw - 2rem))}.subscription-defaults,.subscription-custom-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.7rem}.subscription-defaults{margin:.8rem 0}.subscription-defaults span{display:grid;gap:.2rem;padding:.7rem;border:1px solid var(--ts-border-strong);border-radius:10px;background:var(--ts-bg-navy-850-46)}.subscription-defaults small{color:var(--ts-text-sky-400-20)}.subscription-custom-grid label,.requester-field label{min-width:0}.subscription-custom-grid input,.subscription-custom-grid textarea,.requester-field select{width:100%;box-sizing:border-box}.requester-field{display:grid;gap:.3rem;margin:.8rem 0}.requester-field small{color:var(--ts-text-sky-400-20)}.notification-audience{display:flex;align-items:flex-start;gap:.55rem;margin-bottom:.8rem;padding:.7rem;color:var(--ts-text-sky-250-29);border:1px solid var(--ts-border-blue-400-a25-4);border-radius:10px;background:var(--ts-bg-blue-550-a8);font-size:.7rem;line-height:1.45}.notification-audience i{color:var(--ts-text-blue-300-11)}.custom-notes{grid-column:1/-1}.subscription-custom-actions{display:flex;justify-content:flex-end;gap:.6rem;margin-top:1rem}@media(max-width:600px){.subscription-defaults,.subscription-custom-grid{grid-template-columns:1fr}.custom-notes{grid-column:auto}.subscription-custom-actions{display:grid;grid-template-columns:1fr}}
    .subscription-kpi-grid { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:.7rem; margin-top:1rem; }
    .subscription-kpi-grid article { min-width:0; min-height:105px; display:grid; align-content:center; gap:.2rem; padding:.85rem .95rem; border:1px solid var(--ts-border-strong); border-radius:11px; background:var(--ts-bg-navy-850-44); }
    .subscription-kpi-grid span { color:var(--ts-text-sky-300-30); font-size:.72rem; font-weight:700; }
    .subscription-kpi-grid strong { color:var(--ts-text); font-size:1.35rem; letter-spacing:-.03em; }
    .subscription-kpi-grid small { color:var(--ts-text-sky-400-21); font-size:.7rem; }
    .subscription-kpi-grid .subscription-revenue { border-color:var(--ts-border-teal-450-a30); background:linear-gradient(145deg,var(--ts-bg-teal-550-a11),var(--ts-bg-navy-850-44)); }
    .payment-summary { min-height:78px; display:grid; grid-template-columns:auto 1fr; grid-template-areas:"label value" "detail detail"; align-items:center; gap:.1rem .4rem; padding:.75rem .85rem; border:1px solid var(--ts-border-navy-700-17); border-radius:11px; background:var(--ts-bg-navy-850-31); color:var(--ts-text); text-align:left; cursor:pointer; }
    .payment-summary span { grid-area:label; font-weight:700; }.payment-summary strong { grid-area:value; justify-self:end; font-size:1rem; }.payment-summary small { grid-area:detail; color:var(--ts-text-sky-350-33); }
    .payment-summary.approved { border-color:var(--ts-border-teal-450-a30); }.payment-summary.pending { border-color:var(--ts-border-amber-350-a30); }.payment-summary.failed { border-color:var(--ts-border-red-350-a30); }.payment-summary.total { border-color:var(--ts-border-blue-300-a34-4); }
    /* Bug real reportado: dentro de .payout-modal (padding:0 -- ver el
       comentario junto a esa regla), cada sección hija se separa de los
       bordes con SU PROPIO margen horizontal (.owner-contact-strip,
       .payment-summary-grid, app-admin-tabs usan 1.2rem a los lados) --
       este bloque se quedó con margin:1rem 0 (solo vertical), así que
       quedaba pegado a ambos bordes del modal mientras sus vecinos no. */
    .sa-origin-breakdown { display:grid; gap:.75rem; margin:1rem 1.2rem 0; padding:1rem; border:1px solid var(--ts-border-navy-700-17); border-radius:13px; background:linear-gradient(145deg,var(--ts-bg-navy-850-47),var(--ts-bg-navy-900-21)); }
    .sa-origin-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(170px,1fr)); gap:.65rem; }
    .sa-origin-cell { padding:.75rem; border:1px solid var(--ts-border-navy-700-8); border-radius:11px; background:var(--ts-bg-navy-850-24); }
    .sa-origin-cell span { display:block; color:var(--ts-text-sky-300-12); font-size:.64rem; font-weight:750; }
    .sa-origin-cell strong { display:block; margin:.3rem 0 .15rem; font-size:1.1rem; }
    .sa-origin-cell small { display:block; color:var(--ts-text-sky-400-15); font-size:.64rem; line-height:1.4; }
    .sa-origin-position { display:flex; align-items:center; gap:.8rem; padding:.9rem; border:1px solid var(--ts-border-teal-700); border-radius:12px; background:var(--ts-bg-teal-850-2); }
    .sa-origin-position>i { font-size:1.4rem; color:var(--ts-text-green-300-2); }
    .sa-origin-position small { display:block; margin-bottom:.15rem; color:var(--ts-text-green-300-3); font-size:.62rem; font-weight:800; letter-spacing:.06em; text-transform:uppercase; }
    .sa-origin-position strong { font-size:1rem; }
    .sa-origin-position span { display:block; margin-top:.1rem; color:var(--ts-text-green-300-4); font-size:.72rem; line-height:1.5; }
    .sa-origin-position.to-company { border-color:var(--ts-border-orange-650); background:var(--ts-bg-orange-850-2); }
    .sa-origin-position.to-company>i { color:var(--ts-text-orange-250-3); }
    .sa-origin-position.to-company strong { color:var(--ts-text-orange-200-4); }
    .sa-origin-position.to-company span { color:var(--ts-text-orange-250-4); }
    .debt-position-pay-btn { margin-left:auto; flex-shrink:0; }
    .sa-debt-tab-hint { display:flex; align-items:center; gap:.5rem; margin:.6rem 0 0; color:var(--ts-text-sky-350-34); font-size:.72rem; }
    .sa-debt-tab-hint strong { color:var(--ts-text-sky-100-5); }
    .debt-settlement-history { display:grid; gap:.85rem; margin-bottom:1rem; }
    .debt-settlement-card { padding:1rem; border:1px solid var(--ts-border-navy-700-17); border-radius:13px; background:var(--ts-bg-navy-850-27); }
    .debt-settlement-card-head { display:flex; align-items:center; justify-content:space-between; gap:1rem; margin-bottom:.8rem; padding-bottom:.7rem; border-bottom:1px solid var(--ts-border-navy-750-6); }
    .debt-settlement-card-head small { display:block; color:var(--ts-text-sky-400-15); font-size:.62rem; font-weight:800; letter-spacing:.06em; text-transform:uppercase; }
    .debt-settlement-card-head strong { display:block; margin-top:.2rem; font-size:1rem; color:var(--ts-text-sky-50-4); }
    .debt-status { flex-shrink:0; font-size:.62rem; font-weight:800; letter-spacing:.04em; text-transform:uppercase; padding:.15rem .5rem; border-radius:999px; background:var(--ts-bg-orange-850-2); color:var(--ts-text-orange-250-3); }
    .debt-status.paid { background:var(--ts-bg-teal-850-2); color:var(--ts-text-green-300-2); }
    .debt-settlement-detail-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(210px,1fr)); gap:.7rem 1.2rem; margin:0; }
    .debt-settlement-detail-grid div { display:block; padding-top:0; border-top:none; }
    .debt-settlement-detail-grid dt { color:var(--ts-text-sky-400-15); font-size:.64rem; font-weight:700; text-align:left; overflow:visible; text-overflow:clip; white-space:normal; }
    .debt-settlement-detail-grid dd { margin:.25rem 0 0; color:var(--ts-text-sky-50-4); font-size:1rem; font-weight:700; text-align:left; overflow:visible; text-overflow:clip; white-space:normal; word-break:break-word; }
    .debt-settlement-detail-grid .debt-settlement-highlight dd { color:var(--ts-text-orange-200-4); }
    /* Corrección pedida: en tablet/móvil caía a 1 sola columna -- se fuerzan
       2 columnas fijas para aprovechar el espacio. */
    @media(max-width:700px){.debt-settlement-detail-grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:.6rem .8rem}}
    @media(max-width:360px){.debt-settlement-detail-grid{grid-template-columns:1fr}}
    .debt-settlement-card-footer { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:.6rem; margin-top:.9rem; padding-top:.8rem; border-top:1px solid var(--ts-border-navy-750-6); color:var(--ts-text-green-300-3); font-size:.72rem; }
    .debt-settlement-card-footer i { color:var(--ts-text-green-300-2); }
    /* Los botones de acción (copiar/enviar enlace, registrar pago manual)
       van agrupados y centrados para tenerlos cerca y fáciles de alcanzar,
       en vez de repartidos en los extremos con justify-content:space-between. */
    .debt-settlement-card-actions { justify-content:center; }
    .debt-settlement-card-actions { color:var(--ts-text-sky-250-30); }
    .debt-confirm-form { display:grid; gap:.6rem; margin-top:.7rem; padding:.75rem; border:1px solid var(--ts-border-navy-700-17); border-radius:11px; background:var(--ts-bg-navy-900-8); }
    .debt-confirm-form label { display:grid; gap:.3rem; color:var(--ts-text-sky-250-30); font-size:.68rem; font-weight:700; }
    .debt-confirm-form input, .debt-confirm-form select, .debt-confirm-form textarea { box-sizing:border-box; width:100%; }
    .modal-overlay { padding:1rem; background:var(--ts-bg-navy-950-a78-3); backdrop-filter:blur(7px); }
    .modal-box,.business-modal { border:1px solid var(--ts-border-navy-700-17); background:var(--ts-bg-elevated); color:var(--ts-text); box-shadow:0 28px 80px var(--ts-shadow-gray-1000-a50); }
    .modal-box { width:min(620px,94vw); padding:1.2rem; border-radius:14px; }
    .modal-header { border-bottom-color:var(--ts-border-strong); background:var(--ts-bg-navy-850-13); }.modal-header h3 { color:var(--ts-text-blue-50-6); }.close-btn { color:var(--ts-text-sky-250-31); }.modal-header .modal-icon { color:var(--ts-text-blue-250-13); background:var(--ts-bg-blue-350-a14); }
    .modal-intro { margin:0; padding:.7rem .8rem; border:1px solid var(--ts-border-blue-300-a20); border-radius:9px; background:var(--ts-bg-blue-400-a7-2); color:var(--ts-text-sky-200-19); font-size:.8rem; line-height:1.5; }
    .reset-input { box-sizing:border-box; border-color:var(--ts-border-navy-700-17); background:var(--ts-bg-navy-900-8); color:var(--ts-text); }
    @media (max-width: 1050px) { .data-toolbar { grid-template-columns:minmax(220px,1fr) repeat(2,minmax(135px,auto)); } .data-toolbar > .search-control { grid-column:1 / -1; } .data-toolbar > .search-control .search-field { min-width:0; } .payment-summary-grid { grid-template-columns:repeat(2,minmax(0,1fr)); } }
    @media (max-width: 1100px) { .stats-grid,.subscription-kpi-grid { grid-template-columns:repeat(2,minmax(0,1fr)); } }
    @media (max-width: 900px) { .overview-visuals { grid-template-columns:1fr 1fr; } .overview-wide { grid-column:span 2; } .stats-intro { grid-template-columns:1fr; flex-direction:column; }.export-actions { justify-content:flex-start; } }
    /* Pedido del usuario: en tablet y móvil, "Resumen general" (.stats-grid)
       debe verse en DOS columnas, no una sola -- se saca de esta lista para
       que conserve el repeat(2,...) que ya le da la consulta de 1100px de
       arriba, en vez de que esta de 680px lo vuelva a aplastar a 1fr. El
       resto de cuadrículas de este bloque no cambia. */
    @media (max-width: 680px) { .plan-form-grid, .feature-grid, .overview-visuals, .data-toolbar, .payment-summary-grid, .subscription-kpi-grid { grid-template-columns: 1fr; } .overview-wide { grid-column:auto; } .plan-editor { max-height: none; } .section-actions { align-items:flex-start; flex-direction:column; } .data-toolbar select { width:100%; }.superadmin-container{padding:.15rem 0 1rem}.stats-intro{display:flex}.modal-box{width:min(94vw,620px)} }
    @media (max-width: 640px) { .superadmin-container { padding: 1rem; } .results-note { width: 100%; margin-left: 0; } .plans-intro { align-items: flex-start; flex-direction: column; } }
    .payout-business-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(240px,100%),320px));gap:.75rem;margin-bottom:1rem}.payout-business-card{display:grid;gap:.35rem;padding:1rem;text-align:left;border:1px solid var(--ts-border-navy-700-17);border-radius:13px;color:var(--ts-text);background:var(--ts-bg-elevated)}.payout-business-card strong{font-size:1.35rem;color:var(--ts-text-teal-350-7)}.payout-business-card small,.payout-settlement small{color:var(--ts-text-sky-350-34)}.payout-modal{width:min(1280px,calc(100vw - 3rem))!important;max-width:min(1280px,calc(100vw - 3rem))!important;max-height:90dvh;overflow:auto}.payout-settlement{display:grid;gap:.65rem;margin-top:.8rem;padding:.8rem;border:1px solid var(--ts-border-strong);border-radius:10px;background:var(--ts-bg-navy-900-8)}.payout-settlement-heading{display:grid;gap:.25rem}.payout-transfer-history{display:grid;gap:.4rem}.payout-transfer-history>div{display:flex;justify-content:space-between;gap:.75rem;padding:.55rem .65rem;border-radius:8px;background:var(--ts-bg-navy-850-48);color:var(--ts-text-sky-200-20);font-size:.7rem}.payout-transfer-history a{color:var(--ts-text-blue-250-25)}.scope-chip{padding:.4rem .65rem;border:1px solid var(--ts-border-navy-700-17);border-radius:999px;color:var(--ts-text-sky-200-21);background:var(--ts-bg-navy-850-48);font-size:.68rem}.payout-destination-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(290px,100%),1fr));gap:.7rem;margin:1rem 0}.payout-destination-grid article{padding:.85rem;border:1px solid var(--ts-border-strong);border-radius:12px;background:var(--ts-bg-navy-900-8)}.payout-destination-grid article>div{display:grid;gap:.2rem;margin-bottom:.7rem}.payout-destination-grid article>div span{color:var(--ts-text-blue-250-4);font-size:.62rem;font-weight:800}.payout-destination-grid dl{display:grid;gap:.4rem}.payout-destination-grid dl div{display:flex;justify-content:space-between;gap:1rem;padding-top:.4rem;border-top:1px solid var(--ts-border-navy-750-5);font-size:.7rem}.payout-destination-grid dt{color:var(--ts-text-sky-350-15)}.payout-destination-grid dd{margin:0;text-align:right}.primary-destination{border-color:var(--ts-border-sky-550-6)!important}.account-number{color:var(--ts-text-green-200);font-weight:800;letter-spacing:.04em}.payout-warning{padding:.8rem;color:var(--ts-text-orange-200-6);border:1px solid var(--ts-border-amber-650-2);border-radius:10px;background:var(--ts-bg-orange-850-5)}.business-payment-history{margin:1rem 0}.table-scroll{overflow-x:auto}
    @media(max-width:760px){.payout-modal{width:calc(100vw - 1rem);max-height:calc(100dvh - 1rem);padding:.8rem}.payout-modal .payment-summary-grid,.payout-modal .plan-form-grid{grid-template-columns:1fr}.payout-destination-grid{grid-template-columns:1fr}.payout-destination-grid dl div{align-items:flex-start;flex-direction:column;gap:.2rem}.payout-destination-grid dd{max-width:100%;text-align:left;overflow-wrap:anywhere}.payout-transfer-history>div{align-items:flex-start;flex-direction:column}.business-payment-history .section-actions{align-items:flex-start;flex-direction:column}.business-payment-history .table-scroll{overflow:visible}.business-payment-history table,.business-payment-history tbody,.business-payment-history tr,.business-payment-history td,.subscription-payments-table,.subscription-payments-table tbody,.subscription-payments-table tr,.subscription-payments-table td{display:block;width:100%;box-sizing:border-box}.business-payment-history thead,.subscription-payments-table thead{display:none}.business-payment-history tbody,.subscription-payments-table tbody{display:grid;gap:.65rem}.business-payment-history tbody tr,.subscription-payments-table tbody tr{padding:.7rem;border:1px solid var(--ts-border-strong);border-radius:11px;background:var(--ts-bg-navy-900-8)}.business-payment-history td,.subscription-payments-table td{display:flex;justify-content:space-between;gap:1rem;padding:.42rem 0!important;border:0;border-bottom:1px solid var(--ts-border-navy-750-5);text-align:right;white-space:normal;overflow-wrap:anywhere}.business-payment-history td:last-child,.subscription-payments-table td:last-child{border-bottom:0}.business-payment-history td:before,.subscription-payments-table td:before{content:attr(data-label);color:var(--ts-text-sky-350-15);font-size:.61rem;font-weight:800;text-align:left;text-transform:uppercase}.business-payment-history .empty-cell:before,.subscription-payments-table .empty-cell:before{content:none}.business-payment-history .empty-cell,.subscription-payments-table .empty-cell{display:block;text-align:center}.payout-settlement .plan-form-grid>*{width:100%;min-width:0}.account-number{word-break:break-all}}
    .payout-business-grid{grid-template-columns:1fr}.payout-business-card{grid-template-columns:minmax(0,1.5fr) minmax(160px,.7fr) minmax(150px,.6fr);align-items:center;width:100%;max-width:none;padding:1rem 1.15rem}.payout-business-card>div{display:grid;gap:.25rem;min-width:0}.payout-business-card>div>span{color:var(--ts-text-sky-400-19);font-size:.6rem;font-weight:800;letter-spacing:.08em}.payout-business-card>div>strong{overflow-wrap:anywhere}.payout-amount{color:var(--ts-state-success-text, var(--ts-text-teal-350-7))!important}.subscription-payment-card{margin-top:1rem;border:1px solid var(--ts-border-strong);border-radius:15px;background:var(--ts-bg-navy-900-8);overflow:hidden}.subscription-card-header{display:flex;align-items:center;justify-content:space-between;gap:1rem;width:100%;padding:1rem 1.15rem;border:0;color:var(--ts-text);background:var(--ts-bg-navy-850-24);text-align:left;cursor:pointer}.subscription-card-header h3{margin:.25rem 0}.subscription-card-header p{margin:0;color:var(--ts-text-sky-350-34);font-size:.72rem}.subscription-card-total{display:grid;grid-template-columns:auto auto;gap:.2rem .65rem;align-items:center;text-align:right}.subscription-card-total strong{font-size:1.3rem;color:var(--ts-text-teal-350-7)}.subscription-card-total small{grid-column:1;color:var(--ts-text-sky-350-34)}.subscription-card-total i{grid-column:2;grid-row:1/3}.subscription-kpis{padding:1rem}.subscription-payment-detail{padding:0 1rem 1rem}.simple-transfer-form{display:grid;gap:.9rem;margin:1rem 0;padding:1rem;border:1px solid var(--ts-border-sky-550-6);border-radius:14px;background:var(--ts-bg-navy-850-24)}.simple-transfer-form h4{margin:.25rem 0}.simple-transfer-form p{margin:0;color:var(--ts-text-sky-350-34);font-size:.72rem}.simple-transfer-fields{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:.7rem}.simple-transfer-fields label{display:grid;gap:.35rem;color:var(--ts-text-sky-250-30);font-size:.68rem;font-weight:700}.simple-transfer-fields input,.simple-transfer-fields select,.simple-transfer-fields textarea{box-sizing:border-box;width:100%;min-width:0}.comment-field{grid-column:1/-1}.reservation-payment-list,.simple-transfer-history{display:grid;gap:.55rem}.reservation-payment-list article,.simple-transfer-history article{display:grid;grid-template-columns:minmax(0,1fr) auto minmax(120px,.35fr);align-items:center;gap:1rem;padding:.75rem .85rem;border:1px solid var(--ts-border-strong);border-radius:11px;background:var(--ts-bg-navy-850-19)}.reservation-payment-list article>div,.simple-transfer-history article>div{display:grid;gap:.2rem;min-width:0}.reservation-payment-list small,.simple-transfer-history small{color:var(--ts-text-sky-350-34);font-size:.65rem}.reservation-payment-amount{text-align:right}.simple-transfer-history article{grid-template-columns:minmax(150px,.4fr) 1fr}.simple-transfer-history article>div:last-child{text-align:right}
    /* "Pagos de reservas" agrupado: .payout-reservation-group es un <article>
       dentro de .reservation-payment-list, así que hereda display:grid/padding
       de la regla de arriba -- se cancela aquí para que la tarjeta pueda
       crecer con el desglose de fechas debajo, y el grid de 3 columnas pasa a
       vivir en .payout-reservation-group-main (la fila clicable). */
    .reservation-payment-list article.payout-reservation-group{display:block;padding:0;overflow:hidden}
    .payout-reservation-filter{display:flex;flex-wrap:wrap;align-items:flex-end;gap:.6rem;margin-bottom:.85rem}
    .payout-reservations-section>.section-actions{margin-bottom:1.15rem;padding-bottom:.85rem;border-bottom:1px solid var(--ts-border-strong)}
    .payout-reservation-filter label{display:grid;flex:1 1 260px;gap:.3rem;color:var(--ts-text-sky-300-14);font-size:.68rem;font-weight:700;min-width:0}
    .payout-reservation-filter input{box-sizing:border-box;width:100%;min-height:38px;padding:.5rem .65rem;border:1px solid var(--ts-border-strong);border-radius:8px;background:var(--ts-bg-navy-850-19);color:var(--ts-text)}
    .payout-reservation-filter .btn-refresh{flex:0 0 auto;min-height:38px}
    .payout-reservation-filter .icon-only{flex:0 0 auto;width:38px;min-height:38px;padding:0;display:grid;place-items:center;border:1px solid var(--ts-border-strong);border-radius:8px;color:var(--ts-text-sky-300-14);background:var(--ts-bg-navy-850-19);cursor:pointer}
    .payout-reservation-group-main{display:grid;grid-template-columns:minmax(0,1fr) auto minmax(120px,.35fr) auto;align-items:center;gap:1rem;padding:.75rem .85rem}
    .payout-reservation-group-main.expandable{cursor:pointer}
    .payout-reservation-group-main.expandable:hover{background:var(--ts-bg-navy-850-24)}
    .payout-reservation-group-main>div:first-child{display:grid;gap:.2rem;min-width:0}
    .payout-reservation-group-chevron{color:var(--ts-text-sky-400-19);font-size:.85rem}
    .payout-reservation-group-members{display:grid;gap:.5rem;padding:0 .85rem .75rem;border-top:1px solid var(--ts-border-navy-800)}
    .payout-reservation-member{display:flex;flex-wrap:wrap;justify-content:space-between;gap:.5rem 1rem;padding-top:.55rem;color:var(--ts-text-sky-200-20);font-size:.68rem}
    .payout-reservation-member small{color:var(--ts-text-sky-400-19)}
    @media(max-width:760px){.payout-business-card{grid-template-columns:1fr;gap:.8rem}.subscription-card-header{align-items:flex-start;flex-direction:column}.subscription-card-total{text-align:left}.simple-transfer-fields{grid-template-columns:1fr}.comment-field{grid-column:auto}.reservation-payment-list article,.simple-transfer-history article{grid-template-columns:1fr;gap:.55rem}.reservation-payment-amount,.simple-transfer-history article>div:last-child{text-align:left}.payout-reservation-group-main{grid-template-columns:1fr;gap:.4rem}.payout-reservation-group-main .reservation-payment-amount{text-align:left}}
    .search-field input,
    .search-field input:focus,
    .search-field input:focus-visible {
      border: 0 !important;
      outline: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
    }
    .business-payouts-content {
      overflow: visible;
    }
    .payout-section-heading,
    .subscription-section-heading {
      align-items: center;
      background: linear-gradient(135deg, var(--ts-bg-blue-500-a16), var(--ts-bg-navy-800-a20));
    }
    .payout-section-heading h3,
    .subscription-section-heading h3 {
      margin: .2rem 0;
      color: var(--ts-text);
    }
    .payout-heading-summary,
    .subscription-heading-total {
      display: grid;
      flex: 0 0 auto;
      gap: .18rem;
      text-align: right;
    }
    .payout-heading-summary strong,
    .subscription-heading-total strong {
      color: var(--ts-text-green-300-14);
      font-size: 1.35rem;
    }
    .payout-heading-summary small,
    .subscription-heading-total small {
      color: var(--ts-text-sky-300-31);
      font-size: .68rem;
    }
    /* Segunda cifra del encabezado: dirección OPUESTA (negocio -> TuSala) --
       color de advertencia, nunca el mismo verde de "saldo a transferir",
       para que las dos direcciones de dinero no se confundan a simple vista. */
    .payout-heading-debt strong { color: var(--ts-text-orange-250-3); }
    .payout-amount-zero { color: var(--ts-amount-zero-text, var(--ts-text-teal-350-7)) !important; opacity: var(--ts-amount-zero-opacity, .55); }
    .payout-debt-chip {
      display: inline-flex; align-items: center; gap: .35rem;
      margin-top: .3rem; padding: .2rem .5rem;
      border: 1px solid var(--ts-border-orange-650); border-radius: 999px;
      background: var(--ts-bg-orange-850-2); color: var(--ts-text-orange-250-3);
      font-size: .64rem; font-weight: 800; width: fit-content;
    }
    .business-card-status > span.debt {
      color: var(--ts-text-red-200-13); border-color: var(--ts-border-red-400-a35); background: var(--ts-bg-red-500-a12);
    }
    /* Tarjetas de pagos de suscripciones: misma línea visual que Novedades globales.
       Sin cursor de mano ni efectos de hover interactivos: son registros de solo lectura. */
    .sp-list { display: grid; gap: .5rem; margin-top: .9rem; }
    .sp-card {
      --sp-accent: var(--ts-text-blue-300-8); --sp-rgb: 95, 155, 255;
      position: relative; display: grid;
      grid-template-columns: 4px 40px minmax(0, 1.3fr) minmax(0, 1.15fr) minmax(120px, auto) 92px;
      align-items: center; min-height: 68px; gap: .7rem;
      overflow: hidden; border: 1px solid var(--ts-border-navy-700-11); border-radius: 12px;
      background: linear-gradient(110deg, var(--ts-bg-navy-850-32) 0%, var(--ts-bg-navy-850-32) 70%, rgba(var(--sp-rgb), .05) 100%);
      cursor: default;
    }
    .sp-card.sp-succeeded { --sp-accent: var(--ts-accent-teal-450); --sp-rgb: 78, 211, 151; }
    .sp-card.sp-pending { --sp-accent: var(--ts-accent-amber-450); --sp-rgb: 240, 180, 41; }
    .sp-card.sp-failed { --sp-accent: var(--ts-text-red-300-2); --sp-rgb: 255, 111, 121; }
    .sp-indicator { width: 4px; height: 100%; background: rgba(var(--sp-rgb), .55); }
    .sp-icon { display: grid; place-items: center; width: 32px; height: 32px; color: var(--sp-accent); border: 1px solid rgba(var(--sp-rgb), .28); border-radius: 9px; background: rgba(var(--sp-rgb), .1); font-size: .92rem; }
    .sp-main { min-width: 0; }
    .sp-title { margin: 0; overflow: hidden; color: var(--ts-text-blue-50-6); font-size: .86rem; font-weight: 650; text-overflow: ellipsis; white-space: nowrap; }
    .sp-summary { margin: .15rem 0 0; overflow: hidden; color: var(--ts-text-sky-300-19); font-size: .72rem; text-overflow: ellipsis; white-space: nowrap; }
    .sp-tags { display: flex; flex-wrap: wrap; align-items: center; align-content: center; gap: .3rem; min-width: 0; }
    .sp-tag { display: inline-flex; align-items: center; gap: .25rem; max-width: 100%; padding: .15rem .4rem; border-radius: 999px; border: 1px solid rgba(var(--sp-rgb), .24); color: var(--ts-text-sky-250-22); background: var(--ts-bg-navy-800-9); font-size: .6rem; white-space: nowrap; }
    /* Etiquetas largas (p. ej. "Alta de propietario — Plan … (pago por otro medio)"): en celular se parten en vez de salirse de la tarjeta. */
    @media (max-width: 620px) { .sp-tag { white-space: normal; overflow-wrap: anywhere; line-height: 1.3; } }
    .sp-tag i { font-size: .58rem; color: var(--sp-accent); }
    .sp-status-tag { font-size: .6rem !important; padding: .18rem .5rem !important; }
    .sp-context { align-self: stretch; display: flex; min-width: 0; padding: .5rem 0; flex-direction: column; align-items: flex-end; justify-content: center; gap: .25rem; border-left: 1px solid var(--ts-border-sky-450-a18); }
    .sp-amount { color: var(--ts-text-blue-50-6); font-size: .86rem; font-weight: 700; }
    .sp-date { display: inline-flex; align-items: center; gap: .3rem; color: var(--ts-text-sky-350-25); font-size: .6rem; white-space: nowrap; }
    .sp-date i { color: var(--sp-accent); }
    .sp-receipt-col { display: flex; align-items: center; justify-content: center; align-self: stretch; padding-right: .8rem; }
    .sp-receipt { display: inline-flex; align-items: center; gap: .25rem; padding: .4rem .55rem; border: 1px solid var(--ts-divider); border-radius: 7px; background: var(--ts-bg-navy-850-37); color: var(--ts-text-sky-200-12); font-size: .62rem; font-weight: 600; white-space: nowrap; cursor: pointer; }
    .sp-receipt:hover { background: var(--ts-bg-navy-800-8); }
    .sp-receipt-missing { color: var(--ts-text-sky-500-6); font-size: .6rem; text-align: center; white-space: nowrap; }
    .sp-tabs-wrap ::ng-deep .tab-copy small { font-size: .82rem !important; font-weight: 700 !important; color: var(--ts-text-sky-100-11) !important; }
    .sp-tabs-wrap ::ng-deep .admin-tab.active .tab-copy strong,
    .sp-tabs-wrap ::ng-deep .admin-tab.active .tab-copy small,
    .sp-tabs-wrap ::ng-deep .admin-tab.active > i { color: #fff !important; }
    @media (max-width: 1024px) {
      .sp-card { grid-template-columns: 4px 36px minmax(0, 1fr) auto; grid-template-rows: auto auto; min-height: 0; padding-bottom: .6rem; }
      .sp-main { grid-column: 3; grid-row: 1; }
      .sp-receipt-col { grid-column: 4; grid-row: 1; padding-right: 0; }
      .sp-tags { grid-column: 1 / -1; grid-row: 2; padding-left: 52px; }
      .sp-context { grid-column: 1 / -1; grid-row: 3; min-width: 0; padding: .4rem .8rem 0 52px; flex-direction: row; align-items: center; justify-content: space-between; border-left: 0; border-top: 1px solid var(--ts-border-sky-450-a18); }
    }
    @media (max-width: 640px) {
      .sp-context { flex-wrap: wrap; gap: .5rem; padding-left: 0; }
      .sp-tags { padding-left: 0; }
    }
    .business-payouts-content .payout-business-grid {
      gap: .8rem;
      padding: 1rem;
    }
    .business-payouts-content .payout-business-card {
      position: relative;
      overflow: hidden;
      grid-template-columns: 48px minmax(220px, 1.4fr) minmax(180px, .65fr) minmax(150px, .5fr);
      gap: 1rem;
      padding: 1rem;
      border-color: var(--ts-border-navy-650-9);
      background: linear-gradient(135deg, var(--ts-bg-navy-850-5), var(--ts-bg-navy-900-22));
      box-shadow: 0 8px 22px var(--ts-shadow-gray-1000-a12);
      cursor: pointer;
      transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease;
    }
    .business-payouts-content .payout-business-card::before {
      content: '';
      position: absolute;
      inset: 0 auto 0 0;
      width: 4px;
      background: linear-gradient(var(--ts-bg-blue-350-8), var(--ts-bg-teal-400-2));
    }
    .business-payouts-content .payout-business-card:hover {
      transform: translateY(-2px);
      border-color: var(--ts-border-sky-450);
      box-shadow: 0 13px 28px var(--ts-shadow-gray-1000-a20);
    }
    .business-card-icon {
      display: grid;
      place-items: center;
      width: 44px;
      height: 44px;
      color: var(--ts-text-blue-250-14);
      border: 1px solid var(--ts-border-blue-350-a35-2);
      border-radius: 12px;
      background: var(--ts-bg-blue-450-a14);
      font-size: 1.15rem;
    }
    .business-card-identity,
    .business-card-metric {
      display: grid;
      gap: .2rem;
      min-width: 0;
    }
    .business-card-identity > span,
    .business-card-metric > span {
      color: var(--ts-text-sky-400-22);
      font-size: .6rem;
      font-weight: 800;
      letter-spacing: .09em;
    }
    .business-card-identity strong {
      color: var(--ts-text) !important;
      font-size: .98rem !important;
    }
    .business-card-identity small {
      overflow: hidden;
      color: var(--ts-text-sky-300-32);
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .business-card-tags {
      display: flex;
      flex-wrap: wrap;
      gap: .35rem;
      margin-top: .25rem;
    }
    .business-card-tags b {
      display: inline-flex;
      align-items: center;
      gap: .3rem;
      padding: .24rem .42rem;
      color: var(--ts-text-sky-200-22);
      border: 1px solid var(--ts-border-sky-400-a28-3);
      border-radius: 7px;
      background: var(--ts-bg-sky-500-a10);
      font-size: .6rem;
      font-weight: 700;
    }
    .business-card-metric strong {
      font-size: 1.25rem !important;
    }
    .business-card-status {
      display: grid;
      justify-items: end;
      gap: .45rem;
    }
    .business-card-status > span {
      display: inline-flex;
      align-items: center;
      gap: .35rem;
      padding: .34rem .55rem;
      color: var(--ts-text-orange-250-7);
      border: 1px solid var(--ts-border-amber-450-a35);
      border-radius: 999px;
      background: var(--ts-bg-orange-550-a11);
      font-size: .67rem;
      font-weight: 750;
    }
    .business-card-status > span.ready {
      color: var(--ts-text-green-300-15);
      border-color: var(--ts-border-green-500-a35);
      background: var(--ts-bg-green-600-a10-2);
    }
    .business-card-status small {
      color: var(--ts-text-sky-250-21);
      font-weight: 700;
    }
    .section-empty-state { margin-top: 1rem; }
    /* Bug real de raíz encontrado: styles.css (global) trae reglas SIN
       relación con este componente que igual matchean por nombre de clase
       genérico y usan !important -- ".modal-box{background:linear-gradient(...)
       !important}" y ".modal-header{background:var(--ts-header-wash)
       !important}". Un !important le gana a CUALQUIER regla sin !important
       sin importar especificidad ni orden, así que el fondo/ancho de este
       componente nunca llegaba a aplicarse aunque el selector aquí fuera más
       específico. Única forma correcta de ganarle a un !important global es
       usando !important también aquí -- con selector más específico
       (.payout-modal > .modal-header gana a .modal-header a especificidad
       igual de !important), sin tocar styles.css ni afectar ningún otro
       modal que si dependa de esas reglas genéricas. */
    .payout-modal {
      padding: 0 !important;
      overflow-x: hidden;
      border: 1px solid var(--ts-border-sky-600-6) !important;
      border-radius: 18px !important;
      background: var(--ts-bg-navy-900) !important;
      box-shadow: 0 28px 80px var(--ts-shadow-gray-1000-a48) !important;
    }
    .payout-modal > .modal-header {
      position: sticky;
      isolation: isolate;
      z-index: 20;
      top: 0;
      padding: 1.25rem 1.45rem;
      color: var(--ts-text-blue-0);
      border-bottom: 1px solid var(--ts-border-sky-650) !important;
      background: linear-gradient(120deg, var(--ts-primary-strong), var(--ts-primary)) !important;
      background-color: var(--ts-primary-strong) !important;
      /* Redondea junto con el modal (mismo radio que .payout-modal) -- sin
         esto, la barra fija (rectangular) quedaba encima de la esquina
         redondeada del modal y dejaba ver el fondo oscuro detrás en las
         puntas, dando la impresión de que el título era transparente. */
      border-top-left-radius: 18px;
      border-top-right-radius: 18px;
      box-shadow: 0 8px 20px var(--ts-shadow-gray-1000-a20);
    }
    .payout-modal > .modal-header h3 {
      color: var(--ts-text-on-accent);
      font-size: 1.35rem;
    }
    .payout-modal > .modal-header p {
      margin: .35rem 0 0;
      color: var(--ts-text-on-accent-17);
    }
    .payout-modal > .modal-header .close-btn {
      display: grid;
      place-items: center;
      width: 38px;
      height: 38px;
      flex: 0 0 38px;
      color: var(--ts-text-on-accent-17);
      border: 1px solid var(--ts-border-sky-200-a25);
      border-radius: 11px;
      background: var(--ts-bg-navy-750-6);
    }
    .payout-modal > .payment-summary-grid {
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: .75rem;
      padding: 1rem 1.2rem 0;
    }
    .payout-modal > .payment-summary-grid .payment-summary {
      min-height: 96px;
      box-sizing: border-box;
      padding: .85rem;
      border-color: var(--ts-border-navy-650-3);
      border-radius: 13px;
      background: linear-gradient(145deg, var(--ts-bg-navy-800-17), var(--ts-bg-navy-850-49));
    }
    .tusala-light-theme :host .payout-modal > .modal-header .eyebrow {
      color: var(--ts-eyebrow) !important;
    }
    .tusala-light-theme :host .payout-modal > .payment-summary-grid .payment-summary {
      color: var(--ts-text);
      border-color: var(--ts-border-strong);
      border-top: 4px solid var(--ts-primary);
      background: var(--ts-surface) !important;
      box-shadow: var(--ts-shadow-sm);
    }
    .tusala-light-theme :host .payout-modal > .payment-summary-grid .payment-summary.total {
      border-top-color: var(--ts-primary);
      background: color-mix(in srgb, var(--ts-primary) 8%, var(--ts-surface)) !important;
    }
    .tusala-light-theme :host .payout-modal > .payment-summary-grid .payment-summary.approved {
      border-top-color: var(--ts-success);
      background: color-mix(in srgb, var(--ts-success) 9%, var(--ts-surface)) !important;
    }
    .tusala-light-theme :host .payout-modal > .payment-summary-grid .payment-summary.pending {
      border-top-color: var(--ts-warning);
      background: color-mix(in srgb, var(--ts-warning) 10%, var(--ts-surface)) !important;
    }
    .tusala-light-theme :host .payout-modal > .payment-summary-grid .payment-summary.failed {
      border-top-color: var(--ts-danger);
      background: color-mix(in srgb, var(--ts-danger) 8%, var(--ts-surface)) !important;
    }
    .tusala-light-theme :host .payout-modal > .payment-summary-grid .payment-summary small {
      color: var(--ts-text-muted);
    }
    .owner-contact-strip {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: .5rem 1.1rem;
      margin: .9rem 1.2rem 0;
      padding: .65rem .9rem;
      border: 1px solid var(--ts-border-navy-650-9);
      border-radius: 10px;
      background: var(--ts-bg-navy-850-50);
      color: var(--ts-text-sky-100-2);
      font-size: .78rem;
    }
    .tusala-light-theme :host .owner-contact-strip {
      color: var(--ts-text-strong);
      border-color: var(--ts-primary-border);
      background: color-mix(in srgb, var(--ts-primary) 7%, var(--ts-surface));
    }
    .owner-contact-strip span,
    .owner-contact-strip a {
      display: inline-flex;
      align-items: center;
      gap: .35rem;
    }
    .owner-contact-strip a {
      color: var(--ts-text-green-350-6);
      font-weight: 700;
      text-decoration: none;
    }
    .owner-contact-strip a:hover {
      text-decoration: underline;
    }
    .owner-contact-strip .owner-contact-missing {
      color: var(--ts-text-sky-350-35);
    }
    .payout-modal app-admin-tabs {
      display: block;
      margin: .9rem 1.2rem 0;
    }
    .payout-modal .payout-tab-panel {
      display: grid;
      gap: 1rem;
      padding-top: 1rem;
    }
    .payout-modal .payout-destination-grid,
    .payout-modal .simple-transfer-form,
    .payout-modal .business-payment-history,
    .payout-modal .simple-transfer-history,
    .payout-modal .payout-warning,
    .payout-modal .payout-tab-panel > .section-actions,
    .payout-modal .debt-settlement-history {
      margin-right: 1.2rem;
      margin-left: 1.2rem;
    }
    .payout-modal .payout-destination-grid article {
      position: relative;
      overflow: hidden;
      border-color: var(--ts-border-navy-650-10);
      background: linear-gradient(145deg, var(--ts-bg-navy-850-51), var(--ts-bg-navy-900-23));
      box-shadow: 0 8px 20px var(--ts-shadow-gray-1000-a12);
    }
    .tusala-light-theme :host .payout-modal .payout-destination-grid article {
      color: var(--ts-text);
      border-color: var(--ts-border-strong);
      background: linear-gradient(145deg, var(--ts-surface), var(--ts-surface-soft));
      box-shadow: var(--ts-shadow-sm);
    }
    .payout-modal .payout-destination-grid article::before {
      content: '';
      position: absolute;
      inset: 0 0 auto;
      height: 3px;
      background: var(--ts-bg-sky-450-8);
    }
    .payout-modal .primary-destination::before {
      background: var(--ts-bg-teal-450-2) !important;
    }
    .payout-modal .simple-transfer-form {
      padding: 1.1rem;
      color: var(--ts-text);
      border-color: var(--ts-primary-border);
      background: linear-gradient(145deg, var(--ts-bg-navy-800-17), var(--ts-bg-navy-900-23));
      box-shadow: inset 0 1px 0 var(--ts-shadow-gray-0-a3);
    }
    .payout-modal .simple-transfer-form h4 { color: var(--ts-text); }
    .payout-modal .simple-transfer-form p,
    .payout-modal .simple-transfer-form label { color: var(--ts-text-sky-200-20); }
    .payout-modal .simple-transfer-form label > small { color: var(--ts-text-sky-350-34); }
    .payout-modal .simple-transfer-form .action-btn {
      color: var(--ts-text-on-accent) !important;
      border-color: var(--ts-primary-border) !important;
      background: linear-gradient(135deg, var(--ts-primary), var(--ts-primary-grad-end)) !important;
    }
    .payout-modal .simple-transfer-form .action-btn:hover:not(:disabled),
    .payout-modal .simple-transfer-form .action-btn:focus-visible {
      color: var(--ts-text-on-accent) !important;
      filter: brightness(1.08);
    }
    .payout-modal .simple-transfer-form .action-btn:active:not(:disabled) {
      color: var(--ts-text-on-accent) !important;
      filter: brightness(.94);
      transform: translateY(0);
    }
    .payout-modal .simple-transfer-form .action-btn:disabled {
      color: var(--ts-text-disabled) !important;
      border-color: var(--ts-border-strong) !important;
      background: var(--ts-raised) !important;
      opacity: 1 !important;
      filter: none;
    }
    .payout-business-card:hover:not(:disabled),
    .payout-business-card:focus-visible {
      color: var(--ts-text);
      border-color: var(--ts-primary-border);
      background: linear-gradient(145deg, var(--ts-bg-navy-800-17), var(--ts-bg-navy-850-49));
      outline: none;
      box-shadow: 0 0 0 3px var(--ts-primary-soft-14), 0 14px 30px var(--ts-shadow-gray-1000-a20);
    }
    .payout-business-card:active:not(:disabled) { color: var(--ts-text); transform: translateY(1px); }
    .payout-business-card:disabled { color: var(--ts-text-disabled); opacity: 1; cursor: not-allowed; }
    .tusala-light-theme :host .payout-modal .simple-transfer-form {
      color: var(--ts-text);
      border-color: var(--ts-primary-border);
      background: color-mix(in srgb, var(--ts-primary) 6%, var(--ts-surface));
      box-shadow: var(--ts-shadow-sm);
    }
    .tusala-light-theme :host .payout-business-card:hover:not(:disabled),
    .tusala-light-theme :host .payout-business-card:focus-visible {
      color: var(--ts-text);
      border-color: var(--ts-primary);
      background: color-mix(in srgb, var(--ts-primary) 9%, var(--ts-surface));
    }
    .tusala-light-theme :host .payout-modal .sa-origin-breakdown {
      color: var(--ts-text);
      border-color: var(--ts-border-strong);
      background: var(--ts-surface-soft);
    }
    .tusala-light-theme :host .payout-modal .sa-origin-cell {
      color: var(--ts-text);
      border-color: var(--ts-border);
      background: var(--ts-surface);
    }
    .tusala-light-theme :host .payout-modal .sa-origin-cell span,
    .tusala-light-theme :host .payout-modal .sa-origin-cell small {
      color: var(--ts-text-muted);
    }
    /* Overrides finales del tema claro. Deben vivir dentro del componente:
       sus estilos encapsulados se insertan después del CSS global y eran los
       que devolvían los KPI a tonos pastel. El tema oscuro no coincide con
       :host-context(.tusala-light-theme) y conserva su diseño original. */
    :host-context(.tusala-light-theme) .sp-tabs-wrap ::ng-deep .admin-tab.active .tab-copy strong,
    :host-context(.tusala-light-theme) .sp-tabs-wrap ::ng-deep .admin-tab.active .tab-copy small,
    :host-context(.tusala-light-theme) .sp-tabs-wrap ::ng-deep .admin-tab.active > i {
      color:#fff!important;
    }
    :host-context(.tusala-light-theme) .payout-modal > .payment-summary-grid .payment-summary {
      color:#fff!important;border:0!important;border-top:0!important;box-shadow:none!important;
    }
    :host-context(.tusala-light-theme) .payout-modal > .payment-summary-grid .payment-summary.total { background:#1d4ed8!important; }
    :host-context(.tusala-light-theme) .payout-modal > .payment-summary-grid .payment-summary.approved { background:#15803d!important; }
    :host-context(.tusala-light-theme) .payout-modal > .payment-summary-grid .payment-summary.pending { background:#c2410c!important; }
    :host-context(.tusala-light-theme) .payout-modal > .payment-summary-grid .payment-summary.failed { background:#be123c!important; }
    :host-context(.tusala-light-theme) .payout-modal > .payment-summary-grid .payment-summary :is(span,strong,small) { color:#fff!important; }
    :host-context(.tusala-light-theme) .payout-modal .sa-origin-breakdown { border:2px solid #2563eb!important;background:#fff!important;box-shadow:none!important; }
    :host-context(.tusala-light-theme) .payout-modal .sa-origin-cell { color:#10233e!important;border:0!important;border-top:5px solid #2563eb!important;background:#fff!important;box-shadow:none!important; }
    :host-context(.tusala-light-theme) .payout-modal .sa-origin-cell:nth-child(2){border-top-color:#0891b2!important}
    :host-context(.tusala-light-theme) .payout-modal .sa-origin-cell:nth-child(3){border-top-color:#7c3aed!important}
    :host-context(.tusala-light-theme) .payout-modal .sa-origin-cell:nth-child(4){border-top-color:#dc2626!important}
    :host-context(.tusala-light-theme) .payout-modal .sa-origin-cell :is(span,strong,small){color:#10233e!important}
    :host-context(.tusala-light-theme) .payout-modal .sa-origin-position { color:#fff!important;border:0!important;background:#15803d!important; }
    :host-context(.tusala-light-theme) .payout-modal .sa-origin-position.to-company { background:#c2410c!important; }
    :host-context(.tusala-light-theme) .payout-modal .sa-origin-position :is(i,small,strong,span){color:#fff!important}
    /* Panel analítico global: datos reales de suscripciones, propietarios y clientes. */
    .analytics-dashboard { display:grid; gap:1rem; padding:0; border:0; background:transparent; box-shadow:none; }
    .analytics-hero { display:flex; justify-content:space-between; align-items:center; gap:1.5rem; padding:1.35rem 1.5rem; border:1px solid var(--ts-border-navy-650-4); border-radius:18px; background:radial-gradient(circle at 85% 0,var(--ts-bg-blue-350-a20),transparent 36%),linear-gradient(135deg,var(--ts-bg-navy-800-19),var(--ts-bg-navy-850-9)); box-shadow:0 16px 38px var(--ts-shadow-gray-1000-a18); }
    .analytics-hero h3 { margin:.25rem 0 .35rem; color:var(--ts-text); font-size:clamp(1.35rem,2.2vw,1.8rem); letter-spacing:-.025em; }
    .analytics-hero p { max-width:720px; margin:0; color:var(--ts-text-sky-300-2); line-height:1.5; }
    .analytics-kpis { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:.8rem; }
    .analytics-kpi { position:relative; display:grid; grid-template-columns:44px minmax(0,1fr); align-items:center; gap:.8rem; min-height:112px; overflow:hidden; padding:1rem; border:1px solid var(--ts-border-navy-700-18); border-radius:15px; background:linear-gradient(145deg,var(--ts-bg-navy-850-5),var(--ts-bg-elevated)); box-shadow:0 10px 26px var(--ts-shadow-gray-1000-a12); }
    .analytics-kpi::after { content:''; position:absolute; right:-32px; bottom:-48px; width:105px; height:105px; border-radius:50%; background:var(--ts-bg-blue-300-a6); }
    .analytics-kpi.kpi-primary { border-color:var(--ts-border-blue-300-a42-3); background:radial-gradient(circle at 100% 0,var(--ts-bg-indigo-350-a18-2),transparent 45%),linear-gradient(145deg,var(--ts-bg-navy-800-20),var(--ts-bg-elevated)); }
    .analytics-kpi.kpi-revenue { border-color:var(--ts-border-teal-450-a36); background:radial-gradient(circle at 100% 0,var(--ts-bg-teal-450-a14),transparent 45%),linear-gradient(145deg,var(--ts-bg-navy-850-52),var(--ts-bg-elevated)); }
    .kpi-icon { display:grid; place-items:center; width:42px; height:42px; border:1px solid var(--ts-border-blue-300-a28-2); border-radius:12px; color:var(--ts-text-blue-250-26); background:var(--ts-bg-blue-400-a12-2); font-size:1.05rem; }
    .kpi-revenue .kpi-icon { color:var(--ts-text-teal-350-8); border-color:var(--ts-border-teal-450-a28); background:var(--ts-bg-teal-450-a9); }
    .analytics-kpi span,.analytics-kpi strong,.analytics-kpi small { display:block; }
    .analytics-kpi span { color:var(--ts-text-sky-350-36); font-size:.69rem; font-weight:750; text-transform:uppercase; letter-spacing:.045em; }
    .analytics-kpi strong { margin:.25rem 0 .18rem; color:var(--ts-text-sky-0-2); font-size:1.48rem; letter-spacing:-.03em; }
    .analytics-kpi small { color:var(--ts-text-sky-400-23); font-size:.67rem; line-height:1.35; }
    .analytics-grid { display:grid; gap:.8rem; }
    .analytics-main-grid { grid-template-columns:minmax(0,1.65fr) minmax(320px,.75fr); }
    .analytics-secondary-grid { grid-template-columns:repeat(3,minmax(0,1fr)); }
    .analytics-panel { min-width:0; padding:1.1rem; border:1px solid var(--ts-border-navy-700-18); border-radius:15px; background:linear-gradient(145deg,var(--ts-bg-navy-850-12),var(--ts-bg-navy-900-3)); box-shadow:0 12px 30px var(--ts-shadow-gray-1000-a14); }
    .analytics-panel>header { display:flex; justify-content:space-between; align-items:center; gap:1rem; margin-bottom:1rem; padding-bottom:.8rem; border-bottom:1px solid var(--ts-border-sky-300-a14-2); }
    .analytics-panel h4 { margin:.16rem 0 0; color:var(--ts-text-soft-2); font-size:1rem; }
    .revenue-trend-panel>header p { margin:.25rem 0 0; color:var(--ts-text-sky-400-23); font-size:.68rem; }
    .panel-kicker { color:var(--ts-text-blue-250-26); font-size:.65rem; font-weight:850; letter-spacing:.11em; }
    .analytics-chip { padding:.3rem .55rem; color:var(--ts-text-blue-200-13); border:1px solid var(--ts-border-blue-300-a25-2); border-radius:999px; background:var(--ts-bg-blue-450-a8-2); font-size:.64rem; white-space:nowrap; }
    .chart-legend { display:flex; align-items:center; gap:.42rem; color:var(--ts-text-sky-300-33); font-size:.67rem; white-space:nowrap; }.chart-legend i{width:10px;height:10px;border-radius:3px;background:linear-gradient(180deg,var(--ts-bg-sky-300-3),var(--ts-bg-indigo-350-15));box-shadow:0 0 10px var(--ts-shadow-blue-300-a30)}
    .revenue-chart-summary { display:flex; gap:1.5rem; margin-bottom:.8rem; padding:.65rem .8rem; border:1px solid var(--ts-border-sky-350-a14); border-radius:10px; background:var(--ts-bg-navy-950-a24); }.revenue-chart-summary>div{display:grid;grid-template-columns:auto auto;gap:.1rem .5rem;align-items:end}.revenue-chart-summary span{grid-column:1/-1;color:var(--ts-text-sky-400-11);font-size:.62rem}.revenue-chart-summary strong{color:var(--ts-text-blue-50-6);font-size:.88rem}.revenue-chart-summary small{color:var(--ts-text-teal-350-4);font-size:.68rem}
    .monthly-bar-chart { display:grid; grid-template-columns:48px minmax(0,1fr); height:255px; }
    .bar-y-axis { display:flex; flex-direction:column; justify-content:space-between; padding:1.45rem 8px 24px 0; color:var(--ts-text-sky-450-9); font-size:.57rem; text-align:right; }
    .bar-plot { position:relative; display:grid; grid-template-columns:repeat(12,minmax(22px,1fr)); align-items:end; gap:clamp(4px,.8vw,10px); min-width:0; padding:1.45rem .35rem 0; }
    .month-column { z-index:1; height:100%; display:grid; grid-template-rows:20px minmax(0,1fr) 22px; align-items:end; justify-items:center; min-width:0; }
    .bar-value { color:var(--ts-text-sky-250-12); font-size:.55rem; white-space:nowrap; opacity:.85; }
    .bar-track { position:relative; width:min(38px,72%); height:100%; display:flex; align-items:flex-end; border-radius:7px 7px 3px 3px; background:var(--ts-bg-sky-450-a8); }
    .bar-track i { width:100%; min-height:0; border-radius:7px 7px 3px 3px; transform-origin:bottom; animation:barGrow .55s cubic-bezier(.2,.75,.25,1) both; animation-delay:calc(var(--bar-delay, 0ms)); background:linear-gradient(180deg,var(--ts-bg-sky-300) 0%,var(--ts-bg-blue-350-5) 58%,var(--ts-bg-indigo-350-7) 100%); box-shadow:0 0 14px var(--ts-shadow-blue-350-a20); }
    .bar-track i.has-value:hover { filter:brightness(1.16); box-shadow:0 0 20px var(--ts-shadow-blue-350-a35); }
    .month-column>strong { align-self:center; color:var(--ts-text-sky-400-5); font-size:.59rem; font-weight:650; text-transform:capitalize; }
    .bar-plot .grid-line { z-index:0; }.bar-plot .grid-top{top:1.45rem}.bar-plot .grid-mid{top:calc(50% + .72rem)}.bar-plot .grid-bottom{bottom:22px}
    .chart-explanation { display:flex; align-items:center; gap:.4rem; margin-top:.65rem; padding-top:.65rem; color:var(--ts-text-sky-450-9); border-top:1px solid var(--ts-border-sky-300-a11-2); font-size:.63rem; }
    .line-chart { display:grid; grid-template-columns:50px minmax(0,1fr); height:260px; }
    .chart-y-labels { display:flex; flex-direction:column; justify-content:space-between; padding:3px 8px 25px 0; color:var(--ts-text-sky-450-9); font-size:.58rem; text-align:right; }
    .line-chart-stage { position:relative; min-width:0; padding-bottom:25px; }
    .line-chart-stage svg { position:absolute; inset:0 0 25px; width:100%; height:calc(100% - 25px); overflow:visible; }
    .grid-line { position:absolute; right:0; left:0; height:1px; background:var(--ts-bg-sky-300-a11); }
    .grid-top{top:0}.grid-mid{top:calc(50% - 12px)}.grid-bottom{bottom:25px}
    .chart-months { position:absolute; right:0; bottom:0; left:0; display:flex; justify-content:space-between; gap:4px; color:var(--ts-text-sky-400-24); font-size:.57rem; text-transform:capitalize; }
    .analytics-empty { min-height:220px; display:grid; place-content:center; justify-items:center; gap:.6rem; color:var(--ts-text-sky-400-24); }
    .analytics-empty i { font-size:1.6rem; }
    .lifecycle-content { display:grid; grid-template-columns:150px minmax(0,1fr); align-items:center; gap:1.25rem; padding:.7rem 0; }
    .lifecycle-ring { width:142px; aspect-ratio:1; display:grid; place-items:center; border-radius:50%; box-shadow:0 0 28px var(--ts-shadow-sky-450-a13); animation:ringReveal 1.25s cubic-bezier(.4,0,.2,1) both; }
    .lifecycle-ring>div { width:106px; aspect-ratio:1; display:grid; place-content:center; text-align:center; border-radius:50%; background:var(--ts-bg-elevated); box-shadow:inset 0 0 0 1px var(--ts-border-strong); }
    .lifecycle-ring strong,.lifecycle-ring span { display:block; }.lifecycle-ring strong{font-size:1.5rem}.lifecycle-ring span{color:var(--ts-text-sky-400-25);font-size:.65rem}
    .lifecycle-legend { display:grid; gap:.8rem; }.lifecycle-legend>div{display:grid;grid-template-columns:9px 1fr auto;align-items:center;gap:.55rem;color:var(--ts-text-sky-300-32);font-size:.7rem}.lifecycle-legend i{width:8px;height:8px;border-radius:50%}.lifecycle-legend .active{background:var(--ts-bg-teal-450-3)}.lifecycle-legend .pending{background:var(--ts-bg-amber-350-2)}.lifecycle-legend .closed{background:var(--ts-bg-red-300-2)}.lifecycle-legend strong{color:var(--ts-text-blue-0-4)}
    .plan-bars { display:grid; gap:1rem; }.plan-row-head{display:flex;justify-content:space-between;gap:1rem;margin-bottom:.38rem;color:var(--ts-text-sky-200-23);font-size:.74rem}.plan-row-head strong{color:var(--ts-text-blue-0-2)}.plan-row-head small{color:var(--ts-text-sky-400-26);font-weight:500}.plan-track{height:7px;overflow:hidden;border-radius:8px;background:var(--ts-bg-gray-0-a6)}.plan-track i{display:block;height:100%;min-width:4px;border-radius:8px;background:linear-gradient(90deg,var(--ts-bg-blue-400-8),var(--ts-bg-indigo-350-16));box-shadow:0 0 12px var(--ts-shadow-blue-350-a25)}
    .audience-comparison { display:grid; gap:1rem; }.audience-bar{display:grid;grid-template-columns:1fr auto;gap:.4rem;color:var(--ts-text-sky-250-19);font-size:.72rem}.audience-bar>strong{color:var(--ts-text)}.audience-bar>i{grid-column:1/-1;display:block;height:8px;min-width:4px;border-radius:8px;background:linear-gradient(90deg,var(--ts-bg-blue-350-9),var(--ts-bg-sky-300-4))}.audience-bar.customers>i{background:linear-gradient(90deg,var(--ts-bg-indigo-350-17),var(--ts-bg-violet-250))}.audience-panel footer{display:flex;justify-content:space-between;gap:.8rem;margin-top:1.1rem;padding-top:.8rem;border-top:1px solid var(--ts-border-sky-300-a14-2);color:var(--ts-text-sky-400-23);font-size:.67rem}.audience-panel footer strong{color:var(--ts-text-sky-200-24);text-align:right}
    .decision-list { display:grid; gap:.65rem; }.decision-list>div{display:grid;grid-template-columns:30px 1fr;align-items:center;gap:.65rem;padding:.65rem;border:1px solid var(--ts-border-sky-350-a13);border-radius:10px;background:var(--ts-bg-navy-950-a20);color:var(--ts-text-sky-300-34);font-size:.7rem;line-height:1.45}.decision-list>div>i{display:grid;place-items:center;width:29px;height:29px;color:var(--ts-text-blue-250-26);border-radius:8px;background:var(--ts-bg-blue-400-a12-3)}.decision-list b{color:var(--ts-text-on-accent-16);font-size:.8rem}
    .analytics-note { display:flex;align-items:center;gap:.45rem;margin:0;padding:.7rem .85rem;color:var(--ts-text-sky-400-23);border:1px solid var(--ts-border-sky-450-a18-2);border-radius:10px;background:var(--ts-bg-navy-900-a45-2);font-size:.66rem; }
    .analytics-dashboard>* { animation:analyticsEnter .62s cubic-bezier(.4,0,.2,1) both; }.analytics-dashboard>*:nth-child(2){animation-delay:.06s}.analytics-dashboard>*:nth-child(3){animation-delay:.12s}.analytics-dashboard>*:nth-child(4){animation-delay:.18s}.analytics-dashboard>*:nth-child(5){animation-delay:.24s}
    .analytics-kpis .analytics-kpi{opacity:0;animation:analyticsEnter .5s cubic-bezier(.4,0,.2,1) both}.analytics-kpis .analytics-kpi:nth-child(1){animation-delay:.06s}.analytics-kpis .analytics-kpi:nth-child(2){animation-delay:.12s}.analytics-kpis .analytics-kpi:nth-child(3){animation-delay:.18s}.analytics-kpis .analytics-kpi:nth-child(4){animation-delay:.24s}
    .analytics-panel{opacity:0;animation:analyticsEnter .55s cubic-bezier(.4,0,.2,1) both}.analytics-main-grid .analytics-panel:nth-child(1){animation-delay:.1s}.analytics-main-grid .analytics-panel:nth-child(2){animation-delay:.18s}
    .analytics-loading{display:grid;gap:.8rem;width:100%;min-height:500px}.analytics-loading-head{height:105px;border-radius:18px}.analytics-loading-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:.8rem}.analytics-loading-kpis i{height:110px;border-radius:15px}.analytics-loading-panels{display:grid;grid-template-columns:1.65fr .75fr;gap:.8rem}.analytics-loading-panels i{height:320px;border-radius:15px}.skeleton-pulse{display:block;background:linear-gradient(100deg,var(--ts-bg-navy-850-53) 20%,var(--ts-bg-navy-800-21) 38%,var(--ts-bg-navy-850-53) 56%);background-size:220% 100%;animation:skeletonShift 1.25s linear infinite}
    .plan-track i{transform-origin:left;animation:horizontalFill 1.2s cubic-bezier(.4,0,.2,1) both;animation-delay:var(--fill-delay)}.audience-bar>i{transform-origin:left;animation:horizontalFill 1.3s cubic-bezier(.4,0,.2,1) .16s both}.audience-bar.customers>i{animation-delay:.28s}
    @keyframes barGrow{from{transform:scaleY(0);opacity:.25}to{transform:scaleY(1);opacity:1}}@keyframes horizontalFill{from{transform:scaleX(0);opacity:.3}to{transform:scaleX(1);opacity:1}}@keyframes ringReveal{from{opacity:0;transform:scale(.82) rotate(-20deg)}to{opacity:1;transform:scale(1) rotate(0)}}@keyframes analyticsEnter{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}@keyframes skeletonShift{to{background-position:-220% 0}}
    @media(max-width:1100px){.analytics-kpis{grid-template-columns:repeat(2,minmax(0,1fr))}.analytics-main-grid{grid-template-columns:1fr}.analytics-secondary-grid{grid-template-columns:1fr 1fr}.decision-panel{grid-column:1/-1}.analytics-kpi span{font-size:.64rem}.analytics-kpi strong{font-size:1.3rem}}
    @media(max-width:700px){.analytics-hero{align-items:flex-start;flex-direction:column}.analytics-kpis{grid-template-columns:repeat(2,minmax(0,1fr))}.analytics-secondary-grid{grid-template-columns:1fr}.decision-panel{grid-column:auto}.lifecycle-content{grid-template-columns:1fr;justify-items:center}.lifecycle-legend{width:100%}.monthly-bar-chart{grid-template-columns:38px minmax(0,1fr);height:225px}.bar-plot{gap:3px}.bar-value{display:none}.bar-plot{padding-top:.4rem}.bar-plot .grid-top{top:.4rem}.chart-legend{white-space:normal}.revenue-chart-summary{justify-content:space-between}.analytics-note{align-items:flex-start}.analytics-loading-kpis,.analytics-loading-panels{grid-template-columns:1fr}.analytics-loading-kpis i:nth-child(n+3){display:none}.analytics-kpi{min-height:90px;padding:.75rem}.analytics-kpi span{font-size:.58rem}.analytics-kpi strong{font-size:1.15rem}.analytics-kpi small{font-size:.56rem}.analytics-kpi .kpi-icon{width:34px;height:34px}.analytics-kpi .kpi-icon i{font-size:1rem}}
    @media(prefers-reduced-motion:reduce){.analytics-dashboard>*,.bar-track i,.plan-track i,.audience-bar>i,.lifecycle-ring,.skeleton-pulse{animation:none!important}.analytics-kpi,.analytics-panel{transition:none!important}}
    /* La versión de 4 columnas necesita ~680px de tarjeta; con el menú lateral eso
       ocurre por encima de ~1100px de pantalla (antes se cortaba el estado entre 900 y 1100). */
    @media(max-width: 1180px) {
      .business-payouts-content .payout-business-card {
        grid-template-columns: 44px minmax(0, 1fr) minmax(150px, .55fr);
      }
      .business-card-status {
        grid-column: 2 / -1;
        grid-template-columns: 1fr auto;
        justify-items: start;
      }
    }
    .owners-content{display:flex;flex-direction:column;gap:1rem}.owners-content>.section-actions{order:1}.owner-directory{order:2;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.9rem}.owners-content>.data-table{display:none}.owners-content>app-pagination{order:3}.owner-card{min-width:0;overflow:hidden;border:1px solid var(--ts-border-strong);border-radius:16px;background:linear-gradient(145deg,var(--ts-bg-navy-850-14),var(--ts-bg-navy-850-54));box-shadow:0 12px 28px var(--ts-shadow-navy-950-a14)}.owner-card-head{display:grid;grid-template-columns:44px minmax(0,1fr) auto;align-items:center;gap:.75rem;padding:1rem}.owner-avatar{display:grid;place-items:center;width:44px;height:44px;border:1px solid var(--ts-border-sky-500-4);border-radius:13px;color:var(--ts-text);background:linear-gradient(135deg,var(--ts-bg-blue-450-8),var(--ts-bg-indigo-350-18));font-size:1.05rem;font-weight:850}.owner-card-head h3{margin:.12rem 0;color:var(--ts-text);font-size:1rem}.owner-card-head small,.owner-business-band small{color:var(--ts-text-blue-250-22);font-size:.6rem;font-weight:850;letter-spacing:.08em}.owner-card-head p,.owner-business-band span{overflow:hidden;margin:0;color:var(--ts-text-sky-350-37);font-size:.7rem;text-overflow:ellipsis;white-space:nowrap}.owner-business-band{display:flex;align-items:center;justify-content:space-between;gap:.8rem;padding:.78rem 1rem;border-block:1px solid var(--ts-border-strong);background:var(--ts-bg-navy-850-19)}.owner-business-band div{display:grid;gap:.14rem;min-width:0}.owner-business-band strong{overflow:hidden;color:var(--ts-text);font-size:.82rem;text-overflow:ellipsis;white-space:nowrap}.plan-pill{display:inline-flex!important;align-items:center;gap:.35rem;flex:0 0 auto;padding:.35rem .5rem;color:var(--ts-text-indigo-100)!important;border:1px solid var(--ts-border-indigo-550);border-radius:999px;background:var(--ts-bg-indigo-750);font-weight:750}.owner-facts{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.55rem;padding:.85rem 1rem}.owner-facts>div{display:flex;align-items:center;gap:.55rem;min-width:0;padding:.6rem;border-radius:10px;background:var(--ts-wash-035)}.owner-facts>div>i{color:var(--ts-text-blue-250-4)}.owner-facts span{display:grid;gap:.08rem;min-width:0}.owner-facts small{color:var(--ts-text-sky-400-27);font-size:.58rem;text-transform:uppercase}.owner-facts strong{overflow:hidden;color:var(--ts-text-sky-100-12);font-size:.68rem;text-overflow:ellipsis;white-space:nowrap}.owner-empty{display:grid;justify-items:center;gap:.35rem;padding:2rem}.owner-empty i{font-size:1.8rem}.owner-archive-modal{box-sizing:border-box;width:min(570px,100%);max-height:calc(100dvh - 32px);overflow:auto;padding:1.35rem;color:var(--ts-text);border:1px solid var(--ts-border-red-650-3);border-radius:18px;background:linear-gradient(145deg,var(--ts-surface-soft),var(--ts-bg-navy-900-11));box-shadow:0 28px 80px var(--ts-shadow-gray-1000-a48)}.archive-warning-icon{display:grid;place-items:center;width:52px;height:52px;margin-bottom:.8rem;border:1px solid var(--ts-border-red-600);border-radius:15px;color:var(--ts-text-red-200-14);background:var(--ts-bg-pink-800-4);font-size:1.25rem}.owner-archive-modal h3{margin:.3rem 0;font-size:1.25rem}.owner-archive-modal>p{color:var(--ts-text-sky-250-32);line-height:1.55}.archive-consequences{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.55rem;margin:1rem 0}.archive-consequences span{display:flex;align-items:center;gap:.55rem;padding:.7rem;border-radius:10px;color:var(--ts-text-sky-150-23);background:var(--ts-bg-gray-0-a4);font-size:.72rem}.archive-consequences i{color:var(--ts-text-red-200-14)}.owner-archive-modal footer{display:flex;justify-content:flex-end;gap:.6rem;margin-top:1.1rem}.archive-password{display:grid;gap:.4rem;margin-top:1rem;color:var(--ts-text-sky-150-23);font-size:.72rem;font-weight:750}.archive-password input{box-sizing:border-box;width:100%;min-height:44px;padding:.7rem;color:var(--ts-text);border:1px solid var(--ts-border-sky-600-7);border-radius:10px;background:var(--ts-bg-navy-900-5)}.archive-password input:focus{outline:3px solid var(--ts-border-blue-400-a20);border-color:var(--ts-primary)}
    .owners-content>.owner-empty{order:2;color:var(--ts-text-sky-300-10);text-align:center;border:1px dashed var(--ts-border-navy-650-3);border-radius:16px;background:var(--ts-bg-navy-850-19)}.owner-empty i{color:var(--ts-text-blue-250-27)}.owner-empty strong{color:var(--ts-text)}.owner-empty span{max-width:540px;font-size:.75rem;line-height:1.5}
    .owner-header-actions{display:flex;align-items:center;gap:.6rem}.owner-header-actions button{display:inline-flex;align-items:center;gap:.4rem}.archived-owners-modal{box-sizing:border-box;width:min(920px,calc(100vw - 32px));max-height:calc(100dvh - 32px);overflow:auto;color:var(--ts-text);border:1px solid var(--ts-border-navy-650-3);border-radius:20px;background:var(--ts-bg-navy-850-54);box-shadow:0 28px 80px var(--ts-shadow-gray-1000-a52)}.archived-owners-modal>header{position:sticky;z-index:2;top:0;display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;padding:1.25rem 1.35rem;border-bottom:1px solid var(--ts-border-strong);background:var(--ts-bg-navy-800-22)}.archived-owners-modal h3{margin:.25rem 0;font-size:1.35rem}.archived-owners-modal header p{margin:0;color:var(--ts-text-sky-300-34)}.icon-close{display:grid;place-items:center;flex:0 0 auto;width:40px;height:40px;color:var(--ts-text-sky-150-24);border:1px solid var(--ts-border-sky-600-7);border-radius:12px;background:var(--ts-bg-navy-850-19)}.archived-owner-list{display:grid;gap:.7rem;padding:1.15rem}.archived-owner-list article{display:grid;grid-template-columns:44px minmax(0,1fr) minmax(0,1fr) auto;align-items:center;gap:.9rem;padding:.9rem;border:1px solid var(--ts-border-strong);border-radius:14px;background:var(--ts-bg-navy-850-14)}.archived-owner-list article>div{display:grid;gap:.15rem;min-width:0}.archived-owner-list small{color:var(--ts-text-sky-400-28);font-size:.58rem;font-weight:850;letter-spacing:.06em}.archived-owner-list strong,.archived-owner-list span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.archived-owner-list span{color:var(--ts-text-sky-300-35);font-size:.7rem}.archived-owner-avatar{display:grid;place-items:center;width:44px;height:44px;color:var(--ts-text-on-accent-26)!important;border:1px solid var(--ts-border-red-650-4);border-radius:13px;background:var(--ts-bg-pink-800-5);font-weight:850}.restore-btn{display:inline-flex;align-items:center;justify-content:center;gap:.4rem;min-height:42px;padding:.6rem .8rem;color:var(--ts-text-green-250-2);border:1px solid var(--ts-border-teal-700-3);border-radius:10px;background:var(--ts-bg-teal-850-4);font-weight:800}.restore-btn:hover:not(:disabled){background:var(--ts-bg-teal-800)}.restore-confirm-modal{border-color:var(--ts-border-teal-650)}.restore-confirm-icon{display:grid;place-items:center;width:52px;height:52px;margin-bottom:.8rem;color:var(--ts-text-green-250-2);border:1px solid var(--ts-border-teal-700-3);border-radius:15px;background:var(--ts-bg-teal-850-4);font-size:1.25rem}
    @media(max-width:900px){.owner-directory{grid-template-columns:1fr}.archived-owner-list article{grid-template-columns:44px minmax(0,1fr)}.archived-owner-list article>div:nth-of-type(2),.archived-owner-list .restore-btn{grid-column:1/-1}.archived-owner-list .restore-btn{width:100%}}
    @media(max-width: 620px) {
      .owner-card-head{grid-template-columns:40px minmax(0,1fr)}.owner-facts{grid-template-columns:1fr}.archive-consequences{grid-template-columns:1fr}.owner-archive-modal footer{flex-direction:column-reverse}.owner-archive-modal footer button{width:100%}.owner-header-actions{width:100%;flex-direction:column}.owner-header-actions button{width:100%;justify-content:center}.archived-owners-modal{width:calc(100vw - 16px);max-height:calc(100dvh - 16px);border-radius:15px}.archived-owners-modal>header{padding:1rem}.archived-owner-list{padding:.75rem}.archived-owner-list article{gap:.65rem;padding:.75rem}.owners-subsection-head,.archived-owners-panel-head{align-items:flex-start;padding:.8rem}.owners-subsection-head p,.archived-owners-panel-head p{max-width:none}
      .payout-section-heading,
      .subscription-section-heading {
        align-items: flex-start;
      }
      .payout-heading-summary,
      .subscription-heading-total {
        text-align: left;
      }
      .business-payouts-content .payout-business-grid {
        padding: .7rem;
      }
      .business-payouts-content .payout-business-card {
        grid-template-columns: 40px minmax(0, 1fr);
        gap: .7rem;
      }
      .business-card-metric,
      .business-card-status {
        grid-column: 1 / -1;
      }
      .business-card-status {
        grid-template-columns: 1fr auto;
        align-items: center;
      }
      .business-card-identity small {
        white-space: normal;
        overflow-wrap: anywhere;
      }
      .payout-modal > .payment-summary-grid {
        grid-template-columns: 1fr;
        padding: .8rem;
      }
      .owner-contact-strip,
      .payout-modal app-admin-tabs {
        margin-right: .8rem;
        margin-left: .8rem;
      }
      .payout-modal .payout-destination-grid,
      .payout-modal .simple-transfer-form,
      .payout-modal .business-payment-history,
      .payout-modal .simple-transfer-history,
      .payout-modal .payout-warning {
        margin-right: .8rem;
        margin-left: .8rem;
      }
    }
    .search-control {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto auto;
      gap: .55rem;
      min-width: 0;
    }
    .search-control .search-field {
      box-sizing: border-box;
      width: 100%;
      min-width: 0;
      margin: 0;
    }
    .search-submit {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: .4rem;
      min-height: 42px;
      padding: 0 .95rem;
      color: var(--ts-text);
      border: 1px solid var(--ts-border-blue-350-4);
      border-radius: 9px;
      background: linear-gradient(135deg, var(--ts-bg-blue-450-9), var(--ts-bg-indigo-350-19));
      font: inherit;
      font-weight: 800;
      cursor: pointer;
    }
    .search-submit:disabled { cursor: wait; opacity: .62; }
    .search-reset {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      width: 42px;
      min-height: 42px;
      padding: 0;
      color: var(--ts-text-muted);
      border: 1px solid var(--ts-border-strong);
      border-radius: 9px;
      background: transparent;
      font-size: 1rem;
      cursor: pointer;
      transition: color var(--ts-transition), background var(--ts-transition), border-color var(--ts-transition), transform var(--ts-transition);
    }
    .search-reset:hover:not(:disabled) { color: var(--ts-text); background: var(--ts-surface-hover); border-color: var(--ts-primary); transform: rotate(-40deg); }
    .search-reset:disabled { cursor: wait; opacity: .5; }
    .archived-owners-overlay {
      box-sizing: border-box;
      padding: 24px;
      overflow: hidden;
    }
    .archived-owners-modal {
      width: min(680px, 100%);
      max-width: 100%;
      max-height: min(82dvh, 720px);
      min-width: 0;
      overflow-x: hidden;
      overflow-y: auto;
    }
    .archived-owner-list,
    .archived-owner-list article,
    .archived-owner-list article > * {
      box-sizing: border-box;
      min-width: 0;
      max-width: 100%;
    }
    .archived-owner-list article {
      grid-template-columns: 40px minmax(0, 1fr) minmax(0, 1fr);
      gap: .7rem;
      padding: .75rem;
    }
    .archived-owner-avatar { width: 40px; height: 40px; }
    .archived-owner-list .restore-btn {
      grid-column: 2 / -1;
      justify-self: start;
      width: auto;
      min-height: 36px;
      padding: .45rem .75rem;
    }
    @media (max-width: 680px) {
      .search-control { grid-template-columns: minmax(0, 1fr) auto auto; }
      .search-submit { padding-inline: .75rem; }
      .search-reset { width: 38px; min-height: 38px; }
      .archived-owners-overlay { padding: 18px 12px; }
      .archived-owners-modal {
        width: 100%;
        max-height: calc(100dvh - 36px);
        border-radius: 14px;
      }
      .archived-owners-modal > header { padding: .85rem; }
      .archived-owners-modal h3 { font-size: 1.1rem; }
      .archived-owners-modal header p { font-size: .72rem; line-height: 1.4; }
      .archived-owner-list { gap: .55rem; padding: .65rem; overflow-x: hidden; }
      .archived-owner-list article {
        grid-template-columns: 36px minmax(0, 1fr);
        gap: .55rem;
        padding: .65rem;
      }
      .archived-owner-avatar { width: 36px; height: 36px; }
      .archived-owner-list article > div:nth-of-type(2),
      .archived-owner-list .restore-btn { grid-column: 1 / -1; }
      .archived-owner-list .restore-btn { width: 100%; }
    }
    .users-content {
      min-width: 0;
      overflow: visible;
      border: 0;
      background: transparent;
      box-shadow: none;
    }
    .users-heading {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      margin-top: 1rem;
      padding: 1rem 1.1rem;
      border: 1px solid var(--ts-border-strong);
      border-radius: 14px;
      background: var(--ts-bg-navy-850-35);
    }
    .users-heading h3 { margin: .18rem 0; color: var(--ts-text-blue-50-6); font-size: 1.12rem; }
    .users-heading p { margin: 0; color: var(--ts-text-sky-350-26); font-size: .78rem; }
    .users-total {
      display: inline-flex;
      align-items: baseline;
      gap: .35rem;
      flex: 0 0 auto;
      padding: .45rem .65rem;
      color: var(--ts-text-sky-300-36);
      border: 1px solid var(--ts-border-navy-650-3);
      border-radius: 999px;
      background: var(--ts-bg-navy-850-19);
      font-size: .68rem;
    }
    .users-total strong { color: var(--ts-text); font-size: .9rem; }
    .user-directory {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: .8rem;
      margin-top: 1rem;
    }
    .user-card {
      min-width: 0;
      overflow: hidden;
      border: 1px solid var(--ts-border-strong);
      border-radius: 14px;
      background: linear-gradient(145deg, var(--ts-bg-navy-850-55), var(--ts-bg-navy-850-15));
      box-shadow: 0 10px 24px var(--ts-shadow-navy-950-a13);
    }
    .user-card-head {
      display: grid;
      grid-template-columns: 42px minmax(0, 1fr) auto;
      align-items: center;
      gap: .7rem;
      padding: .85rem;
    }
    .user-avatar {
      display: grid;
      place-items: center;
      width: 42px;
      height: 42px;
      color: var(--ts-text);
      border: 1px solid var(--ts-border-sky-500-5);
      border-radius: 12px;
      background: linear-gradient(135deg, var(--ts-bg-blue-450-8), var(--ts-bg-indigo-350-18));
      font-weight: 850;
      text-transform: uppercase;
    }
    .user-card-head > div { min-width: 0; }
    .user-card-head small,
    .user-context small { color: var(--ts-text-sky-350-38); font-size: .57rem; font-weight: 850; letter-spacing: .07em; }
    .user-card-head h3 { overflow: hidden; margin: .1rem 0; color: var(--ts-text-blue-0-4); font-size: .94rem; text-overflow: ellipsis; white-space: nowrap; }
    .user-card-head p { overflow: hidden; margin: 0; color: var(--ts-text-sky-350-37); font-size: .68rem; text-overflow: ellipsis; white-space: nowrap; }
    .user-state {
      display: inline-flex;
      align-items: center;
      gap: .28rem;
      padding: .3rem .48rem;
      color: var(--ts-text-green-300-11);
      border: 1px solid var(--ts-border-teal-700-4);
      border-radius: 999px;
      background: var(--ts-bg-teal-850-3);
      font-size: .6rem;
      font-weight: 800;
      white-space: nowrap;
    }
    .user-state.inactive { color: var(--ts-text-red-150-13); border-color: var(--ts-border-red-650-5); background: var(--ts-bg-pink-800-6); }
    .user-context {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: .5rem;
      padding: .7rem .85rem;
      border-block: 1px solid var(--ts-border-strong);
      background: var(--ts-bg-navy-850-56);
    }
    .user-context > div { display: flex; align-items: center; gap: .55rem; min-width: 0; padding: .55rem; border-radius: 9px; background: var(--ts-wash-035); }
    .user-context > div > i { flex: 0 0 auto; color: var(--ts-text-blue-250-28); }
    .user-context span { display: grid; gap: .1rem; min-width: 0; }
    .user-context strong { overflow: hidden; color: var(--ts-text-blue-50-11); font-size: .7rem; text-overflow: ellipsis; white-space: nowrap; }
    .user-card-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .5rem; padding: .72rem .85rem; }
    .user-card-actions button { display: inline-flex; align-items: center; justify-content: center; gap: .35rem; box-sizing: border-box; min-width: 0; min-height: 38px; padding: .5rem; border-radius: 9px; font-size: .66rem; font-weight: 800; cursor: pointer; }
    .user-status-action { color: var(--ts-text-red-100); border: 1px solid var(--ts-border-red-650-6); background: var(--ts-bg-pink-850); }
    .user-status-action.activate { color: var(--ts-text-green-200-2); border-color: var(--ts-border-teal-700-5); background: var(--ts-bg-teal-850-5); }
    .user-recovery-action { color: var(--ts-text-blue-100-3); border: 1px solid var(--ts-border-blue-600); background: var(--ts-bg-navy-800-23); }
    .user-recovery-action:disabled { color: var(--ts-text-sky-450-15); border-color: var(--ts-border-navy-700-19); background: var(--ts-bg-navy-850-57); cursor: not-allowed; opacity: .72; }
    .users-pagination { margin-top: 1rem; }
    @media (max-width: 900px) {
      .user-directory { grid-template-columns: 1fr; }
    }
    @media (max-width: 560px) {
      .users-heading { align-items: flex-start; padding: .8rem; }
      .users-heading p { max-width: 240px; }
      .users-total { padding: .35rem .5rem; }
      .user-directory { gap: .6rem; }
      .user-card-head { grid-template-columns: 38px minmax(0, 1fr); padding: .7rem; }
      .user-avatar { width: 38px; height: 38px; }
      .user-state { grid-column: 1 / -1; justify-self: start; }
      .user-context { grid-template-columns: 1fr; padding: .6rem .7rem; }
      .user-card-actions { grid-template-columns: 1fr; padding: .65rem .7rem; }
    }
    /* Una tarjeta por fila, compactada en dos filas internas. */
    .users-content .user-directory {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) !important;
      gap: .5rem;
    }
    .user-card {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(280px, .72fr);
    }
    .user-card-head {
      grid-column: 1 / -1;
      grid-template-columns: 34px minmax(0, 1fr) minmax(145px, auto) auto;
      gap: .55rem;
      padding: .55rem .7rem;
    }
    .user-card-head .user-avatar { width: 34px; height: 34px; border-radius: 10px; font-size: .75rem; }
    .user-card-head h3 { font-size: .82rem; }
    .user-card-head p { font-size: .61rem; }
    .user-state { padding: .22rem .4rem; font-size: .54rem; }
    .user-last-login { display: flex; align-items: center; gap: .35rem; min-width: 0; color: var(--ts-text-sky-350-39); }
    .user-last-login > i { flex: 0 0 auto; font-size: .72rem; }
    .user-last-login > span { display: grid; gap: .03rem; min-width: 0; }
    .user-last-login small { color: var(--ts-text-sky-350-38); font-size: .43rem; font-weight: 850; letter-spacing: .06em; }
    .user-last-login strong { overflow: hidden; color: var(--ts-text-sky-100-13); font-size: .55rem; text-overflow: ellipsis; white-space: nowrap; }
    .user-context {
      grid-column: 1;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: .4rem;
      padding: .42rem .6rem;
      border-bottom: 0;
      border-right: 1px solid var(--ts-border-strong);
    }
    .user-context > div { gap: .4rem; padding: .38rem .45rem; }
    .user-context > div > i { font-size: .76rem; }
    .user-context small { font-size: .5rem; }
    .user-context strong { font-size: .62rem; }
    .user-card-actions {
      grid-column: 2;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      align-content: center;
      gap: .35rem;
      padding: .42rem .6rem;
      border-top: 1px solid var(--ts-border-strong);
    }
    .user-card-actions button { min-height: 32px; padding: .34rem .4rem; font-size: .58rem; border-radius: 8px; }
    @media (max-width: 760px) {
      .users-heading { padding: .7rem .75rem; }
      .users-heading h3 { font-size: .95rem; }
      .users-heading p { font-size: .67rem; line-height: 1.35; }
      .users-content .user-directory { grid-template-columns: minmax(0, 1fr) !important; gap: .42rem; }
      .user-card { grid-template-columns: minmax(0, 1.12fr) minmax(120px, .88fr); border-radius: 11px; }
      .user-card-head { grid-template-columns: 30px minmax(0, 1fr) auto; gap: .4rem; padding: .45rem .5rem; }
      .user-card-head .user-avatar { width: 30px; height: 30px; border-radius: 9px; font-size: .67rem; }
      .user-card-head small { font-size: .45rem; }
      .user-card-head h3 { font-size: .7rem; }
      .user-card-head p { font-size: .52rem; }
      .user-state { grid-column: auto; justify-self: end; padding: .17rem .3rem; font-size: .47rem; }
      .user-last-login { grid-column: 2 / -1; grid-row: 2; }
      .user-last-login small { font-size: .4rem; }.user-last-login strong { font-size: .48rem; }
      .user-context { grid-template-columns: 1fr; gap: .25rem; padding: .34rem .4rem; }
      .user-context > div { gap: .28rem; padding: .3rem; }
      .user-context strong { font-size: .53rem; }
      .user-card-actions { grid-template-columns: 1fr; gap: .25rem; padding: .34rem .4rem; }
      .user-card-actions button { min-height: 29px; padding: .28rem .2rem; font-size: .49rem; line-height: 1.12; }
    }
    .subscriptions-content { min-width: 0; overflow: visible; border-color: var(--ts-border-strong); background: transparent; box-shadow: none; }
    .subscriptions-heading {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      padding: .85rem 1rem;
      border: 1px solid var(--ts-border-strong);
      border-radius: 14px;
      background: var(--ts-bg-navy-850-35);
    }
    .subscriptions-heading h3 { margin: .15rem 0; color: var(--ts-text-blue-50-6); font-size: 1.05rem; }
    .subscriptions-heading p { margin: 0; color: var(--ts-text-sky-350-26); font-size: .74rem; }
    .subscriptions-total { display: inline-flex; align-items: baseline; gap: .3rem; flex: 0 0 auto; padding: .4rem .58rem; color: var(--ts-text-sky-300-36); border: 1px solid var(--ts-border-navy-650-3); border-radius: 999px; background: var(--ts-bg-navy-850-19); font-size: .64rem; }
    .subscriptions-total strong { color: var(--ts-text); font-size: .85rem; }
    .subscription-directory { display: grid; grid-template-columns: minmax(0, 1fr); gap: .5rem; margin-top: 1rem; }
    .subscription-edit { color: var(--ts-text-indigo-100-2); border-color: var(--ts-border-indigo-550-2); background: var(--ts-bg-indigo-800); }
    .subscription-edit:hover { color: var(--ts-text); background: var(--ts-bg-indigo-700); }
    .subscription-payment { color: var(--ts-text-green-250-3); border-color: var(--ts-border-teal-700-6); background: var(--ts-bg-teal-850-5); }
    .subscription-payment:hover { color: var(--ts-text); background: var(--ts-bg-teal-800-2); }
    .subscription-renewal { color: var(--ts-text-blue-150-9); border-color: var(--ts-border-sky-600-8); background: var(--ts-bg-navy-800-23); }
    .subscription-renewal:hover { color: var(--ts-text); background: var(--ts-bg-navy-750-7); }
    .subscription-collect-email { color: var(--ts-text-orange-200-7); border-color: var(--ts-border-orange-650-2); background: var(--ts-bg-orange-850-6); }
    .subscription-collect-email:hover { color: var(--ts-text); background: var(--ts-bg-orange-800); }
    .subscription-collect-copy { color: var(--ts-text-teal-250); border-color: var(--ts-border-teal-700-7); background: var(--ts-bg-teal-850-6); }
    .subscription-collect-copy:hover { color: var(--ts-text); background: var(--ts-bg-teal-850-7); }
    .subscription-state { display: inline-flex; align-items: center; gap: .28rem; padding: .24rem .42rem; color: var(--ts-state-success-text, var(--ts-text-green-300-11)); border: 1px solid var(--ts-border-teal-700-4); border-radius: 999px; background: var(--ts-bg-teal-850-3); font-size: var(--ts-state-font-size, .53rem); font-weight: 800; white-space: nowrap; }
    .renewal-urgency-badge { display: inline-flex; align-items: center; gap: .28rem; margin-top: .3rem; padding: .24rem .48rem; color: var(--ts-text-amber-250-3); border: 1px solid var(--ts-border-orange-650-2); border-radius: 999px; background: var(--ts-bg-orange-850-6); font-size: .58rem; font-weight: 800; white-space: nowrap; width: fit-content; }
    .renewal-urgency-badge.overdue { color: var(--ts-text-red-150-14); border-color: var(--ts-border-red-700); background: var(--ts-bg-red-850-2); }
    .subscription-state i { font-size: .38rem; }
    .subscription-state.state-pending,.subscription-state.state-inactive { border-color: var(--ts-border-amber-650-3); background: var(--ts-bg-amber-850); color: var(--ts-state-warning-text, var(--ts-text-green-300-11)); }
    .subscription-state.state-cancelled,.subscription-state.state-expired { border-color: var(--ts-border-red-650-5); background: var(--ts-bg-pink-800-6); color: var(--ts-state-danger-text, var(--ts-text-green-300-11)); }
    .subscription-state.state-trial { border-color: var(--ts-border-sky-650-4); background: var(--ts-bg-navy-850-58); color: var(--ts-state-info-text, var(--ts-text-green-300-11)); }
    .subscription-price-line { display:flex; flex-wrap:wrap; align-items:baseline; gap:.35rem; margin-top:.35rem; }
    .subscription-price-line b { color:var(--ts-text-on-accent-16); font-size:.76rem; }
    .subscription-price-line em { color:var(--ts-text-sky-350-26); font-size:.62rem; font-style:normal; }
    .subscriptions-pagination { margin-top: 1rem; }
    @media (max-width: 600px) {
      .subscriptions-heading { align-items: flex-start; padding: .7rem; }
      .subscriptions-heading h3 { font-size: .92rem; }.subscriptions-heading p { max-width: 235px; font-size: .65rem; line-height: 1.35; }
      .subscription-directory { gap: .42rem; }
      .subscription-state { padding: .16rem .28rem; font-size: var(--ts-state-font-size, .45rem); }
    }
    .payment-modal {
      box-sizing: border-box;
      width: min(500px, calc(100vw - 32px));
      max-height: calc(100dvh - 32px);
      overflow-x: hidden;
      overflow-y: auto;
      padding: 1.2rem;
      color: var(--ts-text);
      border: 1px solid var(--ts-border-sky-600-9);
      border-radius: 18px;
      background: linear-gradient(145deg, var(--ts-bg-navy-800-22), var(--ts-bg-navy-900));
      box-shadow: 0 28px 80px var(--ts-shadow-gray-1000-a50);
    }
    .payment-modal.subscription-custom-modal { width: min(720px, calc(100vw - 32px)); max-height: min(760px, calc(100dvh - 32px)); overflow-y: auto; overscroll-behavior: contain; }
    .payment-modal h3 { margin: .22rem 2.3rem .42rem 0; color: var(--ts-text); font-size: 1.2rem; }
    .payment-modal > p { margin: 0 0 .85rem; color: var(--ts-text-sky-300-34); font-size: .76rem; line-height: 1.5; }
    .payment-modal .close-modal { position: absolute; top: 1rem; right: 1rem; display: grid; place-items: center; width: 38px; height: 38px; color: var(--ts-text-sky-150-24); border: 1px solid var(--ts-border-sky-600-7); border-radius: 11px; background: var(--ts-bg-navy-850-19); font-size: 1.2rem; cursor: pointer; }
    .payment-modal { position: relative; }
    .payment-modal label { display: grid; gap: .34rem; margin-top: .7rem; color: var(--ts-text-sky-200-3); font-size: .7rem; font-weight: 750; }
    .payment-modal input,.payment-modal select,.payment-modal textarea { box-sizing: border-box; width: 100%; min-height: 42px; padding: .62rem .68rem; color: var(--ts-text); border: 1px solid var(--ts-border-navy-650-3); border-radius: 9px; outline: 0; background: var(--ts-bg-navy-900-8); font: inherit; }
    .payment-modal textarea { min-height: 82px; resize: vertical; }
    .payment-modal input:focus,.payment-modal select:focus,.payment-modal textarea:focus { border-color: var(--ts-border-blue-350-5); box-shadow: 0 0 0 3px var(--ts-shadow-blue-400-a16); }
    .payment-modal input:disabled { color: var(--ts-text-sky-250-33); background: var(--ts-bg-navy-850-59); }
    .payment-modal .payment-btn { width: 100%; margin-top: 1rem; min-height: 42px; }
    .confirm-payment-review { display: flex; align-items: flex-start; gap: .55rem; margin: .9rem 0 0; padding: .75rem; color: var(--ts-warning-text); border: 1px solid var(--ts-warning-soft-3); border-radius: 10px; background: var(--ts-warning-soft); font-size: .72rem; line-height: 1.55; }
    .confirm-payment-review i { flex-shrink: 0; margin-top: .1rem; font-size: .95rem; }
    .confirm-payment-actions { display: flex; gap: .6rem; margin-top: 1rem; }
    .confirm-payment-actions .secondary-btn { flex: 1; }
    .confirm-payment-actions .payment-btn { flex: 1; margin-top: 0; }
    .subscription-defaults span { border-color: var(--ts-border-navy-650-3); background: var(--ts-bg-navy-850-19); }
    .subscription-defaults strong { color: var(--ts-text-on-accent-16); }
    .owner-directory { grid-template-columns: minmax(0,1fr); gap: .75rem; }
    .owners-tab-content { min-height:0; overflow:visible; border:0; border-radius:0; background:transparent; box-shadow:none; }
    .owners-content { gap:.85rem; }
    .owners-content>.section-actions { border:1px solid var(--ts-border-strong); border-radius:14px; background:var(--ts-bg-navy-850-13); }
    .pending-owner-signups { display:flex; flex-direction:column; gap:.75rem; margin:.25rem 0 1.1rem; }
    .pending-owner-signups app-management-record-card { display:block; margin-top:.1rem; }
    .owners-subsection-head,.archived-owners-panel-head { display:flex; align-items:center; justify-content:space-between; gap:1rem; padding:1rem 1.1rem; border:1px solid var(--ts-border-strong); border-radius:14px; background:var(--ts-bg-navy-850-35); }
    .owners-subsection-head h3,.archived-owners-panel-head h3 { margin:.18rem 0; color:var(--ts-text-on-accent-24); font-size:1.12rem; }
    .owners-subsection-head p,.archived-owners-panel-head p { margin:0; color:var(--ts-text-sky-350-26); font-size:.78rem; max-width:640px; }
    .pending-owner-signups .owners-subsection-head { border-color:var(--ts-border-orange-450-a35); background:linear-gradient(90deg,var(--ts-bg-orange-450-a10),transparent); }
    .archived-owners-panel .archived-owner-toolbar { position:static; top:auto; }
    .owner-record-actions { gap:.65rem!important; }
    .owner-record-actions>.owner-manage-btn:not(:disabled) { color:var(--ts-on-primary)!important; border-color:transparent!important; background:linear-gradient(135deg,var(--ts-primary),var(--ts-indigo))!important; }
    .owner-record-actions>.owner-account-btn { color:var(--ts-primary)!important; border:1px solid color-mix(in srgb,var(--ts-primary) 45%,transparent)!important; background:color-mix(in srgb,var(--ts-primary) 9%,transparent)!important; box-shadow:none!important; }
    .owner-record-actions>button { margin:0!important; }
    .owner-record-actions>.owner-activate-btn { color:var(--ts-on-primary)!important; border-color:var(--ts-border-red-450-2)!important; background:linear-gradient(135deg,var(--ts-bg-red-500-4),var(--ts-bg-red-350-4))!important; box-shadow:0 5px 14px var(--ts-shadow-red-450-a22); }
    .owner-record-actions>.owner-icon-action { position:relative; width:42px; min-width:42px; padding:0!important; }
    .owner-record-actions>.owner-icon-action i { margin:0; font-size:.9rem; }
    
    
    .permission-chip { display:inline-flex; align-items:center; gap:.3rem; padding:.2rem .55rem; border:1px solid var(--ts-border-sky-650-5); border-radius:999px; color:var(--ts-text-sky-200-21); background:var(--ts-bg-navy-800-24); font-size:.64rem; font-weight:700; text-transform:capitalize; }
    .super-admin-plan-tag { display:inline-flex; align-items:center; gap:.3rem; margin-top:.15rem; color:var(--ts-text-sky-200-21); font-size:.72rem; font-weight:650; }
    .super-admin-plan-tag i { color:var(--ts-text-blue-250); }
    .permission-chip.empty { color:var(--ts-text-sky-350-15); border-style:dashed; }
    .archived-owner-toolbar { position:sticky; z-index:1; top:112px; display:grid; grid-template-columns:minmax(0,1fr) auto; gap:.75rem; padding:.85rem 1.15rem; border-bottom:1px solid var(--ts-border-strong); background:var(--ts-bg-navy-850-49); }
    .archived-owner-search { display:grid; grid-template-columns:34px minmax(0,1fr) auto; align-items:center; min-width:0; border:1px solid var(--ts-border-navy-650-3); border-radius:11px; background:var(--ts-bg-navy-900-5); }
    .archived-owner-search>i { justify-self:center; color:var(--ts-text-sky-300-38); }
    .archived-owner-search input { min-width:0; min-height:42px; padding:.6rem .35rem; color:var(--ts-text); border:0; outline:0; background:transparent; }
    .archived-owner-search button { margin:.3rem; }
    .archived-owner-toolbar>label { display:flex; align-items:center; gap:.5rem; padding:.3rem .45rem .3rem .65rem; color:var(--ts-text-sky-350-40); border-radius:10px; background:var(--ts-bg-navy-850-13); font-size:.7rem; font-weight:750; }
    .archived-owner-toolbar select { min-height:38px; padding:.4rem 2rem .4rem .65rem; color:var(--ts-text); border:1px solid var(--ts-border-navy-650-3); border-radius:8px; background:var(--ts-bg-navy-900-5); }
.archived-owners-modal>app-pagination { display:block; padding:0 1.15rem 1.15rem; }
  .modal-overlay{position:fixed;inset:0;z-index:9000;display:grid;place-items:center;padding:var(--ts-space-4)}
  .archived-owner-list article { grid-template-columns:44px minmax(0,1fr) minmax(0,1fr) minmax(150px,.8fr) auto; }
  .archived-date { min-width:0; }
  .user-create-modal { padding:0; overflow-x:hidden; }
    .user-create-modal>.modal-header { position:sticky; z-index:3; top:0; align-items:center; padding:1rem 1.25rem; border-bottom:1px solid var(--ts-border-sky-600-10); background:var(--ts-bg-navy-800-25); box-shadow:0 8px 20px var(--ts-shadow-gray-1000-a20); }
    .user-create-modal>.modal-header h3 { margin:.15rem 0 0; }
    .user-create-modal>.modal-intro { margin:.6rem 1.25rem 0; padding:.55rem .7rem; }
    /* Corrección pedida (round 3): "compacto y simétrico, en la misma
       altura todos" -- el motivo de la asimetría era .limit-hint (el
       contador "0/50" que initCharLimitHints() inyecta como hermano del
       <input>, ver shared/char-limit-hints.ts): los campos CON maxlength
       tenían 3 filas reales (texto/input/contador) y los que no lo tienen
       (selects, números) solo 2, así que la fila de la cuadrícula se
       estiraba a la altura del más alto y el resto quedaba con un hueco
       muerto debajo. Se desactiva el contador en TODOS los campos de este
       modal (atributo data-no-limit-hint, ya soportado por
       initCharLimitHints para contraseñas) y la plantilla de filas baja a
       2 (auto + 42px) para todos por igual -- ya no hay una 3ª fila que
       unos campos usan y otros no. */
    .user-create-modal>.user-create-grid { align-items:start; padding:.6rem 1.25rem; gap:.3rem .9rem; }
    .user-create-grid>label { display:flex; flex-direction:column; gap:.18rem; min-width:0; margin:0; }
    .user-create-grid input,.user-create-grid select { box-sizing:border-box; width:100%; height:42px; min-height:42px; margin:0; padding:.5rem .7rem; }
    .user-create-grid select { appearance:none; -webkit-appearance:none; background-image:linear-gradient(45deg,transparent 50%,currentColor 50%),linear-gradient(135deg,currentColor 50%,transparent 50%); background-position:calc(100% - 15px) 50%,calc(100% - 10px) 50%; background-size:5px 5px,5px 5px; background-repeat:no-repeat; padding-right:2rem; }
        .user-create-modal form>.notification-audience,.user-create-modal form>.plan-form-error { margin:.65rem 1.25rem .6rem; padding-inline:1rem; }
    .user-create-modal form>.switch-row { margin:0 1.25rem .65rem; padding-inline:1rem; }
    /* Comprobante de pago externo: no comparte la plantilla rígida de 2
       filas (label de texto/input) del resto de la cuadrícula porque su
       contenido es variable (input de archivo + aviso de "subiendo" o la
       lista de comprobantes ya adjuntos). */
    .user-create-grid .receipt-upload-field { grid-template-rows:auto auto auto; gap:.35rem; }
    .user-create-grid .receipt-upload-field input[type="file"] { height:auto; min-height:0; padding:.5rem .6rem; font-size:.78rem; cursor:pointer; }
    .receipt-upload-hint { display:flex; align-items:center; gap:.4rem; color:var(--ts-text-sky-350-26); font-size:.72rem; }
    .receipt-upload-hint .spin { animation:spin .8s linear infinite; }
    .receipt-upload-list { display:grid; gap:.35rem; margin:0; padding:0; list-style:none; }
    .receipt-upload-list li { display:flex; align-items:center; gap:.5rem; padding:.45rem .6rem; border:1px solid var(--ts-border-navy-700-5); border-radius:8px; background:var(--ts-bg-navy-850-19); font-size:.75rem; color:var(--ts-text-sky-150-25); }
    .receipt-upload-list li i.bi-paperclip { color:var(--ts-text-blue-250); }
    .receipt-upload-list li span { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .receipt-upload-list li button { display:grid; place-items:center; width:24px; height:24px; padding:0; border:1px solid var(--ts-border-sky-600-2); border-radius:6px; color:var(--ts-text-red-150-4); background:transparent; cursor:pointer; }
    .user-create-modal>.modal-actions { margin-top:.5rem; padding:.7rem 1.25rem; border-top:1px solid var(--ts-border-strong); background:var(--ts-bg-navy-850-60); }
    .global-statistics-filters { display:grid; gap:.75rem; padding:1rem; border:1px solid var(--ts-border-navy-650-4); border-radius:14px; background:var(--ts-bg-navy-850-16); }
    .gsf-row { display:grid; grid-template-columns:1.1fr 1.1fr .9fr .9fr auto; gap:.65rem; align-items:end; }
    .global-statistics-filters label { display:grid; gap:.35rem; color:var(--ts-text-sky-350-9); font-size:.67rem; font-weight:800; }
    .global-statistics-filters label>div { display:grid; grid-template-columns:34px minmax(0,1fr); align-items:center; border:1px solid var(--ts-border-navy-650-3); border-radius:9px; background:var(--ts-bg-navy-900-5); }
    .global-statistics-filters label i { justify-self:center; color:var(--ts-text-sky-350-10); }
    .global-statistics-filters input { min-width:0; height:40px; color:var(--ts-text); border:0; outline:0; background:transparent; }
    .global-period-label { align-self:center; justify-self:end; color:var(--ts-text-sky-300-10); font-size:.72rem; }
    .chart-month-navigation { display:grid; grid-template-columns:34px minmax(145px,auto) 34px; align-items:center; gap:.45rem; }
    .chart-month-navigation button { display:grid; place-items:center; width:34px; height:34px; color:var(--ts-text-sky-150-14); border:1px solid var(--ts-border-navy-650-3); border-radius:9px; background:var(--ts-bg-navy-850-19); }
    .chart-month-navigation button:disabled { opacity:.35; }
    .chart-month-navigation strong { color:var(--ts-text-on-accent-16); font-size:.75rem; text-align:center; }
    .month-column.focus-month { padding-inline:.2rem; border-radius:8px; background:var(--ts-bg-blue-400-a18-3); box-shadow:inset 0 0 0 2px var(--ts-shadow-blue-300-a40); }
    .month-column.focus-month .bar-value{display:block!important;color:var(--ts-text-soft-2);font-size:.6rem}
    @media (max-width:900px) { .archived-owner-toolbar { position:static; grid-template-columns:1fr; } .archived-owner-toolbar>label { justify-content:space-between; } .archived-owner-list article { grid-template-columns:40px minmax(0,1fr); } .archived-owner-list article>div:nth-of-type(2),.archived-owner-list .archived-date,.archived-owner-list .restore-btn { grid-column:1/-1; } }
    @media (min-width:1001px) { .gsf-row { grid-template-columns:1.1fr 1.1fr .9fr .9fr auto; } }
    @media (max-width:1000px) { .gsf-row { grid-template-columns:1fr 1fr; } .global-period-label { justify-self:start; grid-column:1/-1; } }
    @media (max-width:680px) { .archived-owner-toolbar { padding:.65rem; } .archived-owner-search { grid-template-columns:30px minmax(0,1fr) auto; } .archived-owner-search input { font-size:.68rem; } .archived-owner-search button { padding:.4rem .55rem!important; font-size:.65rem!important; } .archived-owners-modal>app-pagination { padding:0 .65rem .65rem; } .user-create-modal>.modal-header { padding:.75rem .85rem; } .user-create-modal>.modal-intro,.user-create-modal form>.notification-audience,.user-create-modal form>.plan-form-error { margin-inline:.8rem; } .user-create-modal>.user-create-grid { grid-template-columns:1fr; padding:.55rem .8rem; gap:.3rem; } .user-create-modal>.modal-actions { padding:.65rem .8rem; } .global-statistics-filters { padding:.75rem; } .gsf-row { grid-template-columns:1fr 1fr; } .global-period-label { justify-self:start; grid-column:1/-1; } .chart-month-navigation { align-self:stretch; grid-template-columns:34px 1fr 34px; width:100%; } }
    .requester-field,.subscription-custom-grid { padding: .15rem; }
    .plan-card-actions { display:flex; flex-wrap:wrap; gap:.5rem; margin-top:auto; padding-top:1rem; }
    .plan-card-actions button { flex:1 1 150px; min-height:38px; }
    .commission-contracts-panel { display:grid; gap:.8rem; margin:1rem 0; padding:1rem; border:1px solid var(--ts-border-strong); border-radius:14px; background:linear-gradient(145deg,var(--ts-bg-navy-850-35),var(--ts-bg-navy-850-61)); }
    .commission-contracts-panel .section-actions { margin:0; padding:0; border:0; background:transparent; }
    .commission-contract-list { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:.45rem; max-height:320px; overflow:auto; padding:.2rem; }
    .commission-contract-toolbar { display:flex; align-items:center; gap:.7rem; min-width:0; }
    .commission-contract-toolbar > span { flex:0 0 auto; color:var(--ts-text-sky-350-26); font-size:.68rem; white-space:nowrap; }
    .commission-search { display:grid; grid-template-columns:28px minmax(0,1fr) 28px; align-items:center; flex:1 1 auto; min-width:0; min-height:40px; padding:0 .25rem; color:var(--ts-text-sky-250-35); border:1px solid var(--ts-border-navy-650-3); border-radius:10px; background:var(--ts-bg-navy-900-5); }
    .commission-search i { justify-self:center; }
    .commission-search input { min-width:0; min-height:36px; padding:0 .3rem; color:var(--ts-text); border:0; outline:0; background:transparent; font:inherit; }
    .commission-search button { display:grid; place-items:center; width:26px; height:26px; color:var(--ts-text-sky-200-3); border:0; border-radius:7px; background:transparent; cursor:pointer; font-size:1rem; }
    .commission-search button:hover { background:var(--ts-bg-navy-800-26); }
    .commission-contract-row { display:grid; grid-template-columns:auto minmax(0,1fr) auto; align-items:center; gap:.65rem; min-width:0; padding:.7rem .75rem; color:var(--ts-text-sky-100-5); border:1px solid var(--ts-border-navy-700-20); border-radius:10px; background:var(--ts-bg-navy-850-19); cursor:pointer; }
    .commission-contract-row:hover { border-color:var(--ts-border-blue-350-5); background:var(--ts-bg-navy-800-22); }
    .commission-contract-row input { width:16px; height:16px; accent-color:var(--ts-accent-blue-350); }
    .commission-contract-row span { overflow:hidden; font-size:.72rem; font-weight:700; text-overflow:ellipsis; white-space:nowrap; }
    .commission-contract-row strong { color:var(--ts-text-green-300-11); font-size:.7rem; white-space:nowrap; }
    .commission-contract-actions { display:flex; flex-wrap:wrap; gap:.5rem; }
    .commission-contract-actions button { min-height:34px; }
    .subscription-custom-actions { padding-top: .85rem; border-top: 1px solid var(--ts-border-strong); }
    .commission-history{display:grid;gap:.7rem}
    .commission-change-card{display:flex;align-items:center;justify-content:space-between;gap:1rem;padding:1rem 1.1rem;border:1px solid var(--ts-border-strong);border-radius:14px;background:var(--ts-bg-navy-850-35);cursor:pointer;transition:border-color .15s,transform .15s}
    .commission-change-card:hover{border-color:var(--ts-border-blue-300-a50-2);transform:translateY(-1px)}
    .commission-change-header{display:flex;align-items:baseline;justify-content:space-between;gap:.9rem;width:100%}
    .commission-change-rates{display:inline-flex;align-items:center;gap:.45rem;font-weight:800}
    .commission-rate-old{color:var(--ts-text-sky-350-26);text-decoration:line-through}
    .commission-change-rates .bi{font-size:.85rem;color:var(--ts-text-blue-300-12)}
    .commission-rate-new{color:var(--ts-text-teal-450);font-size:1rem}
    .commission-change-date{font-size:.68rem;color:var(--ts-text-sky-350-41)}
    .commission-change-meta{display:flex;gap:.6rem;align-items:center;flex-wrap:wrap;font-size:.68rem;color:var(--ts-text-sky-350-9)}
    .commission-change-by{font-weight:700}
    .commission-history-empty{display:grid;gap:.4rem;place-items:center;padding:2.5rem 1.25rem;color:var(--ts-text-sky-350-42);text-align:center;border:1px dashed var(--ts-border-navy-700-21);border-radius:14px}
    .commission-history-empty i{font-size:1.4rem;color:var(--ts-text-blue-250);opacity:.8}
    .contract-history-modal { width:min(620px,calc(100vw - 2rem)); max-height:min(760px,calc(100dvh - 2rem)); display:flex; flex-direction:column; overflow:hidden; }
    .history-modal-header { display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; flex:0 0 auto; padding-bottom:.75rem; border-bottom:1px solid var(--ts-border-strong); }
    .history-modal-header h3 { margin:.25rem 0 0; }
    .history-modal-header .close-modal { position:static; flex:0 0 auto; }
    .history-modal-body { min-height:0; overflow-y:auto; padding-top:.75rem; }
    .plan-price-application { display:flex; justify-content:space-between; align-items:center; gap:.8rem; margin-top:.75rem; padding:.7rem; border:1px solid var(--ts-border-blue-400-a25-4); border-radius:10px; background:var(--ts-bg-blue-550-a8); color:var(--ts-text-sky-250-29); font-size:.7rem; line-height:1.4; }
    .plan-price-application .secondary-btn.active { border-color:var(--ts-border-blue-300-5); color:var(--ts-text-on-accent); background:var(--ts-bg-navy-650); }
    .plan-price-selected { display:flex; align-items:flex-start; gap:.5rem; margin-top:.55rem; padding:.65rem .75rem; border:1px solid var(--ts-border-teal-450-a35); border-radius:9px; background:var(--ts-bg-teal-450-a8); color:var(--ts-text-green-150-3); font-size:.72rem; line-height:1.4; }
    .plan-price-selected i { color:var(--ts-text-teal-400-3); margin-top:.1rem; }
    .contract-history-list { display:grid; gap:.55rem; }
    .contract-history-list article { display:grid; grid-template-columns:minmax(0,1fr) auto; gap:.25rem .8rem; padding:.75rem; border:1px solid var(--ts-border-navy-700-20); border-radius:10px; background:var(--ts-bg-navy-850-19); }
    .contract-history-list article strong { color:var(--ts-text-soft-2); font-size:.75rem; }
    .contract-history-list article span { color:var(--ts-text-sky-300-36); font-size:.68rem; }
    .contract-history-list article span:last-child { grid-column:1/-1; color:var(--ts-text-green-300-11); font-weight:800; }
    @media (max-width:760px) {
      .commission-contract-list { grid-template-columns:1fr; max-height:280px; }
      .commission-contract-toolbar { align-items:stretch; flex-direction:column; gap:.35rem; }
      .commission-contract-toolbar > span { font-size:.62rem; }
      .commission-contracts-panel { padding:.75rem; }
      .commission-contracts-panel .section-actions { align-items:flex-start; }
      .commission-contracts-panel .scope-chip { flex:0 0 auto; }
      .commission-change-card { align-items:stretch; flex-direction:column; gap:.55rem; padding:.8rem; }
      .commission-change-header { align-items:flex-start; flex-direction:column; gap:.3rem; }
      .commission-change-meta { justify-content:space-between; }
      .plan-card-actions { flex-direction:column; }
      .plan-card-actions button { width:100%; }
      .contract-history-modal { width:calc(100vw - 1rem); max-height:calc(100dvh - 1rem); padding:.9rem; }
      .plan-price-application { align-items:stretch; flex-direction:column; }
      .contract-history-list article { grid-template-columns:1fr; }
      .contract-history-list article span:last-child { grid-column:auto; }
    }
    .commission-detail-modal{width:min(560px,calc(100vw - 2rem))}
    .cc-metric{display:flex;justify-content:space-between;gap:1rem;padding:.6rem 0;border-bottom:1px dashed var(--ts-border-navy-750-11)}
    .cc-metric span{color:var(--ts-text-sky-350-26);font-size:.7rem;font-weight:600;letter-spacing:.03em}
    .cc-metric strong{color:var(--ts-text-soft-2);font-size:.78rem}
    .cc-businesses{margin-top:.9rem}
    .cc-businesses-head{display:flex;align-items:center;gap:.5rem;color:var(--ts-text-sky-150-26);font-size:.72rem;font-weight:800;padding:.5rem .85rem;border-radius:9px;background:var(--ts-bg-blue-450-a8-3);border-left:3px solid var(--ts-border-blue-400-7)}
    .cc-businesses-head strong{flex:1}
    .cc-businesses ul{list-style:none;margin:0;padding:0}
    .cc-businesses li{display:flex;justify-content:space-between;gap:.8rem;padding:.55rem .85rem;border-bottom:1px solid var(--ts-border-gray-0-a6);color:var(--ts-text-sky-200-25);font-size:.7rem}
    .cc-biz-name{color:var(--ts-text-soft-2);overflow:hidden;text-overflow: ellipsis;white-space:nowrap}
    .cc-biz-rate{color:var(--ts-text-teal-450);font-weight:700;font-variant-numeric:tabular-nums}
    @media (max-width: 600px) {
      .payment-modal,.payment-modal.subscription-custom-modal { width: calc(100vw - 20px); max-height: calc(100dvh - 20px); padding: 1rem; border-radius: 15px; }
      .payment-modal h3 { font-size: 1.05rem; }
      .subscription-card-actions button i { display: none; }
    }
  `]
})
export class SuperAdminComponent implements OnInit, OnDestroy {
  @Input() embeddedNavigation = false;
  @Input() set initialTab(tab: string) {
    if (!tab) return;
    if (tab === 'personal') {
      this.userSection.set('personal');
      if (this.activeTab() !== 'users') {
        this.resetGlobalStatsFilterIfLeavingEstadisticas('users');
        this.activeTab.set('users');
        this.currentPage.set(1);
        this.searchTerm.set('');
        if (this.initialized) this.loadTabData('users');
      }
      return;
    }
    if (tab === this.activeTab()) return;
    // Este setter es el que realmente cambia de pestaña cuando la
    // navegación viene de AFUERA del componente (el menú lateral de
    // "Centro de operaciones", vía [initialTab] en dashboard.component.ts)
    // -- setTab() solo cubre los botones de pestaña internos (Propietarios,
    // Usuarios, Suscripciones, Planes, Pagos...), que no incluyen
    // "Estadísticas globales". Sin este mismo reset aquí, salir de
    // Estadísticas por el menú lateral dejaba el filtro de periodo pegado.
    this.resetGlobalStatsFilterIfLeavingEstadisticas(tab);
    this.activeTab.set(tab);
    this.currentPage.set(1);
    this.searchTerm.set('');
    if (this.initialized) this.loadTabData(tab);
  }
  private resetGlobalStatsFilterIfLeavingEstadisticas(nextTab: string): void {
    if (this.activeTab() === 'estadisticas' && nextTab !== 'estadisticas' && (this.globalStatsFrom || this.globalStatsTo)) {
      this.globalStatsFrom = '';
      this.globalStatsTo = '';
      this.analyticsFocusMonth.set(this.monthKey(new Date()));
    }
  }

  stats          = signal<SuperAdminStats | null>(null);
  businesses     = signal<SuperAdminBusiness[]>([]);
  owners         = signal<SuperAdminOwner[]>([]);
  pendingOwnerSignups = signal<PendingOwnerSignup[]>([]);
  pendingOwnerSignupsLoading = signal(false);
  pendingOwnerSignupsLoaded = false;
  resendingPendingSignupId = signal<number | null>(null);
  copyingPendingSignupId = signal<number | null>(null);
  // Corrección pedida: editar y confirmar pago externo directamente sobre
  // una alta pendiente, sin depender del enlace en línea.
  editingPendingSignup = signal<PendingOwnerSignup | null>(null);
  pendingEditForm: any = {};
  editPendingSignupError = signal('');
  isSavingPendingEdit = signal(false);
  confirmingPendingExternalPayment = signal<PendingOwnerSignup | null>(null);
  pendingExternalPaymentForm: any = { method: 'efectivo', reference: '', receipts: [] as { file_name: string; original_name: string; file_type: string; mime: string }[] };
  confirmPendingPaymentError = signal('');
  isConfirmingPendingPayment = signal(false);
  isUploadingPendingReceipt = signal(false);
  users          = signal<any[]>([]);
  subscriptions  = signal<any[]>([]);
  allPayments    = signal<any[]>([]);
  revenue        = signal<SuperAdminRevenue[]>([]);
  analyticsFocusMonth = signal(this.monthKey(new Date()));
  globalStatsFrom = '';
  globalStatsTo = '';
  plans          = signal<any[]>([]);
  planesTab = signal<'planes'|'comision'>('planes');
  readonly planesModuleTabs: AdminTabOption[] = [
    { id: 'planes', label: 'Planes', icon: 'bi-layers' },
    { id: 'comision', label: 'Comisión', icon: 'bi-percent' },
  ];
  private readonly planesTabHashes: Record<string, string> = { planes: 'planes-catalogo', comision: 'planes-comision' };
  // Personalización: cada pestaña tiene su propio componente en el template.
  personalizacionTab = signal<'redes' | 'legales'>('redes');
  readonly personalizacionTabs: AdminTabOption[] = [
    { id: 'redes', label: 'Redes sociales', description: 'Enlaces oficiales en todos los frentes', icon: 'bi-share' },
    { id: 'legales', label: 'Documentos legales', description: 'Términos, privacidad y más en la landing y el sitio', icon: 'bi-file-earmark-text' },
  ];
  selectPersonalizacionTab(id: string): void {
    if (id === 'redes' || id === 'legales') this.personalizacionTab.set(id);
  }
  platformSettingsForm: PlatformSettings = { commission_rate: 5, trial_days: 7, grace_days: 2 };
  savingGraceDays = signal(false);
  graceDaysInvalid = signal(false);
  savingPlatformSettings = signal(false);
  adminReservations = signal<any[]>([]);
  adminReservationsTotal = signal(0);
  adminReservationsPage = signal(1);
  adminReservationsLimit = signal(25);
  loadingAdminReservations = signal(false);
  commissionHistory = signal<CommissionChangeEvent[]>([]);
  commissionContracts = signal<CommissionContract[]>([]);
  commissionBusinessSearch = '';
  selectedCommissionBusinessIds = new Set<number>();
  commissionHistoryLoading = signal(false);
  commissionChangeDetail = signal<CommissionChangeEvent | null>(null);
  subscriptionHistoryTarget = signal<any | null>(null);
  subscriptionContractHistory = signal<any[]>([]);
  subscriptionContractHistoryLoading = signal(false);
  showSubscriptionHistoryModal = signal(false);
  planHistoryTarget = signal<any | null>(null);
  planHistory = signal<any[]>([]);
  planHistoryLoading = signal(false);
  showPlanHistoryModal = signal(false);
  // --- Seguridad (2FA de super_admin) -- antes no existía NINGUNA pantalla
  // para activar/desactivar el segundo factor, aunque el backend ya tenía
  // (sin funcionar) los endpoints. Ver auth.service.ts::getTwoFactorStatus.
  twoFactorStatus = signal<{ enabled: boolean } | null>(null);
  twoFactorStatusLoading = signal(false);
  twoFactorSetupData = signal<{ secret: string; qrCode: string } | null>(null);
  twoFactorSetupLoading = signal(false);
  twoFactorVerifyCode = '';
  twoFactorVerifyLoading = signal(false);
  twoFactorVerifyError = signal('');
  showDisableTwoFactorForm = signal(false);
  showDisableTwoFactorPassword = signal(false);
  disableTwoFactorPassword = '';
  disableTwoFactorCode = '';
  disableTwoFactorLoading = signal(false);
  disableTwoFactorError = signal('');
  payoutBusinesses=signal<any[]>([]);
  payoutDetail=signal<any|null>(null);
  payoutModalTab=signal<'general'|'reservas'|'transferencias'|'deuda'>('general');
  readonly payoutModalTabs: AdminTabOption[] = [
    { id: 'general', label: 'General', icon: 'bi-wallet2' },
    { id: 'reservas', label: 'Pagos de reservas', icon: 'bi-calendar2-check' },
    { id: 'transferencias', label: 'Transferencias', icon: 'bi-arrow-left-right' },
    { id: 'deuda', label: 'Comisión pendiente', icon: 'bi-receipt-cutoff' },
  ];
  // "Pagos de reservas" del modal -- lista agrupada por conjunto multi-día,
  // con su propia paginación/búsqueda (ver getAdminBusinessReservationPayments
  // en el backend). Independiente de payoutDetail(): se recarga sola sin
  // tener que refrescar todo el detalle financiero del negocio.
  payoutReservationGroups = signal<AdminReservationPaymentGroup[]>([]);
  payoutReservationGroupsTotal = signal(0);
  payoutReservationGroupsPage = signal(1);
  payoutReservationGroupsLimit = signal(25);
  payoutReservationGroupsPages = signal(1);
  payoutReservationGroupsLoading = signal(false);
  payoutReservationSearch = signal('');
  payoutReservationExpanded = signal<Set<string>>(new Set());
  transferHistory = computed<any[]>(() => {
    const grouped = new Map<string, any>();
    const settlements = this.payoutDetail()?.settlements || [];
    for (const transfer of settlements.flatMap((settlement: any) => settlement.transfers || [])) {
      const minute = Math.floor(new Date(transfer.performed_at).getTime() / 60000);
      const key = `${transfer.transfer_reference || 'sin-referencia'}|${transfer.performed_by_user_id}|${minute}|${transfer.notes || ''}`;
      const current = grouped.get(key);
      if (current) current.amount = Number(current.amount) + Number(transfer.amount);
      else grouped.set(key, { ...transfer, amount: Number(transfer.amount) });
    }
    return [...grouped.values()].sort(
      (a: any, b: any) => new Date(b.performed_at).getTime() - new Date(a.performed_at).getTime(),
    );
  });
  subscriptionsExpanded=signal(true);
  payoutTransferSaving=signal(false);
  businessTransferForm:any={amount:0,payout_method_id:null,transfer_reference:'',notes:''};

  // Deuda del negocio hacia TuSala (dirección contraria) -- corrección
  // pedida por el usuario: antes "el negocio debe a TuSala $X" era solo
  // informativo, sin forma de cobrarlo, registrarlo pagado, ni contarlo mes
  // a mes. Ver createDebtSettlement/confirmDebtSettlementPayment en
  // payouts.controller.ts.
  debtSettlementForm:any={period_start:'',period_end:'',notes:''};
  debtSettlementSaving=signal(false);
  debtConfirmForm:Record<number,{payment_method:string;payment_reference:string;notes:string}>={};
  debtConfirmingId=signal<number|null>(null);
  debtLinkCopyingId=signal<number|null>(null);
  debtConfirmOpenId=signal<number|null>(null);
  readonly debtSettlements = computed<any[]>(() => this.payoutDetail()?.debt_settlements ?? []);
  readonly pendingAdminDebtSettlement = computed(() => this.debtSettlements().find((s: any) => s.status === 'pending') ?? null);

  private emptyDebtConfirmForm(): { payment_method: string; payment_reference: string; notes: string } {
    return { payment_method: 'efectivo', payment_reference: '', notes: '' };
  }

  toggleDebtConfirmForm(settlementId: number): void {
    if (this.debtConfirmOpenId() === settlementId) { this.debtConfirmOpenId.set(null); return; }
    this.debtConfirmForm[settlementId] = this.debtConfirmForm[settlementId] || this.emptyDebtConfirmForm();
    this.debtConfirmOpenId.set(settlementId);
  }

  // Bug real reportado: al confirmar un pago de deuda, el detalle del modal
  // se refrescaba (openPayoutBusiness), pero la TARJETA de la lista externa
  // "Pago a negocios" (payoutBusinesses, cargada antes de abrir el detalle)
  // seguía mostrando el monto viejo hasta recargar la página a mano -- ahí
  // es donde el usuario vio "el monto principal no se reseteó" aunque el
  // detalle ya mostraba el periodo en 0.
  private refreshPayoutBusinessesList(): void {
    this.payouts.adminBusinesses(this.searchTerm(), this.currentPage(), this.pageSize).subscribe({
      next: r => { this.payoutBusinesses.set(r.businesses || []); this.totalRecords.set(r.total || 0); this.totalPages.set(r.pages || 1); },
      error: () => {},
    });
  }

  async createDebtSettlement(): Promise<void> {
    const d = this.payoutDetail();
    if (!d) return;
    const { period_start, period_end } = this.debtSettlementForm;
    if (!period_start || !period_end || new Date(period_start) > new Date(period_end)) {
      this.notifications.warning('Indica un periodo válido (inicio no puede ser posterior al fin).');
      return;
    }
    this.debtSettlementSaving.set(true);
    try {
      const response: any = await this.dialogs.run(
        'Creando liquidación de deuda…',
        () => this.payouts.createDebtSettlement(d.business.id, this.debtSettlementForm),
        'Liquidación de deuda creada. Genera el enlace de pago o regístralo manualmente cuando el negocio pague.',
        (error: any) => friendlyError(error, 'No se pudo crear la liquidación de deuda.'),
      );
      if (response !== null) {
        this.debtSettlementForm = { period_start: '', period_end: '', notes: '' };
        this.openPayoutBusiness({ business_id: d.business.id });
        this.refreshPayoutBusinessesList();
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.debtSettlementSaving.set(false);
    }
  }

  // Corrección pedida: antes la ÚNICA forma de cobrar la deuda vigente era
  // usar los calendarios para "adivinar" un rango -- aunque el mes en curso
  // ni siquiera hubiera terminado. Cierra el periodo automático vigente
  // (desde el pago más antiguo sin liquidar hasta hoy) con un clic.
  // Corrección pedida: esta acción (a diferencia de crear/pagar una
  // liquidación con datos de formulario) no necesita el modal de
  // "procesando -> resultado" de dialogs.run -- es un solo clic que ya lleva
  // directo a ver el resultado en la pestaña de deuda, así que un modal
  // adicional de confirmación se sentía redundante. Se reemplaza por un
  // aviso simple (toast), igual que copyDebtSettlementLink/
  // sendDebtSettlementLinkEmail ya hacen para el resto de acciones rápidas
  // de esta misma pestaña.
  async collectCurrentDebtNow(businessId: number): Promise<void> {
    if (this.debtSettlementSaving()) return;
    this.debtSettlementSaving.set(true);
    try {
      await firstValueFrom(this.payouts.createDebtSettlement(businessId, {}));
      this.notifications.success('Periodo cerrado. Genera el enlace de pago o regístralo manualmente cuando el negocio pague.');
      this.openPayoutBusiness({ business_id: businessId });
      this.refreshPayoutBusinessesList();
      // Antes había que buscar la pestaña "Comisión pendiente" a mano para
      // encontrar el botón de copiar/enviar el enlace -- ahora "Cobrar
      // ahora" lleva directo ahí. Cambiar la pestaña activa no mueve el
      // scroll del modal por sí solo (el panel puede quedar fuera de vista
      // más abajo) -- se hace scroll explícito en cuanto Angular termina de
      // renderizar el panel nuevo.
      this.payoutModalTab.set('deuda');
      this.scrollPayoutModalToTabs();
    } catch (error: any) {
      this.notifications.error(friendlyError(error, 'No se pudo cerrar el periodo vigente.'));
    } finally {
      this.debtSettlementSaving.set(false);
    }
  }

  private scrollPayoutModalToTabs(): void {
    setTimeout(() => {
      document.querySelector('.payout-modal .admin-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  debtEmailSendingId = signal<number | null>(null);
  collectionLinkCopyingId = signal<number | null>(null);
  collectionLinkEmailSendingId = signal<number | null>(null);

  sendDebtSettlementLinkEmail(settlement: any): void {
    if (this.debtEmailSendingId() === settlement.id) return;
    this.debtEmailSendingId.set(settlement.id);
    this.payouts.sendDebtSettlementPaymentLinkEmail(settlement.id).subscribe({
      next: (response) => {
        this.debtEmailSendingId.set(null);
        this.notifications.success(response?.msg || 'Enlace de pago enviado por correo.');
      },
      error: (error) => {
        this.debtEmailSendingId.set(null);
        this.notifications.error(friendlyError(error, 'No fue posible enviar el correo.'));
      },
    });
  }

  async confirmDebtSettlementPayment(settlement: any): Promise<void> {
    const form = this.debtConfirmForm[settlement.id];
    if (!form?.payment_method) { this.notifications.warning('Selecciona el método de pago utilizado.'); return; }
    this.debtConfirmingId.set(settlement.id);
    try {
      const response: any = await this.dialogs.run(
        'Registrando pago…',
        () => this.payouts.confirmDebtSettlementPayment(settlement.id, form),
        'Pago registrado. El saldo de este periodo quedó saldado.',
        (error: any) => friendlyError(error, 'No se pudo registrar el pago.'),
      );
      if (response !== null) {
        this.debtConfirmOpenId.set(null);
        const d = this.payoutDetail();
        if (d) this.openPayoutBusiness({ business_id: d.business.id });
        this.refreshPayoutBusinessesList();
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.debtConfirmingId.set(null);
    }
  }

  copyDebtSettlementLink(settlement: any): void {
    if (this.debtLinkCopyingId() === settlement.id) return;
    this.debtLinkCopyingId.set(settlement.id);
    this.payouts.getDebtSettlementPaymentLink(settlement.id).subscribe({
      next: async (response) => {
        this.debtLinkCopyingId.set(null);
        if (!response?.paymentLinkUrl) { this.notifications.error('No fue posible obtener el enlace de pago.'); return; }
        try {
          await navigator.clipboard.writeText(response.paymentLinkUrl);
          this.notifications.success('Enlace copiado al portapapeles.');
        } catch {
          this.notifications.error(`No fue posible copiar automáticamente. Enlace: ${response.paymentLinkUrl}`);
        }
      },
      error: (error) => {
        this.debtLinkCopyingId.set(null);
        this.notifications.error(friendlyError(error, 'No fue posible obtener el enlace de pago.'));
      },
    });
  }

  isLoading     = signal(true);
  tabLoading    = signal(false);
  errorMsg      = signal('');
  activeTab     = signal<string>('overview');
  userSection   = signal<string>('usuarios');
  readonly userSectionTabs: (AdminTabOption & { hash: string })[] = [
    { id: 'usuarios', label: 'Usuarios', description: 'Directorio de accesos', hash: 'usuarios-directorio' },
    { id: 'personal', label: 'Personal administrativo de TuSala', description: 'Soporte y dueños', hash: 'usuarios-personal' }
  ];
  ownersSection = signal<string>('control');
  readonly ownersSectionTabs: (AdminTabOption & { hash: string })[] = [
    { id: 'control', label: 'Control de propietarios', description: 'Gestión, plan y acceso', icon: 'bi-people', hash: 'propietarios-control' },
    { id: 'pendientes', label: 'Pagos pendientes', description: 'Altas sin completar', icon: 'bi-hourglass-split', hash: 'propietarios-pendientes' },
    { id: 'eliminados', label: 'Propietarios eliminados', description: 'Negocios dados de baja', icon: 'bi-archive', hash: 'propietarios-eliminados' },
  ];
  currentPage   = signal(1);
  totalPages    = signal(1);
  totalRecords  = signal(0);
  pageSize = 25;
  readonly pageSizeOptions = [25, 50, 75, 100];
  pagesTotalAmount = signal(0);
  searchTerm    = signal('');

  subsStatusFilter = '';
  subsPlanFilter = '';
  paymentStatusFilter = '';
  userRoleFilter = '';
  isResetting = signal(false);
  showUserCreateModal = signal(false);
  isCreatingUser = signal(false);
  manualUserError = signal('');
  manualUserForm: any = this.emptyManualUserForm();
  paymentSummary = signal<Record<string, { count: number; amount: number }>>({ succeeded: { count: 0, amount: 0 }, pending: { count: 0, amount: 0 }, failed: { count: 0, amount: 0 } });
  showRenewalDateModal = signal(false);
  renewalDateTarget = signal<any | null>(null);
  renewalDateInput = '';
  renewalDateError = signal('');
  isSavingRenewalDate = signal(false);
  showPlanModal = signal(false);
  isSavingPlan = signal(false);
  planFormError = signal('');
  editingPlanId: number | null = null;
  advancedBenefitsJson = '';
  additionalPlanBenefits: Record<string, unknown> = {};
  planForm: any = this.emptyPlanForm();
  private planTrialDaysInitial: number | null = null;
  archiveOwnerTarget = signal<any | null>(null);
  archiveOwnerStep = signal<1 | 2>(1);
  archiveOwnerError = signal('');
  archiveOwnerSaving = signal(false);
  archiveOwnerPassword = '';
  showArchiveOwnerPassword = signal(false);
  archivedOwners = signal<any[]>([]);
  archivedOwnersLoaded = false;
  archivedOwnersLoading = signal(false);
  archivedOwnersPage = signal(1);
  archivedOwnersPages = signal(1);
  archivedOwnersTotal = signal(0);
  archivedOwnersPageSize = 10;
  readonly archivedOwnersPageSizeOptions = [10, 25, 50, 100];
  archivedOwnersSearchDraft = '';
  archivedOwnersSearch = '';
  restoreOwnerTarget = signal<any | null>(null);
  restoringBusinessId = signal<number | null>(null);
  showConfirmPaymentModal = signal(false);
  confirmPaymentTarget = signal<any | null>(null);
  confirmPaymentForm: any = { payment_method: 'efectivo', payment_reference: '' };
  confirmPaymentError = signal('');
  isConfirmingPayment = signal(false);
  // Paso previo de revisión obligatorio antes de disparar saveConfirmPayment():
  // esta acción reactiva negocio + cuenta del propietario, crea un pago
  // permanente y notifica por correo -- no debe poder confirmarse con un
  // solo clic accidental sobre el formulario.
  confirmPaymentStep = signal<'form' | 'review'>('form');
  showSubscriptionCustomizationModal = signal(false);
  subscriptionCustomizationTarget = signal<any | null>(null);
  subscriptionPriceApplication = false;
  subscriptionCustomizationError = signal('');
  isSavingSubscriptionCustomization = signal(false);
  subscriptionBusinessUsers = signal<any[]>([]);
  subscriptionBusinessUsersLoading = signal(false);
  subscriptionCustomizationForm: any = { new_plan: '', custom_max_spaces: '', custom_price_monthly: '', custom_pricing_notes: '', requested_by_user_id: null };
  subscriptionRestorationPending = signal(false);
  subscriptionCustomizationField = signal<string | null>(null);

  private setSubscriptionError(message: string, field: string | null): void {
    this.subscriptionCustomizationError.set(message);
    this.subscriptionCustomizationField.set(field);
    if (field) {
      this.notifications.error(message);
      setTimeout(() => {
        const el = document.querySelector<HTMLElement>('.subscription-custom-modal .field-error');
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('field-error-flash');
        setTimeout(() => el.classList.remove('field-error-flash'), 1900);
      }, 30);
    }
  }

  private subscriptionCustomizationInitial = '';
  private planEditInitial = '';
  private tabRequestId = 0;
  private initialized = false;
  private searchTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private subscriptionService: SubscriptionService,
    private dialogs: DialogService,
    private notifications: NotificationService,
    private payouts: PayoutService,
    private noveltyService: NoveltyService,
    private platformSettingsService: PlatformSettingsService,
    private authService: AuthService,
    readonly cooldown: ActionCooldownService,
  ) {}

  readonly formatSpacesLabel = formatSpacesLabel;
  readonly formatLongDate = formatLongDate;
  readonly formatLongDateTime = formatLongDateTime;

  selectUserSection(tab: string): void {
    this.userSection.set(tab);
    const selected = this.userSectionTabs.find((item) => item.id === tab);
    if (!selected || window.location.hash === `#${selected.hash}`) {
      return;
    }
    window.history.pushState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}#${selected.hash}`
    );
  }

  @HostListener('window:hashchange')
  onUserSectionHashChange(): void {
    this.syncUserSectionFromHash();
    this.syncPaymentStatusFromHash();
    this.syncPlanesTabFromHash();
    this.syncOwnersSectionFromHash();
  }

  @HostListener('window:popstate')
  onUserSectionHistoryChange(): void {
    this.syncUserSectionFromHash();
    this.syncPaymentStatusFromHash();
    this.syncPlanesTabFromHash();
    this.syncOwnersSectionFromHash();
  }

  private syncUserSectionFromHash(): void {
    const hash = window.location.hash.replace(/^#/, '').trim().toLowerCase();
    const selected = this.userSectionTabs.find((item) => item.hash === hash);
    if (selected) {
      this.userSection.set(selected.id);
    }
  }

  ngOnInit(): void {
    this.initialized = true;
    this.syncUserSectionFromHash();
    this.syncPaymentStatusFromHash();
    this.syncPlanesTabFromHash();
    this.syncOwnersSectionFromHash();
    this.loadTabData(this.activeTab());
    // Se carga siempre (no solo en la pestaña "Planes") porque la lista de
    // Suscripciones también necesita el plan "gratis" para mostrar el cupo
    // de espacios en prueba (trialCapacityLabel) sin haber visitado Planes.
    if (this.activeTab() !== 'planes' && this.plans().length === 0) {
      this.subscriptionService.getAdminPlans().subscribe({
        next: r => this.plans.set(r.plans),
        error: () => {},
      });
    }
    // Corrección pedida: los formularios de "Crear propietario" y
    // "Personalizar suscripción" (sección Propietarios) muestran el
    // placeholder "Usa el periodo de gracia global (X días)" en su campo
    // Días de gracia -- necesitan el valor real de platformSettingsForm sin
    // depender de que el super_admin haya visitado antes la pestaña
    // Planes, igual que ya pasa con `plans()` arriba.
    if (this.activeTab() !== 'planes') this.loadPlatformSettings();
    this.subscriptionService.getAdminStats(this.activeTab() === 'estadisticas').subscribe({
      next: (s) => {
        this.stats.set(s);
        this.revenue.set(this.normalizeMonthlyRevenue(s.revenue.monthly_subscriptions || []));
        this.isLoading.set(false);
      },
      error: (err) => {
        const msg = friendlyError(err, 'Error al cargar estadísticas.');
        this.errorMsg.set(msg);
        this.notifications.error(msg);
        this.isLoading.set(false);
      },
    });
  }

  ngOnDestroy(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  setTab(tab: string): void {
    if (this.tabLoading() && tab === this.activeTab()) return;
    this.resetGlobalStatsFilterIfLeavingEstadisticas(tab);
    this.activeTab.set(tab);
    this.currentPage.set(1);
    this.searchTerm.set('');
    this.loadTabData(tab);
  }

  hasDirectoryTools(): boolean {
    return ['negocios', 'owners', 'users', 'subs', 'pagos', 'pagosNegocios'].includes(this.activeTab());
  }

  // Bug real corregido: 'gratis' es un plan real (Plan.findOne({code:'gratis'}),
  // seed en server.ts, ya usado en facturación/altas/estadísticas del
  // backend) -- pero faltaba en este mapeo, así que CUALQUIER negocio en el
  // plan gratis cae en el "|| 'Sin plan'" y se ve idéntico a un negocio que
  // de verdad no tiene ninguna suscripción. Este método se usa en todo el
  // panel de super admin (directorio de propietarios, ficha, negocios,
  // suscripciones, "Mix comercial" de Estadísticas, modales de
  // personalización/confirmar pago) -- un solo fix cubre las 9 pantallas.
  planLabel(plan?: string): string { return ({ gratis: 'Gratis', basico: 'Básico', estandar: 'Estándar', premium: 'Premium' } as Record<string, string>)[plan || ''] || 'Sin plan'; }
  roleLabel(role?: string): string { return ({ customer: 'Cliente', owner: 'Propietario', employee: 'Subadministrador', super_admin: 'Superadministrador' } as Record<string, string>)[role || ''] || 'Sin rol'; }
  paymentTypeLabel(type?: string): string { return ({ subscription: 'Suscripción', reservation: 'Reserva' } as Record<string, string>)[type || ''] || 'No especificado'; }
  paymentConceptLabel(payment: any): string {
    if (payment?.payment_concept) return payment.payment_concept;
    const reference = String(payment?.payment_reference || '');
    if (reference.startsWith('pending:')) return 'Contratación de suscripción';
    if (reference.startsWith('plan-change:')) return 'Cambio de suscripción';
    if (reference.startsWith('renewal:')) return 'Pago por uso (renovación mensual)';
    return payment?.payment_type === 'subscription' ? 'Pago de suscripción' : 'Pago de reserva';
  }
  statusLabel(status?: string): string { return ({ active: 'Activo', succeeded: 'Aprobado', pending: 'Pendiente', cancelled: 'Cancelado', expired: 'Expirado', completed: 'Completado', failed: 'Fallido', inactive: 'Inactivo', trial: 'Prueba' } as Record<string, string>)[status || ''] || 'Sin información'; }

  paymentMethodLabel(method?: string): string {
    return ({ efectivo: 'Efectivo', transferencia: 'Transferencia', apay: 'Enlace APay', tarjeta: 'Tarjeta', otro: 'Otro' } as Record<string, string>)[String(method || '').toLowerCase()] || 'No indicado';
  }

  enabledBenefitList(plan: any): string[] {
    return Object.entries(plan?.benefits || {}).filter(([, value]) => this.isEnabledBenefit(value)).map(([key]) => this.benefitLabel(key));
  }

  subscriptionHealthPercent(stats: any): number { const total = Number(stats?.totals?.subscriptions || 0); return total ? Math.min(100, Math.round((Number(stats?.subscriptions?.active || 0) / total) * 100)) : 0; }
  planPercent(value: unknown, byPlan: any): number { const values = Object.values(byPlan || {}).map(Number); const max = Math.max(...values, 1); return Math.min(100, Math.round((Number(value) / max) * 100)); }
  customerOwnerRatio(stats: SuperAdminStats): string {
    return this.customerOwnerRatioNumber(stats).toFixed(1);
  }
  customerOwnerRatioNumber(stats: SuperAdminStats): number {
    const owners = Number(stats.totals.owners || 0);
    return owners ? Number(stats.totals.customers || 0) / owners : 0;
  }
  planShare(value: unknown, stats: SuperAdminStats): number {
    const total = Number(stats.totals.subscriptions || 0);
    return total ? Math.round((Number(value) / total) * 100) : 0;
  }
  audiencePercent(value: number, stats: SuperAdminStats): number {
    const max = Math.max(Number(stats.totals.owners || 0), Number(stats.totals.customers || 0), 1);
    return Math.max(4, Math.round((Number(value || 0) / max) * 100));
  }
  businessCoverage(stats: SuperAdminStats): number {
    const businesses = Number(stats.totals.businesses || 0);
    return businesses ? Math.min(100, Math.round((Number(stats.totals.subscriptions || 0) / businesses) * 100)) : 0;
  }
  subscriptionRevenueShare(stats: SuperAdminStats): number {
    const total = Number(stats.revenue.total || 0);
    return total ? Math.round((Number(stats.revenue.subscriptions || 0) / total) * 100) : 0;
  }
  subscriptionRing(stats: SuperAdminStats): string {
    const active = this.subscriptionHealthPercent(stats);
    const total = Number(stats.totals.subscriptions || 0);
    const pendingCount = Number(stats.subscriptions.pending || 0) + Number(stats.subscriptions.inactive || 0);
    const pending = total ? Math.round((pendingCount / total) * 100) : 0;
    return `conic-gradient(#43d392 0 ${active}%, #f1c766 ${active}% ${Math.min(100, active + pending)}%, #ef6b78 ${Math.min(100, active + pending)}% 100%)`;
  }
  analyticsMaxRevenue(): number {
    return Math.max(...this.visibleAnalyticsRevenue().map(item => Number(item.subscriptions || 0)), 1);
  }
  visibleAnalyticsRevenue(): SuperAdminRevenue[] {
    const rows = this.revenue();
    const focus = rows.findIndex(row => row.month === this.analyticsFocusMonth());
    if (focus < 0) return rows.slice(-7);
    const start = Math.max(0, Math.min(rows.length - 7, focus - 3));
    return rows.slice(start, start + 7);
  }
  moveAnalyticsMonth(direction: -1 | 1): void {
    const rows = this.revenue();
    const index = rows.findIndex(row => row.month === this.analyticsFocusMonth());
    const next = index + direction;
    if (next >= 0 && next < rows.length) this.analyticsFocusMonth.set(rows[next].month);
  }
  canMoveAnalyticsForward(): boolean { const rows = this.revenue(); return rows.findIndex(row => row.month === this.analyticsFocusMonth()) < rows.length - 1; }
  applyGlobalStatsPeriod(): void {
    if (!this.cooldown.consume('sa-stats-filter')) return;
    if (this.globalStatsFrom && this.globalStatsTo && this.globalStatsFrom > this.globalStatsTo) { this.notifications.error('La fecha inicial no puede ser posterior a la fecha final.'); return; }
    this.loadTabData('estadisticas');
  }
  resetGlobalStatsPeriod(): void { if (!this.cooldown.consume('sa-stats-filter')) return; this.globalStatsFrom = ''; this.globalStatsTo = ''; this.analyticsFocusMonth.set(this.monthKey(new Date())); this.loadTabData('estadisticas'); }
  filterAnalyticsByMonth(month: string): void {
    const [year, monthNumber] = String(month).split('-').map(Number);
    if (!year || !monthNumber) return;
    const toIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    this.globalStatsFrom = toIso(new Date(year, monthNumber - 1, 1));
    this.globalStatsTo = toIso(new Date(year, monthNumber, 0));
    this.analyticsFocusMonth.set(month);
    this.applyGlobalStatsPeriod();
  }
  globalStatsPeriodLabel(): string { return this.globalStatsFrom || this.globalStatsTo ? `${this.globalStatsFrom || 'Inicio'} — ${this.globalStatsTo || 'Hoy'}` : 'Últimos 12 meses'; }
  private monthKey(date: Date): string { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; }
  private ensureAnalyticsFocusVisible(): void {
    const rows = this.revenue();
    if (!rows.some(row => row.month === this.analyticsFocusMonth())) this.analyticsFocusMonth.set(rows.at(-1)?.month || this.monthKey(new Date()));
  }
  revenueBarPercent(value: number): number {
    const amount = Number(value || 0);
    return amount > 0 ? Math.max(5, Math.round((amount / this.analyticsMaxRevenue()) * 100)) : 0;
  }
  bestRevenueMonthAmount(): number {
    return this.revenue().reduce((best, row) => Math.max(best, Number(row.subscriptions || 0)), 0);
  }
  bestRevenueMonthLabel(): string {
    const best = this.revenue().reduce<SuperAdminRevenue | null>((current, row) => !current || Number(row.subscriptions || 0) > Number(current.subscriptions || 0) ? row : current, null);
    return best ? this.monthLong(best.month) : 'Sin datos';
  }
  compactCurrency(value: number): string {
    const amount = Number(value || 0);
    if (!amount) return '$0';
    return amount >= 1000 ? `$${(amount / 1000).toFixed(1)}k` : `$${Math.round(amount)}`;
  }
  formatCurrencyValue(value: number): string {
    return new Intl.NumberFormat('es-SV', { style: 'currency', currency: 'USD' }).format(Number(value || 0));
  }
  monthShort(month: string): string {
    const [year, monthNumber] = String(month).split('-').map(Number);
    if (!year || !monthNumber) return month;
    return new Intl.DateTimeFormat('es-SV', { month: 'short' }).format(new Date(year, monthNumber - 1, 1)).replace('.', '');
  }
  monthLong(month: string): string {
    const [year, monthNumber] = String(month).split('-').map(Number);
    if (!year || !monthNumber) return month;
    const label = new Intl.DateTimeFormat('es-SV', { month: 'long', year: 'numeric' }).format(new Date(year, monthNumber - 1, 1));
    return label.charAt(0).toUpperCase() + label.slice(1);
  }
  private normalizeMonthlyRevenue(rows: Array<{ month: string; subscriptions: number }>): SuperAdminRevenue[] {
    const amounts = new Map(rows.map(row => [String(row.month), Number(row.subscriptions || 0)]));
    const current = new Date();
    current.setDate(1);
    const result: SuperAdminRevenue[] = [];
    for (let offset = 11; offset >= 0; offset--) {
      const date = new Date(current.getFullYear(), current.getMonth() - offset, 1);
      const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const subscriptions = amounts.get(month) || 0;
      result.push({ month, subscriptions, reservations: 0, total: subscriptions });
    }
    return result;
  }

  searchPlaceholder(): string {
    if (this.activeTab() === 'owners' && this.ownersSection() === 'pendientes') {
      return 'Buscar por nombre, negocio, correo o teléfono';
    }
    if (this.activeTab() === 'owners' && this.ownersSection() === 'eliminados') {
      return 'Buscar propietario, correo o negocio eliminado';
    }
    const labels: Record<string, string> = {
      negocios: 'Buscar negocio, empresa o correo',
      subs: 'Buscar propietario o negocio',
      owners: 'Buscar propietario, correo o teléfono',
      users: 'Buscar usuario, correo o teléfono',
      pagos: 'Buscar pago por correo electrónico',
      pagosNegocios: 'Buscar negocio, propietario o correo',
    };
    return labels[this.activeTab()] || 'Buscar';
  }

  // "Pagos pendientes" se filtra íntegramente en el cliente (ver
  // filteredPendingOwnerSignups) — no hace ninguna petición al servidor.
  private get isPendingOwnersSection(): boolean {
    return this.activeTab() === 'owners' && this.ownersSection() === 'pendientes';
  }

  setSearch(value: string): void {
    this.searchTerm.set(value);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = undefined;
  }

  applySearchNow(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = undefined;
    if (this.tabLoading()) return;
    if (!this.cooldown.consume('sa-search')) return;
    this.currentPage.set(1);
    if (this.isPendingOwnersSection) return;
    if (this.activeTab() === 'owners' && this.ownersSection() === 'eliminados') {
      this.archivedOwnersSearch = this.searchTerm().trim();
      this.loadArchivedOwners(1);
      return;
    }
    this.loadTabData(this.activeTab());
  }

  resetDirectorySearch(): void {
    if (this.tabLoading()) return;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = undefined;
    this.searchTerm.set('');
    const tab = this.activeTab();
    if (tab === 'users') this.userRoleFilter = '';
    if (tab === 'subs') { this.subsStatusFilter = ''; this.subsPlanFilter = ''; }
    if (tab === 'pagos') this.paymentStatusFilter = '';
    this.currentPage.set(1);
    if (this.isPendingOwnersSection) return;
    if (tab === 'owners' && this.ownersSection() === 'eliminados') {
      this.archivedOwnersSearch = '';
      this.loadArchivedOwners(1);
      return;
    }
    this.loadTabData(tab);
  }

  onPageSizeChange(rawValue: number | string): void {
    const nextSize = Number(rawValue);
    if (!this.pageSizeOptions.includes(nextSize) || nextSize === this.pageSize || this.tabLoading()) return;
    this.pageSize = nextSize;
    this.archivedOwnersPageSize = nextSize;
    this.currentPage.set(1);
    if (this.isPendingOwnersSection) return;
    if (this.activeTab() === 'owners' && this.ownersSection() === 'eliminados') {
      this.loadArchivedOwners(1);
      return;
    }
    this.loadTabData(this.activeTab());
  }

  filterSubscriptions(status: string): void {
    this.subsStatusFilter = status;
    this.currentPage.set(1);
    this.loadTabData('subs');
  }

  filterPaymentStatus(status: string): void {
    this.paymentStatusFilter = status;
    this.currentPage.set(1);
    this.loadTabData('pagos');
  }

  openRenewalDateModal(item: any): void {
    this.renewalDateTarget.set(item);
    const isTrial = this.isTrialItem(item);
    const sourceDate = isTrial ? item?.subscription?.trial_ends_at : item?.subscription?.renewal_at;
    this.renewalDateInput = sourceDate ? new Date(sourceDate).toISOString().slice(0, 10) : '';
    this.renewalDateError.set('');
    this.showRenewalDateModal.set(true);
  }

  /** Una cuenta de prueba sigue siéndolo aunque ya se haya marcado "expired"
   * automáticamente al vencer — se identifica por conservar trial_ends_at.
   * El plan también debe ser 'gratis': un plan pagado real puede conservar
   * un trial_ends_at viejo de cuando empezó como prueba (p. ej. si se le
   * cambió el plan manualmente), y sin este chequeo aparecía en este listado
   * como "Sin costo (prueba)" aunque fuera un cliente pagando un plan real. */
  isTrialItem(item: any): boolean {
    const sub = item?.subscription;
    return sub?.plan === 'gratis' && (sub?.status === 'trial' || (sub?.status === 'expired' && !!sub?.trial_ends_at));
  }

  isTrialRenewalTarget(): boolean {
    return this.isTrialItem(this.renewalDateTarget());
  }

  trialCapacityLabel(): string {
    const gratisPlan = this.plans().find(plan => plan.code === 'gratis');
    const limit = Number(gratisPlan?.limits?.max_spaces);
    if (!Number.isFinite(limit)) return 'Espacios de prueba no configurados';
    return `Hasta ${limit} ${limit === 1 ? 'espacio' : 'espacios'} (prueba)`;
  }

  subscriptionSpaceLimitLabel(item: any): string {
    const limit = Number(item?.effective?.max_spaces || item?.subscription?.planDefinition?.limits?.max_spaces || 0);
    return item?.subscription?.plan === 'premium' || limit >= 9999 ? 'Ilimitados' : `${limit} espacios`;
  }

  private readonly themeService = inject(ThemeService);

  // En tema claro los tonos (pensados para fondo oscuro) se oscurecen.
  subscriptionStateColor(item: any): string {
    const base = this.baseSubscriptionStateColor(item);
    // 5.2:1 sobre blanco deja margen AA sobre el fondo teñido de la insignia.
    return this.themeService.theme() === 'light' ? readableOnLight(base, 5.2) : base;
  }

  /** Tono semántico de la fila de suscripción (solo presentación). */
  subscriptionTone(item: any): 'success' | 'warning' | 'danger' | 'info' {
    const status = item?.subscription?.status;
    if (this.isTrialItem(item)) return status === 'expired' ? 'danger' : 'info';
    if (status === 'pending' || status === 'inactive') return 'warning';
    if (status === 'cancelled' || status === 'expired') return 'danger';
    return 'success';
  }

  private baseSubscriptionStateColor(item: any): string {
    if (this.isTrialItem(item)) return item?.subscription?.status === 'expired' ? '#ffb2b8' : '#8ecdff';
    const status = item?.subscription?.status;
    if (status === 'pending' || status === 'inactive') return '#ffd680';
    if (status === 'cancelled' || status === 'expired') return '#ffb2b8';
    return '#8ce4b2';
  }

  // Pedido del usuario: diferenciar en la tarjeta cuando un negocio está
  // próximo a vencer (3 días, mismo umbral que el aviso proactivo por
  // correo -- ver subscription-lifecycle.worker.ts) o ya vencido, tanto
  // para plan de pago (renewal_at) como para prueba (trial_ends_at).
  // 'cancelled'/'pending'/'inactive' no tienen una fecha límite relevante
  // que perseguir, así que siempre quedan 'ok' (sin insignia).
  subscriptionRenewalUrgency(item: any): 'ok' | 'soon' | 'overdue' {
    const sub = item?.subscription;
    if (!sub) return 'ok';
    const deadline = this.isTrialItem(item) ? sub.trial_ends_at : (sub.status === 'active' || sub.status === 'expired') ? sub.renewal_at : null;
    if (!deadline) return 'ok';
    const daysRemaining = (new Date(deadline).getTime() - Date.now()) / (24 * 60 * 60 * 1000);
    if (daysRemaining < 0) return 'overdue';
    if (daysRemaining <= 3) return 'soon';
    return 'ok';
  }

  subscriptionUrgencyLabel(item: any): string {
    const urgency = this.subscriptionRenewalUrgency(item);
    if (urgency === 'ok') return '';
    const isTrial = this.isTrialItem(item);
    if (urgency === 'overdue') return isTrial ? 'Prueba vencida' : 'Vencida';
    const sub = item?.subscription;
    const deadline = isTrial ? sub?.trial_ends_at : sub?.renewal_at;
    const days = Math.max(0, Math.ceil((new Date(deadline).getTime() - Date.now()) / (24 * 60 * 60 * 1000)));
    const dayLabel = days === 1 ? '1 día' : `${days} días`;
    return isTrial ? `Prueba vence en ${dayLabel}` : `Vence en ${dayLabel}`;
  }

  copySubscriptionCollectionLink(item: any): void {
    const subscriptionId = item?.subscription?.id;
    if (!subscriptionId || this.collectionLinkCopyingId() === subscriptionId) return;
    this.collectionLinkCopyingId.set(subscriptionId);
    this.subscriptionService.getSubscriptionCollectionLink(subscriptionId).subscribe({
      next: async (response) => {
        this.collectionLinkCopyingId.set(null);
        if (!response?.paymentLinkUrl) { this.notifications.error('No fue posible obtener el enlace de cobro.'); return; }
        try {
          await navigator.clipboard.writeText(response.paymentLinkUrl);
          this.notifications.success('Enlace de cobro copiado al portapapeles.');
        } catch {
          this.notifications.error(`No fue posible copiar automáticamente. Enlace: ${response.paymentLinkUrl}`);
        }
      },
      error: (error) => {
        this.collectionLinkCopyingId.set(null);
        this.notifications.error(friendlyError(error, 'No fue posible obtener el enlace de cobro.'));
      },
    });
  }

  // Pedido del usuario: enviar el correo de cobro es una acción que llega
  // directo a la bandeja del propietario -- necesita el mismo paso de
  // confirmación que ya tienen el resto de acciones sensibles de este
  // panel (activar/desactivar usuario, cancelar suscripción), en vez de
  // dispararse con un solo clic sin aviso.
  async sendSubscriptionCollectionLinkEmail(item: any): Promise<void> {
    const subscriptionId = item?.subscription?.id;
    if (!subscriptionId || this.collectionLinkEmailSendingId() === subscriptionId) return;
    const businessName = item?.business?.name || 'este negocio';
    const planName = this.planLabel(item?.subscription?.plan);
    const amount = this.confirmPaymentAmount(item);
    await this.dialogs.confirmAndRun(
      {
        title: 'Enviar correo de cobro',
        message: `Se enviará un correo a ${businessName} con un enlace para pagar su ${planName} ($${amount.toFixed(2)}). El propietario podrá pagarlo directamente desde ese enlace.`,
        tone: 'info',
        confirmText: 'Sí, enviar correo',
      },
      () => {
        this.collectionLinkEmailSendingId.set(subscriptionId);
        return this.subscriptionService.sendSubscriptionCollectionLinkEmail(subscriptionId);
      },
      response => response?.msg || 'Correo de cobro enviado.',
      (error: any) => friendlyError(error, 'No fue posible enviar el correo de cobro.'),
    ).catch(() => null);
    this.collectionLinkEmailSendingId.set(null);
  }

  planSpaceLimit(plan: any): string {
    const limit = Number(plan?.limits?.max_spaces || 0);
    return plan?.code === 'premium' || limit >= 9999 ? 'Ilimitados' : `${limit} espacios`;
  }

  openSubscriptionCustomization(item: any): void {
    this.subscriptionRestorationPending.set(false);
    this.subscriptionPriceApplication = false;
    this.subscriptionCustomizationTarget.set(item);
    const subscription = item?.subscription || {};
    this.subscriptionCustomizationForm = {
      new_plan: subscription.plan || '',
      custom_max_spaces: subscription.custom_max_spaces ?? '',
      custom_price_monthly: subscription.custom_price_monthly ?? '',
      commission_rate: item?.business?.commission_rate ?? '',
      grace_days: subscription.grace_days ?? '',
      custom_pricing_notes: subscription.custom_pricing_notes ?? '',
      requested_by_user_id: null,
    };
    this.subscriptionCustomizationInitial = this.stableValue({
      new_plan: String(subscription.plan || ''),
      custom_max_spaces: this.nullableValue(subscription.custom_max_spaces),
      custom_price_monthly: this.nullableValue(subscription.custom_price_monthly),
      commission_rate: this.nullableValue(item?.business?.commission_rate),
      grace_days: this.nullableValue(subscription.grace_days),
      custom_pricing_notes: String(subscription.custom_pricing_notes || '').trim() || null,
    });
    this.subscriptionCustomizationError.set('');
    this.showSubscriptionCustomizationModal.set(true);
    this.subscriptionBusinessUsers.set([]);
    this.subscriptionBusinessUsersLoading.set(true);
    this.subscriptionService.getSubscriptionBusinessUsers(subscription.id).subscribe({
      next: response => {
        const users = response?.users || [];
        this.subscriptionBusinessUsers.set(users);
        if (!this.subscriptionCustomizationForm.requested_by_user_id) {
          const owner = users.find((user: any) => user.role === 'owner' || user.role_label === 'Propietario');
          if (owner?.user_id) this.subscriptionCustomizationForm.requested_by_user_id = owner.user_id;
        }
        this.subscriptionBusinessUsersLoading.set(false);
      },
      error: error => { this.subscriptionBusinessUsersLoading.set(false); const msg = friendlyError(error, 'No fue posible cargar los usuarios del negocio.'); this.subscriptionCustomizationError.set(msg); this.notifications.error(msg); },
    });
    if (this.plans().length === 0) {
      this.subscriptionService.getAdminPlans().subscribe({
        next: r => this.plans.set(r.plans),
        error: () => {},
      });
    }
  }

  closeSubscriptionCustomization(): void {
    if (this.isSavingSubscriptionCustomization()) return;
    this.showSubscriptionCustomizationModal.set(false);
    this.subscriptionCustomizationTarget.set(null);
    this.subscriptionBusinessUsers.set([]);
    this.subscriptionCustomizationError.set('');
  }

  resetSubscriptionCustomization(): void {
    const requester = this.subscriptionCustomizationForm.requested_by_user_id;
    const currentPlan = this.subscriptionCustomizationTarget()?.subscription?.plan || '';
    this.subscriptionCustomizationForm = { new_plan: currentPlan, custom_max_spaces: '', custom_price_monthly: '', custom_pricing_notes: '', requested_by_user_id: requester };
    this.subscriptionRestorationPending.set(true);
    this.subscriptionPriceApplication = false;
    this.saveSubscriptionCustomization();
  }

  applyCurrentPlanPrice(): void {
    this.subscriptionPriceApplication = true;
    this.subscriptionCustomizationForm.custom_price_monthly = '';
    this.subscriptionRestorationPending.set(false);
    this.subscriptionCustomizationError.set('');
    this.subscriptionCustomizationField.set(null);
  }

  // Corrección pedida: 'gratis' (Prueba Gratuita) solo debe poder elegirse
  // al CREAR un propietario nuevo (ver el <select> de "Crear propietario",
  // que sigue usando plans() completo) -- nunca como destino al personalizar
  // una suscripción YA EXISTENTE (ni si ya está en gratis, ni si ya tiene un
  // plan pagado): este modal es la vía para CAMBIAR a un plan real, no para
  // "reasignar" gratis. Ver openSubscriptionCustomization()/el <select>
  // "Plan de suscripción".
  subscriptionPlanOptions(): any[] {
    return this.plans().filter(plan => plan.code !== 'gratis');
  }

  selectedPlanCurrentPrice(): number {
    const code = String(this.subscriptionCustomizationForm.new_plan || '').trim().toLowerCase();
    return Number(this.plans().find(plan => plan.code === code)?.price_monthly
      ?? (this.subscriptionCustomizationTarget()?.subscription?.planDefinition?.code === code
        ? this.subscriptionCustomizationTarget()?.subscription?.planDefinition?.price_monthly
        : 0));
  }

  async saveSubscriptionCustomization(): Promise<void> {
    if (this.isSavingSubscriptionCustomization()) return;
    const target = this.subscriptionCustomizationTarget();
    if (!target?.subscription?.id) return;
    const nullable = (value: any) => value === '' || value === null || value === undefined ? null : Number(value);
    const selectedPlan = String(this.subscriptionCustomizationForm.new_plan || '').trim().toLowerCase();
    const payload = { new_plan: selectedPlan || null, custom_max_spaces: nullable(this.subscriptionCustomizationForm.custom_max_spaces), custom_price_monthly: nullable(this.subscriptionCustomizationForm.custom_price_monthly), commission_rate: nullable(this.subscriptionCustomizationForm.commission_rate), grace_days: nullable(this.subscriptionCustomizationForm.grace_days), custom_pricing_notes: String(this.subscriptionCustomizationForm.custom_pricing_notes || '').trim() || null, requested_by_user_id: Number(this.subscriptionCustomizationForm.requested_by_user_id), apply_current_plan_price: this.subscriptionPriceApplication };
    if (!this.subscriptionRestorationPending() && (!Number.isInteger(payload.requested_by_user_id) || payload.requested_by_user_id < 1)) { this.setSubscriptionError('Selecciona quién solicitó el cambio.', 'requested_by_user_id'); return; }
    if ((payload.custom_max_spaces !== null && (!Number.isInteger(payload.custom_max_spaces) || payload.custom_max_spaces < 1)) || (payload.custom_price_monthly !== null && (!Number.isFinite(payload.custom_price_monthly) || payload.custom_price_monthly < 0))) { this.setSubscriptionError('Revisa la cantidad de espacios y el precio mensual.', 'amounts'); return; }
    const comparable = this.stableValue({ new_plan: payload.new_plan, custom_max_spaces: payload.custom_max_spaces, custom_price_monthly: payload.custom_price_monthly, custom_pricing_notes: payload.custom_pricing_notes });
    if (comparable === this.subscriptionCustomizationInitial && !this.subscriptionPriceApplication) {
      this.subscriptionCustomizationError.set('No hay cambios por guardar. Modifica plan, capacidad, precio mensual o condiciones.');
      this.subscriptionCustomizationField.set(null);
      this.notifications.warning('No se realizó ninguna actualización porque los datos no cambiaron.');
      return;
    }
    this.isSavingSubscriptionCustomization.set(true); this.subscriptionCustomizationError.set(''); this.subscriptionCustomizationField.set(null);
    try {
      const response = await this.dialogs.confirmAndRun(
        { title: this.subscriptionRestorationPending() ? 'Confirmar restauración' : 'Confirmar personalización', message: this.subscriptionRestorationPending() ? '¿Restauras esta suscripción a las condiciones estándar del plan?' : '¿Guardas esta personalización de la suscripción?', tone: 'warning', confirmText: this.subscriptionRestorationPending() ? 'Confirmar restauración' : 'Guardar cambios' },
        () => this.subscriptionService.updateSubscriptionCustomization(target.subscription.id, payload),
        'Personalización actualizada correctamente.',
        (error: any) => friendlyError(error, 'No fue posible guardar la personalización.'),
      );
      if (response !== null) {
        this.isSavingSubscriptionCustomization.set(false);
        this.closeSubscriptionCustomization();
        this.loadTabData('subs');
      }
    } catch (error: any) {
      const msg = friendlyError(error, 'No fue posible guardar la personalización.');
      this.subscriptionCustomizationError.set(msg);
      this.subscriptionCustomizationField.set(null);
      this.notifications.error(msg);
    } finally {
      this.isSavingSubscriptionCustomization.set(false);
    }
  }

  closeRenewalDateModal(): void { this.showRenewalDateModal.set(false); this.renewalDateTarget.set(null); }

  async saveRenewalDate(): Promise<void> {
    const item = this.renewalDateTarget();
    if (!item?.subscription?.id || !/^\d{4}-\d{2}-\d{2}$/.test(this.renewalDateInput)) {
      this.renewalDateError.set('Selecciona una fecha válida.');
      this.notifications.error('Selecciona una fecha válida para la renovación.');
      setTimeout(() => {
        const el = document.querySelector<HTMLElement>('.renewal-date-modal input[type="date"]');
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('field-error-flash');
        setTimeout(() => el.classList.remove('field-error-flash'), 1900);
      }, 30);
      return;
    }
    const isTrial = this.isTrialItem(item);
    const currentDate = (isTrial ? item.subscription.trial_ends_at : item.subscription.renewal_at) || '';
    const currentDateStr = currentDate ? String(currentDate).slice(0, 10) : '';
    if (currentDateStr === this.renewalDateInput) {
      this.renewalDateError.set(isTrial ? 'No hay cambios por guardar: la fecha seleccionada ya es el vencimiento actual de la prueba.' : 'No hay cambios por guardar: la fecha seleccionada ya es la fecha de renovación actual.');
      this.notifications.warning(isTrial ? 'La fecha de vencimiento de la prueba no cambió.' : 'La fecha de renovación no cambió.');
      return;
    }
    this.isSavingRenewalDate.set(true);
    this.renewalDateError.set('');
    try {
      const result = await this.dialogs.confirmAndRun(
        { title: isTrial ? 'Confirmar fecha de prueba' : 'Confirmar fecha de renovación', message: isTrial ? 'Se actualizará el vencimiento de la prueba de este negocio.' : 'Se actualizará la fecha de renovación de esta suscripción.', tone: 'warning', confirmText: isTrial ? 'Guardar fecha de prueba' : 'Guardar fecha' },
        () => this.subscriptionService.updateSubscriptionRenewalDate(item.subscription.id, this.renewalDateInput),
        isTrial ? 'Fecha de vencimiento de la prueba actualizada correctamente.' : 'Fecha de renovación actualizada correctamente.',
        (error: any) => friendlyError(error, 'No fue posible actualizar la fecha.'),
      );
      if (result !== null) {
        const subscription = result.subscription;
        this.subscriptions.update(items => items.map(current => current.subscription?.id === subscription.id ? { ...current, subscription } : current));
        this.closeRenewalDateModal();
      }
    } catch (error: any) {
      const msg = friendlyError(error, 'No fue posible actualizar la fecha.');
      this.renewalDateError.set(msg);
      this.notifications.error(msg);
    } finally {
      this.isSavingRenewalDate.set(false);
    }
  }

  subscriptionRevenueTotal(): number {
    return this.revenue().reduce((total, item) => total + Number(item.subscriptions || 0), 0);
  }

  exportPlatformAnalytics(): void {
    this.subscriptionService.exportPlatformAnalytics().subscribe({
      next: file => this.downloadFile(file, `reporte-superadmin-${this.fileDate()}.csv`),
      error: () => { const msg = 'No fue posible exportar el reporte de estadísticas.'; this.errorMsg.set(msg); this.notifications.error(msg); },
    });
  }

  exportPlatformAnalyticsPdf(): void {
    if (!this.cooldown.consume('sa-pdf-export')) return;
    this.subscriptionService.exportPlatformAnalyticsPdf().subscribe({
      next: file => this.downloadFile(file, `reporte-superadmin-${this.fileDate()}.pdf`),
      error: () => { const msg = 'No fue posible generar el reporte PDF.'; this.errorMsg.set(msg); this.notifications.error(msg); },
    });
  }

  private downloadFile(file: Blob, filename: string): void {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  downloadSubscriptionReceipt(payment: SubscriptionPayment): void {
    if (payment.payment_type !== 'subscription' || payment.status !== 'succeeded') return;
    this.subscriptionService.downloadAdminSubscriptionReceipt(payment.payment_id).subscribe({
      next: file => this.downloadFile(file, `comprobante-suscripcion-TSL-SUB-${String(payment.payment_id).padStart(8, '0')}.pdf`),
      error: () => { const msg = 'No fue posible descargar el comprobante del pago seleccionado.'; this.errorMsg.set(msg); this.notifications.error(msg); },
    });
  }

  private fileDate(): string { return new Date().toISOString().slice(0, 10); }

  prevPage(): void {
    if (!this.tabLoading() && this.currentPage() > 1) {
      this.currentPage.update(p => p - 1);
      this.loadTabData(this.activeTab());
    }
  }

  nextPage(): void {
    if (!this.tabLoading() && this.currentPage() < this.totalPages()) {
      this.currentPage.update(p => p + 1);
      this.loadTabData(this.activeTab());
    }
  }

  goToPage(page: number): void {
    if (this.tabLoading() || page < 1 || page > this.totalPages() || page === this.currentPage()) return;
    this.currentPage.set(page);
    this.loadTabData(this.activeTab());
  }

  private loadTabData(tab: string): void {
    this.tabLoading.set(true);
    const loadingStartedAt = Date.now();
    const page = this.currentPage();
    const requestId = ++this.tabRequestId;

    switch (tab) {
      case 'negocios':
        this.subscriptionService.getAllBusinesses(page, this.pageSize, this.searchTerm()).subscribe({
          next: (r) => {
            if (requestId !== this.tabRequestId) return;
            this.businesses.set(r.businesses);
            this.totalPages.set(r.pages);
            this.totalRecords.set(r.total);
            this.finishTabLoading(requestId, loadingStartedAt);
          },
          error: () => this.finishTabLoading(requestId, loadingStartedAt),
        });
        break;

      case 'owners':
        this.subscriptionService.getAllOwners(page, this.pageSize, this.searchTerm()).subscribe({
          next: (r) => {
            if (requestId !== this.tabRequestId) return;
            this.owners.set(r.owners);
            this.totalPages.set(r.pages);
            this.totalRecords.set(r.total);
            this.finishTabLoading(requestId, loadingStartedAt);
          },
          error: () => this.finishTabLoading(requestId, loadingStartedAt),
        });
        break;

      case 'subs':
        this.subscriptionService.getAllSubscriptions(page, this.pageSize, this.subsStatusFilter || undefined, this.subsPlanFilter || undefined, this.searchTerm()).subscribe({
          next: (r) => {
            if (requestId !== this.tabRequestId) return;
            this.subscriptions.set(r.subscriptions);
            this.totalPages.set(r.pages);
            this.totalRecords.set(r.total);
            this.finishTabLoading(requestId, loadingStartedAt);
          },
          error: () => this.finishTabLoading(requestId, loadingStartedAt),
        });
        break;

      case 'planes':
        this.subscriptionService.getAdminPlans().subscribe({
          next: (r) => {
            if (requestId !== this.tabRequestId) return;
            this.plans.set(r.plans); this.finishTabLoading(requestId, loadingStartedAt);
          },
          error: () => this.finishTabLoading(requestId, loadingStartedAt),
        });
        this.loadPlatformSettings();
        this.loadCommissionContracts();
        if (this.planesTab() === 'comision') this.loadCommissionHistory();
        break;

      case 'reciclaje':
        this.subscriptionService.getAllBusinesses(page, this.pageSize, this.searchTerm(), true).subscribe({
          next: (r) => {
            if (requestId !== this.tabRequestId) return;
            this.businesses.set(r.businesses); this.totalPages.set(r.pages); this.totalRecords.set(r.total); this.finishTabLoading(requestId, loadingStartedAt);
          },
          error: () => this.finishTabLoading(requestId, loadingStartedAt),
        });
        break;

      case 'users':
        this.subscriptionService.getAllUsers(page, this.pageSize, this.userRoleFilter || undefined, this.searchTerm()).subscribe({
          next: (r) => {
            if (requestId !== this.tabRequestId) return;
            this.users.set(r.users);
            this.totalPages.set(r.pages);
            this.totalRecords.set(r.total);
            this.finishTabLoading(requestId, loadingStartedAt);
          },
          error: () => this.finishTabLoading(requestId, loadingStartedAt),
        });
        break;

      case 'pagos':
        this.subscriptionService.getAllPayments(page, this.pageSize, 'subscription', this.searchTerm(), this.paymentStatusFilter || undefined).subscribe({
          next: (r) => {
            if (requestId !== this.tabRequestId || this.activeTab() !== 'pagos') return;
            this.allPayments.set(r.payments);
            this.totalPages.set(r.pages);
            this.totalRecords.set(r.total);
            this.pagesTotalAmount.set(r.totalAmount);
            this.paymentSummary.set(r.paymentSummary || { succeeded: { count: 0, amount: 0 }, pending: { count: 0, amount: 0 }, failed: { count: 0, amount: 0 } });
            this.finishTabLoading(requestId, loadingStartedAt);
          },
          error: () => this.finishTabLoading(requestId, loadingStartedAt),
        });
        break;

      case 'pagosNegocios':
        this.payouts.adminBusinesses(this.searchTerm(), page, this.pageSize).subscribe({
          next: (r) => {
            if (requestId !== this.tabRequestId || this.activeTab() !== 'pagosNegocios') return;
            this.payoutBusinesses.set(r.businesses || []);
            this.totalRecords.set(r.total || 0);
            this.totalPages.set(r.pages || 1);
            this.finishTabLoading(requestId, loadingStartedAt);
          },
          error: () => this.finishTabLoading(requestId, loadingStartedAt),
        });
        break;

      case 'estadisticas':
        this.subscriptionService.getAdminStats(true, this.globalStatsFrom, this.globalStatsTo).subscribe({
          next: stats => {
            if (requestId !== this.tabRequestId || this.activeTab() !== 'estadisticas') return;
            this.stats.set(stats);
            this.revenue.set(this.normalizeMonthlyRevenue(stats.revenue.monthly_subscriptions || []));
            this.ensureAnalyticsFocusVisible();
            this.finishTabLoading(requestId, loadingStartedAt);
          },
          error: err => { const msg = friendlyError(err, 'No fue posible cargar las estadísticas globales.'); this.errorMsg.set(msg); this.notifications.error(msg); this.finishTabLoading(requestId, loadingStartedAt); },
        });
        break;

      case 'seguridad':
        this.twoFactorStatusLoading.set(true);
        this.authService.getTwoFactorStatus().subscribe({
          next: (r) => {
            if (requestId !== this.tabRequestId) return;
            this.twoFactorStatus.set(r);
            this.twoFactorStatusLoading.set(false);
            this.finishTabLoading(requestId, loadingStartedAt);
          },
          error: (err) => {
            this.twoFactorStatusLoading.set(false);
            this.notifications.error(friendlyError(err, 'No fue posible consultar el estado de la verificación en dos pasos.'));
            this.finishTabLoading(requestId, loadingStartedAt);
          },
        });
        break;

      default:
        this.finishTabLoading(requestId, loadingStartedAt);
    }
  }

  // --- Seguridad (2FA) ---------------------------------------------------

  startTwoFactorSetup(): void {
    this.twoFactorSetupLoading.set(true);
    this.twoFactorVerifyError.set('');
    this.authService.setupTwoFactor().subscribe({
      next: (r) => {
        this.twoFactorSetupLoading.set(false);
        this.twoFactorSetupData.set({ secret: r.secret, qrCode: r.qrCode });
        this.twoFactorVerifyCode = '';
      },
      error: (err) => {
        this.twoFactorSetupLoading.set(false);
        this.notifications.error(friendlyError(err, 'No fue posible iniciar la configuración de 2FA.'));
      },
    });
  }

  cancelTwoFactorSetup(): void {
    this.twoFactorSetupData.set(null);
    this.twoFactorVerifyCode = '';
    this.twoFactorVerifyError.set('');
  }

  confirmTwoFactorSetup(): void {
    if (this.twoFactorVerifyCode.trim().length !== 6) return;
    this.twoFactorVerifyLoading.set(true);
    this.twoFactorVerifyError.set('');
    this.authService.verifyTwoFactor(this.twoFactorVerifyCode.trim()).subscribe({
      next: () => {
        this.twoFactorVerifyLoading.set(false);
        this.twoFactorSetupData.set(null);
        this.twoFactorStatus.set({ enabled: true });
        this.notifications.success('Verificación en dos pasos activada correctamente.');
      },
      error: (err) => {
        this.twoFactorVerifyLoading.set(false);
        const msg = friendlyError(err, 'Código inválido. Intenta de nuevo.');
        this.twoFactorVerifyError.set(msg);
        this.notifications.error(msg);
      },
    });
  }

  toggleDisableTwoFactorForm(): void {
    this.showDisableTwoFactorForm.set(!this.showDisableTwoFactorForm());
    this.disableTwoFactorPassword = '';
    this.disableTwoFactorCode = '';
    this.disableTwoFactorError.set('');
    this.showDisableTwoFactorPassword.set(false);
  }

  confirmDisableTwoFactor(): void {
    if (!this.disableTwoFactorPassword) return;
    this.disableTwoFactorLoading.set(true);
    this.disableTwoFactorError.set('');
    this.authService.disableTwoFactor(this.disableTwoFactorPassword, this.disableTwoFactorCode.trim() || undefined).subscribe({
      next: () => {
        this.disableTwoFactorLoading.set(false);
        this.showDisableTwoFactorForm.set(false);
        this.twoFactorStatus.set({ enabled: false });
        this.notifications.success('Verificación en dos pasos desactivada.');
      },
      error: (err) => {
        this.disableTwoFactorLoading.set(false);
        const msg = friendlyError(err, 'No fue posible desactivar la verificación en dos pasos.');
        this.disableTwoFactorError.set(msg);
        this.notifications.error(msg);
      },
    });
  }

  // Piso bajo (evita parpadeo del esqueleto en respuestas muy rápidas) en
  // vez de una espera artificial de segundos al cambiar de pestaña.
  private finishTabLoading(requestId: number, startedAt: number): void {
    const remaining = Math.max(0, 0 - (Date.now() - startedAt));
    setTimeout(() => {
      if (requestId === this.tabRequestId) this.tabLoading.set(false);
    }, remaining);
  }

  filterSubscriptionPlan(plan: string): void {
    this.subsPlanFilter = plan;
    this.currentPage.set(1);
    this.loadTabData('subs');
  }

  filterUsers(role: string): void {
    this.userRoleFilter = role;
    this.currentPage.set(1);
    this.loadTabData('users');
  }

  /**
   * Abrir un propietario lleva directo a su negocio ("Gestionar negocio"):
   * ahí están, tal como los ve el propietario, sus datos, accesos, espacios
   * y clientes. Sin negocio vinculado se abre la gestión de su cuenta.
   */
  openOwnerRecord(item: SuperAdminOwner): void {
    if (item.business?.id) this.openBusinessContext(item.business.id, item.business.name, item.owner.name);
    else this.openUserEditor(item.owner.user_id);
  }

  async toggleBusiness(item: any): Promise<void> {
    const business = item?.business;
    if (!business?.id) return;
    const action = business.status ? 'Desactivar' : 'Activar';
    try {
      const result = await this.dialogs.confirmAndRun(
        { title: `${action} negocio`, message: `Estás a punto de ${action.toLowerCase()} el negocio «${business.name}».`, tone: business.status ? 'danger' : 'info', confirmText: `Sí, ${action.toLowerCase()}` },
        () => this.subscriptionService.updateBusinessStatus(business.id, !business.status),
        `Negocio ${business.status ? 'desactivado' : 'activado'} correctamente.`,
        (error: any) => friendlyError(error, 'No fue posible actualizar el negocio.'),
      );
      if (result !== null) {
        const updated = result.business;
        this.owners.update(items => items.map(current => current.business?.id === updated.id ? { ...current, business: { ...current.business, ...updated }, owner: { ...current.owner, is_active: updated.status } } : current));
      }
    } catch (error: any) {
      const msg = friendlyError(error, 'No fue posible actualizar el negocio.');
      this.errorMsg.set(msg);
      this.notifications.error(msg);
    }
  }

  archiveOwnerBusiness(item: any): void {
    if (!item?.business?.id) return;
    this.archiveOwnerTarget.set(item);
    this.archiveOwnerStep.set(1);
    this.archiveOwnerPassword = '';
    this.archiveOwnerError.set('');
    this.showArchiveOwnerPassword.set(false);
  }
  continueOwnerArchive(): void { this.archiveOwnerStep.set(2); this.archiveOwnerError.set(''); }
  closeOwnerArchive(): void {
    if (this.archiveOwnerSaving()) return;
    this.archiveOwnerTarget.set(null); this.archiveOwnerStep.set(1); this.archiveOwnerPassword = ''; this.archiveOwnerError.set(''); this.showArchiveOwnerPassword.set(false);
  }
  confirmOwnerArchive(passwordInput?: HTMLInputElement): void {
    const target = this.archiveOwnerTarget();
    if (!target?.business?.id || this.archiveOwnerSaving()) return;
    if (!this.archiveOwnerPassword) {
      this.archiveOwnerError.set('Ingresa tu contraseña actual para confirmar la baja.');
      this.notifications.error('Ingresa tu contraseña actual para confirmar la baja.');
      if (passwordInput) {
        passwordInput.classList.add('field-error-flash');
        passwordInput.focus();
        setTimeout(() => passwordInput.classList.remove('field-error-flash'), 1900);
      }
      return;
    }
    this.archiveOwnerSaving.set(true); this.archiveOwnerError.set('');
    this.subscriptionService.archiveBusiness(target.business.id, this.archiveOwnerPassword).subscribe({
      next: response => {
        this.owners.update(items => items.filter(current => current.business?.id !== target.business.id));
        this.totalRecords.update(total => Math.max(0, total - 1));
        this.archiveOwnerSaving.set(false); this.closeOwnerArchive();
        this.notifications.show(response.msg || 'Propietario y negocio dados de baja correctamente.', response.notification_warning ? 'warning' : 'success', 5500, `business-archive:${target.business.id}`);
        this.subscriptionService.getAdminStats().subscribe({ next: stats => this.stats.set(stats), error: err => console.error('No fue posible refrescar las estadísticas globales:', err) });
      },
      error: err => {
        this.archiveOwnerSaving.set(false);
        this.archiveOwnerPassword = '';
        const message = friendlyError(err, 'No fue posible confirmar la baja.');
        this.closeOwnerArchive();
        this.notifications.error(message);
      },
    });
  }

  selectOwnersSection(tab: string): void {
    if (tab === 'eliminados') {
      this.searchTerm.set(this.archivedOwnersSearch || '');
    } else {
      this.archivedOwnersSearch = this.searchTerm().trim();
    }
    this.ownersSection.set(tab);
    this.loadOwnersSectionDataIfNeeded(tab);
    const selected = this.ownersSectionTabs.find((item) => item.id === tab);
    if (!selected || window.location.hash === `#${selected.hash}`) {
      return;
    }
    window.history.pushState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}#${selected.hash}`
    );
  }

  private syncOwnersSectionFromHash(): void {
    const hash = window.location.hash.replace(/^#/, '').trim().toLowerCase();
    const selected = this.ownersSectionTabs.find((item) => item.hash === hash);
    if (selected) {
      if (selected.id === 'eliminados') this.searchTerm.set(this.archivedOwnersSearch || '');
      this.ownersSection.set(selected.id);
      this.loadOwnersSectionDataIfNeeded(selected.id);
    }
  }

  private loadOwnersSectionDataIfNeeded(tab: string): void {
    if (tab === 'pendientes' && !this.pendingOwnerSignupsLoaded) this.loadPendingOwnerSignups();
    if (tab === 'eliminados' && !this.archivedOwnersLoaded) this.loadArchivedOwners(1);
  }

  searchArchivedOwners(): void {
    this.archivedOwnersSearch = this.archivedOwnersSearchDraft.trim();
    this.loadArchivedOwners(1);
  }
  changeArchivedOwnersPageSize(value: number): void {
    this.archivedOwnersPageSize = Number(value) || 10;
    this.loadArchivedOwners(1);
  }
  loadArchivedOwners(page = this.archivedOwnersPage()): void {
    this.archivedOwnersLoading.set(true);
    this.subscriptionService.getAllBusinesses(page, this.archivedOwnersPageSize, this.archivedOwnersSearch, true).subscribe({
      next: response => { this.archivedOwners.set(response.businesses || []); this.archivedOwnersPage.set(response.page || page); this.archivedOwnersPages.set(response.pages || 1); this.archivedOwnersTotal.set(response.total || 0); this.archivedOwnersLoading.set(false); this.archivedOwnersLoaded = true; },
      error: err => { this.archivedOwnersLoading.set(false); this.archivedOwnersLoaded = true; this.notifications.error(friendlyError(err, 'No fue posible consultar los propietarios eliminados.')); },
    });
  }
  requestOwnerRestore(business: any): void { if (business?.id && this.restoringBusinessId() === null) this.restoreOwnerTarget.set(business); }
  closeRestoreConfirmation(): void { if (this.restoringBusinessId() === null) this.restoreOwnerTarget.set(null); }
  confirmOwnerRestore(): void {
    const business = this.restoreOwnerTarget();
    if (!business?.id || this.restoringBusinessId() !== null) return;
    this.restoringBusinessId.set(business.id);
    this.subscriptionService.restoreBusiness(business.id).subscribe({
      next: response => {
        this.archivedOwners.update(items => items.filter(item => item.id !== business.id));
        this.archivedOwnersTotal.update(total => Math.max(0, total - 1));
        this.restoringBusinessId.set(null); this.restoreOwnerTarget.set(null);
        this.notifications.show(response.msg || 'Propietario y negocio reactivados correctamente.', response.notification_warning ? 'warning' : 'success', 5500, `business-restore:${business.id}`);
        this.subscriptionService.getAdminStats().subscribe({ next: stats => this.stats.set(stats), error: err => console.error('No fue posible refrescar las estadísticas globales:', err) });
        this.currentPage.set(1); this.loadTabData('owners');
      },
      error: err => { this.restoringBusinessId.set(null); this.notifications.error(friendlyError(err, 'No fue posible reactivar el propietario.')); },
    });
  }

  async restoreArchivedBusiness(business: any): Promise<void> {
    if (!business?.id) return;
    try {
      const result = await this.dialogs.confirmAndRun(
        { title: 'Restaurar negocio', message: `El negocio «${business.name}» volverá a estar visible para su propietario.`, tone: 'info', confirmText: 'Sí, restaurar' },
        () => this.subscriptionService.restoreBusiness(business.id),
        'Negocio restaurado correctamente.',
        (error: any) => friendlyError(error, 'No fue posible restaurar el negocio.'),
      );
      if (result !== null) this.businesses.update(items => items.filter(item => item.id !== business.id));
    } catch (error: any) {
      const msg = friendlyError(error, 'No fue posible restaurar el negocio.');
      this.errorMsg.set(msg);
      this.notifications.error(msg);
    }
  }
  async toggleUserStatus(user: any): Promise<void> {
    if (!user?.user_id) return;
    const action = user.is_active ? 'Desactivar' : 'Activar';
    try {
      const result = await this.dialogs.confirmAndRun(
        { title: `${action} usuario`, message: `Estás a punto de ${action.toLowerCase()} el acceso de «${user.name}» (${user.email}).`, tone: user.is_active ? 'danger' : 'info', confirmText: `Sí, ${action.toLowerCase()}` },
        () => this.subscriptionService.updateUserStatus(user.user_id, !user.is_active),
        `Usuario ${user.is_active ? 'desactivado' : 'activado'} correctamente.`,
        (error: any) => friendlyError(error, 'No fue posible actualizar el usuario.'),
      );
      if (result !== null) {
        const updated = result.user;
        this.users.update(items => items.map(item => item.user_id === updated.user_id ? { ...item, is_active: updated.is_active } : item));
      }
    } catch (error: any) {
      const msg = friendlyError(error, 'No fue posible actualizar el usuario.');
      this.errorMsg.set(msg);
      this.notifications.error(msg);
    }
  }
  openConfirmPaymentModal(item: any): void { this.confirmPaymentTarget.set(item); this.confirmPaymentForm = { payment_method: 'efectivo', payment_reference: '' }; this.confirmPaymentError.set(''); this.confirmPaymentStep.set('form'); this.showConfirmPaymentModal.set(true); }
  closeConfirmPaymentModal(): void { if (!this.isConfirmingPayment()) { this.showConfirmPaymentModal.set(false); this.confirmPaymentTarget.set(null); this.confirmPaymentError.set(''); this.confirmPaymentStep.set('form'); } }

  // Mismo criterio que el backend (custom_price_monthly ?? agreed_price_monthly
  // ?? precio vigente del plan) para que el monto mostrado en la revisión
  // coincida con el que realmente va a quedar registrado -- ver
  // confirmManualSubscriptionPayment en superadmin.controller.ts.
  confirmPaymentAmount(item: any): number {
    const subscription = item?.subscription;
    if (!subscription) return 0;
    if (subscription.custom_price_monthly != null) return Number(subscription.custom_price_monthly);
    if (subscription.agreed_price_monthly != null) return Number(subscription.agreed_price_monthly);
    const planPrice = this.plans().find(plan => plan.code === subscription.plan)?.price_monthly
      ?? subscription.planDefinition?.price_monthly;
    return Number(planPrice || 0);
  }

  confirmPaymentMethodLabel(): string {
    return ({ efectivo: 'Efectivo', transferencia: 'Transferencia', apay: 'Enlace APay', tarjeta: 'Tarjeta', otro: 'Otro' } as Record<string, string>)[this.confirmPaymentForm.payment_method] || this.confirmPaymentForm.payment_method;
  }

  // Valida el formulario y pasa a la etapa de revisión -- todavía NO envía
  // nada al backend. El comprobante deja de ser opcional aquí: el backend lo
  // acepta vacío y genera una referencia genérica de respaldo, pero para un
  // pago que un administrador certifica a mano hace falta que quede
  // documentado qué comprobante verificó.
  reviewConfirmPayment(): void {
    const target = this.confirmPaymentTarget();
    const allowedMethods = ['efectivo', 'transferencia', 'apay', 'tarjeta', 'otro'];
    if (!target?.subscription?.id || !allowedMethods.includes(this.confirmPaymentForm.payment_method)) {
      this.confirmPaymentError.set('Selecciona un método de pago válido.');
      return;
    }
    if (String(this.confirmPaymentForm.payment_reference || '').trim().length < 3) {
      this.confirmPaymentError.set('Indica la referencia o el número de comprobante que verificaste (mínimo 3 caracteres).');
      return;
    }
    this.confirmPaymentError.set('');
    this.confirmPaymentStep.set('review');
  }

  backToConfirmPaymentForm(): void {
    this.confirmPaymentStep.set('form');
    this.confirmPaymentError.set('');
  }

  async saveConfirmPayment(): Promise<void> {
    const target = this.confirmPaymentTarget();
    if (!target?.subscription?.id || !this.confirmPaymentForm.payment_method) { this.confirmPaymentError.set('Selecciona un método de pago válido.'); this.notifications.error('Selecciona un método de pago válido.'); return; }
    if (String(this.confirmPaymentForm.payment_reference || '').trim().length < 3) { this.confirmPaymentError.set('Indica la referencia o el número de comprobante que verificaste (mínimo 3 caracteres).'); this.confirmPaymentStep.set('form'); return; }
    this.isConfirmingPayment.set(true); this.confirmPaymentError.set('');
    try {
      const result = await this.dialogs.run(
        'Confirmando pago…',
        () => this.subscriptionService.confirmManualSubscriptionPayment(target.subscription.id, this.confirmPaymentForm),
        'Pago confirmado y renovación aplicada correctamente.',
        (error: any) => friendlyError(error, 'No fue posible confirmar el pago.'),
      );
      if (result !== null) {
        const subscription = result.subscription;
        this.subscriptions.update(items => items.map(item => item.subscription?.id === subscription.id ? { ...item, subscription } : item));
        this.isConfirmingPayment.set(false);
        this.closeConfirmPaymentModal();
        this.loadTabData('subs');
        this.subscriptionService.getAdminStats().subscribe({ next: stats => this.stats.set(stats), error: err => console.error('No fue posible refrescar las estadísticas globales:', err) });
      }
    } catch (error: any) {
      const msg = friendlyError(error, 'No fue posible confirmar el pago.');
      this.confirmPaymentError.set(msg);
      this.notifications.error(msg);
    } finally {
      this.isConfirmingPayment.set(false);
    }
  }

  editPlan(plan: any): void {
    this.editingPlanId = plan.plan_id;
    this.planForm = {
      name: plan.name || '', code: plan.code || '', description: plan.description || '', price_monthly: Number(plan.price_monthly || 0), original_price_monthly: Number(plan.price_monthly || 0), price_scope: 'preserve_existing', price_change_reason: '', is_active: plan.is_active ? 1 : 0,
      limits: { max_spaces: Number(plan.limits?.max_spaces ?? 0), max_images_per_space: Number(plan.limits?.max_images_per_space ?? 10), max_reservations_monthly: Number(plan.limits?.max_reservations_monthly ?? 100), max_customers: Number(plan.limits?.max_customers ?? 500), max_featured_spaces: Number(plan.limits?.max_featured_spaces ?? 0) },
      benefits: {
        extras: this.toFlag(plan.benefits?.extras), schedules: this.toFlag(plan.benefits?.schedules), pricing_tiers: this.toFlag(plan.benefits?.pricing_tiers),
        advanced_statistics: this.toFlag(plan.benefits?.advanced_statistics), reports_export: this.toFlag(plan.benefits?.reports_export), featured_listing: this.toFlag(plan.benefits?.featured_listing),
        personalized_site: this.toFlag(plan.benefits?.personalized_site), custom_site_url: this.toFlag(plan.benefits?.custom_site_url),
        private_spaces: this.toFlag(plan.benefits?.private_spaces),
      },
      trial_days: Number(this.platformSettingsForm.trial_days) || 7,
    };
    this.planTrialDaysInitial = plan.code === 'gratis' ? this.planForm.trial_days : null;
    const known = ['extras', 'schedules', 'pricing_tiers', 'advanced_statistics', 'reports_export', 'featured_listing', 'personalized_site', 'custom_site_url', 'private_spaces'];
    this.additionalPlanBenefits = Object.fromEntries(Object.entries(plan.benefits || {}).filter(([key]) => !known.includes(key)));
    this.planEditInitial = this.stableValue({
      name: this.planForm.name.trim(), code: this.planForm.code.trim(), description: this.planForm.description.trim(), price_monthly: Number(this.planForm.price_monthly),
      price_scope: 'preserve_existing',
      is_active: this.planForm.is_active === 1, limits: this.normalizedLimits(Number(this.planForm.limits.max_spaces), Number(this.planForm.limits.max_images_per_space)),
      benefits: { ...this.additionalPlanBenefits, ...this.planForm.benefits },
    });
    this.planFormError.set('');
    this.showPlanModal.set(true);
  }

  enabledBenefits(plan: any): string {
    const enabled = Object.entries(plan.benefits || {})
      .filter(([, value]) => this.isEnabledBenefit(value))
      .map(([key]) => this.benefitLabel(key));
    return enabled.length ? enabled.join(', ') : 'Sin beneficios adicionales';
  }

  closePlanModal(): void {
    if (this.isSavingPlan()) return;
    this.showPlanModal.set(false);
    this.planFormError.set('');
    this.editingPlanId = null;
  }

  setPlanBenefit(feature: string, enabled: boolean): void { this.planForm.benefits[feature] = enabled ? 1 : 0; }
  openPayoutBusiness(b:any):void{
    this.payoutModalTab.set('general');
    this.payoutReservationSearch.set('');
    this.payoutReservationExpanded.set(new Set());
    this.payoutReservationGroupsPage.set(1);
    this.payouts.adminDetail(b.business_id).subscribe({next:r=>{this.payoutDetail.set(r);this.businessTransferForm={amount:Number(r.balance.available)||0,payout_method_id:r.methods.find((m:any)=>m.is_primary)?.id||null,transfer_reference:'',notes:''}},error:e=>this.notifications.error(friendlyError(e,'No se pudo abrir el detalle financiero del negocio.'))});
    this.loadPayoutReservationGroups(b.business_id, 1);
  }
  primaryPayoutMethod():any{return this.payoutDetail()?.methods?.find((m:any)=>m.is_primary)||null}
  // Aclara de qué está hecho "Total pendiente" cuando incluye una
  // liquidación ya agrupada (en business_settlements, pending/partial, a un
  // % de comisión que pudo fijarse en OTRO momento) además de lo vigente
  // sin agrupar -- sin esto, "Total pendiente" y "TuSala debe al negocio
  // (periodo vigente)" pueden mostrar cifras distintas sin explicación
  // visible, aunque ambas sean correctas para lo que cada una describe.
  payoutPendingBreakdownLabel(): string | null {
    const balance = this.payoutDetail()?.balance;
    const inSettlement = Number(balance?.in_settlement || 0);
    if (inSettlement <= 0) return null;
    const fmt = (n: number) => `$${n.toFixed(2)}`;
    return `Incluye ${fmt(inSettlement)} ya agrupados en una liquidación pendiente de transferir, más ${fmt(Number(balance?.unallocated || 0))} del periodo vigente sin agrupar.`;
  }
  selectPayoutTab(id:string):void{if(id==='general'||id==='reservas'||id==='transferencias'||id==='deuda')this.payoutModalTab.set(id)}
  payoutSettlementPages():number{const d=this.payoutDetail();return Math.max(1,Math.ceil((d?.settlements_total||0)/(d?.settlements_limit||25)))}
  loadPayoutSettlementPage(page:number):void{const d=this.payoutDetail();if(!d)return;this.payouts.adminDetail(d.business.id,d.reservation_payments_page||1,d.reservation_payments_limit||25,page,d.settlements_limit||25).subscribe({next:r=>this.payoutDetail.set(r),error:e=>this.notifications.error(friendlyError(e,'No se pudo cargar la página de transferencias.'))})}
  trackPayoutBusiness(_index: number, business: any): number { return business.business_id; }
  trackPayoutReservationGroup(_index: number, group: AdminReservationPaymentGroup): string { return group.group_key; }
  trackPayoutReservationMember(_index: number, member: any): number { return member.payment_id; }
  loadPayoutReservationGroups(businessId: number, page: number): void {
    this.payoutReservationGroupsLoading.set(true);
    this.payouts.adminBusinessReservationPayments(businessId, page, this.payoutReservationGroupsLimit(), this.payoutReservationSearch().trim()).subscribe({
      next: r => {
        this.payoutReservationGroups.set(r.reservation_payments || []);
        this.payoutReservationGroupsTotal.set(r.reservation_payments_total || 0);
        this.payoutReservationGroupsPage.set(r.reservation_payments_page || 1);
        this.payoutReservationGroupsPages.set(r.reservation_payments_pages || 1);
        this.payoutReservationGroupsLoading.set(false);
      },
      error: e => {
        this.payoutReservationGroupsLoading.set(false);
        this.notifications.error(friendlyError(e, 'No se pudo cargar los pagos de reservas.'));
      },
    });
  }
  goToPayoutReservationPage(page: number): void {
    const d = this.payoutDetail();
    if (!d || this.payoutReservationGroupsLoading() || page < 1 || page > this.payoutReservationGroupsPages() || page === this.payoutReservationGroupsPage()) return;
    this.loadPayoutReservationGroups(d.business.id, page);
  }
  onPayoutReservationSearchChange(value: string): void { this.payoutReservationSearch.set(value); }
  applyPayoutReservationFilters(): void {
    const d = this.payoutDetail();
    if (!d) return;
    this.loadPayoutReservationGroups(d.business.id, 1);
  }
  resetPayoutReservationFilters(): void {
    const d = this.payoutDetail();
    if (!d) return;
    this.payoutReservationSearch.set('');
    this.loadPayoutReservationGroups(d.business.id, 1);
  }
  togglePayoutReservationGroup(groupKey: string): void {
    const next = new Set(this.payoutReservationExpanded());
    if (next.has(groupKey)) next.delete(groupKey);
    else next.add(groupKey);
    this.payoutReservationExpanded.set(next);
  }
  trackPayoutTransfer(_index: number, transfer: any): number | string { return transfer.id || `${transfer.transfer_reference || ''}:${transfer.performed_at}`; }
  businessesPendingTotal():number{return this.payoutBusinesses().reduce((sum:any,b:any)=>sum+Number(b.available_balance||0),0)}
  businessesWithBalance():number{return this.payoutBusinesses().filter((b:any)=>Number(b.available_balance)>0).length}
  // Corrección pedida: la lista "Pago a negocios" solo mostraba lo que
  // TuSala le debe al negocio (available_balance) -- nunca lo contrario
  // (debt_balance, comisión que el negocio le debe a TuSala). Son
  // direcciones de dinero OPUESTAS y deben verse claramente diferenciadas,
  // nunca mezcladas en el mismo total.
  businessesDebtTotal():number{return this.payoutBusinesses().reduce((sum:any,b:any)=>sum+Number(b.debt_balance||0),0)}
  businessesWithDebt():number{return this.payoutBusinesses().filter((b:any)=>Number(b.debt_balance)>0).length}
  subscriptionPaymentsTotal():number{return Object.values(this.paymentSummary()).reduce((sum,item)=>sum+Number(item.amount||0),0)}
  paymentStatusTabs():AdminTabOption[]{
    const s=this.paymentSummary();
    const row=(id:string)=>s[id]||{count:0,amount:0};
    return [
      {id:'succeeded',label:'Aprobados',icon:'bi-check-circle',description:`$${row('succeeded').amount.toFixed(2)} · ${this.subscriptionStatusPercent('succeeded')}% · ${row('succeeded').count} pagos`},
      {id:'pending',label:'Pendientes',icon:'bi-hourglass-split',description:`$${row('pending').amount.toFixed(2)} · ${this.subscriptionStatusPercent('pending')}% · ${row('pending').count} pagos`},
      {id:'failed',label:'Rechazados',icon:'bi-x-circle',description:`$${row('failed').amount.toFixed(2)} · ${this.subscriptionStatusPercent('failed')}% · ${row('failed').count} pagos`},
    ];
  }
  subscriptionStatusPercent(status:string):number{const total=this.subscriptionPaymentsTotal();return total?Math.round((Number(this.paymentSummary()[status]?.amount||0)/total)*100):0}
  selectPlanesTab(id:string):void{
    if(id!=='planes'&&id!=='comision')return;
    this.planesTab.set(id);
    if(id==='comision') this.loadCommissionHistory();
    const hash=this.planesTabHashes[id];
    if(!hash||window.location.hash===`#${hash}`)return;
    window.history.pushState(window.history.state,'',`${window.location.pathname}${window.location.search}#${hash}`);
  }
  private syncPlanesTabFromHash():void{
    const hash=window.location.hash.replace(/^#/,'').trim().toLowerCase();
    const entry=Object.entries(this.planesTabHashes).find(([,h])=>h===hash);
    if(entry&&entry[0]!==this.planesTab()){
      this.planesTab.set(entry[0] as 'planes'|'comision');
      if(entry[0]==='comision'&&this.activeTab()==='planes')this.loadCommissionHistory();
    }
  }
  loadPlatformSettings():void{
    this.platformSettingsService.get().subscribe({
      next:(r)=>{this.platformSettingsForm={...r.settings};},
      error:()=>this.notifications.error('No se pudo cargar la configuración de comisión y prueba.'),
    });
  }
  commissionRateInvalid = signal(false);
  @ViewChild('commissionRateInput') commissionRateInput!: ElementRef<HTMLInputElement>;
  @ViewChild('graceDaysInput') graceDaysInput!: ElementRef<HTMLInputElement>;

  // Mismo patrón exacto que saveGlobalCommission -- acción independiente,
  // nunca mezclada con la de comisión (esa separación ya se corrigió antes
  // por el mismo motivo, ver el comentario junto a saveGlobalCommission).
  async saveGlobalGraceDays(): Promise<void> {
    if (!this.cooldown.consume('sa-grace-save')) return;
    const days = Number(this.platformSettingsForm.grace_days);
    if (!Number.isInteger(days) || days < 0 || days > 365) {
      this.graceDaysInvalid.set(true);
      this.notifications.warning('Indica un número de días de gracia válido (0-365).');
      this.flashInvalidField(this.graceDaysInput);
      return;
    }
    this.graceDaysInvalid.set(false);
    this.savingGraceDays.set(true);
    try {
      const r = await this.dialogs.confirmAndRun(
        { title: 'Confirmar periodo de gracia', message: `Se aplicarán ${days} día(s) de gracia por defecto a las suscripciones que nunca fueron personalizadas manualmente. Las que ya tienen su propio valor asignado no cambian.`, tone: 'warning', confirmText: 'Guardar periodo de gracia' },
        () => this.platformSettingsService.update({ grace_days: days }),
        'Periodo de gracia actualizado correctamente.',
        (error: any) => friendlyError(error, 'No se pudo actualizar el periodo de gracia.'),
      );
      if (r !== null) {
        this.platformSettingsForm = { ...this.platformSettingsForm, ...r.settings };
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.savingGraceDays.set(false);
    }
  }

  private flashInvalidField(el?: ElementRef<HTMLElement>): void {
    const target = el?.nativeElement;
    if (!target) return;
    setTimeout(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('field-error-flash');
      target.focus({ preventScroll: true });
      setTimeout(() => target.classList.remove('field-error-flash'), 1900);
    }, 30);
  }

  // Bug real corregido: antes, tanto "Guardar comisión" (tasa global, arriba)
  // como "Aplicar comisión a seleccionados" (abajo) llamaban al mismo método,
  // que SIEMPRE enviaba `affected_business_ids` con lo que estuviera marcado
  // en la sección de contratos por negocio -- guardar la tasa global de
  // arriba aplicaba de paso a negocios marcados de una exploración anterior,
  // y viceversa, aplicar a seleccionados también sobrescribía en silencio la
  // tasa por defecto para negocios nuevos. Ahora son dos métodos separados,
  // cada uno enviando solo lo que su propio botón promete.
  async saveGlobalCommission():Promise<void>{
    if (!this.cooldown.consume('sa-commission-save')) return;
    const rate=Number(this.platformSettingsForm.commission_rate);
    if(!Number.isFinite(rate)||rate<0||rate>100){
      this.commissionRateInvalid.set(true);
      this.notifications.warning('Indica un porcentaje de comisión válido (0-100).');
      this.flashInvalidField(this.commissionRateInput);
      return;
    }
    this.commissionRateInvalid.set(false);
    this.savingPlatformSettings.set(true);
    try {
      const r = await this.dialogs.confirmAndRun(
        { title: 'Confirmar comisión', message: `Se aplicará ${rate}% de comisión a los negocios NUEVOS que se registren de aquí en adelante. Los negocios ya existentes conservan la comisión que tenían.`, tone: 'warning', confirmText: 'Guardar comisión' },
        () => this.platformSettingsService.update({ commission_rate: rate }),
        'Comisión global actualizada correctamente.',
        (error: any) => friendlyError(error, 'No se pudo actualizar la comisión.'),
      );
      if (r !== null) {
        this.platformSettingsForm={...this.platformSettingsForm,...r.settings};
        this.loadCommissionHistory();
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.savingPlatformSettings.set(false);
    }
  }
  async applyCommissionToSelected():Promise<void>{
    if (!this.selectedCommissionBusinessIds.size) return;
    if (!this.cooldown.consume('sa-commission-apply-selected')) return;
    const rate=Number(this.platformSettingsForm.commission_rate);
    if(!Number.isFinite(rate)||rate<0||rate>100){
      this.commissionRateInvalid.set(true);
      this.notifications.warning('Indica un porcentaje de comisión válido (0-100) en el campo "Comisión (%)" de arriba.');
      this.flashInvalidField(this.commissionRateInput);
      return;
    }
    this.commissionRateInvalid.set(false);
    const count = this.selectedCommissionBusinessIds.size;
    this.savingPlatformSettings.set(true);
    try {
      const r = await this.dialogs.confirmAndRun(
        { title: 'Aplicar comisión a negocios seleccionados', message: `Se fijará ${rate}% de comisión SOLO para ${count} negocio(s) seleccionado(s). La tasa por defecto para negocios nuevos no cambia.`, tone: 'warning', confirmText: 'Aplicar comisión' },
        () => this.platformSettingsService.update({ commission_rate: rate, affected_business_ids: [...this.selectedCommissionBusinessIds] }),
        'Comisión aplicada a los negocios seleccionados.',
        (error: any) => friendlyError(error, 'No se pudo aplicar la comisión a los negocios seleccionados.'),
      );
      if (r !== null) {
        this.clearCommissionBusinesses();
        this.loadCommissionContracts();
        this.loadCommissionHistory();
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.savingPlatformSettings.set(false);
    }
  }
  loadCommissionContracts(search = this.commissionBusinessSearch): void {
      this.platformSettingsService.getCommissionContracts(search).subscribe({
        next: response => this.commissionContracts.set(response.businesses || []),
        error: () => this.notifications.error('No se pudieron cargar las comisiones por negocio.'),
      });
    }
    toggleCommissionBusiness(id: number, selected: boolean): void {
      if (selected) this.selectedCommissionBusinessIds.add(id);
      else this.selectedCommissionBusinessIds.delete(id);
    }
    selectAllCommissionBusinesses(): void {
      this.commissionContracts().forEach(business => this.selectedCommissionBusinessIds.add(business.id));
    }
    clearCommissionBusinesses(): void {
      this.selectedCommissionBusinessIds.clear();
    }
    openSubscriptionContractHistory(item: any): void {
      const id = Number(item?.subscription?.id);
      if (!Number.isInteger(id) || id < 1) return;
      this.subscriptionHistoryTarget.set(item);
      this.subscriptionContractHistory.set([]);
      this.subscriptionContractHistoryLoading.set(true);
      this.showSubscriptionHistoryModal.set(true);
      this.subscriptionService.getSubscriptionContractHistory(id).subscribe({
        next: response => {
          this.subscriptionContractHistory.set(response.history || []);
          this.subscriptionContractHistoryLoading.set(false);
        },
        error: error => {
          this.subscriptionContractHistoryLoading.set(false);
          this.notifications.error(friendlyError(error, 'No se pudo cargar el historial contractual.'));
        },
      });
    }
  closeSubscriptionContractHistory(): void {
      this.showSubscriptionHistoryModal.set(false);
      this.subscriptionHistoryTarget.set(null);
      this.subscriptionContractHistory.set([]);
  }
  openPlanHistory(plan: any): void {
      const id = Number(plan?.plan_id);
      if (!Number.isInteger(id) || id < 1) return;
      this.planHistoryTarget.set(plan);
      this.planHistory.set([]);
      this.planHistoryLoading.set(true);
      this.showPlanHistoryModal.set(true);
      this.subscriptionService.getPlanHistory(id).subscribe({
        next: response => {
          this.planHistory.set(response.history || []);
          this.planHistoryLoading.set(false);
        },
        error: error => {
          this.planHistoryLoading.set(false);
          this.notifications.error(friendlyError(error, 'No se pudo cargar el histórico del plan.'));
        },
      });
  }
  closePlanHistory(): void {
      this.showPlanHistoryModal.set(false);
      this.planHistoryTarget.set(null);
      this.planHistory.set([]);
  }
  planHistoryActionLabel(action: string | null | undefined, snapshot?: any): string {
      // "updated" cubre CUALQUIER edición del plan (precio, límites,
      // beneficios, nombre...), no solo el precio -- antes decía siempre
      // "Precio actualizado" aunque el cambio real fuera otro. Solo se
      // etiqueta como cambio de precio cuando el snapshot realmente trae
      // `price_change` (ver updatePlan en plan.controller.ts).
      if (String(action || '').toLowerCase() === 'updated') {
        return snapshot?.price_change ? 'Precio actualizado' : 'Plan actualizado';
      }
      const labels: Record<string, string> = { created: 'Plan creado', activated: 'Plan reactivado', archived: 'Plan archivado' };
      return labels[String(action || '').toLowerCase()] || 'Cambio de plan';
  }
  subscriptionHistoryActionLabel(action: string | null | undefined): string {
      const labels: Record<string, string> = {
        created: 'Contratación',
        contract_initialized: 'Precio contractual establecido',
        plan_changed: 'Cambio de plan',
        price_changed: 'Cambio de precio',
        customized: 'Personalización',
      };
      return labels[String(action || '').toLowerCase()] || 'Actualización contractual';
  }
  loadCommissionHistory():void{
    this.commissionHistoryLoading.set(true);
    this.platformSettingsService.getCommissionHistory().subscribe({
      next:(r)=>{this.commissionHistoryLoading.set(false);this.commissionHistory.set(r.changes||[]);},
      error:()=>{this.commissionHistoryLoading.set(false);this.notifications.error('No se pudo cargar el historial de comisiones.');},
    });
  }
  openCommissionChangeDetail(change:any):void{
    this.commissionChangeDetail.set(change);
  }
  closeCommissionChangeDetail():void{
    this.commissionChangeDetail.set(null);
  }
  private readonly paymentStatusHashes:Record<string,string>={succeeded:'pagos-aprobados',pending:'pagos-pendientes',failed:'pagos-rechazados'};
  selectPaymentStatusTab(status:string):void{
    this.filterPaymentStatus(status);
    const hash=this.paymentStatusHashes[status];
    if(!hash||window.location.hash===`#${hash}`)return;
    window.history.pushState(window.history.state,'',`${window.location.pathname}${window.location.search}#${hash}`);
  }
  private syncPaymentStatusFromHash():void{
    const hash=window.location.hash.replace(/^#/,'').trim().toLowerCase();
    const entry=Object.entries(this.paymentStatusHashes).find(([,h])=>h===hash);
    if(entry&&entry[0]!==this.paymentStatusFilter){
      this.paymentStatusFilter=entry[0];
      if(this.activeTab()==='pagos')this.loadTabData('pagos');
    }
  }
  async recordBusinessTransfer():Promise<void>{
    if (!this.cooldown.consume('sa-transfer-save')) return;
    const d=this.payoutDetail();
    if(!d){this.notifications.warning('Selecciona un negocio antes de registrar la transferencia.');return}
    const amount=Number(this.businessTransferForm.amount);
    if(!amount||amount<=0||amount>Number(d.balance.available)||!this.businessTransferForm.payout_method_id){this.notifications.warning('Indica un monto válido y selecciona el destino utilizado.');return}
    this.payoutTransferSaving.set(true);
    try {
      // Corrección pedida por el usuario: esto es dinero saliendo de TuSala
      // HACIA el negocio (nosotros les pagamos a ellos) -- a diferencia de un
      // pago que el cliente/negocio nos hace a nosotros (donde el propio
      // formulario ya es la confirmación), aquí sí debe preguntarse antes de
      // ejecutar. dialogs.run() disparaba la transferencia de inmediato al
      // enviar el formulario, sin ningún "¿estás seguro?" -- ahora usa
      // confirmAndRun().
      const response:any = await this.dialogs.confirmAndRun(
        {
          title: 'Confirmar transferencia',
          message: `¿Confirmas que ya transferiste USD ${amount.toFixed(2)} a ${d.business.company || d.business.name}? Esta acción registra el pago como realizado y descuenta el saldo pendiente -- no se puede deshacer.`,
          tone: 'warning',
          confirmText: 'Sí, ya transferí este monto',
        },
        () => this.payouts.recordBusinessTransfer(d.business.id,this.businessTransferForm),
        'Transferencia externa registrada y saldo actualizado.',
        (error: any) => friendlyError(error, 'No se pudo registrar la transferencia externa.'),
      );
      if (response !== null) {
        this.openPayoutBusiness({business_id:d.business.id});
        this.payouts.adminBusinesses(this.searchTerm(), this.currentPage(), this.pageSize).subscribe({next:r=>{this.payoutBusinesses.set(r.businesses||[]);this.totalRecords.set(r.total||0);this.totalPages.set(r.pages||1);}});
        this.noveltyService.refreshGlobalCount().subscribe({error:()=>{}});
        this.noveltyService.refreshGlobalPaymentCount().subscribe({error:()=>{}});
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.payoutTransferSaving.set(false);
    }
  }
  hasPlanPriceChange(): boolean { return Number(this.planForm.price_monthly) !== Number(this.planForm.original_price_monthly); }

  @ViewChild('planEditorForm') planEditorForm!: NgForm;
  @ViewChild('planEditorFormEl') planEditorFormEl!: ElementRef<HTMLFormElement>;

  async savePlan(): Promise<void> {
    if (!this.editingPlanId) return;
    if (!validateAndFocusFirstError(this.planEditorForm, this.planEditorFormEl?.nativeElement, msg => this.notifications.error(msg), 'Completa correctamente los campos marcados antes de guardar el plan.')) return;
    const price = Number(this.planForm.price_monthly);
    const maxSpaces = Number(this.planForm.limits.max_spaces);
    const maxImages = Number(this.planForm.limits.max_images_per_space);
    const priceScope = this.planForm.price_scope || 'preserve_existing';
    const priceChangeReason = String(this.planForm.price_change_reason || '').trim();
    const payload = {
      name: this.planForm.name.trim(), code: this.planForm.code.trim(), description: this.planForm.description.trim(), price_monthly: price,
      price_scope: priceScope, price_change_reason: priceScope === 'apply_existing' ? priceChangeReason : undefined,
      is_active: this.planForm.is_active === 1, limits: this.normalizedLimits(maxSpaces, maxImages),
      benefits: { ...this.additionalPlanBenefits, ...this.planForm.benefits },
    };
    const isGratis = this.planForm.code === 'gratis';
    const trialDays = Number(this.planForm.trial_days);
    const trialDaysChanged = isGratis && this.planTrialDaysInitial !== null && Number.isInteger(trialDays) && trialDays > 0 && trialDays !== this.planTrialDaysInitial;
    const planChanged = this.stableValue(payload) !== this.planEditInitial;
    if (!planChanged && !trialDaysChanged) {
      this.planFormError.set('No hay cambios por guardar en la configuración del plan.');
      this.notifications.warning('La configuración del plan no cambió.');
      return;
    }
    this.isSavingPlan.set(true);
    try {
      const result: any = await this.dialogs.confirmAndRun(
        { title: 'Confirmar cambios del plan', message: this.hasPlanPriceChange() ? 'El precio del plan cambió. Revisa el alcance seleccionado antes de guardar.' : '¿Guardas los cambios de este plan?', tone: this.hasPlanPriceChange() ? 'warning' : 'info', confirmText: 'Guardar cambios' },
        async () => {
          const planResult = planChanged ? await firstValueFrom(this.subscriptionService.updatePlan(this.editingPlanId!, payload)) : null;
          if (trialDaysChanged) {
            const settingsResult = await firstValueFrom(this.platformSettingsService.update({ trial_days: trialDays }));
            this.platformSettingsForm = { ...this.platformSettingsForm, ...settingsResult.settings };
          }
          return { plan: planResult?.plan ?? null };
        },
        'Plan actualizado correctamente.',
        (error: any) => friendlyError(error, 'No fue posible guardar los cambios del plan.'),
      );
      if (result !== null) {
        if (result.plan) {
          const updated = result.plan;
          this.plans.update(items => items.map(item => item.plan_id === updated.plan_id ? updated : item));
        }
        this.isSavingPlan.set(false);
        this.closePlanModal();
      }
    } catch (error: any) {
      const msg = friendlyError(error, 'No fue posible guardar los cambios del plan.');
      this.planFormError.set(msg);
      this.notifications.error(msg);
    } finally {
      this.isSavingPlan.set(false);
    }
  }

  private normalizedLimits(maxSpaces: number, maxImages: number): Record<string, number> { const source = this.planForm.limits; const positive = ['max_reservations_monthly', 'max_customers']; const result: Record<string, number> = { max_spaces: maxSpaces, max_images_per_space: maxImages }; for (const key of positive) { const value = Number(source[key]); result[key] = Number.isInteger(value) && value > 0 ? value : 1; } const featuredEnabled = this.planForm.benefits.featured_listing === 1; const featuredValue = Number(source['max_featured_spaces']); result['max_featured_spaces'] = featuredEnabled ? (Number.isInteger(featuredValue) && featuredValue > 0 ? featuredValue : 1) : 0; return result; }
  private nullableValue(value: unknown): number | null { return value === '' || value === null || value === undefined ? null : Number(value); }
  private stableValue(value: unknown): string {
    const normalize = (entry: any): any => Array.isArray(entry)
      ? entry.map(normalize)
      : entry && typeof entry === 'object'
        ? Object.keys(entry).sort().reduce((result: Record<string, any>, key) => { result[key] = normalize(entry[key]); return result; }, {})
        : entry;
    return JSON.stringify(normalize(value));
  }
  private emptyPlanForm(): any { return { name: '', code: '', description: '', price_monthly: 0, original_price_monthly: 0, price_scope: 'preserve_existing', price_change_reason: '', is_active: 1, limits: { max_spaces: 0, max_images_per_space: 10, max_reservations_monthly: 100, max_customers: 500, max_featured_spaces: 0 }, benefits: { extras: 0, schedules: 0, pricing_tiers: 0, advanced_statistics: 0, reports_export: 0, featured_listing: 0, personalized_site: 0, custom_site_url: 0, private_spaces: 0 } }; }
  private emptyManualUserForm(): any {
    return {
      name: '', email: '', phone_number: '', role: 'owner', business_name: '', business_phone: '', plan: '',
      // Corrección pedida: dar de alta un propietario de forma manual no
      // tenía forma de registrar un pago ya recibido por otro medio (efectivo,
      // transferencia, etc.) -- igual que payment_origin:'external' en una
      // reserva, este alta ahora admite marcarlo aquí mismo.
      external_payment_enabled: false, external_payment_method: 'efectivo', external_payment_reference: '',
      // Pedido explícito: un pago externo siempre debe quedar respaldado con
      // el comprobante subido (foto o PDF) -- mismo patrón que receipts en
      // reservas.component.ts (subida inmediata, se acumulan referencias
      // aquí y se envían junto con el resto del alta).
      external_payment_receipts: [] as { file_name: string; original_name: string; file_type: string; mime: string }[],
      custom_price_monthly: '', commission_rate: '', grace_days: '',
    };
  }

  isUploadingManualUserReceipt = signal(false);

  onManualUserReceiptFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    input.value = '';
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      this.notifications.error('Solo se permiten imágenes (JPG, PNG, WEBP) o PDF.');
      return;
    }
    this.isUploadingManualUserReceipt.set(true);
    this.subscriptionService.uploadManualUserReceipt(file).subscribe({
      next: (res) => {
        this.manualUserForm.external_payment_receipts.push({
          file_name: res.file_name,
          original_name: res.original_name || file.name,
          file_type: res.file_type,
          mime: res.mime,
        });
        this.isUploadingManualUserReceipt.set(false);
      },
      error: (error: any) => {
        this.notifications.error(friendlyError(error, 'No fue posible subir el comprobante.'));
        this.isUploadingManualUserReceipt.set(false);
      },
    });
  }

  removeManualUserReceipt(index: number): void {
    this.manualUserForm.external_payment_receipts.splice(index, 1);
  }

  readonly externalPaymentMethods = [
    { value: 'efectivo', label: 'Efectivo' },
    { value: 'transferencia', label: 'Transferencia bancaria' },
    { value: 'apay', label: 'APay' },
    { value: 'tarjeta', label: 'Tarjeta' },
    { value: 'otro', label: 'Otro' },
  ];
  private toFlag(value: unknown): number { return this.isEnabledBenefit(value) ? 1 : 0; }
  private isEnabledBenefit(value: unknown): boolean { return value === true || value === 1 || value === '1' || value === 'true'; }
  private benefitLabel(key: string): string {
    const labels: Record<string, string> = {
      extras: 'Servicios adicionales', schedules: 'Horarios configurables', pricing_tiers: 'Tarifas por duración',
      advanced_statistics: 'Estadísticas avanzadas', advance_statistics: 'Estadísticas avanzadas', advanced_statics: 'Estadísticas avanzadas', advance_statics: 'Estadísticas avanzadas',
      reports_export: 'Exportación de reportes', featured_listing: 'Publicación destacada', priority_support: 'Soporte prioritario',
      executive_support: 'Soporte ejecutivo', automated_confirmations: 'Confirmaciones automáticas', recurring_reservations: 'Reservas recurrentes',
      api_access: 'Acceso a la API', webhooks: 'Integraciones y automatizaciones', custom_branding: 'Identidad visual personalizada', custom_domain: 'Dominio personalizado',
      personalized_site: 'Sitio web personalizado', custom_site_url: 'URL propia para tu sitio', private_spaces: 'Espacios/negocio privados',
    };
    return labels[key] || key.replace(/[_-]+/g, ' ').replace(/\bapi\b/gi, 'API').replace(/\bvip\b/gi, 'VIP').replace(/\b\w/g, char => char.toUpperCase());
  }

  loadPendingOwnerSignups(): void {
    this.pendingOwnerSignupsLoading.set(true);
    this.subscriptionService.listPendingOwnerSignups().subscribe({
      next: r => { this.pendingOwnerSignups.set(r.items); this.pendingOwnerSignupsLoaded = true; this.pendingOwnerSignupsLoading.set(false); },
      error: () => { this.pendingOwnerSignups.set([]); this.pendingOwnerSignupsLoaded = true; this.pendingOwnerSignupsLoading.set(false); },
    });
  }

  // Filtro en el cliente: el volumen de altas manuales pendientes de pago es
  // siempre chico (son creadas una por una por un super admin), así que no
  // amerita paginación ni búsqueda del lado del servidor — reutiliza el
  // mismo campo de búsqueda del encabezado compartido.
  filteredPendingOwnerSignups(): PendingOwnerSignup[] {
    const term = this.searchTerm().trim().toLowerCase();
    const items = this.pendingOwnerSignups();
    if (!term) return items;
    return items.filter(p =>
      p.full_name.toLowerCase().includes(term)
      || p.company.toLowerCase().includes(term)
      || p.email.toLowerCase().includes(term)
      || (p.phone || '').toLowerCase().includes(term)
    );
  }

  async resendPendingOwnerLink(item: { id: number; full_name: string; email: string }): Promise<void> {
    this.resendingPendingSignupId.set(item.id);
    try {
      await this.dialogs.confirmAndRun(
        { title: 'Reenviar enlace de pago', message: `Se enviará un correo con el enlace de pago a ${item.email}.`, tone: 'info', confirmText: 'Reenviar enlace' },
        () => this.subscriptionService.resendOwnerPaymentLink(item.id),
        (result: any) => ({ tone: 'success', message: result?.msg || 'Enlace de pago reenviado correctamente.' }),
        (error: any) => friendlyError(error, 'No fue posible reenviar el enlace de pago.'),
      );
    } finally {
      this.resendingPendingSignupId.set(null);
    }
  }

  // Igual que copyPaymentLink en reservas.component.ts: respaldo por si el
  // propietario tiene problemas para recibir el correo -- copia el enlace
  // vigente para compartirlo a mano (WhatsApp u otra red), sin enviar correo.
  async copyPendingOwnerLink(item: { id: number }): Promise<void> {
    if (this.copyingPendingSignupId() === item.id) return;
    this.copyingPendingSignupId.set(item.id);
    try {
      const response = await firstValueFrom(this.subscriptionService.getPendingOwnerPaymentLink(item.id));
      if (!response?.paymentLinkUrl) {
        this.notifications.error('No fue posible obtener el enlace de pago.');
        return;
      }
      try {
        await navigator.clipboard.writeText(response.paymentLinkUrl);
        this.notifications.success('Enlace copiado al portapapeles.');
      } catch {
        this.notifications.error(`No fue posible copiar automáticamente. Enlace: ${response.paymentLinkUrl}`);
      }
    } catch (error: any) {
      this.notifications.error(friendlyError(error, 'No fue posible obtener el enlace de pago.'));
    } finally {
      this.copyingPendingSignupId.set(null);
    }
  }

  @ViewChild('pendingEditFormRef') pendingEditFormRef!: NgForm;
  @ViewChild('pendingEditFormEl') pendingEditFormEl!: ElementRef<HTMLFormElement>;

  openEditPendingSignup(item: PendingOwnerSignup): void {
    this.editingPendingSignup.set(item);
    this.editPendingSignupError.set('');
    this.pendingEditForm = {
      full_name: item.full_name,
      email: item.email,
      business_email: item.business_email || '',
      phone: item.phone || '',
      company: item.company,
      plan: item.plan,
      custom_price_monthly: item.custom_price_monthly ?? '',
      commission_rate: item.commission_rate ?? '',
      grace_days: item.grace_days ?? '',
    };
  }

  closeEditPendingSignup(): void {
    if (this.isSavingPendingEdit()) return;
    this.editingPendingSignup.set(null);
    this.editPendingSignupError.set('');
  }

  async saveEditPendingSignup(): Promise<void> {
    const item = this.editingPendingSignup();
    if (!item) return;
    if (!validateAndFocusFirstError(this.pendingEditFormRef, this.pendingEditFormEl?.nativeElement, msg => this.notifications.error(msg), 'Completa correctamente los campos marcados antes de guardar.')) return;
    this.isSavingPendingEdit.set(true);
    this.editPendingSignupError.set('');
    try {
      const form = this.pendingEditForm;
      const response = await firstValueFrom(this.subscriptionService.editPendingOwnerSignup(item.id, {
        full_name: form.full_name.trim(),
        company: form.company.trim(),
        email: form.email.trim(),
        business_email: form.business_email?.trim() || undefined,
        phone: form.phone?.trim() || undefined,
        plan: form.plan,
        custom_price_monthly: form.custom_price_monthly,
        commission_rate: form.commission_rate,
        grace_days: form.grace_days,
      }));
      this.pendingOwnerSignups.update(items => items.map(p => p.id === item.id ? response.item : p));
      this.notifications.success(response.msg || 'Cuenta pendiente actualizada correctamente.');
      this.editingPendingSignup.set(null);
    } catch (error: any) {
      const msg = friendlyError(error, 'No fue posible actualizar la cuenta pendiente.');
      this.editPendingSignupError.set(msg);
      this.notifications.error(msg);
    } finally {
      this.isSavingPendingEdit.set(false);
    }
  }

  @ViewChild('pendingPayFormRef') pendingPayFormRef!: NgForm;
  @ViewChild('pendingPayFormEl') pendingPayFormEl!: ElementRef<HTMLFormElement>;

  openConfirmPendingExternalPayment(item: PendingOwnerSignup): void {
    this.confirmingPendingExternalPayment.set(item);
    this.confirmPendingPaymentError.set('');
    this.pendingExternalPaymentForm = { method: 'efectivo', reference: '', receipts: [] };
  }

  closeConfirmPendingExternalPayment(): void {
    if (this.isConfirmingPendingPayment()) return;
    this.confirmingPendingExternalPayment.set(null);
    this.confirmPendingPaymentError.set('');
  }

  onPendingExternalPaymentReceiptFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    input.value = '';
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      this.notifications.error('Solo se permiten imágenes (JPG, PNG, WEBP) o PDF.');
      return;
    }
    this.isUploadingPendingReceipt.set(true);
    this.subscriptionService.uploadManualUserReceipt(file).subscribe({
      next: (res) => {
        this.pendingExternalPaymentForm.receipts.push({
          file_name: res.file_name,
          original_name: res.original_name || file.name,
          file_type: res.file_type,
          mime: res.mime,
        });
        this.isUploadingPendingReceipt.set(false);
      },
      error: (error: any) => {
        this.notifications.error(friendlyError(error, 'No fue posible subir el comprobante.'));
        this.isUploadingPendingReceipt.set(false);
      },
    });
  }

  removePendingExternalPaymentReceipt(index: number): void {
    this.pendingExternalPaymentForm.receipts.splice(index, 1);
  }

  async submitConfirmPendingExternalPayment(): Promise<void> {
    const item = this.confirmingPendingExternalPayment();
    if (!item) return;
    if (!validateAndFocusFirstError(this.pendingPayFormRef, this.pendingPayFormEl?.nativeElement, msg => this.notifications.error(msg), 'Completa correctamente los campos marcados antes de activar la cuenta.')) return;
    this.isConfirmingPendingPayment.set(true);
    this.confirmPendingPaymentError.set('');
    try {
      const form = this.pendingExternalPaymentForm;
      const response = await this.dialogs.confirmAndRun(
        { title: 'Activar cuenta con pago externo', message: `¿Confirmas que ${item.full_name} (${item.company}) ya pagó por ${form.method} y quieres activar su cuenta ahora?`, tone: 'warning', confirmText: 'Sí, activar cuenta' },
        () => this.subscriptionService.confirmPendingOwnerExternalPayment(item.id, {
          external_payment: { method: form.method, reference: (form.reference || '').trim(), receipts: form.receipts },
        }),
        (result: any) => ({ tone: 'success', message: result?.msg || 'Cuenta activada correctamente.' }),
        (error: any) => friendlyError(error, 'No fue posible activar la cuenta.'),
      );
      if (response !== null) {
        this.confirmingPendingExternalPayment.set(null);
        this.loadPendingOwnerSignups();
        this.loadTabData('users');
        this.subscriptionService.getAdminStats().subscribe({ next: stats => this.stats.set(stats), error: err => console.error('No fue posible refrescar las estadísticas globales:', err) });
      }
    } catch (error: any) {
      const msg = friendlyError(error, 'No fue posible activar la cuenta.');
      this.confirmPendingPaymentError.set(msg);
      this.notifications.error(msg);
    } finally {
      this.isConfirmingPendingPayment.set(false);
    }
  }

  openUserCreateModal(): void {
    this.manualUserForm = this.emptyManualUserForm();
    this.manualUserError.set('');
    this.subscriptionService.getAdminPlans().subscribe({
      next: ({ plans }) => {
        this.plans.set(plans.filter(plan => plan.is_active));
        this.manualUserForm.plan = this.plans()[0]?.code || '';
        this.showUserCreateModal.set(true);
      },
      error: () => { const msg = 'No fue posible cargar los planes disponibles.'; this.manualUserError.set(msg); this.notifications.error(msg); },
    });
  }

  closeUserCreateModal(): void {
    if (this.isCreatingUser()) return;
    this.showUserCreateModal.set(false);
    this.manualUserError.set('');
  }

  @ViewChild('manualUserFormRef') manualUserFormRef!: NgForm;
  @ViewChild('manualUserFormEl') manualUserFormEl!: ElementRef<HTMLFormElement>;

  async submitManualUser(): Promise<void> {
    const form = this.manualUserForm;
    if (!validateAndFocusFirstError(this.manualUserFormRef, this.manualUserFormEl?.nativeElement, msg => this.notifications.error(msg), 'Completa correctamente los campos marcados antes de crear la cuenta.')) return;
    this.isCreatingUser.set(true);
    this.manualUserError.set('');
    try {
      const hasExternalPayment = form.role === 'owner' && form.plan && form.plan !== 'gratis' && form.external_payment_enabled;
      const payload: any = { ...form, name: form.name.trim(), email: form.email.trim(), business_name: form.business_name?.trim(), business_phone: form.business_phone?.trim() };
      delete payload.external_payment_enabled;
      delete payload.external_payment_method;
      delete payload.external_payment_reference;
      delete payload.external_payment_receipts;
      payload.external_payment = hasExternalPayment
        ? { method: form.external_payment_method, reference: (form.external_payment_reference || '').trim(), receipts: form.external_payment_receipts }
        : undefined;
      const response: any = await this.dialogs.confirmAndRun(
        { title: 'Confirmar creación', message: form.role === 'owner' ? '¿Creas esta cuenta de propietario y su negocio?' : '¿Creas esta cuenta manualmente?', tone: 'info', confirmText: 'Crear cuenta' },
        () => this.subscriptionService.createManualUser(payload),
        (result: any) => result?.status === 'pending_payment'
          ? { tone: 'info', message: result.msg || 'Cuenta creada como pendiente de pago.' }
          : { tone: 'success', message: result?.msg || 'Cuenta creada y correo de acceso enviado.' },
        (error: any) => friendlyError(error, 'No fue posible crear la cuenta.'),
      );
      if (response !== null) {
        this.isCreatingUser.set(false);
        this.closeUserCreateModal();
        this.loadTabData('users');
        if (response?.status === 'pending_payment') this.loadPendingOwnerSignups();
        this.subscriptionService.getAdminStats().subscribe({ next: stats => this.stats.set(stats), error: err => console.error('No fue posible refrescar las estadísticas globales:', err) });
      }
    } catch (error: any) {
      const msg = friendlyError(error, 'No fue posible crear la cuenta.');
      this.manualUserError.set(msg);
      this.notifications.error(msg);
    } finally {
      this.isCreatingUser.set(false);
    }
  }

  async openResetModal(user: any): Promise<void> {
    if (!this.canSendAdminRecovery(user)) {
      this.notifications.error('Usuario inválido.');
      return;
    }
    this.isResetting.set(true);
    try {
      await this.dialogs.confirmAndRun(
        {
          title: 'Enviar recuperación de contraseña',
          message: `TuSala enviará un enlace seguro y de uso temporal al correo registrado de ${user.name} (${user.email || 'sin correo registrado'}).\n\nLa contraseña actual no se envía ni se muestra: el usuario definirá una nueva mediante el enlace recibido.`,
          tone: 'warning',
          confirmText: 'Confirmar envío',
        },
        () => this.subscriptionService.resetUserPassword(user.user_id, currentFrontendBaseUrl()),
        'Enlace de recuperación enviado correctamente.',
        (error: any) => friendlyError(error, 'No se pudo enviar el enlace de recuperación.'),
      );
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.isResetting.set(false);
    }
  }

  canSendAdminRecovery(user: any): boolean {
    return !!user?.user_id;
  }

  // ---------------------------------------------------------------------------
  // Gestionar negocio / gestión de cuentas
  // ---------------------------------------------------------------------------
  readonly managedBusiness = signal<{ id: number; name: string; ownerName: string } | null>(null);
  readonly editingUserId = signal<number | null>(null);
  private accountsChanged = false;

  openBusinessContext(businessId: number | null | undefined, name?: string | null, ownerName?: string | null): void {
    const id = Number(businessId);
    if (!Number.isInteger(id) || id <= 0) {
      this.notifications.error('Este propietario no tiene un negocio vinculado.');
      return;
    }
    this.managedBusiness.set({ id, name: String(name || ''), ownerName: String(ownerName || '') });
  }

  closeBusinessContext(): void {
    this.managedBusiness.set(null);
    // Lo que se cambió dentro del negocio (nombre, estado, espacios…) se
    // refleja en los listados y en la ficha abierta.
    this.refreshAfterManagement();
  }

  openBusinessFromEditor(business: { id: number; name: string }): void {
    this.editingUserId.set(null);
    this.openBusinessContext(business.id, business.name);
  }

  openUserEditor(userId: number | null | undefined): void {
    const id = Number(userId);
    if (!Number.isInteger(id) || id <= 0) return;
    this.editingUserId.set(id);
  }

  closeUserEditor(): void {
    this.editingUserId.set(null);
    if (this.accountsChanged && !this.managedBusiness()) this.refreshAfterManagement();
    this.accountsChanged = false;
  }

  onUserAccountChanged(user: AdminUserAccount): void {
    this.accountsChanged = true;
    // Actualiza en el momento la fila del directorio, sin recargar todo.
    this.users.update(list => list.map((item: any) => item.user_id === user.user_id ? { ...item, ...user } : item));
  }

  private refreshAfterManagement(): void {
    const tab = this.activeTab();
    if (tab === 'owners' || tab === 'users') this.loadTabData(tab);
  }
}
